import { useState, useEffect, useRef, useCallback } from "react";
import { createClient } from "@supabase/supabase-js";
import { SkilledTradesPage } from "./SkilledTrades";

// ============================================================
// CONFIGURATION — Replace with your actual keys
// ============================================================
const SUPABASE_URL = "https://opkueclytmmwmrhkbjqj.supabase.co";
const SUPABASE_ANON_KEY = "eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6Im9wa3VlY2x5dG1td21yaGtianFqIiwicm9sZSI6ImFub24iLCJpYXQiOjE3NzM3MTc3NjYsImV4cCI6MjA4OTI5Mzc2Nn0.LRbA-H02aB8pTGoJhlurN5A3GDmlZD3mWYvkck6VRJ4";
// GEMINI_API_KEY removed — now stored securely as a Supabase Edge Function secret
const ADMIN_EMAIL = "hirespotgy@gmail.com";
const JOB_CATEGORIES = ["Banking & Finance","Oil & Energy","Healthcare","Retail & FMCG","BPO & Call Centre","Government","Tech & Startups","Hospitality","Construction","Accounting","Legal","Admin & Clerical","Other"];

export const supabase = createClient(SUPABASE_URL, SUPABASE_ANON_KEY);
window.__supabase = supabase;

// ============================================================
// PLATFORM SETTINGS — loaded from Supabase at runtime
// ============================================================
let _platformSettings = {};
const loadPlatformSettings = async () => {
  const { data } = await supabase.from("platform_settings").select("key, value");
  if (data) data.forEach(r => { _platformSettings[r.key] = r.value; });
  return _platformSettings;
};
const getSetting = (key, fallback = "") => _platformSettings[key] ?? fallback;
const saveSetting = async (key, value) => {
  await supabase.from("platform_settings").upsert({ key, value, updated_at: new Date().toISOString() });
  _platformSettings[key] = value;
};

const SECRET_QUESTIONS = [
  "What was the name of your first school?",
  "What is your mother's maiden name?",
  "What was the name of your first pet?",
  "What was the name of the street you grew up on?",
  "What was your first employer's name?",
  "What was the last place you worked at?",
];

const getActiveWhatsApp = () => {
  const which = getSetting("active_whatsapp", "primary");
  return which === "secondary"
    ? getSetting("whatsapp_secondary", "")
    : getSetting("whatsapp_primary", "");
};

// ============================================================
// DESIGN TOKENS
// ============================================================
const styles = `
  @import url('https://fonts.googleapis.com/css2?family=Syne:wght@400;500;600;700;800&family=DM+Sans:ital,opsz,wght@0,9..40,300;0,9..40,400;0,9..40,500;1,9..40,300&display=swap');

  *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }

  :root {
    --bg:          #FAF8F4;
    --bg2:         #FFFFFF;
    --bg3:         #F3F0EA;
    --border:      #E2DDD4;
    --border2:     #EAE6DE;
    --text:        #1A1714;
    --text2:       #6B6560;
    --text3:       #9E9890;
    --gold:        #B8922A;
    --gold-light:  #D4A83C;
    --gold-dim:    #D4B97A;
    --green:       #2D9E4E;
    --green-bg:    #F0FAF3;
    --amber:       #C17D20;
    --amber-bg:    #FEF7EC;
    --red:         #D93025;
    --red-bg:      #FEF2F0;
    --blue:        #1A6FD4;
    --blue-bg:     #EEF5FE;
    --radius:      8px;
    --radius-lg:   12px;
    --radius-xl:   16px;
  }

  html, body, #root {
    height: 100%;
    background: var(--bg);
    color: var(--text);
    font-family: 'DM Sans', sans-serif;
    font-size: 15px;
    line-height: 1.6;
    -webkit-font-smoothing: antialiased;
  }

  ::-webkit-scrollbar { width: 6px; height: 6px; }
  ::-webkit-scrollbar-track { background: var(--bg); }
  ::-webkit-scrollbar-thumb { background: var(--border); border-radius: 3px; }
  ::-webkit-scrollbar-thumb:hover { background: var(--text3); }

  .font-display { font-family: 'Syne', sans-serif; }

  @keyframes fadeUp {
    from { opacity: 0; transform: translateY(20px); }
    to   { opacity: 1; transform: translateY(0); }
  }
  @keyframes fadeIn { from { opacity: 0; } to { opacity: 1; } }
  @keyframes shimmer {
    0%   { background-position: -200% center; }
    100% { background-position:  200% center; }
  }
  @keyframes pulse { 0%, 100% { opacity: 1; } 50% { opacity: 0.4; } }
  @keyframes spin { to { transform: rotate(360deg); } }
  @keyframes glow {
    0%, 100% { box-shadow: 0 0 20px rgba(201,168,76,0.15); }
    50%       { box-shadow: 0 0 40px rgba(201,168,76,0.35); }
  }
  @keyframes borderGlow {
    0%, 100% { border-color: var(--gold-dim); }
    50%       { border-color: var(--gold); }
  }
  @keyframes slideIn {
    from { opacity: 0; transform: translateX(20px); }
    to   { opacity: 1; transform: translateX(0); }
  }

  .animate-fade-up { animation: fadeUp 0.5s ease forwards; }
  .animate-fade-in { animation: fadeIn 0.3s ease forwards; }
  .animate-slide-in { animation: slideIn 0.3s ease forwards; }

  .gold-text {
    background: linear-gradient(135deg, #B8922A 0%, #D4A83C 50%, #B8922A 100%);
    background-size: 200% auto;
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
    animation: shimmer 3s linear infinite;
  }

  /* Buttons */
  .btn {
    display: inline-flex; align-items: center; justify-content: center; gap: 8px;
    padding: 10px 20px; border-radius: var(--radius); font-size: 14px;
    font-weight: 500; cursor: pointer; transition: all 0.2s ease;
    border: 1px solid transparent; font-family: 'DM Sans', sans-serif;
    letter-spacing: 0.01em; white-space: nowrap;
  }
  .btn:disabled { opacity: 0.4; cursor: not-allowed; }
  .btn-gold { background: var(--gold); color: #FAF8F4; border-color: var(--gold); font-weight: 600; }
  .btn-gold:hover:not(:disabled) { background: var(--gold-light); border-color: var(--gold-light); transform: translateY(-1px); box-shadow: 0 8px 24px rgba(201,168,76,0.3); }
  .btn-gold:active:not(:disabled) { transform: translateY(0); }
  .btn-outline { background: transparent; color: var(--text); border-color: var(--border); }
  .btn-outline:hover:not(:disabled) { background: var(--bg3); border-color: var(--text3); }
  .btn-ghost { background: transparent; color: var(--text2); border-color: transparent; }
  .btn-ghost:hover:not(:disabled) { background: var(--bg3); color: var(--text); }
  .btn-danger { background: transparent; color: var(--red); border-color: var(--red); }
  .btn-danger:hover:not(:disabled) { background: var(--red-bg); }
  .btn-green { background: var(--green); color: #FAF8F4; border-color: var(--green); font-weight: 600; }
  .btn-green:hover:not(:disabled) { opacity: 0.9; transform: translateY(-1px); }
  .btn-whatsapp { background: #25D366; color: #fff; border-color: #25D366; font-weight: 600; }
  .btn-whatsapp:hover:not(:disabled) { background: #20bd5c; border-color: #20bd5c; transform: translateY(-1px); box-shadow: 0 8px 24px rgba(37,211,102,0.3); }
  .btn-sm { padding: 6px 14px; font-size: 13px; }
  .btn-lg { padding: 14px 28px; font-size: 15px; font-weight: 600; }
  .btn-xl { padding: 18px 36px; font-size: 16px; font-weight: 600; letter-spacing: 0.02em; }

  /* Cards */
  .card { background: var(--bg2); border: 1px solid var(--border2); border-radius: var(--radius-lg); padding: 24px; }
  .card-hover { transition: all 0.2s ease; cursor: pointer; }
  .card-hover:hover { border-color: var(--border); background: var(--bg3); transform: translateY(-1px); box-shadow: 0 8px 32px rgba(0,0,0,0.3); }

  /* Inputs */
  .input {
    width: 100%; background: var(--bg); border: 1px solid var(--border2);
    border-radius: var(--radius); padding: 10px 14px; color: var(--text);
    font-size: 14px; font-family: 'DM Sans', sans-serif;
    transition: border-color 0.2s ease, box-shadow 0.2s ease; outline: none;
  }
  .input:focus { border-color: var(--gold-dim); box-shadow: 0 0 0 3px rgba(201,168,76,0.1); }
  .input::placeholder { color: var(--text3); }
  textarea.input { resize: vertical; min-height: 100px; }
  select.input {
    appearance: none;
    background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 24 24' fill='none' stroke='%238B949E' stroke-width='2'%3E%3Cpath d='m6 9 6 6 6-6'/%3E%3C/svg%3E");
    background-repeat: no-repeat; background-position: right 12px center; padding-right: 36px;
  }

  .label { display: block; font-size: 12px; font-weight: 500; color: var(--text2); margin-bottom: 6px; letter-spacing: 0.05em; text-transform: uppercase; }

  /* Badges */
  .badge { display: inline-flex; align-items: center; gap: 4px; padding: 2px 8px; border-radius: 4px; font-size: 11px; font-weight: 600; letter-spacing: 0.03em; text-transform: uppercase; }
  .badge-gold   { background: rgba(201,168,76,0.15); color: var(--gold); }
  .badge-green  { background: var(--green-bg); color: var(--green); }
  .badge-amber  { background: var(--amber-bg); color: var(--amber); }
  .badge-red    { background: var(--red-bg); color: var(--red); }
  .badge-blue   { background: var(--blue-bg); color: var(--blue); }
  .badge-gray   { background: var(--bg3); color: var(--text3); }

  /* Score pill */
  .score-pill { display: inline-flex; align-items: center; justify-content: center; min-width: 56px; height: 32px; padding: 0 12px; border-radius: 16px; font-size: 14px; font-weight: 800; font-family: 'Syne', sans-serif; letter-spacing: 0.02em; box-shadow: 0 2px 8px rgba(0,0,0,0.1); }
  .score-excellent { background: rgba(63,185,80,0.15); color: var(--green); border: 1px solid rgba(63,185,80,0.3); }
  .score-good      { background: rgba(201,168,76,0.15); color: var(--gold); border: 1px solid rgba(201,168,76,0.3); }
  .score-fair      { background: rgba(210,153,34,0.15); color: var(--amber); border: 1px solid rgba(210,153,34,0.3); }
  .score-poor      { background: rgba(248,81,73,0.15); color: var(--red); border: 1px solid rgba(248,81,73,0.3); }

  .divider { border: none; border-top: 1px solid var(--border2); margin: 20px 0; }

  .spinner { width: 20px; height: 20px; border: 2px solid var(--border); border-top-color: var(--gold); border-radius: 50%; animation: spin 0.8s linear infinite; flex-shrink: 0; }

  /* Sidebar — premium dark */
  .sidebar { width: 240px; height: 100vh; background: #1C1917; border-right: none; display: flex; flex-direction: column; flex-shrink: 0; position: sticky; top: 0; }
  .nav-item { display: flex; align-items: center; gap: 10px; padding: 10px 16px; border-radius: var(--radius); color: rgba(255,255,255,0.45); font-size: 14px; cursor: pointer; transition: all 0.15s ease; margin: 1px 8px; border: 1px solid transparent; }
  .nav-item:hover { background: rgba(255,255,255,0.07); color: rgba(255,255,255,0.85); }
  .nav-item.active { background: rgba(184,146,42,0.18); color: #D4A83C; border-color: rgba(184,146,42,0.25); }
  .nav-item .nav-icon { width: 16px; height: 16px; flex-shrink: 0; }

  /* Stat cards */
  .stat-card { background: var(--bg2); border: 1px solid var(--border2); border-radius: var(--radius-lg); padding: 20px; }
  .stat-value { font-family: 'Syne', sans-serif; font-size: 28px; font-weight: 700; color: var(--text); line-height: 1; }
  .stat-label { font-size: 12px; color: var(--text3); margin-top: 4px; text-transform: uppercase; letter-spacing: 0.05em; }
  .stat-delta { font-size: 12px; margin-top: 8px; }
  .stat-delta.up { color: var(--green); }
  .stat-delta.down { color: var(--red); }

  /* Progress bar */
  .progress-bar { height: 4px; background: var(--bg3); border-radius: 2px; overflow: hidden; }
  .progress-fill { height: 100%; border-radius: 2px; transition: width 1s cubic-bezier(0.4, 0, 0.2, 1); }

  .tag { display: inline-flex; align-items: center; padding: 3px 10px; background: var(--bg3); border: 1px solid var(--border); border-radius: 4px; font-size: 12px; color: var(--text2); }

  /* Table */
  .data-table { width: 100%; border-collapse: collapse; }
  .data-table th { text-align: left; padding: 10px 14px; font-size: 11px; font-weight: 600; color: var(--text3); text-transform: uppercase; letter-spacing: 0.06em; border-bottom: 1px solid var(--border2); }
  .data-table td { padding: 12px 14px; font-size: 14px; color: var(--text2); border-bottom: 1px solid var(--border2); }
  .data-table tr:last-child td { border-bottom: none; }
  .data-table tr:hover td { background: var(--bg3); color: var(--text); }

  /* Drop zone */
  .drop-zone { border: 2px dashed var(--border); border-radius: var(--radius-xl); padding: 60px 40px; text-align: center; cursor: pointer; transition: all 0.3s ease; background: var(--bg2); position: relative; overflow: hidden; }
  .drop-zone:hover, .drop-zone.dragging { border-color: var(--gold); background: rgba(201,168,76,0.04); animation: borderGlow 2s ease infinite; }

  /* Modal */
  .modal-backdrop { position: fixed; inset: 0; background: rgba(0,0,0,0.7); backdrop-filter: blur(4px); z-index: 100; display: flex; align-items: center; justify-content: center; padding: 20px; animation: fadeIn 0.2s ease; }
  .modal { background: var(--bg2); border: 1px solid var(--border); border-radius: var(--radius-xl); padding: 32px; max-width: 560px; width: 100%; max-height: 90vh; overflow-y: auto; animation: fadeUp 0.25s ease; }

  /* Toast */
  .toast-container { position: fixed; bottom: 24px; right: 24px; z-index: 200; display: flex; flex-direction: column; gap: 8px; }
  .toast { background: var(--bg2); border: 1px solid var(--border); border-radius: var(--radius); padding: 12px 16px; display: flex; align-items: center; gap: 10px; font-size: 14px; animation: fadeUp 0.3s ease; box-shadow: 0 8px 32px rgba(0,0,0,0.4); min-width: 280px; }
  .toast-success { border-color: rgba(63,185,80,0.4); }
  .toast-error   { border-color: rgba(248,81,73,0.4); }
  .toast-info    { border-color: rgba(201,168,76,0.4); }

  /* Candidate card */
  .candidate-card { background: var(--bg2); border: 1px solid var(--border2); border-radius: var(--radius-lg); overflow: hidden; transition: all 0.2s ease; box-shadow: 0 2px 12px rgba(26,23,20,0.06); }
  .candidate-card:hover { border-color: rgba(184,146,42,0.3); box-shadow: 0 8px 32px rgba(26,23,20,0.1); transform: translateY(-1px); }
  .candidate-card.locked { opacity: 0.7; }

  /* Match card */
  .match-card { background: var(--bg2); border: 1px solid var(--border2); border-radius: var(--radius-lg); overflow: hidden; transition: all 0.2s ease; cursor: pointer; }
  .match-card:hover { border-color: var(--gold-dim); transform: translateY(-2px); box-shadow: 0 12px 40px rgba(0,0,0,0.3); }

  /* Upgrade request card */
  .upgrade-request-card { background: var(--bg2); border: 1px solid rgba(201,168,76,0.2); border-radius: var(--radius-lg); padding: 20px 24px; transition: all 0.2s ease; }
  .upgrade-request-card:hover { border-color: rgba(201,168,76,0.4); }

  /* Notification dot */
  .notif-dot { width: 8px; height: 8px; background: var(--red); border-radius: 50%; flex-shrink: 0; }
  .notif-badge { background: var(--red); color: #fff; border-radius: 10px; padding: 1px 7px; font-size: 11px; font-weight: 700; margin-left: auto; }

  /* Loading skeleton */
  .skeleton { background: linear-gradient(90deg, var(--bg3) 25%, var(--border2) 50%, var(--bg3) 75%); background-size: 200% 100%; animation: shimmer 1.5s infinite; border-radius: var(--radius); }

  .applicant-sheet-handle { display: none; }
  .sheet-backdrop-emp { display: none; }

  /* Mobile — push toast above bottom nav */
  .mobile-header { display: none; }
  @media (max-width: 768px) {
    .sidebar { display: none; }
    .mobile-header { display: flex !important; }
    .grid-2, .grid-3, .grid-4 { grid-template-columns: 1fr !important; }
    main { padding: 20px !important; }
    /* Push toast above bottom nav */
    .toast-container { bottom: 80px !important; right: 12px !important; left: 12px !important; }
    .toast { min-width: unset !important; width: 100%; }
  }

  /* Global Mobile Bottom Nav */
  .app-bottom-nav { display: none; }
  @media (max-width: 768px) {
    /* ── BOTTOM NAV ── */
    .app-bottom-nav {
      display: flex;
      position: fixed;
      bottom: 0; left: 0; right: 0;
      z-index: 9999;
      background: rgba(14,13,11,0.97);
      backdrop-filter: blur(20px);
      -webkit-backdrop-filter: blur(20px);
      border-top: 1px solid rgba(255,255,255,0.09);
      height: 68px;
      align-items: stretch;
      padding: 0 4px;
      padding-bottom: env(safe-area-inset-bottom, 0px);
    }
    .app-nav-tab {
      flex: 1;
      min-width: 0;
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      gap: 3px;
      cursor: pointer;
      padding: 8px 2px;
      border-radius: 12px;
      transition: background 0.15s ease;
      color: rgba(250,248,244,0.35);
      font-family: 'DM Sans', sans-serif;
      font-size: 10px;
      font-weight: 500;
      letter-spacing: 0.01em;
      border: none;
      background: transparent;
      -webkit-tap-highlight-color: transparent;
      overflow: hidden;
    }
    .app-nav-tab:active { background: rgba(255,255,255,0.05); }
    .app-nav-tab.active { color: #B8922A; }
    .app-nav-tab svg { flex-shrink: 0; width: 20px; height: 20px; }
    .app-nav-tab span, .app-nav-tab { white-space: nowrap; overflow: hidden; text-overflow: ellipsis; max-width: 100%; }

    /* Login popup sheet */
    .app-login-sheet {
      position: fixed;
      bottom: 72px; left: 12px; right: 12px;
      background: #1A1714;
      border: 1px solid rgba(255,255,255,0.1);
      border-radius: 20px;
      padding: 20px 16px;
      z-index: 10000;
      animation: fadeUp 0.2s ease;
      box-shadow: 0 -8px 40px rgba(0,0,0,0.6);
    }
    .app-login-sheet-btn {
      display: flex;
      align-items: center;
      gap: 14px;
      width: 100%;
      padding: 14px 16px;
      border-radius: 12px;
      border: 1px solid rgba(255,255,255,0.08);
      background: rgba(255,255,255,0.03);
      cursor: pointer;
      margin-bottom: 10px;
      transition: background 0.15s;
      font-family: 'DM Sans', sans-serif;
      text-align: left;
    }
    .app-login-sheet-btn:last-child { margin-bottom: 0; }
    .app-login-sheet-btn:active { background: rgba(255,255,255,0.07); }
    .app-sheet-icon { width: 40px; height: 40px; border-radius: 10px; display: flex; align-items: center; justify-content: center; flex-shrink: 0; }
    .app-sheet-title { font-size: 14px; font-weight: 600; color: #FAF8F4; line-height: 1.3; }
    .app-sheet-sub { font-size: 11px; color: rgba(250,248,244,0.4); margin-top: 2px; }
    .app-sheet-backdrop { position: fixed; inset: 0; z-index: 9998; }

    /* ── ALL-PAGE MOBILE FIXES ── */

    /* Universal nav bar fix — all pages */
    nav, .page-nav {
      padding-left: 16px !important;
      padding-right: 16px !important;
    }

    /* SeekerDashboard header */
    .seeker-header {
      padding: 0 16px !important;
    }
    .seeker-header-tabs {
      display: none !important;
    }
    .seeker-header-actions {
      gap: 6px !important;
    }

    /* SeekerDashboard content area */
    .seeker-content {
      padding: 20px 16px 100px !important;
    }

    /* JobsPage content */
    .jobs-content {
      padding: 32px 16px 100px !important;
    }

    /* Auth pages (EmployerAuth, SeekerAuth) — full width on mobile */
    .auth-page {
      padding: 20px 16px 100px !important;
      align-items: flex-start !important;
    }
    .auth-card {
      padding: 24px 20px !important;
    }

    /* JobSeekerPage upload/results */
    .seeker-upload-page {
      padding: 80px 16px 100px !important;
    }

    /* Make content not hide behind bottom nav */
    .page-content-bottom {
      padding-bottom: 100px !important;
    }

    /* Hide back button text overflow on mobile navs */
    .mobile-hide { display: none !important; }

    /* Stat/info rows that overflow */
    .mobile-stack {
      flex-direction: column !important;
      align-items: flex-start !important;
    }

    /* Profile header — stack on mobile */
    .profile-header-row {
      flex-direction: column !important;
      align-items: flex-start !important;
      gap: 12px !important;
    }
    .profile-header-right {
      text-align: left !important;
    }

    /* CV expired banner */
    .cv-banner {
      flex-direction: column !important;
      align-items: flex-start !important;
      gap: 12px !important;
    }

    /* JobSeekerPage upload nav */
    .page-nav {
      padding-left: 16px !important;
      padding-right: 16px !important;
    }

    /* Upload page main content — give more breathing room */
    .seeker-upload-page {
      padding-top: 72px !important;
      padding-left: 0 !important;
      padding-right: 0 !important;
    }

    /* Results nav — hide "New scan" label on tiny screens, keep icon */
    .page-nav .btn-outline span { display: none; }

    /* Auth page full-width on mobile */
    .auth-page {
      padding: 20px 16px 100px !important;
      align-items: flex-start !important;
      justify-content: flex-start !important;
    }
    .auth-page > div { margin-top: 20px; }

    /* TermsPage */
    .terms-content {
      padding: 24px 16px 100px !important;
    }

    /* ── EMPLOYER DASHBOARD MOBILE ── */

    /* Hide the sidebar entirely on mobile */
    .sidebar {
      display: none !important;
    }

    /* Employer main content — full width, proper padding */
    .employer-main {
      padding: 16px 16px 100px !important;
    }

    /* Employer mobile top bar — shown only on mobile */
    .employer-mobile-header {
      display: flex !important;
    }

    /* Stat cards grid — 2 cols on mobile */
    .employer-stats-grid {
      grid-template-columns: 1fr 1fr !important;
      gap: 10px !important;
    }

    /* Welcome header row — stack on mobile */
    .employer-welcome-row {
      flex-direction: column !important;
      align-items: flex-start !important;
      gap: 12px !important;
      margin-bottom: 16px !important;
    }

    /* Dashboard two-column split — stack on mobile */
    .employer-dashboard-split {
      grid-template-columns: 1fr !important;
    }

    /* Job listing rows — stack details on mobile */
    .employer-job-row {
      flex-direction: column !important;
      align-items: flex-start !important;
      gap: 10px !important;
    }

    /* Announcement banner */
    .employer-announcement {
      margin: 0 0 16px !important;
    }

    /* ── PIPELINE MOBILE ── */
    .pipeline-mobile-hint { display: block !important; }
    .pipeline-move-btns button {
      padding: 6px 10px !important;
      font-size: 12px !important;
      min-height: 32px !important;
    }

    /* ── PIPELINE MOBILE ── */
    .pipeline-mobile-hint { display: block !important; }
    .pipeline-move-btns button {
      padding: 6px 10px !important;
      font-size: 12px !important;
      min-height: 32px !important;
    }
    /* Make pipeline columns full-width scroll on mobile */
    .pipeline-cols-wrap {
      min-width: unset !important;
      flex-direction: column !important;
      gap: 16px !important;
    }

    /* ── APPLICANT VIEW TOOLBAR MOBILE ── */
    .applicant-header-row1 {
      flex-wrap: nowrap !important;
    }
    .applicant-toolbar {
      flex-wrap: wrap !important;
      gap: 8px !important;
    }

    /* ── MY JOBS MOBILE POLISH ── */

    /* Job card — stack vertically on mobile */
    .job-card-row {
      flex-direction: column !important;
      align-items: flex-start !important;
      gap: 12px !important;
    }

    /* Job card meta row — wrap */
    .job-card-meta {
      flex-wrap: wrap !important;
      gap: 6px !important;
    }

    /* Job card stats + actions — full width row */
    .job-card-actions {
      flex-direction: row !important;
      flex-wrap: wrap !important;
      align-items: center !important;
      gap: 8px !important;
      width: 100% !important;
    }

    /* Hide Feature/Share/Edit text on mobile, show icons only */
    .job-btn-label { display: none !important; }

    /* Expiry warning — stack buttons */
    .expiry-warn-row {
      flex-direction: column !important;
      align-items: flex-start !important;
      gap: 8px !important;
    }

    /* Dashboard home — listings+activity split goes single column */
    .employer-home-split {
      grid-template-columns: 1fr !important;
    }

    /* ── PROFILE TAB ── */
    .app-nav-tab.profile-tab { display: flex !important; }

    /* Stats grid inside candidate card — 2x2 on mobile */
    .cand-stats-grid {
      grid-template-columns: 1fr 1fr !important;
    }

    /* Card action bar — stack on mobile */
    .cand-action-bar {
      flex-direction: column !important;
      align-items: stretch !important;
      gap: 8px !important;
    }
    .cand-action-bar > div {
      justify-content: stretch !important;
      gap: 6px !important;
    }
    .cand-action-bar button {
      flex: 1 !important;
      justify-content: center !important;
    }

    /* Full report — bottom sheet on mobile */
    .applicant-full-report {
      position: fixed !important;
      bottom: 68px !important;
      left: 0 !important; right: 0 !important;
      max-height: 75vh !important;
      overflow-y: auto !important;
      background: #1A1714 !important;
      border-radius: 20px 20px 0 0 !important;
      border: 1px solid rgba(255,255,255,0.1) !important;
      border-bottom: none !important;
      padding: 0 !important;
      z-index: 500 !important;
      animation: slideUpSheet 0.25s ease !important;
      box-shadow: 0 -8px 40px rgba(0,0,0,0.6) !important;
    }
    .applicant-full-report-inner {
      padding: 20px 20px 32px !important;
    }
    .applicant-sheet-handle {
      display: block !important;
    }
    .report-grid-2 {
      grid-template-columns: 1fr !important;
    }
    @keyframes slideUpSheet {
      from { transform: translateY(100%); opacity: 0; }
      to   { transform: translateY(0);   opacity: 1; }
    }
    .sheet-backdrop-emp {
      display: block !important;
      position: fixed;
      inset: 0;
      background: rgba(0,0,0,0.6);
      z-index: 499;
    }

    /* ── PILL ROWS: scroll horizontally instead of overflowing ── */
    .lp-platform-pills {
      flex-wrap: nowrap !important;
      overflow-x: auto !important;
      justify-content: flex-start !important;
      padding-bottom: 4px !important;
      -webkit-overflow-scrolling: touch;
      scrollbar-width: none;
    }
    .lp-platform-pills::-webkit-scrollbar { display: none; }

    /* ── FEATURED JOB PILLS: smaller min-width on mobile ── */
    .lp-featured-pill {
      min-width: 140px !important;
    }

    /* ── JOBS PAGE NAV: shrink buttons on mobile so they don't overflow ── */
    .jobs-page-nav-actions {
      gap: 6px !important;
    }
    .jobs-page-nav-actions button {
      font-size: 11px !important;
      padding: 5px 10px !important;
    }
  }

  /* ── VERY SMALL PHONES (< 360px): tighten nav tab labels ── */
  @media (max-width: 360px) {
    .app-nav-tab {
      font-size: 8px !important;
      gap: 2px !important;
      padding: 6px 1px !important;
    }
    .app-nav-tab svg {
      width: 18px !important;
      height: 18px !important;
    }
    .lp-pill-item {
      font-size: 10px !important;
      padding: 5px 10px !important;
    }
  }

  /* Utility */
  .flex { display: flex; }
  .flex-col { display: flex; flex-direction: column; }
  .items-center { align-items: center; }
  .items-start { align-items: flex-start; }
  .justify-between { justify-content: space-between; }
  .justify-center { justify-content: center; }
  .gap-2 { gap: 8px; }
  .gap-3 { gap: 12px; }
  .gap-4 { gap: 16px; }
  .gap-6 { gap: 24px; }
  .gap-8 { gap: 32px; }
  .w-full { width: 100%; }
  .text-sm { font-size: 13px; }
  .text-xs { font-size: 11px; }
  .text-muted { color: var(--text2); }
  .text-gold { color: var(--gold); }
  .text-green { color: var(--green); }
  .text-red { color: var(--red); }
  .font-bold { font-weight: 700; }
  .font-medium { font-weight: 500; }
  .mt-2 { margin-top: 8px; }
  .mt-4 { margin-top: 16px; }
  .mb-2 { margin-bottom: 8px; }
  .mb-4 { margin-bottom: 16px; }
  .mb-6 { margin-bottom: 24px; }
  .truncate { overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
  .grid-2 { display: grid; grid-template-columns: 1fr 1fr; gap: 16px; }
  .grid-3 { display: grid; grid-template-columns: repeat(3, 1fr); gap: 16px; }
  .grid-4 { display: grid; grid-template-columns: repeat(4, 1fr); gap: 16px; }
`;

// ============================================================
// ICONS
// ============================================================
const Icon = ({ name, size = 16, color = "currentColor" }) => {
  const icons = {
    briefcase: <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><rect x="2" y="7" width="20" height="14" rx="2"/><path d="M16 7V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v2"/><line x1="12" y1="12" x2="12" y2="12"/><path d="M2 12h20"/></svg>,
    upload: <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="17 8 12 3 7 8"/><line x1="12" y1="3" x2="12" y2="15"/></svg>,
    users: <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></svg>,
    chart: <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="20" x2="18" y2="10"/><line x1="12" y1="20" x2="12" y2="4"/><line x1="6" y1="20" x2="6" y2="14"/></svg>,
    settings: <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="3"/><path d="M19.07 4.93l-1.41 1.41M4.93 4.93l1.41 1.41M4.93 19.07l1.41-1.41M19.07 19.07l-1.41-1.41M20 12h2M2 12h2M12 20v2M12 2v2"/></svg>,
    plus: <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>,
    lock: <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="11" width="18" height="11" rx="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/></svg>,
    check: <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>,
    x: <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>,
    arrow_right: <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><line x1="5" y1="12" x2="19" y2="12"/><polyline points="12 5 19 12 12 19"/></svg>,
    star: <svg width={size} height={size} viewBox="0 0 24 24" fill={color} stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/></svg>,
    zap: <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2"/></svg>,
    eye: <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>,
    download: <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>,
    mail: <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/><polyline points="22,6 12,13 2,6"/></svg>,
    logout: <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/><polyline points="16 17 21 12 16 7"/><line x1="21" y1="12" x2="9" y2="12"/></svg>,
    trending_up: <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="23 6 13.5 15.5 8.5 10.5 1 18"/><polyline points="17 6 23 6 23 12"/></svg>,
    map_pin: <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/><circle cx="12" cy="10" r="3"/></svg>,
    clock: <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>,
    dollar: <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><line x1="12" y1="1" x2="12" y2="23"/><path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"/></svg>,
    shield: <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/></svg>,
    alert: <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>,
    file: <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M13 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V9z"/><polyline points="13 2 13 9 20 9"/></svg>,
    crown: <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M2 20h20M4 20L2 8l5 5 5-7 5 7 5-5-2 12"/></svg>,
    refresh: <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="23 4 23 10 17 10"/><polyline points="1 20 1 14 7 14"/><path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15"/></svg>,
    phone: <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07A19.5 19.5 0 0 1 4.69 13a19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 3.6 2.22h3a2 2 0 0 1 2 1.72c.127.96.361 1.903.7 2.81a2 2 0 0 1-.45 2.11L7.91 9.91a16 16 0 0 0 6.06 6.06l1.27-1.27a2 2 0 0 1 2.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0 1 22 16.92z"/></svg>,
    bell: <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"/><path d="M13.73 21a2 2 0 0 1-3.46 0"/></svg>,
    megaphone: <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M3 11l19-9-9 19-2-8-8-2z"/></svg>,
    toggle: <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><rect x="1" y="5" width="22" height="14" rx="7"/><circle cx="16" cy="12" r="3" fill={color}/></svg>,
    whatsapp: <svg width={size} height={size} viewBox="0 0 24 24" fill={color}><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 0 1-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 0 1-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 0 1 2.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0 0 12.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 0 0 5.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 0 0-3.48-8.413z"/></svg>,
    receipt: <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M4 2v20l3-2 3 2 3-2 3 2 3-2V2"/><path d="M8 7h8M8 11h8M8 15h4"/></svg>,
    info: <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><line x1="12" y1="16" x2="12" y2="12"/><line x1="12" y1="8" x2="12.01" y2="8"/></svg>,
    search: <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>,
    hammer: <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="m15 12-8.5 8.5c-.83.83-2.17.83-3 0a2.12 2.12 0 0 1 0-3L12 9"/><path d="M17.64 15 22 10.64"/><path d="M20.91 11.7l-1.25-1.25c-.6-.6-.93-1.4-.93-2.25v-.86L16.01 4.6a5.56 5.56 0 0 0-3.94-1.64H9l.92.82A6.18 6.18 0 0 1 12 8.4v1.56l2 2h2.47l2.26 1.91z"/></svg>,
    wrench: <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M14.7 6.3a1 1 0 0 0 0 1.4l1.6 1.6a1 1 0 0 0 1.4 0l3.77-3.77a6 6 0 0 1-7.94 7.94l-6.91 6.91a2.12 2.12 0 0 1-3-3l6.91-6.91a6 6 0 0 1 7.94-7.94l-3.76 3.76z"/></svg>,
    brush: <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M9.06 11.9l8.07-8.06a2.85 2.85 0 1 1 4.03 4.03l-8.06 8.08"/><path d="M7.07 14.94c-1.66 0-3 1.35-3 3.02 0 1.33-2.5 1.52-2 2.02 1.08 1.1 2.49 2.02 4 2.02 2.2 0 4-1.8 4-4.04a3.01 3.01 0 0 0-3-3.02z"/></svg>,
    car: <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M5 17h14M5 17a2 2 0 1 1-4 0 2 2 0 0 1 4 0zM19 17a2 2 0 1 1-4 0 2 2 0 0 1 4 0zM3 17V9l2-5h14l2 5v8M3 9h18"/></svg>,
    edit: <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>,
    archive: <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="21 8 21 21 3 21 3 8"/><rect x="1" y="3" width="22" height="5"/><line x1="10" y1="12" x2="14" y2="12"/></svg>,
    trash: <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/><path d="M10 11v6M14 11v6"/><path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2"/></svg>,
    chevron_up: <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="18 15 12 9 6 15"/></svg>,
    chevron_down: <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="6 9 12 15 18 9"/></svg>,
  };
  return icons[name] || null;
};

// ============================================================
// UTILITIES
// ============================================================
const formatGYD = (amount) => `GYD ${Number(amount || 0).toLocaleString()}`;
const timeAgo = (date) => {
  if (!date) return "—";
  const diff = Date.now() - new Date(date).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  return `${Math.floor(hours / 24)}d ago`;
};
const getScoreClass = (score) => {
  if (score >= 90) return "score-excellent";
  if (score >= 70) return "score-good";
  if (score >= 50) return "score-fair";
  return "score-poor";
};
const getScoreColor = (score) => {
  if (score >= 90) return "#3FB950";
  if (score >= 70) return "#C9A84C";
  if (score >= 50) return "#D29922";
  return "#F85149";
};
const getPlanBadge = (plan) => {
  const map = { free: "badge-gray", starter: "badge-gold", professional: "badge-blue" };
  return map[plan] || "badge-gray";
};
const openWhatsApp = (phone, message) => {
  const num = phone.replace(/\D/g, "");
  window.open(`https://wa.me/${num}?text=${encodeURIComponent(message)}`, "_blank");
};

// ============================================================
// TOAST
// ============================================================
let toastFn = null;
const Toast = () => {
  const [toasts, setToasts] = useState([]);
  useEffect(() => {
    toastFn = (msg, type = "info") => {
      const id = Date.now();
      setToasts(t => [...t, { id, msg, type }]);
      setTimeout(() => setToasts(t => t.filter(x => x.id !== id)), 3500);
    };
  }, []);
  const icons = { success: "check", error: "x", info: "zap" };
  const colors = { success: "#3FB950", error: "#F85149", info: "#C9A84C" };
  return (
    <div className="toast-container">
      {toasts.map(t => (
        <div key={t.id} className={`toast toast-${t.type}`}>
          <Icon name={icons[t.type]} size={14} color={colors[t.type]} />
          <span style={{ fontSize: 14, color: "var(--text)" }}>{t.msg}</span>
        </div>
      ))}
    </div>
  );
};
const toast = (msg, type) => toastFn?.(msg, type);

// ============================================================
// GEMINI AI ENGINE — proxied via Supabase Edge Function
// ============================================================
const callGemini = async (prompt, pdfBase64 = null) => {
  const res = await fetch(
    `${SUPABASE_URL}/functions/v1/gemini-scan`,
    {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "Authorization": `Bearer ${SUPABASE_ANON_KEY}`,
      },
      body: JSON.stringify({ prompt, pdfBase64: pdfBase64 || null }),
    }
  );
  const data = await res.json();
  if (!res.ok) throw new Error(data.error || "Scan failed. Please try again.");
  return data.text || "";
};

const analyzeCV = async (pdfBase64, jobs, existingCandidate = null) => {
  const jobsContext = jobs.map(j => `
    JOB ID: ${j.id}
    Title: ${j.title}
    Category: ${j.category || "General"}
    Description: ${j.description}
    Requirements: ${j.requirements}
    Location: ${j.location}
    Work Type: ${j.work_type}
    Salary: ${j.salary_min ? `GYD ${j.salary_min} - ${j.salary_max}` : "Not specified"}
  `).join("\n---\n");

  const scoringRules = `
SCORING RULES — follow these strictly:

1. FIELD RELEVANCE GATE: If the candidate's entire career history and education is in a completely different field from the job (e.g. healthcare applying for retail, engineering applying for finance, medicine applying for hospitality), apply a hard maximum score of 50 regardless of transferable skills. Transferable skills alone do not constitute a strong match. State this clearly in the match_explanation.

2. SCORE THRESHOLDS:
   - 90–100: Exceptional. Near-perfect skill, experience, seniority and field alignment.
   - 75–89: Strong. Most requirements met, same or adjacent field, right seniority level.
   - 60–74: Moderate. Some relevant experience, field is adjacent or partially relevant.
   - Below 60: Do not include in results. These are not useful matches.

3. VERDICT: Write one analytical sentence that tells the employer something concrete and actionable. Do NOT use hype words like "formidable", "exceptional", "ideal". Be honest — if there's a mismatch, say so clearly.

4. GREEN FLAGS: Only include genuine, specific positive signals that are actually present in the CV. If no strong green flags exist, return an empty array. Never manufacture flags to fill the field.

5. RED FLAGS: Be honest. If the candidate is clearly overqualified, under-experienced, or from a different field, flag it. This is the most useful thing the AI can tell an employer.

6. SENIORITY FIT: If a candidate is significantly overqualified (e.g. 10+ years for an entry role), mark as "overqualified" and reflect this in the score.`;

  // -- RESCAN (returning seeker, existing parsed data) --
  if (existingCandidate) {
    const rescanPrompt = `You are JobSpot's AI matching engine for Guyana's job market. Match this candidate profile against the provided job listings.

CANDIDATE PROFILE:
Name: ${existingCandidate.name || "Unknown"}
Seniority: ${existingCandidate.seniority_level || "unknown"}
Experience: ${existingCandidate.total_experience_years || 0} years
Career trajectory: ${existingCandidate.career_trajectory || "unknown"}
Industries worked in: ${(existingCandidate.industries || []).join(", ") || "unknown"}
Stability: ${existingCandidate.stability_signal || "unknown"}
Technical skills: ${(existingCandidate.skills?.technical || existingCandidate.top_skills || []).join(", ")}
Soft skills: ${(existingCandidate.skills?.soft || []).join(", ")}
Domain skills: ${(existingCandidate.skills?.domain || []).join(", ")}
Education: ${existingCandidate.education_highest || "unknown"}
Certifications: ${(existingCandidate.certifications || []).join(", ") || "none"}
Inferred salary range: GYD ${existingCandidate.inferred_salary_min || 0} – ${existingCandidate.inferred_salary_max || 0}
${existingCandidate.job_preferences ? `
Job preferences set by candidate:
- Preferred regions: ${(existingCandidate.job_preferences.preferred_regions || []).join(", ") || "any"}
- Preferred work types: ${(existingCandidate.job_preferences.preferred_work_types || []).join(", ") || "any"}
- Minimum salary: ${existingCandidate.job_preferences.minimum_salary_gyd ? `GYD ${existingCandidate.job_preferences.minimum_salary_gyd}/month` : "not specified"}
- Open to relocation: ${existingCandidate.job_preferences.open_to_relocation ? "yes" : "no"}
Note: Factor these preferences into match scores. A job outside the candidate's preferred regions or below their minimum salary should score lower even if skills align.` : ""}

JOBS DATABASE:
${jobsContext}

${scoringRules}

Return ONLY valid JSON (no markdown, no explanation):
{
  "matches": [
    {
      "job_id": "uuid",
      "match_score": 75,
      "match_explanation": "Specific explanation referencing the candidate's actual background and the job's actual requirements",
      "matched_skills": ["only skills that genuinely match"],
      "missing_skills": ["skills the job requires that the candidate lacks"],
      "skill_gap_advice": "Concrete, specific advice — what to study or get certified in",
      "experience_fit": "strong|good|partial|weak",
      "seniority_fit": "overqualified|aligned|underqualified",
      "salary_fit": "above_range|within_range|below_range|unknown",
      "ai_verdict": "One honest, analytical sentence for the employer",
      "green_flags": ["only real, specific signals — empty array if none"],
      "red_flags": ["honest concerns — empty array if none"]
    }
  ]
}

Only include matches scoring 60 or above. Sort by score descending.`;

    const raw = await callGemini(rescanPrompt);
    try {
      const cleaned = raw.replace(/```json\n?/g, "").replace(/```\n?/g, "").trim();
      let parsed;
      try { parsed = JSON.parse(cleaned); } catch {
        const m = cleaned.match(/\{[\s\S]*\}/);
        if (m) parsed = JSON.parse(m[0]); else throw new Error("No JSON");
      }
      return { candidate: existingCandidate, matches: parsed.matches || [] };
    } catch {
      throw new Error("Scan failed. Please try again.");
    }
  }

  // -- INITIAL CV UPLOAD --
  const prompt = `You are JobSpot's AI matching engine for Guyana's job market. Analyze this CV/resume and match it against the provided job listings.

JOBS DATABASE:
${jobsContext}

${scoringRules}

Return ONLY valid JSON (no markdown, no explanation):
{
  "candidate": {
    "name": "extracted full name or Anonymous",
    "email": "extracted email or null",
    "phone": "extracted phone or null",
    "location": "extracted location or null",
    "seniority_level": "entry|mid|senior|lead",
    "career_trajectory": "ascending|lateral|pivot|early",
    "total_experience_years": 0,
    "average_tenure_months": 0,
    "longest_tenure_months": 0,
    "shortest_tenure_months": 0,
    "stability_signal": "stable|moderate|high_churn",
    "industries": ["list of industries the candidate has actually worked in"],
    "skills": {
      "technical": ["hard technical skills only"],
      "soft": ["genuine soft skills — max 5, only if clearly evidenced"],
      "domain": ["industry-specific knowledge areas"]
    },
    "certifications": [],
    "education_highest": "degree, field, and institution",
    "inferred_salary_min": 0,
    "inferred_salary_max": 0,
    "ai_profile_summary": "2-3 sentence factual summary of who this person is professionally — no hype"
  },
  "matches": [
    {
      "job_id": "uuid",
      "match_score": 75,
      "match_explanation": "Specific explanation referencing the candidate's actual background and the job's actual requirements",
      "matched_skills": ["only skills that genuinely match the job requirements"],
      "missing_skills": ["skills the job requires that the candidate lacks"],
      "skill_gap_advice": "Concrete, specific advice — what to study, get certified in, or gain experience doing",
      "experience_fit": "strong|good|partial|weak",
      "seniority_fit": "overqualified|aligned|underqualified",
      "salary_fit": "above_range|within_range|below_range|unknown",
      "ai_verdict": "One honest, analytical sentence the employer can act on",
      "green_flags": ["only real, specific positive signals — empty array if none"],
      "red_flags": ["honest concerns the employer should know — empty array if none"]
    }
  ]
}

Only include matches scoring 60 or above. Sort by match_score descending.`;

  const raw = await callGemini(prompt, pdfBase64);
  try {
    const cleaned = raw.replace(/```json\n?/g, "").replace(/```\n?/g, "").trim();
    try { return JSON.parse(cleaned); } catch {}
    const match = cleaned.match(/\{[\s\S]*\}/);
    if (match) return JSON.parse(match[0]);
    throw new Error("No JSON found");
  } catch {
    throw new Error("AI analysis failed. Please try again.");
  }
};


// ============================================================
// SHARED COMPONENTS
// ============================================================
const ScoreDisplay = ({ score, size = "md", app = null }) => {
  const [displayed, setDisplayed] = useState(0);
  const [hovered, setHovered] = useState(false);
  useEffect(() => {
    let frame;
    const start = Date.now();
    const animate = () => {
      const progress = Math.min((Date.now() - start) / 1200, 1);
      const ease = 1 - Math.pow(1 - progress, 3);
      setDisplayed(Math.round(ease * score));
      if (progress < 1) frame = requestAnimationFrame(animate);
    };
    frame = requestAnimationFrame(animate);
    return () => cancelAnimationFrame(frame);
  }, [score]);
  const cls = getScoreClass(score);
  const label = score >= 80 ? "Excellent match" : score >= 65 ? "Good match" : score >= 50 ? "Fair match" : "Weak match";
  const color = getScoreColor(score);

  // Build breakdown from app data if available
  const breakdown = app ? [
    { label: "Skills",    val: app.matched_skills?.length > 0 ? `${app.matched_skills.length} matched` : "—",       good: (app.matched_skills?.length || 0) > 2 },
    { label: "Gaps",      val: app.missing_skills?.length > 0 ? `${app.missing_skills.length} missing` : "None",    good: (app.missing_skills?.length || 0) < 3 },
    { label: "Exp fit",   val: app.experience_fit || "—",                                                             good: app.experience_fit === "strong" },
    { label: "Level",     val: app.seniority_fit || "—",                                                             good: app.seniority_fit === "aligned" },
    { label: "Salary",    val: app.salary_fit?.replace(/_/g," ") || "—",                                            good: app.salary_fit === "within_range" },
  ] : [];

  return (
    <div style={{ position: "relative", display: "inline-flex" }}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}>
      <div className={`score-pill ${cls}`}
        style={{ ...(size === "lg" ? { fontSize: 18, padding: "0 16px", height: 40, minWidth: 70 } : {}), cursor: app ? "default" : "default" }}>
        {displayed}%
      </div>
      {hovered && app && (
        <div style={{
          position: "absolute", top: "calc(100% + 8px)", right: 0,
          background: "var(--bg2)", border: "1px solid var(--border)",
          borderRadius: 10, padding: "12px 14px", zIndex: 50,
          boxShadow: "0 8px 32px rgba(0,0,0,0.15)", minWidth: 200,
          animation: "fadeUp 0.15s ease",
        }}>
          <div style={{ fontSize: 11, fontWeight: 700, color, textTransform: "uppercase", letterSpacing: "0.07em", marginBottom: 8 }}>{label}</div>
          {breakdown.map((b, i) => (
            <div key={i} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "4px 0", borderBottom: i < breakdown.length - 1 ? "1px solid var(--border2)" : "none" }}>
              <span style={{ fontSize: 12, color: "var(--text3)" }}>{b.label}</span>
              <span style={{ fontSize: 12, fontWeight: 600, color: b.good ? "var(--green)" : b.val === "—" || b.val === "None" ? "var(--text2)" : "var(--amber)" }}>{b.val}</span>
            </div>
          ))}
          <div style={{ fontSize: 11, color: "var(--text3)", marginTop: 8, textAlign: "center" }}>Hover for details · Click Full report for more</div>
        </div>
      )}
    </div>
  );
};

const SkillTags = ({ skills, color = "default", max = 8 }) => {
  const shown = skills?.slice(0, max) || [];
  const rest = (skills?.length || 0) - shown.length;
  const isGreen = color === "green";
  const isRed = color === "red";
  return (
    <div className="flex" style={{ flexWrap: "wrap", gap: 6 }}>
      {shown.map((s, i) => (
        <span key={i} style={{
          display: "inline-flex", alignItems: "center", gap: 4,
          padding: "4px 10px", borderRadius: 20, fontSize: 12, fontWeight: 500,
          background: isGreen ? "var(--green)" : isRed ? "var(--red-bg)" : "var(--bg3)",
          color: isGreen ? "#fff" : isRed ? "var(--red)" : "var(--text2)",
          border: isRed ? "1px solid rgba(217,48,37,0.3)" : isGreen ? "none" : "1px solid var(--border2)",
          maxWidth: 200, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
        }}>
          {isGreen && <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3"><polyline points="20 6 9 17 4 12"/></svg>}
          {isRed && <svg width="9" height="9" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>}
          {s.length > 24 ? s.slice(0, 24) + "…" : s}
        </span>
      ))}
      {rest > 0 && <span style={{ padding: "4px 10px", borderRadius: 20, fontSize: 12, background: "var(--bg3)", color: "var(--text3)", border: "1px solid var(--border2)" }}>+{rest} more</span>}
    </div>
  );
};

const PlanBadge = ({ plan }) => {
  const labels = { free: "Free", starter: "Starter", professional: "Pro" };
  return <span className={`badge ${getPlanBadge(plan)}`}>{labels[plan] || plan}</span>;
};

const EmptyState = ({ icon, title, subtitle, action }) => (
  <div className="flex-col items-center justify-center" style={{ padding: "60px 20px", textAlign: "center" }}>
    <div style={{ width: 56, height: 56, background: "var(--bg3)", border: "1px solid var(--border2)", borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center", marginBottom: 16 }}>
      <Icon name={icon} size={22} color="var(--text3)" />
    </div>
    <div className="font-display" style={{ fontSize: 16, fontWeight: 600, marginBottom: 6 }}>{title}</div>
    <div style={{ fontSize: 14, color: "var(--text2)", maxWidth: 320 }}>{subtitle}</div>
    {action && <div style={{ marginTop: 20 }}>{action}</div>}
  </div>
);

const ConfirmModal = ({ title, message, onConfirm, onCancel, danger }) => (
  <div className="modal-backdrop" onClick={onCancel}>
    <div className="modal" style={{ maxWidth: 420 }} onClick={e => e.stopPropagation()}>
      <h3 className="font-display" style={{ fontSize: 18, fontWeight: 700, marginBottom: 10 }}>{title}</h3>
      <p style={{ color: "var(--text2)", fontSize: 14, marginBottom: 24 }}>{message}</p>
      <div className="flex gap-3 justify-end">
        <button className="btn btn-outline" onClick={onCancel}>Cancel</button>
        <button className={`btn ${danger ? "btn-danger" : "btn-gold"}`} onClick={onConfirm}>Confirm</button>
      </div>
    </div>
  </div>
);


// ============================================================
// LANDING PAGE
// ============================================================

// ============================================================
// LANDING PAGE — Premium Redesign
// ============================================================

const LandingPage = ({ onEmployerClick, onSeekerClick, onTermsClick, onPrivacyClick, onAdminClick, onJobsClick, onServicesClick, deepLinkJobId, onDeepLink }) => {
  const [zapClicks, setZapClicks] = useState(0);
  const zapTimerRef = useRef(null);
  const [featuredJobs, setFeaturedJobs] = useState([]);
  const [featuredModal, setFeaturedModal] = useState(null);

  useEffect(() => {
    const loadFeatured = async () => {
      const now = new Date().toISOString();
      const { data: jobs, error } = await supabase
        .from("jobs")
        .select("*, employers(company_name)")
        .eq("status", "active")
        .eq("featured", true)
        .gt("featured_until", now)
        .order("featured_until", { ascending: true })
        .limit(50);
      if (error) { console.error("Featured jobs error:", error); return; }
      const filtered = (jobs || []).filter(j => !j.expires_at || new Date(j.expires_at) > new Date());
      setFeaturedJobs(filtered);
    };
    loadFeatured();
  }, []);

  const handleZapClick = () => {
    const next = zapClicks + 1;
    setZapClicks(next);
    clearTimeout(zapTimerRef.current);
    if (next >= 5) { setZapClicks(0); onAdminClick && onAdminClick(); return; }
    zapTimerRef.current = setTimeout(() => setZapClicks(0), 3000);
  };

  const lp = `
    @import url('https://fonts.googleapis.com/css2?family=Syne:wght@400;500;600;700;800&family=DM+Sans:ital,opsz,wght@0,9..40,300;0,9..40,400;0,9..40,500;1,9..40,300&display=swap');

    .lp {
      background: #0A0908;
      color: #FAF8F4;
      font-family: 'DM Sans', sans-serif;
      -webkit-font-smoothing: antialiased;
      overflow-x: hidden;
    }

    /* ── KEYFRAMES ── */
    @keyframes lpShimmer   { 0%{background-position:-200% center} 100%{background-position:200% center} }
    @keyframes lpFadeUp    { from{opacity:0;transform:translateY(28px)} to{opacity:1;transform:translateY(0)} }
    @keyframes lpFadeIn    { from{opacity:0} to{opacity:1} }
    @keyframes lpPulse     { 0%,100%{opacity:1;transform:scale(1)} 50%{opacity:0.5;transform:scale(0.85)} }
    @keyframes lpFloat     { 0%,100%{transform:translateY(0px)} 50%{transform:translateY(-6px)} }
    @keyframes lpGlow      { 0%,100%{box-shadow:0 0 30px rgba(184,146,42,0.12)} 50%{box-shadow:0 0 60px rgba(184,146,42,0.28)} }
    @keyframes lpOrb       { 0%{transform:translate(0,0) scale(1)} 33%{transform:translate(30px,-20px) scale(1.05)} 66%{transform:translate(-20px,15px) scale(0.97)} 100%{transform:translate(0,0) scale(1)} }
    @keyframes lpOrb2      { 0%{transform:translate(0,0) scale(1)} 33%{transform:translate(-25px,20px) scale(1.03)} 66%{transform:translate(20px,-15px) scale(0.98)} 100%{transform:translate(0,0) scale(1)} }
    @keyframes lpSpin      { to{transform:rotate(360deg)} }
    @keyframes lpBlink     { 0%,100%{opacity:1} 50%{opacity:0.3} }
    @keyframes lpSlideRight{ from{opacity:0;transform:translateX(-20px)} to{opacity:1;transform:translateX(0)} }
    @keyframes lpStagger1  { from{opacity:0;transform:translateY(16px)} to{opacity:1;transform:translateY(0)} }
    @keyframes lpStagger2  { from{opacity:0;transform:translateY(16px)} to{opacity:1;transform:translateY(0)} }
    @keyframes lpStagger3  { from{opacity:0;transform:translateY(16px)} to{opacity:1;transform:translateY(0)} }
    @keyframes lpTickerX   { 0%{transform:translateX(0)} 100%{transform:translateX(-50%)} }

    .lp-a1 { animation: lpFadeUp 0.65s 0.0s ease both; }
    .lp-a2 { animation: lpFadeUp 0.65s 0.1s ease both; }
    .lp-a3 { animation: lpFadeUp 0.65s 0.2s ease both; }
    .lp-a4 { animation: lpFadeUp 0.65s 0.3s ease both; }

    /* ── GOLD TEXT ── */
    .lp-gold {
      background: linear-gradient(135deg, #C9A84C 0%, #E8C96A 40%, #C9A84C 70%, #A67C28 100%);
      background-size: 200% auto;
      -webkit-background-clip: text;
      -webkit-text-fill-color: transparent;
      background-clip: text;
      animation: lpShimmer 4s linear infinite;
    }
    .lp-blue { color: #60A5FA; }

    /* ── NAV ── */
    .lp-nav {
      position: sticky; top: 0; z-index: 50;
      background: rgba(10,9,8,0.85);
      backdrop-filter: blur(24px); -webkit-backdrop-filter: blur(24px);
      border-bottom: 1px solid rgba(255,255,255,0.06);
      padding: 0 48px; height: 60px;
      display: flex; align-items: center; justify-content: space-between;
    }
    .lp-nav-logo { display: flex; align-items: center; gap: 10px; }
    .lp-nav-links { display: flex; align-items: center; gap: 6px; }
    .lp-nav-link {
      padding: 7px 16px; border-radius: 8px; font-size: 13px; font-weight: 500;
      color: rgba(250,248,244,0.5); background: transparent; border: none;
      cursor: pointer; font-family: 'DM Sans', sans-serif; transition: all 0.18s;
      letter-spacing: 0.01em;
    }
    .lp-nav-link:hover { color: #FAF8F4; background: rgba(255,255,255,0.06); }
    .lp-nav-cta {
      padding: 8px 20px; border-radius: 9px; font-size: 13px; font-weight: 700;
      background: #B8922A; color: #0A0908; border: none; cursor: pointer;
      font-family: 'DM Sans', sans-serif; transition: all 0.18s; letter-spacing: 0.01em;
    }
    .lp-nav-cta:hover { background: #D4A83C; box-shadow: 0 4px 20px rgba(184,146,42,0.4); }

    /* ── SKILLED TRADES STRIP ── */
    .lp-trades-strip {
      background: #FAF8F4;
      display: flex; align-items: center; gap: 18px;
      padding: 11px 40px;
      overflow-x: auto;
      white-space: nowrap;
      scrollbar-width: none;
    }
    .lp-trades-strip::-webkit-scrollbar { display: none; }
    .lp-trades-strip-label {
      font-size: 12px; color: #6B6560; flex-shrink: 0; font-weight: 500;
    }
    .lp-trades-strip-item {
      display: inline-flex; align-items: center; gap: 6px;
      background: none; border: none; cursor: pointer;
      font-family: 'DM Sans', sans-serif; font-size: 13px; color: #1A1714;
      padding: 4px 2px; flex-shrink: 0; transition: opacity 0.15s;
    }
    .lp-trades-strip-item:hover { opacity: 0.6; }
    .lp-trades-strip-more {
      background: none; border: none; cursor: pointer;
      font-family: 'DM Sans', sans-serif; font-size: 12.5px; font-weight: 600;
      color: #B8922A; flex-shrink: 0; padding: 4px 2px; margin-left: 4px;
    }
    @media (max-width: 640px) {
      .lp-trades-strip { padding: 10px 16px; gap: 14px; }
    }

    /* ── HERO ── */
    .lp-hero {
      min-height: calc(100vh - 60px);
      display: flex; flex-direction: column;
      align-items: center; justify-content: center;
      text-align: center;
      padding: 80px 48px 60px;
      position: relative; overflow: hidden;
    }

    /* Ambient orbs */
    .lp-orb1 {
      position: absolute; top: -80px; left: -120px;
      width: 500px; height: 500px; border-radius: 50%;
      background: radial-gradient(circle, rgba(184,146,42,0.09) 0%, transparent 65%);
      pointer-events: none; animation: lpOrb 18s ease-in-out infinite;
    }
    .lp-orb2 {
      position: absolute; bottom: -100px; right: -80px;
      width: 600px; height: 600px; border-radius: 50%;
      background: radial-gradient(circle, rgba(26,111,212,0.07) 0%, transparent 65%);
      pointer-events: none; animation: lpOrb2 22s ease-in-out infinite;
    }
    .lp-orb3 {
      position: absolute; top: 30%; left: 50%; transform: translate(-50%,-50%);
      width: 800px; height: 300px; border-radius: 50%;
      background: radial-gradient(ellipse, rgba(184,146,42,0.04) 0%, transparent 70%);
      pointer-events: none;
    }

    /* Grain overlay */
    .lp-grain {
      position: absolute; inset: 0; opacity: 0.025; pointer-events: none;
      background-image: url("data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noise'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noise)'/%3E%3C/svg%3E");
      background-size: 180px;
    }

    .lp-hero-eyebrow {
      display: inline-flex; align-items: center; gap: 8px;
      background: rgba(184,146,42,0.08); border: 1px solid rgba(184,146,42,0.2);
      border-radius: 100px; padding: 6px 16px; margin-bottom: 28px;
      font-size: 11px; font-weight: 700; letter-spacing: 0.1em; text-transform: uppercase;
      color: #C9A84C; position: relative; z-index: 1;
    }
    .lp-hero-eyebrow-dot {
      width: 6px; height: 6px; border-radius: 50%; background: #2D9E4E;
      animation: lpPulse 2s ease infinite; flex-shrink: 0;
    }

    .lp-hero-headline {
      font-family: 'Syne', sans-serif;
      font-size: clamp(40px, 6vw, 80px);
      font-weight: 800;
      line-height: 0.95;
      letter-spacing: -0.04em;
      margin-bottom: 24px;
      position: relative; z-index: 1;
      max-width: 820px;
    }

    .lp-hero-sub {
      font-size: clamp(15px, 2vw, 18px);
      color: rgba(250,248,244,0.5);
      line-height: 1.65;
      max-width: 520px;
      margin: 0 auto 40px;
      position: relative; z-index: 1;
    }

    .lp-hero-actions {
      display: flex; gap: 12px; justify-content: center; flex-wrap: wrap;
      position: relative; z-index: 1; margin-bottom: 56px;
    }

    .lp-btn-gold {
      display: inline-flex; align-items: center; gap: 8px;
      background: linear-gradient(135deg, #B8922A, #D4A83C);
      color: #0A0908; border: none; border-radius: 12px;
      padding: 15px 32px; font-size: 15px; font-weight: 700;
      cursor: pointer; font-family: 'DM Sans', sans-serif;
      transition: all 0.2s; letter-spacing: 0.01em; white-space: nowrap;
      box-shadow: 0 4px 24px rgba(184,146,42,0.3);
    }
    .lp-btn-gold:hover { transform: translateY(-2px); box-shadow: 0 12px 40px rgba(184,146,42,0.45); background: linear-gradient(135deg, #C9A83C, #E0B244); }

    .lp-btn-outline {
      display: inline-flex; align-items: center; gap: 8px;
      background: rgba(255,255,255,0.04); color: rgba(250,248,244,0.75);
      border: 1px solid rgba(255,255,255,0.12); border-radius: 12px;
      padding: 15px 28px; font-size: 15px; font-weight: 500;
      cursor: pointer; font-family: 'DM Sans', sans-serif;
      transition: all 0.2s; white-space: nowrap;
    }
    .lp-btn-outline:hover { background: rgba(255,255,255,0.08); border-color: rgba(255,255,255,0.22); color: #FAF8F4; transform: translateY(-1px); }

    .lp-btn-blue {
      display: inline-flex; align-items: center; gap: 8px;
      background: rgba(26,111,212,0.12); color: #60A5FA;
      border: 1px solid rgba(26,111,212,0.3); border-radius: 12px;
      padding: 15px 28px; font-size: 15px; font-weight: 600;
      cursor: pointer; font-family: 'DM Sans', sans-serif;
      transition: all 0.2s; white-space: nowrap;
    }
    .lp-btn-blue:hover { background: rgba(26,111,212,0.2); border-color: rgba(26,111,212,0.5); transform: translateY(-1px); box-shadow: 0 8px 28px rgba(26,111,212,0.2); }

    /* ── PLATFORM PILLS ── */
    .lp-platform-pills {
      display: flex; gap: 10px; justify-content: center; flex-wrap: wrap;
      position: relative; z-index: 1;
    }
    .lp-pill-item {
      display: flex; align-items: center; gap: 8px;
      padding: 8px 16px; border-radius: 100px;
      font-size: 12px; font-weight: 500; letter-spacing: 0.01em;
      border: 1px solid rgba(255,255,255,0.07);
      background: rgba(255,255,255,0.03); color: rgba(250,248,244,0.5);
    }
    .lp-pill-dot { width: 6px; height: 6px; border-radius: 50%; flex-shrink: 0; }

    /* ── TICKER ── */
    .lp-ticker {
      overflow: hidden; border-top: 1px solid rgba(255,255,255,0.05);
      border-bottom: 1px solid rgba(255,255,255,0.05);
      padding: 14px 0; background: rgba(255,255,255,0.02);
    }
    .lp-ticker-track {
      display: flex; gap: 0;
      animation: lpTickerX 30s linear infinite;
      width: max-content;
    }
    .lp-ticker-item {
      display: flex; align-items: center; gap: 8px;
      padding: 0 32px; font-size: 12px; font-weight: 600;
      color: rgba(250,248,244,0.3); text-transform: uppercase;
      letter-spacing: 0.08em; white-space: nowrap;
      border-right: 1px solid rgba(255,255,255,0.06);
    }
    .lp-ticker-item:last-child { border-right: none; }
    .lp-ticker-dot { width: 4px; height: 4px; border-radius: 50%; background: rgba(184,146,42,0.6); flex-shrink: 0; }

    /* ── DUAL SPLIT SECTION ── */
    .lp-split {
      display: grid; grid-template-columns: 1fr 1fr;
      gap: 2px; background: rgba(255,255,255,0.04);
      margin: 0; border-top: 1px solid rgba(255,255,255,0.06);
      border-bottom: 1px solid rgba(255,255,255,0.06);
    }
    .lp-split-panel {
      padding: 72px 56px; position: relative; overflow: hidden;
      display: flex; flex-direction: column;
    }
    .lp-split-jobs  { background: #0E0C0A; }
    .lp-split-svc   { background: #090D14; }

    /* Panel ambient glows */
    .lp-panel-glow-gold {
      position: absolute; top: -60px; right: -60px;
      width: 360px; height: 360px; border-radius: 50%;
      background: radial-gradient(circle, rgba(184,146,42,0.08) 0%, transparent 65%);
      pointer-events: none;
    }
    .lp-panel-glow-blue {
      position: absolute; top: -60px; left: -60px;
      width: 360px; height: 360px; border-radius: 50%;
      background: radial-gradient(circle, rgba(26,111,212,0.1) 0%, transparent 65%);
      pointer-events: none;
    }

    .lp-panel-label {
      font-size: 10px; font-weight: 800; letter-spacing: 0.14em;
      text-transform: uppercase; margin-bottom: 20px; position: relative; z-index: 1;
      display: inline-flex; align-items: center; gap: 8px;
    }
    .lp-panel-label-gold { color: #B8922A; }
    .lp-panel-label-blue { color: #3B82F6; }

    .lp-panel-headline {
      font-family: 'Syne', sans-serif;
      font-size: clamp(26px, 2.8vw, 40px);
      font-weight: 800; line-height: 1.05;
      letter-spacing: -0.025em; margin-bottom: 16px;
      position: relative; z-index: 1; color: #FAF8F4;
    }
    .lp-panel-sub {
      font-size: 14px; color: rgba(250,248,244,0.45);
      line-height: 1.7; margin-bottom: 28px;
      position: relative; z-index: 1; max-width: 380px;
    }

    /* Panel feature list */
    .lp-panel-list {
      display: flex; flex-direction: column; gap: 10px;
      margin-bottom: 32px; position: relative; z-index: 1;
    }
    .lp-panel-list-item {
      display: flex; align-items: center; gap: 10px;
      font-size: 13px; color: rgba(250,248,244,0.7);
    }
    .lp-panel-check-gold {
      width: 18px; height: 18px; border-radius: 50%; flex-shrink: 0;
      background: rgba(184,146,42,0.15); border: 1px solid rgba(184,146,42,0.3);
      display: flex; align-items: center; justify-content: center;
    }
    .lp-panel-check-blue {
      width: 18px; height: 18px; border-radius: 50%; flex-shrink: 0;
      background: rgba(59,130,246,0.15); border: 1px solid rgba(59,130,246,0.3);
      display: flex; align-items: center; justify-content: center;
    }

    /* Panel CTA buttons */
    .lp-panel-btn-gold {
      display: inline-flex; align-items: center; gap: 8px;
      background: #B8922A; color: #0A0908; border: none; border-radius: 10px;
      padding: 13px 26px; font-size: 14px; font-weight: 700;
      cursor: pointer; font-family: 'DM Sans', sans-serif;
      transition: all 0.2s; letter-spacing: 0.01em; white-space: nowrap;
      position: relative; z-index: 1; align-self: flex-start;
    }
    .lp-panel-btn-gold:hover { background: #D4A83C; transform: translateY(-1px); box-shadow: 0 8px 28px rgba(184,146,42,0.4); }

    .lp-panel-btn-blue {
      display: inline-flex; align-items: center; gap: 8px;
      background: #1A6FD4; color: #FAF8F4; border: none; border-radius: 10px;
      padding: 13px 26px; font-size: 14px; font-weight: 700;
      cursor: pointer; font-family: 'DM Sans', sans-serif;
      transition: all 0.2s; letter-spacing: 0.01em; white-space: nowrap;
      position: relative; z-index: 1; align-self: flex-start;
    }
    .lp-panel-btn-blue:hover { background: #2280E8; transform: translateY(-1px); box-shadow: 0 8px 28px rgba(26,111,212,0.4); }

    /* ── JOBS MOCKUP ── */
    .lp-jobs-mockup {
      background: #1C1917; border: 1px solid rgba(255,255,255,0.08);
      border-radius: 16px; overflow: hidden;
      box-shadow: 0 24px 64px rgba(0,0,0,0.6);
      margin-top: 36px; position: relative; z-index: 1;
    }
    .lp-mockup-bar {
      background: #131110; border-bottom: 1px solid rgba(255,255,255,0.06);
      padding: 10px 14px; display: flex; align-items: center; gap: 8px;
    }
    .lp-dot { width: 9px; height: 9px; border-radius: 50%; }
    .lp-mock-card {
      background: #242220; border: 1px solid rgba(255,255,255,0.06);
      border-radius: 10px; padding: 14px; margin: 0 14px 10px;
    }
    .lp-mock-card:first-of-type { margin-top: 14px; }
    .lp-mock-bar { height: 3px; border-radius: 2px; margin-top: 10px; background: rgba(255,255,255,0.05); }
    .lp-mock-pill {
      display: inline-flex; align-items: center; gap: 3px;
      padding: 3px 8px; border-radius: 10px; font-size: 10px; font-weight: 500;
    }
    .lp-mock-score {
      display: inline-flex; align-items: center; justify-content: center;
      height: 26px; min-width: 48px; border-radius: 13px;
      font-family: 'Syne',sans-serif; font-size: 12px; font-weight: 800;
    }
    .lp-mock-rank {
      width: 28px; height: 28px; border-radius: 7px;
      display: flex; align-items: center; justify-content: center; flex-shrink: 0;
    }

    /* ── SERVICES MOCKUP ── */
    .lp-svc-mockup {
      background: #0D1525; border: 1px solid rgba(59,130,246,0.15);
      border-radius: 16px; overflow: hidden;
      box-shadow: 0 24px 64px rgba(0,0,0,0.6);
      margin-top: 36px; position: relative; z-index: 1;
    }
    .lp-svc-mockup-bar {
      background: #0A1020; border-bottom: 1px solid rgba(59,130,246,0.1);
      padding: 10px 14px; display: flex; align-items: center; gap: 8px;
    }
    .lp-svc-header {
      padding: 16px 14px 10px;
      border-bottom: 1px solid rgba(59,130,246,0.08);
    }
    .lp-svc-cat-pills {
      display: flex; gap: 6px; flex-wrap: wrap; padding: 10px 14px 6px;
    }
    .lp-svc-cat-pill {
      display: inline-flex; align-items: center; gap: 5px;
      padding: 5px 12px; border-radius: 100px; font-size: 11px; font-weight: 600;
      cursor: pointer; transition: all 0.15s; white-space: nowrap;
    }
    .lp-svc-cat-pill.active {
      background: rgba(184,146,42,0.2); border: 1px solid rgba(184,146,42,0.4); color: #D4A83C;
    }
    .lp-svc-cat-pill.inactive {
      background: rgba(255,255,255,0.03); border: 1px solid rgba(255,255,255,0.08); color: rgba(250,248,244,0.4);
    }
    .lp-svc-card {
      display: flex; align-items: center; gap: 12px;
      padding: 12px 14px; border-bottom: 1px solid rgba(59,130,246,0.06);
      transition: background 0.15s;
    }
    .lp-svc-card:last-child { border-bottom: none; margin-bottom: 8px; }
    .lp-svc-card:hover { background: rgba(59,130,246,0.04); }
    .lp-svc-avatar {
      width: 36px; height: 36px; border-radius: 10px;
      display: flex; align-items: center; justify-content: center;
      font-size: 17px; flex-shrink: 0;
    }
    .lp-svc-wa-btn {
      display: inline-flex; align-items: center; gap: 4px;
      padding: 4px 10px; border-radius: 6px;
      background: rgba(37,211,102,0.12); border: 1px solid rgba(37,211,102,0.25);
      color: #25D366; font-size: 10px; font-weight: 700;
      cursor: pointer; white-space: nowrap; font-family: 'DM Sans', sans-serif;
    }

    /* ── FEATURED JOBS ── */
    .lp-featured-strip { max-width: 1200px; margin: 0 auto; padding: 60px 48px; }
    .lp-featured-scroll {
      display: flex; gap: 10px; overflow-x: auto;
      padding-bottom: 8px; -webkit-overflow-scrolling: touch;
      scroll-snap-type: x mandatory; scrollbar-width: none;
    }
    .lp-featured-scroll::-webkit-scrollbar { display: none; }
    .lp-featured-pill {
      display: inline-flex; flex-direction: column; gap: 4px;
      padding: 12px 16px; background: rgba(255,255,255,0.03);
      border: 1px solid rgba(184,146,42,0.18); border-radius: 12px;
      cursor: pointer; transition: all 0.2s ease;
      min-width: 180px; flex-shrink: 0; scroll-snap-align: start;
    }
    .lp-featured-pill:hover { background: rgba(184,146,42,0.07); border-color: rgba(184,146,42,0.4); transform: translateY(-2px); }

    /* ── HOW IT WORKS ── */
    .lp-hiw { max-width: 1200px; margin: 0 auto; padding: 100px 48px; }
    .lp-hiw-steps {
      display: grid; grid-template-columns: repeat(4, 1fr); gap: 2px;
      background: rgba(255,255,255,0.04); border-radius: 20px;
      overflow: hidden; border: 1px solid rgba(255,255,255,0.06);
      margin-top: 52px;
    }
    .lp-hiw-step {
      background: #0E0C0A; padding: 32px 28px;
      position: relative; overflow: hidden;
    }
    .lp-hiw-step::before {
      content: ''; position: absolute; top: 0; left: 0; right: 0; height: 2px;
      background: linear-gradient(90deg, transparent, var(--step-color, rgba(184,146,42,0.4)), transparent);
    }
    .lp-hiw-num {
      font-family: 'Syne', sans-serif; font-size: 11px; font-weight: 800;
      letter-spacing: 0.1em; text-transform: uppercase; margin-bottom: 20px;
      opacity: 0.4;
    }
    .lp-hiw-icon {
      width: 48px; height: 48px; border-radius: 14px;
      display: flex; align-items: center; justify-content: center;
      margin-bottom: 18px;
    }
    .lp-hiw-title {
      font-family: 'Syne', sans-serif; font-size: 15px; font-weight: 700;
      color: #FAF8F4; margin-bottom: 8px; line-height: 1.3;
    }
    .lp-hiw-body { font-size: 13px; color: rgba(250,248,244,0.4); line-height: 1.65; }

    /* ── PRICING ── */
    .lp-pricing { max-width: 1200px; margin: 0 auto; padding: 0 48px 100px; }
    .lp-pricing-grid {
      display: grid; grid-template-columns: 1fr 1fr 1fr;
      gap: 16px; margin-top: 52px; align-items: stretch;
    }
    .lp-plan {
      border-radius: 20px; padding: 32px 28px; position: relative;
      display: flex; flex-direction: column;
    }
    .lp-plan-free    { background: #0E0C0A; border: 1px solid rgba(255,255,255,0.07); }
    .lp-plan-starter { background: #110F0C; border: 1px solid rgba(184,146,42,0.3); }
    .lp-plan-pro     {
      background: linear-gradient(160deg, #0a1628 0%, #080e1c 100%);
      border: 1px solid rgba(59,130,246,0.35);
      box-shadow: 0 20px 60px rgba(26,111,212,0.12), inset 0 1px 0 rgba(255,255,255,0.06);
    }
    .lp-plan-badge {
      position: absolute; top: -12px; left: 50%; transform: translateX(-50%);
      padding: 4px 14px; border-radius: 100px; font-size: 10px; font-weight: 800;
      white-space: nowrap; letter-spacing: 0.06em;
    }
    .lp-plan-check {
      width: 16px; height: 16px; border-radius: 50%;
      display: flex; align-items: center; justify-content: center; flex-shrink: 0;
    }

    /* ── FINAL CTA ── */
    .lp-final-cta { padding: 0 48px 100px; }
    .lp-cta-inner {
      max-width: 1200px; margin: 0 auto;
      background: linear-gradient(135deg, rgba(184,146,42,0.06) 0%, rgba(10,9,8,0) 50%, rgba(26,111,212,0.05) 100%);
      border: 1px solid rgba(255,255,255,0.07); border-radius: 28px;
      padding: 80px 64px; text-align: center; position: relative; overflow: hidden;
    }
    .lp-cta-grid {
      display: grid; grid-template-columns: repeat(3, 1fr);
      gap: 14px; max-width: 860px; margin: 48px auto 0;
    }
    .lp-cta-card {
      border-radius: 18px; padding: 28px 24px; text-align: left;
      cursor: pointer; transition: all 0.22s; border: 1px solid transparent;
      display: flex; flex-direction: column;
    }
    .lp-cta-card-icon {
      width: 44px; height: 44px; border-radius: 13px;
      display: flex; align-items: center; justify-content: center; margin-bottom: 16px;
    }
    .lp-cta-card-title {
      font-family: 'Syne', sans-serif; font-size: 16px; font-weight: 700;
      color: #FAF8F4; margin-bottom: 6px;
    }
    .lp-cta-card-sub { font-size: 12px; color: rgba(250,248,244,0.4); line-height: 1.6; margin-bottom: 16px; flex: 1; }
    .lp-cta-card-link { font-size: 13px; font-weight: 600; }

    .lp-cta-card-a { background: rgba(184,146,42,0.07); border-color: rgba(184,146,42,0.18); }
    .lp-cta-card-a:hover { background: rgba(184,146,42,0.13); border-color: rgba(184,146,42,0.38); transform: translateY(-3px); box-shadow: 0 12px 36px rgba(184,146,42,0.12); }
    .lp-cta-card-b { background: rgba(45,158,78,0.06); border-color: rgba(45,158,78,0.18); }
    .lp-cta-card-b:hover { background: rgba(45,158,78,0.12); border-color: rgba(45,158,78,0.35); transform: translateY(-3px); box-shadow: 0 12px 36px rgba(45,158,78,0.1); }
    .lp-cta-card-c { background: rgba(59,130,246,0.06); border-color: rgba(59,130,246,0.18); }
    .lp-cta-card-c:hover { background: rgba(59,130,246,0.12); border-color: rgba(59,130,246,0.35); transform: translateY(-3px); box-shadow: 0 12px 36px rgba(59,130,246,0.1); }

    /* ── SECTION HEADERS ── */
    .lp-section-label {
      font-size: 10px; font-weight: 800; letter-spacing: 0.14em;
      text-transform: uppercase; color: #B8922A; margin-bottom: 14px;
      display: flex; align-items: center; gap: 10px;
    }
    .lp-section-label::before {
      content: ''; display: block; width: 24px; height: 1px;
      background: rgba(184,146,42,0.5);
    }
    .lp-section-h2 {
      font-family: 'Syne', sans-serif;
      font-size: clamp(28px, 3.5vw, 46px);
      font-weight: 800; letter-spacing: -0.025em;
      line-height: 1.05; color: #FAF8F4;
    }

    /* ── DIVIDER ── */
    .lp-rule { height: 1px; background: linear-gradient(90deg, transparent, rgba(255,255,255,0.06), transparent); margin: 0; }

    /* ── FOOTER ── */
    .lp-footer {
      border-top: 1px solid rgba(255,255,255,0.06);
      padding: 28px 48px; display: flex;
      align-items: center; justify-content: space-between; flex-wrap: wrap; gap: 16px;
    }
    .lp-footer-links { display: flex; align-items: center; gap: 20px; flex-wrap: wrap; }

    /* ── MOBILE ── */
    @media (max-width: 768px) {
      /* Nav */
      .lp-nav { padding: 0 16px !important; height: 56px !important; }
      .lp-nav-links { display: none !important; }

      /* Hero */
      .lp-hero { padding: 52px 20px 40px !important; min-height: unset !important; }
      .lp-hero-headline { font-size: 38px !important; letter-spacing: -0.03em !important; }
      .lp-hero-sub { font-size: 15px !important; margin-bottom: 28px !important; }
      .lp-hero-actions { gap: 10px !important; margin-bottom: 36px !important; }
      .lp-hero-actions button { flex: 1 !important; min-width: 140px !important; padding: 14px 16px !important; font-size: 14px !important; justify-content: center !important; }
      .lp-platform-pills { gap: 6px !important; }
      .lp-pill-item { font-size: 11px !important; padding: 6px 12px !important; }

      /* Ticker */
      .lp-ticker-item { padding: 0 20px !important; font-size: 11px !important; }

      /* Split panels — stack vertically */
      .lp-split { grid-template-columns: 1fr !important; gap: 1px !important; }
      .lp-split-panel { padding: 48px 24px 40px !important; }
      .lp-panel-headline { font-size: 28px !important; }
      .lp-jobs-mockup { display: none !important; }
      .lp-svc-mockup { display: none !important; }
      .lp-panel-btn-gold, .lp-panel-btn-blue { width: 100% !important; justify-content: center !important; }

      /* Featured */
      .lp-featured-strip { padding: 40px 20px !important; }

      /* How it works */
      .lp-hiw { padding: 60px 20px !important; }
      .lp-hiw-steps { grid-template-columns: 1fr 1fr !important; }
      .lp-hiw-step { padding: 24px 18px !important; }

      /* Pricing */
      .lp-pricing { padding: 0 20px 60px !important; }
      .lp-pricing-grid { grid-template-columns: 1fr !important; }

      /* CTA */
      .lp-final-cta { padding: 0 20px 60px !important; }
      .lp-cta-inner { padding: 48px 24px !important; border-radius: 20px !important; }
      .lp-cta-grid { grid-template-columns: 1fr !important; max-width: 100% !important; }

      /* Footer */
      .lp-footer { flex-direction: column !important; align-items: flex-start !important; padding: 24px 20px 100px !important; gap: 16px !important; }
      .lp-footer-links { flex-direction: column !important; gap: 8px !important; }
    }

    @media (max-width: 480px) {
      .lp-hiw-steps { grid-template-columns: 1fr !important; }
    }
  `;

  // Services mockup data
  const svcProviders = [
    { emoji: "🔧", name: "Marcus Reid", trade: "Plumber", region: "Georgetown", rating: "4.9", color: "rgba(59,130,246,0.15)" },
    { emoji: "⚡", name: "Deon Francis", trade: "Electrician", region: "New Amsterdam", rating: "4.8", color: "rgba(184,146,42,0.12)" },
    { emoji: "🎨", name: "Alicia Forte", trade: "Painter", region: "Linden", rating: "5.0", color: "rgba(45,158,78,0.12)" },
  ];

  const jobCandidates = [
    { rank: "#1", rankBg: "rgba(184,146,42,0.2)", rankColor: "#C9A84C", name: "Jermaine L.", score: "88%", scoreColor: "#2D9E4E", scoreBg: "rgba(45,158,78,0.18)", scoreBorder: "rgba(45,158,78,0.3)", barW: "88%", barColor: "#2D9E4E", skills: ["Management","Leadership"], gap: "Salesforce" },
    { rank: "#2", rankBg: "rgba(158,152,144,0.12)", rankColor: "#9E9890", name: "Alicia F.", score: "85%", scoreColor: "#C9A84C", scoreBg: "rgba(184,146,42,0.15)", scoreBorder: "rgba(184,146,42,0.3)", barW: "85%", barColor: "#C9A84C", skills: ["MS Office","Excel"], gap: "Leadership" },
    { rank: "#3", rankBg: "rgba(176,101,60,0.12)", rankColor: "#B0653C", name: "Marcus B.", score: "82%", scoreColor: "#C9A84C", scoreBg: "rgba(184,146,42,0.12)", scoreBorder: "rgba(184,146,42,0.25)", barW: "82%", barColor: "#B8922A", skills: ["Reporting"], gap: "Budget mgmt" },
  ];

  const tickerItems = ["Banking & Finance","Oil & Energy","Healthcare","Retail & FMCG","BPO & Call Centre","Government","Tech & Startups","Hospitality","Construction","Plumbing","Electrical","Painting","Carpentry","Landscaping","Cleaning"];

  return (
    <div className="lp">
      <style>{lp}</style>

      {/* ── NAV ── */}
      <nav className="lp-nav">
        <div className="lp-nav-logo">
          <div onClick={handleZapClick} style={{ width: 34, height: 34, borderRadius: 9, display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer", flexShrink: 0, overflow: "hidden" }}>
            <img src="/icon-512x512.png" alt="JobSpot GY" style={{ width: "100%", height: "100%", objectFit: "cover", borderRadius: 9 }} />
          </div>
          <span style={{ fontFamily: "Syne,sans-serif", fontSize: 18, fontWeight: 800, letterSpacing: "-0.02em", color: "#FAF8F4" }}>
            Job<span className="lp-gold">Spot</span>
            <span style={{ fontSize: 10, color: "rgba(250,248,244,0.25)", fontWeight: 400, marginLeft: 3 }}>GY</span>
          </span>
        </div>
        <div className="lp-nav-links">
          <button className="lp-nav-link" onClick={onJobsClick}>Browse Jobs</button>
          <button className="lp-nav-link" onClick={() => onServicesClick()} style={{ color: "#C9A84C", border: "1px solid rgba(201,168,76,0.35)", borderRadius: 20, padding: "4px 14px" }}>Skilled Trades</button>
          <button className="lp-nav-link" onClick={onSeekerClick}>Job Seekers</button>
          <button className="lp-nav-cta" onClick={onAdminClick}>Login / Sign Up</button>
        </div>
      </nav>

      {/* ── SKILLED TRADES STRIP ── */}
      <div className="lp-trades-strip">
        <span className="lp-trades-strip-label">Skilled trades:</span>
        <button className="lp-trades-strip-item" onClick={() => onServicesClick("plumbing")}><Icon name="wrench" size={14} color="#B8922A" /> Plumber</button>
        <button className="lp-trades-strip-item" onClick={() => onServicesClick("electrical")}><Icon name="zap" size={14} color="#B8922A" /> Electrician</button>
        <button className="lp-trades-strip-item" onClick={() => onServicesClick("painting")}><Icon name="brush" size={14} color="#B8922A" /> Painter</button>
        <button className="lp-trades-strip-item" onClick={() => onServicesClick("carpentry")}><Icon name="hammer" size={14} color="#B8922A" /> Carpenter</button>
        <button className="lp-trades-strip-item" onClick={() => onServicesClick("auto_mechanics")}><Icon name="car" size={14} color="#B8922A" /> Mechanic</button>
        <button className="lp-trades-strip-more" onClick={() => onServicesClick()}>View all trades →</button>
      </div>

      {/* ── HERO ── */}
      <section className="lp-hero">
        <div className="lp-orb1" /><div className="lp-orb2" /><div className="lp-orb3" />
        <div className="lp-grain" />

        <div className="lp-hero-eyebrow lp-a1">
          <div className="lp-hero-eyebrow-dot" />
          Guyana's Work & Services Platform
        </div>

        <h1 className="lp-hero-headline lp-a2">
          Work smarter.<br />
          <span className="lp-gold">Hire better.</span><br />
          Live easier.
        </h1>

        <p className="lp-hero-sub lp-a3">
          Smart-matched jobs. AI-ranked candidates. A marketplace for skilled tradespeople. One platform built for Guyana.
        </p>

        <div className="lp-hero-actions lp-a4">
          <button className="lp-btn-gold" onClick={onEmployerClick}>
            <Icon name="briefcase" size={16} color="#0A0908" />
            Post a Job Free
          </button>
          <button className="lp-btn-blue" onClick={() => onServicesClick()}>
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M14.7 6.3a1 1 0 0 0 0 1.4l1.6 1.6a1 1 0 0 0 1.4 0l3.77-3.77a6 6 0 0 1-7.94 7.94l-6.91 6.91a2.12 2.12 0 0 1-3-3l6.91-6.91a6 6 0 0 1 7.94-7.94l-3.76 3.76z"/></svg>
            Find a Tradesperson
          </button>
          <button className="lp-btn-outline" onClick={onSeekerClick}>
            Upload My CV →
          </button>
        </div>

        {/* Platform pills */}
        <div className="lp-platform-pills lp-a4">
          <div className="lp-pill-item">
            <div className="lp-pill-dot" style={{ background: "#B8922A" }} />
            Smart hiring for employers
          </div>
          <div className="lp-pill-item">
            <div className="lp-pill-dot" style={{ background: "#2D9E4E" }} />
            AI job matching for seekers
          </div>
          <div className="lp-pill-item">
            <div className="lp-pill-dot" style={{ background: "#3B82F6" }} />
            Services directory for Guyana
          </div>
        </div>
      </section>

      {/* ── TICKER ── */}
      <div className="lp-ticker">
        <div className="lp-ticker-track">
          {[...tickerItems, ...tickerItems].map((item, i) => (
            <div key={i} className="lp-ticker-item">
              <div className="lp-ticker-dot" />
              {item}
            </div>
          ))}
        </div>
      </div>

      {/* ── FEATURED JOBS (if any) ── */}
      {featuredJobs.length > 0 && (
        <div className="lp-featured-strip">
          <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 20 }}>
            <div style={{ display: "inline-flex", alignItems: "center", gap: 7, background: "rgba(184,146,42,0.08)", border: "1px solid rgba(184,146,42,0.22)", borderRadius: 100, padding: "4px 14px" }}>
              <div style={{ width: 6, height: 6, borderRadius: "50%", background: "#B8922A", animation: "lpPulse 2s ease infinite" }} />
              <span style={{ fontSize: 10, fontWeight: 800, color: "#B8922A", textTransform: "uppercase", letterSpacing: "0.1em" }}>Now Hiring</span>
            </div>
            <span style={{ fontSize: 12, color: "rgba(250,248,244,0.25)" }}>{featuredJobs.length} active {featuredJobs.length === 1 ? "vacancy" : "vacancies"} from verified employers</span>
          </div>
          <div className="lp-featured-scroll">
            {featuredJobs.slice(0, 16).map((job, i) => {
              const companyName = Array.isArray(job.employers) ? job.employers[0]?.company_name : job.employers?.company_name;
              return (
                <div key={job.id} className="lp-featured-pill" onClick={() => setFeaturedModal(job)}>
                  <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8 }}>
                    <span style={{ fontSize: 12, fontWeight: 700, color: "#FAF8F4", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", maxWidth: 150 }}>{companyName || "Hiring Now"}</span>
                    <span style={{ fontSize: 9, fontWeight: 800, background: "rgba(184,146,42,0.15)", color: "#D4A83C", padding: "1px 6px", borderRadius: 4, border: "1px solid rgba(184,146,42,0.25)", whiteSpace: "nowrap" }}>PRO</span>
                  </div>
                  <div style={{ fontSize: 11, fontWeight: 600, color: "#B8922A", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{job.title}</div>
                  <div style={{ fontSize: 10, color: "rgba(250,248,244,0.35)" }}>{job.location?.split("(")[0]?.split("--")[0]?.trim() || "Guyana"}</div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      <div className="lp-rule" />

      {/* ══════════════════════════════════════════════════
          ── DUAL SPLIT SECTION ──
          Left: Smart Hiring (Jobs)
          Right: Services Marketplace
      ══════════════════════════════════════════════════ */}
      <div className="lp-split">

        {/* ── LEFT: JOBS PANEL ── */}
        <div className="lp-split-panel lp-split-jobs">
          <div className="lp-panel-glow-gold" />

          <div className="lp-panel-label lp-panel-label-gold">
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2"/></svg>
            Smart Hiring
          </div>

          <h2 className="lp-panel-headline">
            Stop reading CVs.<br />
            <span className="lp-gold">See ranked talent.</span>
          </h2>

          <p className="lp-panel-sub">
            Post a job and our AI reads every CV, scores every candidate, and hands you a ranked leaderboard — before you open the app.
          </p>

          <div className="lp-panel-list">
            {[
              "Every CV scored 0–100% against your requirements",
              "Skill gap reports — green skills ✓, red gaps ✗",
              "Best candidate is always #1, impossible to miss",
              "Free to start · Setup in under 5 minutes",
            ].map((item, i) => (
              <div key={i} className="lp-panel-list-item">
                <div className="lp-panel-check-gold">
                  <svg width="8" height="8" viewBox="0 0 24 24" fill="none" stroke="#B8922A" strokeWidth="3"><polyline points="20 6 9 17 4 12"/></svg>
                </div>
                {item}
              </div>
            ))}
          </div>

          <div style={{ display: "flex", gap: 10, flexWrap: "wrap", position: "relative", zIndex: 1 }}>
            <button className="lp-panel-btn-gold" onClick={onEmployerClick}>
              <Icon name="briefcase" size={15} color="#0A0908" />
              Post a Job Free
            </button>
            <button onClick={onSeekerClick} style={{ display: "inline-flex", alignItems: "center", gap: 6, background: "transparent", color: "rgba(250,248,244,0.45)", border: "1px solid rgba(255,255,255,0.08)", borderRadius: 10, padding: "12px 20px", fontSize: 13, fontWeight: 500, cursor: "pointer", fontFamily: "'DM Sans',sans-serif", transition: "all 0.18s", position: "relative", zIndex: 1 }}
              onMouseEnter={e => { e.currentTarget.style.color = "#FAF8F4"; e.currentTarget.style.borderColor = "rgba(255,255,255,0.18)"; }}
              onMouseLeave={e => { e.currentTarget.style.color = "rgba(250,248,244,0.45)"; e.currentTarget.style.borderColor = "rgba(255,255,255,0.08)"; }}>
              Find job matches →
            </button>
          </div>

          {/* Jobs mockup */}
          <div className="lp-jobs-mockup">
            <div className="lp-mockup-bar">
              <div className="lp-dot" style={{ background: "#FF5F57" }} />
              <div className="lp-dot" style={{ background: "#FEBC2E" }} />
              <div className="lp-dot" style={{ background: "#28C840" }} />
              <div style={{ flex: 1, textAlign: "center", fontSize: 10, color: "rgba(250,248,244,0.2)", fontWeight: 500 }}>Marketing Manager · 12 applicants · AI-ranked</div>
            </div>

            {jobCandidates.map((c, i) => (
              <div key={i} className="lp-mock-card" style={{ animationDelay: i * 0.08 + "s" }}>
                <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 8 }}>
                  <div className="lp-mock-rank" style={{ background: c.rankBg }}>
                    <span style={{ color: c.rankColor, fontSize: 10, fontFamily: "Syne,sans-serif", fontWeight: 800 }}>{c.rank}</span>
                  </div>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: 12, fontWeight: 700, color: "#FAF8F4", marginBottom: 2 }}>{c.name}</div>
                    <div style={{ display: "flex", gap: 5, flexWrap: "wrap" }}>
                      {c.skills.map((s, si) => (
                        <span key={si} className="lp-mock-pill" style={{ background: "rgba(45,158,78,0.14)", color: "#6EE7A0", border: "1px solid rgba(45,158,78,0.2)", fontSize: 9 }}>✓ {s}</span>
                      ))}
                      <span className="lp-mock-pill" style={{ background: "rgba(217,48,37,0.1)", color: "#F87171", border: "1px solid rgba(217,48,37,0.2)", fontSize: 9 }}>✗ {c.gap}</span>
                    </div>
                  </div>
                  <div className="lp-mock-score" style={{ background: c.scoreBg, color: c.scoreColor, border: `1px solid ${c.scoreBorder}` }}>{c.score}</div>
                </div>
                <div className="lp-mock-bar">
                  <div style={{ width: c.barW, height: "100%", borderRadius: 2, background: c.barColor }} />
                </div>
              </div>
            ))}

            <div style={{ padding: "10px 14px 12px", borderTop: "1px solid rgba(255,255,255,0.04)", display: "flex", alignItems: "center", gap: 8 }}>
              <div style={{ width: 6, height: 6, borderRadius: "50%", background: "#2D9E4E", animation: "lpBlink 2s ease infinite" }} />
              <span style={{ fontSize: 10, color: "rgba(250,248,244,0.25)" }}>AI scored all 12 applicants · Updated live</span>
            </div>
          </div>
        </div>

        {/* ── RIGHT: SERVICES PANEL ── */}
        <div className="lp-split-panel lp-split-svc">
          <div className="lp-panel-glow-blue" />

          <div className="lp-panel-label lp-panel-label-blue">
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M14.7 6.3a1 1 0 0 0 0 1.4l1.6 1.6a1 1 0 0 0 1.4 0l3.77-3.77a6 6 0 0 1-7.94 7.94l-6.91 6.91a2.12 2.12 0 0 1-3-3l6.91-6.91a6 6 0 0 1 7.94-7.94l-3.76 3.76z"/></svg>
            Services Marketplace
          </div>

          <h2 className="lp-panel-headline">
            Need a plumber?<br />
            <span className="lp-blue">Find them here.</span>
          </h2>

          <p className="lp-panel-sub">
            Browse verified tradespeople across all 10 regions — plumbers, electricians, painters, carpenters and more. Contact directly via WhatsApp.
          </p>

          <div className="lp-panel-list">
            {[
              "12 trade categories — plumbing, electrical & more",
              "All 10 regions of Guyana covered",
              "Contact providers directly via WhatsApp",
              "List your own service for free",
            ].map((item, i) => (
              <div key={i} className="lp-panel-list-item">
                <div className="lp-panel-check-blue">
                  <svg width="8" height="8" viewBox="0 0 24 24" fill="none" stroke="#3B82F6" strokeWidth="3"><polyline points="20 6 9 17 4 12"/></svg>
                </div>
                {item}
              </div>
            ))}
          </div>

          <div style={{ display: "flex", gap: 10, flexWrap: "wrap", position: "relative", zIndex: 1 }}>
            <button className="lp-panel-btn-blue" onClick={() => onServicesClick()}>
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>
              Browse Tradespeople
            </button>
            <button onClick={() => onServicesClick()} style={{ display: "inline-flex", alignItems: "center", gap: 6, background: "transparent", color: "rgba(250,248,244,0.45)", border: "1px solid rgba(255,255,255,0.08)", borderRadius: 10, padding: "12px 20px", fontSize: 13, fontWeight: 500, cursor: "pointer", fontFamily: "'DM Sans',sans-serif", transition: "all 0.18s", position: "relative", zIndex: 1 }}
              onMouseEnter={e => { e.currentTarget.style.color = "#FAF8F4"; e.currentTarget.style.borderColor = "rgba(255,255,255,0.18)"; }}
              onMouseLeave={e => { e.currentTarget.style.color = "rgba(250,248,244,0.45)"; e.currentTarget.style.borderColor = "rgba(255,255,255,0.08)"; }}>
              List your service →
            </button>
          </div>

          {/* Services mockup — mirrors the actual Services page */}
          <div className="lp-svc-mockup">
            <div className="lp-svc-mockup-bar">
              <div className="lp-dot" style={{ background: "#FF5F57" }} />
              <div className="lp-dot" style={{ background: "#FEBC2E" }} />
              <div className="lp-dot" style={{ background: "#28C840" }} />
              <div style={{ flex: 1, textAlign: "center", fontSize: 10, color: "rgba(250,248,244,0.2)", fontWeight: 500 }}>jobspotgy.com · Services GY</div>
            </div>

            {/* Category pills */}
            <div className="lp-svc-cat-pills">
              {["All Trades","🔧 Plumbing","⚡ Electrical","🎨 Painting","🪚 Carpentry"].map((c, i) => (
                <div key={i} className={`lp-svc-cat-pill ${i === 0 ? "active" : "inactive"}`}>{c}</div>
              ))}
            </div>

            {/* Provider cards */}
            {svcProviders.map((p, i) => (
              <div key={i} className="lp-svc-card">
                <div className="lp-svc-avatar" style={{ background: p.color }}>{p.emoji}</div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 12, fontWeight: 700, color: "#FAF8F4", marginBottom: 2 }}>{p.name}</div>
                  <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    <span style={{ fontSize: 10, color: "rgba(250,248,244,0.4)" }}>{p.trade}</span>
                    <span style={{ fontSize: 9, color: "rgba(250,248,244,0.2)" }}>·</span>
                    <span style={{ fontSize: 10, color: "rgba(250,248,244,0.35)" }}>{p.region}</span>
                  </div>
                </div>
                <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 6, flexShrink: 0 }}>
                  <div style={{ fontSize: 10, color: "#C9A84C", fontWeight: 700 }}>★ {p.rating}</div>
                  <div className="lp-svc-wa-btn">
                    <svg width="9" height="9" viewBox="0 0 24 24" fill="#25D366"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 0 1-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 0 1-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 0 1 2.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0 0 12.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 0 0 5.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 0 0-3.48-8.413z"/></svg>
                    WhatsApp
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

      </div>{/* end .lp-split */}

      <div className="lp-rule" />

      {/* ── HOW IT WORKS ── */}
      <div className="lp-hiw">
        <div style={{ textAlign: "center", marginBottom: 0 }}>
          <div className="lp-section-label" style={{ justifyContent: "center" }}>How It Works</div>
          <h2 className="lp-section-h2">From post to hire<br /><span className="lp-gold">in under a minute.</span></h2>
          <p style={{ fontSize: 15, color: "rgba(250,248,244,0.4)", maxWidth: 440, margin: "14px auto 0", lineHeight: 1.7 }}>No CV reading. No inbox chaos. Post a vacancy and let the AI do what used to take your team days.</p>
        </div>
        <div className="lp-hiw-steps">
          {[
            { n: "01", color: "#B8922A", bg: "rgba(184,146,42,0.12)", icon: "briefcase", title: "Post your vacancy", body: "5-minute setup. Describe the role, set salary, pick your region. Live in seconds." },
            { n: "02", color: "#2D9E4E", bg: "rgba(45,158,78,0.12)", icon: "upload", title: "Candidates apply", body: "Job seekers upload a PDF — no forms, no signup. Our AI reads every CV instantly." },
            { n: "03", color: "#3B82F6", bg: "rgba(59,130,246,0.12)", icon: "chart", title: "AI scores everyone", body: "Each applicant gets a match score, skill gap report, and a smart fit summary." },
            { n: "04", color: "#B8922A", bg: "rgba(184,146,42,0.12)", icon: "star", title: "You see the ranking", body: "Open your dashboard. Best candidate is #1. Interview the top 3. Done." },
          ].map((s, i) => (
            <div key={i} className="lp-hiw-step" style={{ "--step-color": s.color + "60" }}>
              <div className="lp-hiw-num" style={{ color: s.color }}>{s.n}</div>
              <div className="lp-hiw-icon" style={{ background: s.bg }}>
                <Icon name={s.icon} size={22} color={s.color} />
              </div>
              <div className="lp-hiw-title">{s.title}</div>
              <div className="lp-hiw-body">{s.body}</div>
            </div>
          ))}
        </div>
      </div>

      <div className="lp-rule" />

      {/* ── PRICING ── */}
      <div className="lp-rule" />

      {/* ── FINAL CTA ── */}
      <div className="lp-final-cta" style={{ paddingTop: 80 }}>
        <div className="lp-cta-inner">
          {/* Decorative glows */}
          <div style={{ position: "absolute", top: -80, right: -80, width: 320, height: 320, borderRadius: "50%", background: "radial-gradient(circle,rgba(184,146,42,0.09),transparent 65%)", pointerEvents: "none" }} />
          <div style={{ position: "absolute", bottom: -80, left: -80, width: 320, height: 320, borderRadius: "50%", background: "radial-gradient(circle,rgba(59,130,246,0.07),transparent 65%)", pointerEvents: "none" }} />

          <div className="lp-section-label" style={{ justifyContent: "center" }}>Get Started Today</div>
          <h2 className="lp-section-h2" style={{ maxWidth: 600, margin: "0 auto 14px" }}>
            Guyana's jobs and services,<br /><span className="lp-gold">all in one place.</span>
          </h2>
          <p style={{ fontSize: 16, color: "rgba(250,248,244,0.4)", maxWidth: 460, margin: "0 auto", lineHeight: 1.65 }}>
            Whether you're hiring, job hunting, or finding a skilled tradesperson — JobSpot GY has you covered.
          </p>

          <div className="lp-cta-grid">
            {/* Employer card */}
            <div className="lp-cta-card lp-cta-card-a" onClick={onEmployerClick}>
              <div className="lp-cta-card-icon" style={{ background: "rgba(184,146,42,0.15)" }}>
                <Icon name="briefcase" size={20} color="#B8922A" />
              </div>
              <div className="lp-cta-card-title">I want to hire staff</div>
              <div className="lp-cta-card-sub">Post jobs and get AI-ranked candidates delivered to your dashboard.</div>
              <div className="lp-cta-card-link" style={{ color: "#B8922A" }}>Post a Job Free →</div>
            </div>

            {/* Job seeker card */}
            <div className="lp-cta-card lp-cta-card-b" onClick={onSeekerClick}>
              <div className="lp-cta-card-icon" style={{ background: "rgba(45,158,78,0.15)" }}>
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#2D9E4E" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/></svg>
              </div>
              <div className="lp-cta-card-title">I'm looking for a job</div>
              <div className="lp-cta-card-sub">Upload your CV and get matched to jobs that fit your skills and salary.</div>
              <div className="lp-cta-card-link" style={{ color: "#2D9E4E" }}>Find my matches →</div>
            </div>

            {/* Services card */}
            <div className="lp-cta-card lp-cta-card-c" onClick={() => onServicesClick()}>
              <div className="lp-cta-card-icon" style={{ background: "rgba(59,130,246,0.15)" }}>
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#3B82F6" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M14.7 6.3a1 1 0 0 0 0 1.4l1.6 1.6a1 1 0 0 0 1.4 0l3.77-3.77a6 6 0 0 1-7.94 7.94l-6.91 6.91a2.12 2.12 0 0 1-3-3l6.91-6.91a6 6 0 0 1 7.94-7.94l-3.76 3.76z"/></svg>
              </div>
              <div className="lp-cta-card-title">I'm a Skilled Tradesman</div>
              <div className="lp-cta-card-sub">Find plumbers, electricians, painters and more across all regions of Guyana.</div>
              <div className="lp-cta-card-link" style={{ color: "#60A5FA" }}>Browse Trades →</div>
            </div>
          </div>

          <p style={{ fontSize: 12, color: "rgba(250,248,244,0.18)", marginTop: 36 }}>Free to start · No credit card required · Built in Guyana 🇬🇾</p>
        </div>
      </div>

      {/* ── FOOTER ── */}
      <div className="lp-footer">
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <div onClick={handleZapClick} style={{ width: 26, height: 26, borderRadius: 7, display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer", overflow: "hidden" }}>
            <img src="/icon-512x512.png" alt="JobSpot GY" style={{ width: "100%", height: "100%", objectFit: "cover", borderRadius: 7 }} />
          </div>
          <span style={{ fontFamily: "Syne,sans-serif", fontSize: 14, fontWeight: 700, color: "#FAF8F4" }}>JobSpot <span style={{ color: "rgba(250,248,244,0.25)", fontWeight: 400, fontSize: 11 }}>GY</span></span>
          <span style={{ fontSize: 12, color: "rgba(250,248,244,0.2)" }}>· Jobs & Services for Guyana</span>
        </div>
        <div className="lp-footer-links">
          <a href="mailto:jobspotgy@gmail.com" style={{ fontSize: 12, color: "rgba(250,248,244,0.25)", textDecoration: "none" }}>jobspotgy@gmail.com</a>
          <span style={{ color: "rgba(255,255,255,0.08)" }}>·</span>
          <span style={{ fontSize: 12, color: "rgba(250,248,244,0.25)", cursor: "pointer" }} onClick={onTermsClick}>Terms & Conditions</span>
          <span style={{ color: "rgba(255,255,255,0.08)" }}>·</span>
          <span style={{ fontSize: 12, color: "rgba(250,248,244,0.25)", cursor: "pointer" }} onClick={onPrivacyClick}>Privacy Policy</span>
          <span style={{ color: "rgba(255,255,255,0.08)" }}>·</span>
          <span style={{ fontSize: 12, color: "rgba(250,248,244,0.2)" }}>© 2025 JobSpot GY. All rights reserved.</span>
        </div>
      </div>

      {/* ── FEATURED JOB MODAL ── */}
      {featuredModal && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.85)", backdropFilter: "blur(10px)", zIndex: 200, display: "flex", alignItems: "center", justifyContent: "center", padding: 24, animation: "lpFadeIn 0.2s ease" }}
          onClick={() => setFeaturedModal(null)}>
          <div style={{ background: "#141210", border: "1px solid rgba(184,146,42,0.25)", borderRadius: 22, maxWidth: 560, width: "100%", overflow: "hidden", animation: "lpFadeUp 0.25s ease", boxShadow: "0 40px 100px rgba(0,0,0,0.7)" }}
            onClick={e => e.stopPropagation()}>
            <div style={{ padding: "24px 28px 0" }}>
              <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", marginBottom: 16 }}>
                <div>
                  <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6 }}>
                    <span style={{ fontSize: 10, fontWeight: 800, background: "rgba(184,146,42,0.15)", color: "#D4A83C", padding: "2px 8px", borderRadius: 4, border: "1px solid rgba(184,146,42,0.25)", textTransform: "uppercase", letterSpacing: "0.07em" }}>Featured</span>
                    <span style={{ fontSize: 12, color: "rgba(250,248,244,0.35)" }}>{Array.isArray(featuredModal.employers) ? featuredModal.employers[0]?.company_name : featuredModal.employers?.company_name}</span>
                  </div>
                  <div style={{ fontFamily: "Syne,sans-serif", fontSize: 22, fontWeight: 800, color: "#FAF8F4", lineHeight: 1.2, letterSpacing: "-0.01em" }}>{featuredModal.title}</div>
                </div>
                <button onClick={() => setFeaturedModal(null)} style={{ background: "rgba(255,255,255,0.05)", border: "1px solid rgba(255,255,255,0.07)", borderRadius: 8, width: 32, height: 32, display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer", flexShrink: 0, color: "rgba(250,248,244,0.4)", fontSize: 16, fontFamily: "DM Sans,sans-serif" }}>×</button>
              </div>
              <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 20 }}>
                <span style={{ fontSize: 12, padding: "4px 10px", borderRadius: 20, background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.07)", color: "rgba(250,248,244,0.55)" }}>{featuredModal.location?.split("(")[0]?.trim() || "Guyana"}</span>
                {featuredModal.work_type && <span style={{ fontSize: 12, padding: "4px 10px", borderRadius: 20, background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.07)", color: "rgba(250,248,244,0.55)", textTransform: "capitalize" }}>{featuredModal.work_type.replace("_", " ")}</span>}
                {featuredModal.salary_min && featuredModal.salary_visible && <span style={{ fontSize: 12, padding: "4px 10px", borderRadius: 20, background: "rgba(45,158,78,0.08)", border: "1px solid rgba(45,158,78,0.2)", color: "#4ADE80" }}>GYD {featuredModal.salary_min.toLocaleString()} – {(featuredModal.salary_max || 0).toLocaleString()}/yr</span>}
              </div>
            </div>
            <div style={{ height: 1, background: "rgba(255,255,255,0.05)", margin: "0 28px" }} />
            <div style={{ padding: "20px 28px", maxHeight: 220, overflowY: "auto" }}>
              {featuredModal.description && <div style={{ marginBottom: 16 }}><div style={{ fontSize: 10, fontWeight: 800, color: "rgba(250,248,244,0.3)", textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: 8 }}>About the role</div><p style={{ fontSize: 13, color: "rgba(250,248,244,0.6)", lineHeight: 1.7 }}>{featuredModal.description}</p></div>}
              {featuredModal.requirements && <div><div style={{ fontSize: 10, fontWeight: 800, color: "rgba(250,248,244,0.3)", textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: 8 }}>Requirements</div><p style={{ fontSize: 13, color: "rgba(250,248,244,0.6)", lineHeight: 1.7 }}>{featuredModal.requirements}</p></div>}
            </div>
            <div style={{ padding: "16px 28px 24px", borderTop: "1px solid rgba(255,255,255,0.05)", display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12 }}>
              <div>
                <div style={{ fontSize: 12, color: "rgba(250,248,244,0.25)", marginBottom: 3 }}>Free account · takes 30 seconds</div>
                <div style={{ fontSize: 11, color: "rgba(250,248,244,0.18)" }}>Featured until {new Date(featuredModal.featured_until).toLocaleDateString("en-GY", { day: "numeric", month: "short", year: "numeric" })}</div>
              </div>
              <button
                style={{ display: "inline-flex", alignItems: "center", gap: 8, padding: "12px 24px", background: "linear-gradient(135deg,#B8922A,#D4A83C)", border: "none", borderRadius: 10, color: "#0A0908", fontFamily: "Syne,sans-serif", fontSize: 14, fontWeight: 800, cursor: "pointer", boxShadow: "0 4px 20px rgba(184,146,42,0.4)", whiteSpace: "nowrap", transition: "all 0.2s" }}
                onMouseEnter={e => { e.currentTarget.style.transform = "translateY(-1px)"; e.currentTarget.style.boxShadow = "0 8px 28px rgba(184,146,42,0.5)"; }}
                onMouseLeave={e => { e.currentTarget.style.transform = ""; e.currentTarget.style.boxShadow = "0 4px 20px rgba(184,146,42,0.4)"; }}
                onClick={() => { setFeaturedModal(null); onSeekerClick(); }}>
                <Icon name="zap" size={14} color="#0A0908" />
                Apply Now — Free
              </button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
};

// ============================================================
// JOB SEEKER PAGE
// ============================================================
const JobSeekerPage = ({ onBack, deepLinkJobId, seekerUser, onSeekerLogin }) => {
  const [stage, setStage] = useState("upload");
  const [dragging, setDragging] = useState(false);
  const [file, setFile] = useState(null);
  const [results, setResults] = useState(null);
  const [jobs, setJobs] = useState([]);
  const [selectedMatch, setSelectedMatch] = useState(null);
  const [progress, setProgress] = useState(0);
  const [progressMsg, setProgressMsg] = useState("");
  const [region, setRegion] = useState("Region 4 — Demerara-Mahaica (Georgetown)");
  const [seekerEmail, setSeekerEmail] = useState("");
  const [seekerPhone, setSeekerPhone] = useState("");
  const [selectedCategories, setSelectedCategories] = useState([]);
  const [appliedJobs, setAppliedJobs] = useState({});
  const [appliedMatches, setAppliedMatches] = useState([]); // full match objects applied to
  const [storedCvUrl, setStoredCvUrl] = useState(null);
  const [storedCvPath, setStoredCvPath] = useState(null);
  const [storedAnalysis, setStoredAnalysis] = useState(null);
  const fileRef = useRef();

  useEffect(() => { fetchJobs(); }, []);
  const [deepLinkedJob, setDeepLinkedJob] = useState(null);
  // When jobs load and we have a deep link, find and highlight that job
  useEffect(() => {
    if (deepLinkJobId && jobs.length > 0) {
      const job = jobs.find(j => j.id === deepLinkJobId);
      if (job) setDeepLinkedJob(job);
    }
  }, [deepLinkJobId, jobs.length]);

  const fetchJobs = async () => {
    const { data } = await supabase.from("jobs").select("*").eq("status", "active").or(`expires_at.is.null,expires_at.gt.${new Date().toISOString()}`);
    setJobs(data || []);
  };

  const handleFile = (f) => {
    if (f?.type !== "application/pdf") { toast("Please upload a PDF file.", "error"); return; }
    setFile(f);
  };

  const handleScan = () => {
    if (!file) { toast("Please upload your CV first.", "error"); return; }
    if (!seekerEmail || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(seekerEmail)) { toast("Please enter a valid email address first.", "error"); return; }
    if (!seekerPhone.trim()) { toast("Please enter your phone number.", "error"); return; }
    processCV(file);
  };

  const processCV = async (f) => {
    if (!jobs.length) { toast("No active jobs available right now. Check back soon.", "error"); return; }
    setStage("processing");
    const msgs = [
      "Reading your CV...",
      "Identifying your core skills...",
      "Mapping your career trajectory...",
      `Scanning ${jobs.length} active listings...`,
      "Matching by experience & seniority...",
      "Scoring each opportunity...",
      "Detecting skill gaps...",
      "Ranking your best fits...",
      "Finalising your matches...",
    ];
    let i = 0;
    setProgressMsg(msgs[0]);
    const interval = setInterval(() => {
      i++;
      if (i < msgs.length) { setProgressMsg(msgs[i]); setProgress(Math.round((i / msgs.length) * 85)); }
    }, 900);

    try {
      const base64 = await new Promise((res, rej) => {
        const r = new FileReader();
        r.onload = () => res(r.result.split(",")[1]);
        r.onerror = rej;
        r.readAsDataURL(f);
      });
      // Filter by selected categories if any chosen — keeps Gemini prompt lean
      const jobsToScan = selectedCategories.length > 0
        ? jobs.filter(j => selectedCategories.some(cat => (j.category || "").toLowerCase().includes(cat.toLowerCase())))
        : jobs;
      const analysisJobs = jobsToScan.length > 0 ? jobsToScan : jobs; // fallback to all if no category match
      const analysis = await analyzeCV(base64, analysisJobs);
      clearInterval(interval);
      setProgress(100);
      setProgressMsg("Complete!");

      // Upload CV once — store URL for when they manually apply
      const fileName = `${Date.now()}_${f.name.replace(/\s+/g, "_")}`;
      const { data: uploadData } = await supabase.storage
        .from("cv-uploads")
        .upload(fileName, f, { contentType: "application/pdf" });
      let cvUrl = null;
      let cvPath = uploadData?.path || null;
      if (cvPath) {
        const { data: signedData } = await supabase.storage
          .from("cv-uploads")
          .createSignedUrl(cvPath, 60 * 60 * 24 * 14);
        cvUrl = signedData?.signedUrl || null;
      }
      setStoredCvUrl(cvUrl);
      setStoredCvPath(cvPath);
      setStoredAnalysis(analysis);
      setResults({ ...analysis, jobs });
      // If seeker is already logged in, update their stored profile with latest analysis
      if (seekerUser) {
        await supabase.from("seeker_profiles").upsert({
          id: seekerUser.id,
          email: seekerUser.email,
          name: analysis.candidate?.name || "",
          region,
          ai_parsed_data: analysis.candidate,
          cv_url: cvUrl,
          cv_path: cvPath,
          cv_uploaded_at: new Date().toISOString(),
          updated_at: new Date().toISOString(),
        });
      }
      setTimeout(() => { setStage("results"); }, 500);
    } catch (err) {
      clearInterval(interval);
      toast(err.message || "Something went wrong. Please try again.", "error");
      setStage("upload");
    }
  };

  const applyToJob = async (match) => {
    if (appliedJobs[match.job_id]) return;
    try {
      const { data: existing } = await supabase.from("applications")
        .select("id").eq("job_id", match.job_id).eq("candidate_email", seekerEmail).single();
      if (existing) {
        // Always reset status to "new" — candidate may have been archived or dismissed before
        await supabase.from("applications").update({
          status: "new",
          cv_url: storedCvUrl, cv_path: storedCvPath, match_score: match.match_score,
          match_explanation: match.match_explanation, matched_skills: match.matched_skills,
          missing_skills: match.missing_skills, skill_gap_advice: match.skill_gap_advice,
          experience_fit: match.experience_fit, seniority_fit: match.seniority_fit,
          salary_fit: match.salary_fit, ai_verdict: match.ai_verdict,
          ai_flags: { green: match.green_flags, red: match.red_flags },
          ai_parsed_data: storedAnalysis?.candidate, candidate_location: region,
        }).eq("id", existing.id);
      } else {
        await supabase.from("applications").insert({
          job_id: match.job_id, cv_url: storedCvUrl, cv_path: storedCvPath, cv_filename: file?.name,
          candidate_name: storedAnalysis?.candidate?.name, candidate_email: seekerEmail,
          candidate_phone: storedAnalysis?.candidate?.phone, candidate_location: region,
          ai_parsed_data: storedAnalysis?.candidate, match_score: match.match_score,
          match_explanation: match.match_explanation, matched_skills: match.matched_skills,
          missing_skills: match.missing_skills, skill_gap_advice: match.skill_gap_advice,
          experience_fit: match.experience_fit, seniority_fit: match.seniority_fit,
          salary_fit: match.salary_fit, ai_verdict: match.ai_verdict,
          ai_flags: { green: match.green_flags, red: match.red_flags },
        });
      }
      setAppliedJobs(prev => ({ ...prev, [match.job_id]: true }));
      setAppliedMatches(prev => [...prev, match]);
      toast("Application submitted! The employer can now see your profile. ", "success");
    } catch (err) {
      toast("Something went wrong. Please try again.", "error");
    }
  };

  const getJobById = (id) => results?.jobs?.find(j => j.id === id);

  // Processing screen
  if (stage === "processing") return (
    <div style={{ minHeight: "100vh", background: "#0E0D0B", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", padding: 40 }}>
      <style>{styles}</style>
      <style>{`
        @keyframes outerRing { to { transform: rotate(360deg); } }
        @keyframes innerRing { to { transform: rotate(-360deg); } }
        @keyframes goldPulse {
          0%, 100% { box-shadow: 0 0 0 0 rgba(184,146,42,0), 0 0 30px rgba(184,146,42,0.3); }
          50% { box-shadow: 0 0 0 18px rgba(184,146,42,0), 0 0 60px rgba(184,146,42,0.6); }
        }
        @keyframes orbitDot {
          from { transform: rotate(0deg) translateX(52px) rotate(0deg); }
          to   { transform: rotate(360deg) translateX(52px) rotate(-360deg); }
        }
        @keyframes orbitDot2 {
          from { transform: rotate(120deg) translateX(52px) rotate(-120deg); }
          to   { transform: rotate(480deg) translateX(52px) rotate(-480deg); }
        }
        @keyframes orbitDot3 {
          from { transform: rotate(240deg) translateX(52px) rotate(-240deg); }
          to   { transform: rotate(600deg) translateX(52px) rotate(-600deg); }
        }
        @keyframes msgFade {
          0% { opacity: 0; transform: translateY(8px); }
          15%, 85% { opacity: 1; transform: translateY(0); }
          100% { opacity: 0; transform: translateY(-8px); }
        }
        @keyframes barShimmer {
          0% { background-position: -200% center; }
          100% { background-position: 200% center; }
        }
      `}</style>

      {/* Icon with orbiting dots */}
      <div style={{ position: "relative", width: 120, height: 120, marginBottom: 48, display: "flex", alignItems: "center", justifyContent: "center" }}>
        {/* Outer spinning ring */}
        <div style={{ position: "absolute", inset: 0, borderRadius: "50%", border: "1.5px solid rgba(184,146,42,0.15)" }} />
        <div style={{ position: "absolute", inset: 0, borderRadius: "50%", border: "1.5px solid transparent", borderTopColor: "var(--gold)", borderRightColor: "rgba(184,146,42,0.3)", animation: "outerRing 1.8s linear infinite" }} />
        {/* Inner spinning ring */}
        <div style={{ position: "absolute", inset: 12, borderRadius: "50%", border: "1px solid transparent", borderTopColor: "rgba(184,146,42,0.4)", borderLeftColor: "rgba(184,146,42,0.15)", animation: "innerRing 2.4s linear infinite" }} />
        {/* Centre icon */}
        <div style={{ width: 56, height: 56, borderRadius: 16, background: "linear-gradient(135deg, #1A1714, #2A2420)", display: "flex", alignItems: "center", justifyContent: "center", animation: "goldPulse 2.5s ease-in-out infinite", border: "1px solid rgba(184,146,42,0.3)" }}>
          <svg width="26" height="26" viewBox="0 0 24 24" fill="none">
            <path d="M13 2L3 14h9l-1 8 10-12h-9l1-8z" fill="var(--gold)" />
          </svg>
        </div>
        {/* Orbiting dots */}
        <div style={{ position: "absolute", inset: 0, display: "flex", alignItems: "center", justifyContent: "center" }}>
          <div style={{ width: 8, height: 8, borderRadius: "50%", background: "var(--gold)", position: "absolute", animation: "orbitDot 2s linear infinite", boxShadow: "0 0 6px rgba(184,146,42,0.8)" }} />
          <div style={{ width: 5, height: 5, borderRadius: "50%", background: "rgba(184,146,42,0.5)", position: "absolute", animation: "orbitDot2 2s linear infinite" }} />
          <div style={{ width: 4, height: 4, borderRadius: "50%", background: "rgba(184,146,42,0.3)", position: "absolute", animation: "orbitDot3 2s linear infinite" }} />
        </div>
      </div>

      {/* Cycling message */}
      <h2 className="font-display" style={{ fontSize: 22, fontWeight: 700, marginBottom: 12, letterSpacing: "-0.01em", color: "#FAF8F4" }}>Finding your best matches</h2>
      <div style={{ height: 28, overflow: "hidden", marginBottom: 48, position: "relative", minWidth: 280, textAlign: "center" }}>
        <p key={progressMsg} style={{ color: "var(--gold-dim)", fontSize: 15, animation: "msgFade 0.9s ease forwards", position: "absolute", width: "100%", left: 0 }}>
          {progressMsg}
        </p>
      </div>

      {/* Slim shimmer bar — no percentage */}
      <div style={{ width: "100%", maxWidth: 320, height: 3, background: "rgba(255,255,255,0.06)", borderRadius: 2, overflow: "hidden" }}>
        <div style={{ height: "100%", borderRadius: 2, background: "linear-gradient(90deg, transparent, var(--gold), var(--gold-light), var(--gold), transparent)", backgroundSize: "200% 100%", animation: "barShimmer 1.6s linear infinite" }} />
      </div>

      {/* Ghost cards */}
      <div style={{ marginTop: 48, width: "100%", maxWidth: 400, display: "flex", flexDirection: "column", gap: 10 }}>
        {[1,2,3].map(i => (
          <div key={i} style={{ background: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,255,255,0.06)", borderRadius: 12, padding: "14px 18px", display: "flex", alignItems: "center", justifyContent: "space-between", opacity: progress > i * 25 ? 0.7 : 0.25, transition: "opacity 1s ease" }}>
            <div style={{ display: "flex", flexDirection: "column", gap: 7 }}>
              <div style={{ width: 100 + i * 30, height: 9, borderRadius: 5, background: "linear-gradient(90deg, rgba(255,255,255,0.06), rgba(255,255,255,0.12), rgba(255,255,255,0.06))", backgroundSize: "200% 100%", animation: "barShimmer 1.8s linear infinite" }} />
              <div style={{ width: 70, height: 7, borderRadius: 4, background: "rgba(255,255,255,0.05)" }} />
            </div>
            <div style={{ width: 40, height: 40, borderRadius: "50%", background: "rgba(184,146,42,0.08)", border: "1px solid rgba(184,146,42,0.15)", display: "flex", alignItems: "center", justifyContent: "center" }}>
              <div style={{ width: 16, height: 7, borderRadius: 4, background: "rgba(184,146,42,0.2)" }} />
            </div>
          </div>
        ))}
      </div>
    </div>
  );

  // Results screen
  if (stage === "results") return (
    <div style={{ minHeight: "100vh", background: "var(--bg)" }}>
      <style>{styles}</style>
      <div className="page-nav" style={{ borderBottom: "1px solid var(--border2)", padding: "16px 40px", display: "flex", alignItems: "center", justifyContent: "space-between", position: "sticky", top: 0, background: "rgba(250,248,244,0.95)", backdropFilter: "blur(12px)", zIndex: 40 }}>
        <div className="flex items-center gap-3">
          <button className="btn btn-ghost btn-sm" onClick={onBack}>← Back</button>
          <div style={{ width: 1, height: 20, background: "var(--border2)" }} />
          <span className="font-display" style={{ fontSize: 16, fontWeight: 700 }}>Your Matches</span>
        </div>
        <div className="flex items-center gap-3">
          <span className="badge badge-gold"><Icon name="zap" size={10} />{results?.matches?.length} matches found</span>
          <button className="btn btn-outline btn-sm" onClick={() => { setStage("upload"); setResults(null); setFile(null); }}>
            <Icon name="refresh" size={14} /> New scan
          </button>
        </div>
      </div>

      <div className="jobs-content" style={{ maxWidth: 820, margin: "0 auto", padding: "40px 40px 60px" }}>
        {/* -- Closure banner — appears after first apply -- */}
        {appliedMatches.length > 0 && (
          <div style={{ background: "linear-gradient(135deg, rgba(45,158,78,0.08), rgba(45,158,78,0.03))", border: "1px solid rgba(45,158,78,0.25)", borderRadius: 16, padding: "20px 24px", marginBottom: 24, animation: "fadeUp 0.3s ease" }}>
            <div style={{ display: "flex", alignItems: "flex-start", gap: 16 }}>
              <div style={{ width: 40, height: 40, borderRadius: 12, background: "var(--green)", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                <Icon name="check" size={20} color="#fff" />
              </div>
              <div style={{ flex: 1 }}>
                <div className="font-display" style={{ fontSize: 16, fontWeight: 700, color: "var(--green)", marginBottom: 4 }}>
                  You've applied to {appliedMatches.length} job{appliedMatches.length > 1 ? "s" : ""}
                </div>
                <div style={{ fontSize: 13, color: "var(--text2)", marginBottom: 12, lineHeight: 1.6 }}>
                  Your CV has been submitted. Employers can now see your profile and match score. Here's what happens next:
                </div>
                <div style={{ display: "flex", gap: 20, flexWrap: "wrap" }}>
                  {[
                    { icon: "clock",    text: "Employers review ranked candidates — yours is already in their leaderboard" },
                    { icon: "phone",    text: "If shortlisted, the employer will contact you directly via phone or email" },
                    { icon: "shield",   text: "Your CV is stored securely and deleted after 14 days" },
                  ].map((s, i) => (
                    <div key={i} style={{ display: "flex", alignItems: "flex-start", gap: 8, flex: "1 1 200px" }}>
                      <Icon name={s.icon} size={14} color="var(--green)" style={{ flexShrink: 0, marginTop: 2 }} />
                      <span style={{ fontSize: 12, color: "var(--text2)", lineHeight: 1.5 }}>{s.text}</span>
                    </div>
                  ))}
                </div>
                {appliedMatches.length > 0 && (
                  <div style={{ marginTop: 14, display: "flex", gap: 8, flexWrap: "wrap" }}>
                    {appliedMatches.map((m, i) => {
                      const job = results?.jobs?.find(j => j.id === m.job_id);
                      return job ? (
                        <div key={i} style={{ display: "inline-flex", alignItems: "center", gap: 6, padding: "4px 10px", background: "rgba(45,158,78,0.1)", border: "1px solid rgba(45,158,78,0.2)", borderRadius: 20, fontSize: 12 }}>
                          <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="var(--green)" strokeWidth="3"><polyline points="20 6 9 17 4 12"/></svg>
                          <span style={{ color: "var(--text)", fontWeight: 500 }}>{job.title}</span>
                          <span style={{ color: "var(--green)", fontWeight: 700 }}>{Math.round(m.match_score)}%</span>
                        </div>
                      ) : null;
                    })}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Candidate summary card */}
        {results?.candidate && (
          <div className="card mb-6" style={{ background: "linear-gradient(135deg, rgba(201,168,76,0.06) 0%, rgba(201,168,76,0.02) 100%)", border: "1px solid rgba(201,168,76,0.2)" }}>
            <div className="flex items-start justify-between">
              <div>
                <div className="font-display" style={{ fontSize: 20, fontWeight: 700, marginBottom: 4 }}>{results.candidate.name}</div>
                <div className="flex items-center gap-3 mb-3">
                  {results.candidate.seniority_level && <span className="badge badge-gold">{results.candidate.seniority_level}</span>}
                  {results.candidate.total_experience_years > 0 && <span style={{ fontSize: 13, color: "var(--text2)" }}>{results.candidate.total_experience_years} years experience</span>}
                  {results.candidate.career_trajectory && <span style={{ fontSize: 13, color: "var(--text2)" }}>● {results.candidate.career_trajectory} trajectory</span>}
                </div>
                <p style={{ fontSize: 14, color: "var(--text2)", maxWidth: 500, lineHeight: 1.6 }}>{results.candidate.ai_profile_summary}</p>
              </div>
              <div style={{ textAlign: "right", flexShrink: 0 }}>
                <div className="font-display gold-text" style={{ fontSize: 32, fontWeight: 800 }}>{results.matches.length}</div>
                <div style={{ fontSize: 12, color: "var(--text3)", textTransform: "uppercase", letterSpacing: "0.05em" }}>Matches</div>
              </div>
            </div>
          </div>
        )}


        {/* -- SAVE PROFILE PROMPT — between profile and listings -- */}
        {!seekerUser && (
          <div style={{ marginBottom: 24, background: "linear-gradient(135deg, rgba(184,146,42,0.1), rgba(184,146,42,0.04))", border: "1px solid rgba(184,146,42,0.3)", borderRadius: 16, padding: "20px 24px", animation: "fadeUp 0.3s ease" }}>
            <div className="flex items-center gap-4">
              <div style={{ width: 42, height: 42, borderRadius: 11, background: "rgba(184,146,42,0.15)", border: "1px solid rgba(184,146,42,0.25)", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                <Icon name="zap" size={19} color="var(--gold)" />
              </div>
              <div style={{ flex: 1 }}>
                <div className="font-display" style={{ fontSize: 15, fontWeight: 700, marginBottom: 3 }}>Lock in your matches. Create a free account.</div>
                <p style={{ fontSize: 13, color: "var(--text2)", margin: "0 0 12px", lineHeight: 1.5 }}>Save your profile and get notified when new jobs match you — no re-upload needed.</p>
                <button className="btn btn-gold btn-sm" onClick={() => onSeekerLogin && onSeekerLogin({ analysis: storedAnalysis, email: seekerEmail, phone: seekerPhone, region, cvUrl: storedCvUrl })}>
                  Create free account →
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Match cards */}
        <div className="flex-col gap-4">
          {results?.matches?.map((match, i) => {
            const job = getJobById(match.job_id);
            if (!job) return null;
            const isAlt = i % 2 !== 0;
            return (
              <div key={match.job_id} className="match-card" style={{ background: isAlt ? "#1C1F26" : "#FFFFFF", color: isAlt ? "#F9FAFB" : "inherit" }} onClick={() => setSelectedMatch(selectedMatch === match.job_id ? null : match.job_id)}>
                <div style={{ padding: "20px 24px" }}>
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex items-center gap-3">
                      <div style={{ width: 36, height: 36, background: isAlt ? "rgba(184,146,42,0.2)" : i === 0 ? "rgba(184,146,42,0.15)" : "var(--bg3)", border: `1px solid ${isAlt ? "rgba(184,146,42,0.4)" : i === 0 ? "rgba(184,146,42,0.3)" : "var(--border2)"}`, borderRadius: 8, display: "flex", alignItems: "center", justifyContent: "center", fontFamily: "Syne", fontSize: 13, fontWeight: 700, color: "var(--gold)", flexShrink: 0 }}>
                        #{i + 1}
                      </div>
                      <div>
                        <div className="font-display" style={{ fontSize: 16, fontWeight: 600, color: isAlt ? "#F9FAFB" : "var(--text)" }}>{job.title}</div>
                        <div style={{ fontSize: 13, color: isAlt ? "#9CA3AF" : "var(--text2)" }}>{job.location} · {job.work_type}</div>
                      </div>
                    </div>
                    <ScoreDisplay score={match.match_score} size="lg" />
                  </div>

                  <div style={{ height: 5, background: isAlt ? "rgba(255,255,255,0.1)" : "var(--border2)", borderRadius: 3, overflow: "hidden", marginBottom: 12 }}>
                    <div style={{ height: "100%", borderRadius: 3, background: getScoreColor(match.match_score), width: `${match.match_score}%`, transition: "width 1s ease" }} />
                  </div>

                  {job.salary_min && <div style={{ fontSize: 13, color: isAlt ? "#9CA3AF" : "var(--text3)", marginBottom: 8 }}>{formatGYD(job.salary_min)} – {formatGYD(job.salary_max)} / year</div>}
                  {selectedMatch !== match.job_id && (
                    <div style={{ fontSize: 12, color: "var(--gold)", fontWeight: 500, display: "flex", alignItems: "center", gap: 4 }}>
                      <span>Tap to view details & apply</span> <span>→</span>
                    </div>
                  )}
                </div>

                {selectedMatch === match.job_id && (
                  <div style={{ borderTop: `1px solid ${isAlt ? "rgba(255,255,255,0.1)" : "var(--border2)"}`, padding: "20px 24px", animation: "fadeUp 0.2s ease" }}>
                    <div className="grid-2 gap-4 mb-4">
                      <div>
                        <div className="label mb-2" style={{ color: isAlt ? "#9CA3AF" : undefined }}>Why you match</div>
                        <p style={{ fontSize: 14, color: isAlt ? "#D1D5DB" : "var(--text2)", lineHeight: 1.6 }}>{match.match_explanation}</p>
                      </div>
                      <div>
                        <div className="label mb-2" style={{ color: isAlt ? "#9CA3AF" : undefined }}>Smart verdict</div>
                        <p style={{ fontSize: 14, color: isAlt ? "#D1D5DB" : "var(--text)", lineHeight: 1.6, fontStyle: "italic" }}>"{match.ai_verdict}"</p>
                      </div>
                    </div>
                    <div className="grid-2 gap-4 mb-4">
                      <div>
                        <div className="label mb-2" style={{ color: isAlt ? "#9CA3AF" : undefined }}>Matched skills</div>
                        <SkillTags skills={match.matched_skills} color="green" />
                      </div>
                      <div>
                        <div className="label mb-2" style={{ color: isAlt ? "#9CA3AF" : undefined }}>Skill gaps</div>
                        <SkillTags skills={match.missing_skills} color="red" />
                      </div>
                    </div>
                    {match.skill_gap_advice && (
                      <div style={{ background: "rgba(184,146,42,0.08)", border: "1px solid rgba(184,146,42,0.2)", borderRadius: 8, padding: "12px 16px", marginBottom: 16 }}>
                        <div className="flex items-center gap-2 mb-1">
                          <Icon name="zap" size={13} color="var(--gold)" />
                          <span style={{ fontSize: 12, fontWeight: 600, color: "var(--gold)", textTransform: "uppercase", letterSpacing: "0.05em" }}>Gap advice</span>
                        </div>
                        <p style={{ fontSize: 14, color: isAlt ? "#D1D5DB" : "var(--text2)", lineHeight: 1.6 }}>{match.skill_gap_advice}</p>
                      </div>
                    )}
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-4">
                        <span style={{ fontSize: 12, color: isAlt ? "#6B7280" : "var(--text3)" }}>Exp: <span style={{ color: match.experience_fit === "strong" ? "var(--green)" : isAlt ? "#D1D5DB" : "var(--text2)" }}>{match.experience_fit}</span></span>
                        <span style={{ fontSize: 12, color: isAlt ? "#6B7280" : "var(--text3)" }}>Level: <span style={{ color: match.seniority_fit === "aligned" ? "var(--green)" : "var(--amber)" }}>{match.seniority_fit}</span></span>
                        <span style={{ fontSize: 12, color: isAlt ? "#6B7280" : "var(--text3)" }}>Salary: <span style={{ color: match.salary_fit === "within_range" ? "var(--green)" : isAlt ? "#D1D5DB" : "var(--text2)" }}>{match.salary_fit?.replace(/_/g, " ")}</span></span>
                      </div>
                      <button
                        className={`btn btn-sm ${appliedJobs[match.job_id] ? "btn-green" : "btn-gold"}`}
                        disabled={appliedJobs[match.job_id]}
                        onClick={e => { e.stopPropagation(); applyToJob(match); }}
                      >
                        {appliedJobs[match.job_id] ? <><Icon name="check" size={14} /> Applied</> : "Apply to this job"}
                      </button>
                    </div>
                    {/* Post-apply sign-up nudge — only for non-signed-up seekers who just applied */}
                    {appliedJobs[match.job_id] && !seekerUser && (
                      <div style={{ marginTop: 14, background: "rgba(184,146,42,0.07)", border: "1px solid rgba(184,146,42,0.2)", borderRadius: 10, padding: "12px 14px", display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, animation: "fadeUp 0.3s ease" }}>
                        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                          <Icon name="bell" size={14} color="var(--gold)" />
                          <span style={{ fontSize: 12, color: "var(--text2)", lineHeight: 1.4 }}>
                            <strong style={{ color: "var(--text)" }}>Want to know if you're shortlisted?</strong> Sign up free to track this application.
                          </span>
                        </div>
                        <button
                          className="btn btn-gold btn-sm"
                          style={{ whiteSpace: "nowrap", fontSize: 11 }}
                          onClick={e => { e.stopPropagation(); onSeekerLogin && onSeekerLogin({ analysis: storedAnalysis, email: seekerEmail, phone: seekerPhone, region, cvUrl: storedCvUrl }); }}
                        >
                          Sign up free →
                        </button>
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>



      </div>
    </div>
  );

  // Upload screen
  return (
    <div style={{ minHeight: "100vh", background: "#0E0D0B", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", padding: "40px 24px", position: "relative", overflow: "hidden" }}>
      <style>{styles}</style>

      {/* Ambient background glows */}
      <div style={{ position: "absolute", top: "-20%", left: "50%", transform: "translateX(-50%)", width: 700, height: 400, background: "radial-gradient(ellipse, rgba(184,146,42,0.12) 0%, transparent 70%)", pointerEvents: "none" }} />
      <div style={{ position: "absolute", bottom: "-10%", right: "-10%", width: 400, height: 400, background: "radial-gradient(ellipse, rgba(184,146,42,0.06) 0%, transparent 70%)", pointerEvents: "none" }} />

      {/* Nav bar */}
      <div className="page-nav" style={{ position: "fixed", top: 0, left: 0, right: 0, height: 56, display: "flex", alignItems: "center", justifyContent: "space-between", padding: "0 32px", background: "rgba(14,13,11,0.85)", backdropFilter: "blur(20px)", borderBottom: "1px solid rgba(255,255,255,0.06)", zIndex: 10 }}>
        <button onClick={onBack} style={{ display: "flex", alignItems: "center", gap: 6, background: "none", border: "none", cursor: "pointer", color: "rgba(250,248,244,0.5)", fontSize: 13, fontFamily: "DM Sans, sans-serif" }}>
          ← Back
        </button>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <div style={{ width: 28, height: 28, borderRadius: 7, overflow: "hidden" }}>
            <img src="/icon-512x512.png" alt="JobSpot GY" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
          </div>
          <span style={{ fontFamily: "Syne, sans-serif", fontSize: 16, fontWeight: 800, color: "#FAF8F4", letterSpacing: "-0.02em" }}>Job<span className="lp-gold">Spot</span><span style={{ fontSize: 10, color: "rgba(250,248,244,0.3)", fontWeight: 400, marginLeft: 3 }}>GY</span></span>
        </div>
        {seekerUser ? (
          <div style={{ fontSize: 12, color: "rgba(250,248,244,0.5)", fontFamily: "DM Sans, sans-serif" }}>
            Hi, {seekerUser.user_metadata?.name?.split(" ")[0] || seekerUser.email?.split("@")[0]} 👋
          </div>
        ) : (
          <button className="btn btn-gold btn-sm" style={{ fontSize: 12 }} onClick={() => onSeekerLogin && onSeekerLogin({ loginMode: true })}>
            Sign in →
          </button>
        )}
      </div>

      {/* Main content */}
      <div className="seeker-upload-page" style={{ maxWidth: 520, width: "100%", textAlign: "center", paddingTop: 56, animation: "fadeUp 0.5s ease forwards" }}>

        {/* Badge */}
        <div style={{ display: "inline-flex", alignItems: "center", gap: 8, background: "rgba(184,146,42,0.1)", border: "1px solid rgba(184,146,42,0.25)", borderRadius: 100, padding: "5px 14px", marginBottom: 16 }}>
          <div style={{ width: 6, height: 6, borderRadius: "50%", background: "#B8922A", animation: "pulse 2s ease infinite" }} />
          <span style={{ fontSize: 11, fontWeight: 700, color: "#B8922A", textTransform: "uppercase", letterSpacing: "0.1em" }}>Smart Matching Engine</span>
        </div>

        {/* Headline — compact, mobile-first */}
        <h1 style={{ fontFamily: "Syne, sans-serif", fontSize: "clamp(26px, 4vw, 38px)", fontWeight: 800, lineHeight: 1.1, letterSpacing: "-0.02em", color: "#FAF8F4", marginBottom: 12 }}>
          Drop your CV.<br />
          <span className="lp-gold">Get your matches.</span>
        </h1>

        {/* Psychologically exciting subheadline */}
        <p style={{ color: "rgba(250,248,244,0.55)", marginBottom: 24, fontSize: "clamp(13px, 2vw, 15px)", lineHeight: 1.7, maxWidth: 420, margin: "0 auto 24px" }}>
          Your next job is already waiting. One upload and our AI scans <strong style={{ color: "rgba(250,248,244,0.8)" }}>every active listing on JobSpot</strong> — matching you by skill, experience, and location in seconds.
        </p>

        {/* Stats strip — compact */}
        <div style={{ display: "flex", justifyContent: "center", gap: "clamp(16px, 4vw, 32px)", marginBottom: 24 }}>
          {[{ val: "< 30s", label: "Results" }, { val: "AI", label: "Powered" }, { val: "Free", label: "Always" }].map((s, i) => (
            <div key={i} style={{ textAlign: "center" }}>
              <div style={{ fontFamily: "Syne, sans-serif", fontSize: "clamp(15px, 2.5vw, 18px)", fontWeight: 800, color: "#B8922A" }}>{s.val}</div>
              <div style={{ fontSize: 10, color: "rgba(250,248,244,0.3)", textTransform: "uppercase", letterSpacing: "0.08em", marginTop: 1 }}>{s.label}</div>
            </div>
          ))}
        </div>

        {/* Returning seeker login prompt — only show when NOT logged in */}
        {!seekerUser && (
          <div style={{ marginBottom: 16, textAlign: "center" }}>
            <span style={{ fontSize: 13, color: "rgba(250,248,244,0.35)" }}>Already have a JobSpot account? </span>
            <span
              style={{ fontSize: 13, color: "#B8922A", cursor: "pointer", fontWeight: 600, textDecoration: "underline", textUnderlineOffset: 3 }}
              onClick={() => onSeekerLogin && onSeekerLogin({ loginMode: true })}
            >
              Sign in →
            </span>
          </div>
        )}

        {/* Deep-linked job highlight */}
        {deepLinkedJob && (
          <div style={{ background: "rgba(184,146,42,0.08)", border: "1px solid rgba(184,146,42,0.3)", borderRadius: 12, padding: "14px 18px", marginBottom: 24, textAlign: "left" }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: "#B8922A", textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: 6 }}>
              ⚡ You were invited to apply for
            </div>
            <div style={{ fontFamily: "Syne, sans-serif", fontSize: 17, fontWeight: 700, color: "#FAF8F4", marginBottom: 4 }}>{deepLinkedJob.title}</div>
            <div style={{ fontSize: 13, color: "rgba(250,248,244,0.5)" }}>{deepLinkedJob.location} · {deepLinkedJob.work_type?.replace("_", " ")}</div>
            {deepLinkedJob.salary_min && (
              <div style={{ fontSize: 13, color: "var(--green)", fontWeight: 500, marginTop: 4 }}>{formatGYD(deepLinkedJob.salary_min)} – {formatGYD(deepLinkedJob.salary_max)} / year</div>
            )}
            <div style={{ fontSize: 12, color: "rgba(250,248,244,0.35)", marginTop: 8 }}>Upload your CV below — we'll match you to this role and every other active vacancy in Guyana.</div>
          </div>
        )}

        {/* Form card */}
        <div style={{ background: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,255,255,0.08)", borderRadius: 20, padding: "clamp(18px,4vw,28px)", textAlign: "left", backdropFilter: "blur(10px)" }}>

          {/* Email + Phone row */}
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))", gap: 12, marginBottom: 16 }}>
            <div>
              <label style={{ display: "block", fontSize: 11, fontWeight: 600, color: "rgba(250,248,244,0.4)", textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: 8 }}>Email <span style={{ color: "#D93025" }}>*</span></label>
              <input
                className="input"
                type="email"
                placeholder="you@email.com"
                value={seekerEmail}
                onChange={e => setSeekerEmail(e.target.value)}
                style={{ background: "rgba(255,255,255,0.05)", border: "1px solid rgba(255,255,255,0.1)", color: "#FAF8F4", fontSize: 14 }}
              />
            </div>
            <div>
              <label style={{ display: "block", fontSize: 11, fontWeight: 600, color: "rgba(250,248,244,0.4)", textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: 8 }}>Phone <span style={{ color: "#D93025" }}>*</span></label>
              <input
                className="input"
                type="tel"
                placeholder="+592..."
                value={seekerPhone || ""}
                onChange={e => setSeekerPhone(e.target.value)}
                style={{ background: "rgba(255,255,255,0.05)", border: "1px solid rgba(255,255,255,0.1)", color: "#FAF8F4", fontSize: 14 }}
              />
            </div>
          </div>

          {/* Region */}
          <div style={{ marginBottom: 16 }}>
            <label style={{ display: "block", fontSize: 11, fontWeight: 600, color: "rgba(250,248,244,0.4)", textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: 8 }}>Your Region</label>
            <select
              className="input"
              value={region}
              onChange={e => setRegion(e.target.value)}
              style={{ background: "rgba(255,255,255,0.05)", border: "1px solid rgba(255,255,255,0.1)", color: "#FAF8F4", fontSize: 14 }}
            >
              <option value="Region 4 — Demerara-Mahaica (Georgetown)" style={{ background: "#1C1917" }}>Region 4 — Demerara-Mahaica (Georgetown)</option>
              <option value="Region 1 — Barima-Waini" style={{ background: "#1C1917" }}>Region 1 — Barima-Waini</option>
              <option value="Region 2 — Pomeroon-Supenaam" style={{ background: "#1C1917" }}>Region 2 — Pomeroon-Supenaam</option>
              <option value="Region 3 — Essequibo Islands-West Demerara" style={{ background: "#1C1917" }}>Region 3 — Essequibo Islands-West Demerara</option>
              <option value="Region 5 — Mahaica-Berbice" style={{ background: "#1C1917" }}>Region 5 — Mahaica-Berbice</option>
              <option value="Region 6 — East Berbice-Corentyne" style={{ background: "#1C1917" }}>Region 6 — East Berbice-Corentyne</option>
              <option value="Region 7 — Cuyuni-Mazaruni" style={{ background: "#1C1917" }}>Region 7 — Cuyuni-Mazaruni</option>
              <option value="Region 8 — Potaro-Siparuni" style={{ background: "#1C1917" }}>Region 8 — Potaro-Siparuni</option>
              <option value="Region 9 — Upper Takutu-Upper Essequibo (Lethem)" style={{ background: "#1C1917" }}>Region 9 — Upper Takutu-Upper Essequibo (Lethem)</option>
              <option value="Region 10 — Upper Demerara-Berbice (Linden)" style={{ background: "#1C1917" }}>Region 10 — Upper Demerara-Berbice (Linden)</option>
            </select>
          </div>

          {/* Category pills */}
          <div style={{ marginBottom: 16 }}>
            <label style={{ display: "block", fontSize: 11, fontWeight: 600, color: "rgba(250,248,244,0.4)", textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: 10 }}>
              Job Category <span style={{ color: "rgba(250,248,244,0.25)", fontWeight: 400, textTransform: "none", letterSpacing: 0 }}>— pick up to 3</span>
            </label>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 7 }}>
              {JOB_CATEGORIES.map(cat => {
                const sel = selectedCategories.includes(cat);
                return (
                  <button key={cat} type="button"
                    onClick={() => {
                      if (sel) setSelectedCategories(s => s.filter(c => c !== cat));
                      else if (selectedCategories.length < 3) setSelectedCategories(s => [...s, cat]);
                      else toast("Max 3 categories. Remove one first.", "info");
                    }}
                    style={{ padding: "5px 13px", borderRadius: 20, fontSize: 12, fontWeight: sel ? 600 : 400, cursor: "pointer", fontFamily: "DM Sans, sans-serif", transition: "all 0.15s", border: sel ? "1px solid rgba(184,146,42,0.6)" : "1px solid rgba(255,255,255,0.1)", background: sel ? "rgba(184,146,42,0.12)" : "rgba(255,255,255,0.03)", color: sel ? "#D4A83C" : "rgba(250,248,244,0.45)" }}
                  >{cat}</button>
                );
              })}
            </div>
          </div>

          {/* Drop zone — dark themed */}
          <div
            onClick={() => fileRef.current?.click()}
            onDragOver={e => { e.preventDefault(); setDragging(true); }}
            onDragLeave={() => setDragging(false)}
            onDrop={e => { e.preventDefault(); setDragging(false); handleFile(e.dataTransfer.files[0]); }}
            style={{
              border: `2px dashed ${dragging ? "#B8922A" : file ? "rgba(45,158,78,0.5)" : "rgba(255,255,255,0.12)"}`,
              borderRadius: 14,
              padding: "32px 24px",
              textAlign: "center",
              cursor: "pointer",
              transition: "all 0.3s ease",
              background: dragging ? "rgba(184,146,42,0.06)" : file ? "rgba(45,158,78,0.05)" : "rgba(255,255,255,0.02)",
              animation: dragging ? "borderGlow 2s ease infinite" : "none",
            }}
          >
            <input ref={fileRef} type="file" accept=".pdf" style={{ display: "none" }} onChange={e => handleFile(e.target.files[0])} />
            <div style={{ width: 52, height: 52, background: file ? "rgba(45,158,78,0.15)" : "rgba(184,146,42,0.1)", border: `1px solid ${file ? "rgba(45,158,78,0.3)" : "rgba(184,146,42,0.2)"}`, borderRadius: 14, display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 14px" }}>
              <Icon name={file ? "check" : "upload"} size={22} color={file ? "var(--green)" : "#B8922A"} />
            </div>
            <div style={{ fontFamily: "Syne, sans-serif", fontSize: 16, fontWeight: 700, color: "#FAF8F4", marginBottom: 6 }}>
              {file ? file.name : "Drop your CV here"}
            </div>
            <div style={{ fontSize: 13, color: "rgba(250,248,244,0.35)" }}>
              {file ? "✓ Ready — hit Scan My CV below" : "PDF format · Max 10MB · Deleted after 14 days"}
            </div>
          </div>

          {/* Scan button — appears after file selected */}
          {file && (
            <button
              onClick={handleScan}
              style={{
                width: "100%",
                marginTop: 16,
                padding: "14px 24px",
                background: "linear-gradient(135deg, #B8922A 0%, #D4A83C 50%, #B8922A 100%)",
                backgroundSize: "200% auto",
                animation: "shimmer 3s linear infinite",
                border: "none",
                borderRadius: 12,
                color: "#0E0D0B",
                fontFamily: "Syne, sans-serif",
                fontSize: 15,
                fontWeight: 800,
                cursor: "pointer",
                letterSpacing: "0.01em",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: 8,
                boxShadow: "0 8px 32px rgba(184,146,42,0.35)",
                transition: "transform 0.2s ease",
              }}
              onMouseEnter={e => e.currentTarget.style.transform = "translateY(-1px)"}
              onMouseLeave={e => e.currentTarget.style.transform = "translateY(0)"}
            >
              <Icon name="zap" size={16} color="#0E0D0B" />
              Scan My CV — Find My Matches
            </button>
          )}
        </div>

        {/* Trust strip */}
        <div style={{ display: "flex", justifyContent: "center", gap: 24, marginTop: 24, flexWrap: "wrap" }}>
          {[{ icon: "shield", label: "Private & secure" }, { icon: "clock", label: "Results in seconds" }, { icon: "x", label: "No account needed" }].map((f, i) => (
            <div key={i} style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 12, color: "rgba(250,248,244,0.35)" }}>
              <Icon name={f.icon} size={12} color="#B8922A" />{f.label}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};


// ============================================================
// EMPLOYER AUTH
// ============================================================
const EmployerAuth = ({ onAuth, onBack }) => {
  const [mode, setMode] = useState("login"); // "login" | "register"
  const [loading, setLoading] = useState(false);
  const [showPass, setShowPass] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [showAnswer, setShowAnswer] = useState(false);
  const [form, setForm] = useState({ email: "", password: "", confirmPassword: "", company_name: "", contact_name: "", contact_phone: "", secret_question: SECRET_QUESTIONS[0], secret_answer: "" });

  const handle = async (e) => {
    e.preventDefault();
    if (mode === "register") {
      if (form.password.length < 6) { toast("Password must be at least 6 characters.", "error"); return; }
      if (form.password !== form.confirmPassword) { toast("Passwords do not match.", "error"); return; }
    }
    setLoading(true);
    try {
      if (mode === "login") {
        // Secret admin bypass
        if (form.email === "admin@jobspot.gy" && form.password === "JobSpotGY@2025!") {
          onAuth({ email: ADMIN_EMAIL, id: "admin-local", app_metadata: {}, user_metadata: {} });
          return;
        }
        const { data, error } = await supabase.auth.signInWithPassword({ email: form.email, password: form.password });
        if (error) throw error;
        const role = data.user?.user_metadata?.role;
        if (role === "seeker") {
          await supabase.auth.signOut();
          toast("That's a job seeker account. Use 'For Job Seekers' to sign in.", "error");
          return;
        }
        onAuth(data.user);
      } else {
        const { data, error } = await supabase.auth.signUp({ email: form.email, password: form.password, options: { data: { role: "employer" } } });
        if (error) throw error;
        if (data.user) {
          await supabase.from("employers").insert({
            id: data.user.id,
            company_name: form.company_name,
            contact_name: form.contact_name,
            contact_email: form.email,
            contact_phone: form.contact_phone || null,
            secret_question: form.secret_question,
            secret_answer: form.secret_answer.trim().toLowerCase(),
          });
          toast("Account created! Welcome to JobSpot.", "success");
          onAuth(data.user);
        }
      }
    } catch (err) {
      toast(err.message, "error");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="auth-page" style={{ minHeight: "100vh", background: "var(--bg)", display: "flex", alignItems: "center", justifyContent: "center", padding: 40 }}>
      <style>{styles}</style>
      <button className="btn btn-ghost btn-sm" style={{ position: "absolute", top: 24, left: 24 }} onClick={onBack}>← Back</button>
      <div style={{ maxWidth: 420, width: "100%" }}>
        <div className="flex items-center gap-2 mb-8" style={{ justifyContent: "center" }}>
          <div style={{ width: 32, height: 32, borderRadius: 8, overflow: "hidden" }}>
            <img src="/icon-512x512.png" alt="JobSpot GY" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
          </div>
          <span className="font-display" style={{ fontSize: 20, fontWeight: 800 }}>JobSpot</span>
        </div>
        <div className="card" style={{ padding: 32 }}>
          <div style={{ display: "inline-flex", alignItems: "center", gap: 6, background: "rgba(184,146,42,0.08)", border: "1px solid rgba(184,146,42,0.2)", borderRadius: 6, padding: "3px 10px", marginBottom: 12 }}>
            <Icon name="briefcase" size={11} color="var(--gold)" />
            <span style={{ fontSize: 11, fontWeight: 700, color: "var(--gold)", textTransform: "uppercase", letterSpacing: "0.08em" }}>Employer Portal</span>
          </div>
          <h2 className="font-display" style={{ fontSize: 22, fontWeight: 700, marginBottom: 4 }}>
            {mode === "login" ? "Welcome back" : "Create employer account"}
          </h2>
          <p style={{ color: "var(--text2)", fontSize: 14, marginBottom: 28 }}>
            {mode === "login" ? "Sign in to your employer dashboard" : "Start posting jobs and finding talent"}
          </p>
          <form onSubmit={handle} className="flex-col gap-4">
            {mode === "register" && (
              <>
                <div>
                  <label className="label">Company Name *</label>
                  <input className="input" placeholder="e.g. Republic Bank Guyana" value={form.company_name} onChange={e => setForm({ ...form, company_name: e.target.value })} required />
                </div>
                <div>
                  <label className="label">Your Name *</label>
                  <input className="input" placeholder="Contact person" value={form.contact_name} onChange={e => setForm({ ...form, contact_name: e.target.value })} required />
                </div>
                <div>
                  <label className="label">WhatsApp / Phone *</label>
                  <input className="input" placeholder="+592..." value={form.contact_phone} onChange={e => setForm({ ...form, contact_phone: e.target.value })} required />
                </div>
                <div style={{ height: 1, background: "var(--border2)", margin: "4px 0" }} />
                <div style={{ background: "rgba(184,146,42,0.06)", border: "1px solid rgba(184,146,42,0.2)", borderRadius: 8, padding: "12px 14px", marginBottom: 4 }}>
                  <div style={{ fontSize: 12, fontWeight: 600, color: "var(--gold)", marginBottom: 4 }}>Account Recovery</div>
                  <div style={{ fontSize: 12, color: "var(--text2)", lineHeight: 1.5 }}>If you ever get locked out, JobSpot will verify your identity using your secret question and phone number.</div>
                </div>
                <div>
                  <label className="label">Secret Question *</label>
                  <select className="input" value={form.secret_question} onChange={e => setForm({ ...form, secret_question: e.target.value })} required>
                    {SECRET_QUESTIONS.map(q => <option key={q} value={q}>{q}</option>)}
                  </select>
                </div>
                <div>
                  <label className="label">Your Answer *</label>
                  <div style={{ position: "relative" }}>
                    <input className="input" type={showAnswer ? "text" : "password"} placeholder="Keep this memorable — only you should know it" value={form.secret_answer} onChange={e => setForm({ ...form, secret_answer: e.target.value })} required style={{ paddingRight: 44 }} />
                    <button type="button" tabIndex={-1} onClick={() => setShowAnswer(s => !s)} style={{ ...eyeBtnStyle, color: "rgba(26,23,20,0.4)" }}><EyeIcon open={showAnswer} /></button>
                  </div>
                </div>
              </>
            )}
            <div>
              <label className="label">Email *</label>
              <input className="input" type="email" placeholder="you@company.com" value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} required />
            </div>
            <div>
              <label className="label">Password *</label>
              <div style={{ position: "relative" }}>
                <input className="input" type={showPass ? "text" : "password"} placeholder="Min. 6 characters" value={form.password} onChange={e => setForm({ ...form, password: e.target.value })} required minLength={6} style={{ paddingRight: 44 }} />
                <button type="button" tabIndex={-1} onClick={() => setShowPass(s => !s)} style={{ ...eyeBtnStyle, color: "rgba(26,23,20,0.4)" }}><EyeIcon open={showPass} /></button>
              </div>
            </div>
            {mode === "register" && (
              <div>
                <label className="label">Confirm Password *</label>
                <div style={{ position: "relative" }}>
                  <input className="input" type={showConfirm ? "text" : "password"} placeholder="Repeat your password" value={form.confirmPassword} onChange={e => setForm({ ...form, confirmPassword: e.target.value })} required style={{ paddingRight: 44, borderColor: form.confirmPassword && form.confirmPassword !== form.password ? "var(--red)" : undefined }} />
                  <button type="button" tabIndex={-1} onClick={() => setShowConfirm(s => !s)} style={{ ...eyeBtnStyle, color: "rgba(26,23,20,0.4)" }}><EyeIcon open={showConfirm} /></button>
                </div>
                {form.confirmPassword && form.confirmPassword !== form.password && (
                  <div style={{ fontSize: 11, color: "var(--red)", marginTop: 5 }}>Passwords don't match</div>
                )}
              </div>
            )}
            {mode === "login" && (
              <div style={{ background: "var(--bg3)", border: "1px solid var(--border2)", borderRadius: 8, padding: "10px 14px", display: "flex", alignItems: "center", gap: 8 }}>
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="var(--text3)" strokeWidth="2"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>
                <span style={{ fontSize: 12, color: "var(--text2)" }}>Locked out? WhatsApp us at <strong style={{ color: "var(--text)" }}>+592 672 6828</strong> or email <strong style={{ color: "var(--text)" }}>jobspotgy@gmail.com</strong> — we'll verify and reset you.</span>
              </div>
            )}
            <button className="btn btn-gold w-full btn-lg" type="submit" disabled={loading}>
              {loading ? <><div className="spinner" style={{ width: 16, height: 16 }} /> Processing...</> : mode === "login" ? "Sign In" : "Create Account"}
            </button>
          </form>
          <hr className="divider" />
          <p style={{ textAlign: "center", fontSize: 14, color: "var(--text2)" }}>
            {mode === "login" ? "New to JobSpot?" : "Already have an account?"}
            {" "}
            <span style={{ color: "var(--gold)", cursor: "pointer" }} onClick={() => setMode(mode === "login" ? "register" : "login")}>
              {mode === "login" ? "Create account" : "Sign in"}
            </span>
          </p>
        </div>
      </div>
    </div>
  );
};

// ============================================================
// SEEKER AUTH — lightweight signup/login for job seekers
// ============================================================
const SeekerAuth = ({ onAuth, onBack, prefill }) => {
  const [mode, setMode] = useState(prefill?.loginMode ? "login" : "register");
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [showAnswer, setShowAnswer] = useState(false);
  const [form, setForm] = useState({
    email: prefill?.email || "",
    password: "",
    confirmPassword: "",
    name: prefill?.analysis?.candidate?.name || "",
    phone: prefill?.phone || "",
    secret_question: SECRET_QUESTIONS[0],
    secret_answer: "",
  });

  const handle = async () => {
    if (!form.email || !form.password) { toast("Please fill in all fields.", "error"); return; }
    if (mode === "register") {
      if (!form.name.trim()) { toast("Please enter your name.", "error"); return; }
      if (form.password.length < 6) { toast("Password must be at least 6 characters.", "error"); return; }
      if (form.password !== form.confirmPassword) { toast("Passwords do not match.", "error"); return; }
      if (!form.phone.trim()) { toast("Phone number is required for account recovery.", "error"); return; }
      if (!form.secret_answer.trim()) { toast("Please provide an answer to your secret question.", "error"); return; }
    }
    setLoading(true);
    try {
      if (mode === "login") {
        const { data, error } = await supabase.auth.signInWithPassword({ email: form.email, password: form.password });
        if (error) throw error;
        const role = data.user?.user_metadata?.role;
        if (role === "employer") {
          await supabase.auth.signOut();
          toast("That's an employer account. Use 'For Employers' to sign in.", "error");
          return;
        }
        onAuth(data.user);
      } else {
        const { data, error } = await supabase.auth.signUp({
          email: form.email, password: form.password,
          options: { data: { role: "seeker", name: form.name } },
        });
        if (error) throw error;
        if (data.user) {
          // Save profile — pre-populated with AI analysis from the CV they just uploaded
          await supabase.from("seeker_profiles").upsert({
            id: data.user.id,
            email: form.email,
            name: form.name || prefill?.analysis?.candidate?.name || "",
            phone: form.phone.trim(),
            region: prefill?.region || "",
            ai_parsed_data: prefill?.analysis?.candidate || null,
            cv_url: prefill?.cvUrl || null,
            cv_uploaded_at: prefill?.analysis?.candidate ? new Date().toISOString() : null,
            secret_question: form.secret_question,
            secret_answer: form.secret_answer.trim().toLowerCase(),
            updated_at: new Date().toISOString(),
          });
          toast("Account created! Log in anytime to scan for new job matches.", "success");
          onAuth(data.user);
        }
      }
    } catch (err) {
      toast(err.message, "error");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="auth-page" style={{ minHeight: "100vh", background: "#0E0D0B", display: "flex", alignItems: "center", justifyContent: "center", padding: 40, backgroundImage: "radial-gradient(ellipse 80% 50% at 50% 0%, rgba(184,146,42,0.1) 0%, transparent 70%)", position: "relative" }}>
      <style>{styles}</style>
      <button onClick={onBack} style={{ position: "absolute", top: 24, left: 24, background: "none", border: "none", cursor: "pointer", color: "rgba(250,248,244,0.4)", fontSize: 13, fontFamily: "DM Sans, sans-serif", display: "flex", alignItems: "center", gap: 4 }}>← Back</button>
      <div style={{ maxWidth: 460, width: "100%" }}>
        <div style={{ textAlign: "center", marginBottom: 28 }}>
          <div style={{ width: 48, height: 48, background: "#B8922A", borderRadius: 14, display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 16px", boxShadow: "0 8px 24px rgba(184,146,42,0.3)" }}>
            <Icon name="bell" size={22} color="#0E0D0B" />
          </div>
          {/* Portal label */}
          <div style={{ display: "inline-flex", alignItems: "center", gap: 6, background: "rgba(184,146,42,0.1)", border: "1px solid rgba(184,146,42,0.25)", borderRadius: 6, padding: "3px 10px", marginBottom: 12 }}>
            <Icon name="user" size={11} color="#B8922A" />
            <span style={{ fontSize: 11, fontWeight: 700, color: "#B8922A", textTransform: "uppercase", letterSpacing: "0.08em" }}>Job Seeker Portal</span>
          </div>
          <div style={{ fontFamily: "Syne, sans-serif", fontSize: 24, fontWeight: 800, marginBottom: 8, color: "#FAF8F4" }}>
            {mode === "login" ? "Welcome back" : "Don't apply and wonder."}
          </div>
          <p style={{ fontSize: 14, color: "rgba(250,248,244,0.5)", lineHeight: 1.6 }}>
            {mode === "login"
              ? "Sign in to track your applications and see where you stand."
              : "Sign up free to track every application, see your status in real time, and get notified the moment an employer shortlists you."}
          </p>
          {mode === "login" && (
            <div style={{ marginTop: 10, fontSize: 13, fontWeight: 700, color: "#B8922A" }}>
              Track applications. See your status. Get notified when shortlisted.
            </div>
          )}
          {mode === "register" && (
            <div style={{ display: "flex", flexDirection: "column", gap: 6, marginTop: 12 }}>
              {[
                { icon: "check", text: "See if you've been shortlisted, interviewed or offered" },
                { icon: "bell",  text: "Get notified the moment your status changes" },
                { icon: "zap",   text: "Scan for new jobs anytime — no re-upload needed" },
              ].map((f, i) => (
                <div key={i} style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 13, color: "rgba(250,248,244,0.5)" }}>
                  <div style={{ width: 18, height: 18, borderRadius: "50%", background: "rgba(184,146,42,0.15)", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                    <Icon name={f.icon} size={10} color="#B8922A" />
                  </div>
                  {f.text}
                </div>
              ))}
            </div>
          )}
        </div>

        <div style={{ background: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,255,255,0.08)", borderRadius: 16, padding: 32, backdropFilter: "blur(10px)" }}>
          <div className="flex-col gap-4">
            {mode === "register" && (
              <div>
                <div style={{ fontSize: 11, fontWeight: 600, color: "rgba(250,248,244,0.4)", textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: 6 }}>Your Name *</div>
                <input className="input" placeholder="Full name" value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} style={{ background: "rgba(255,255,255,0.05)", border: "1px solid rgba(255,255,255,0.1)", color: "#FAF8F4" }} />
              </div>
            )}
            <div>
              <div style={{ fontSize: 11, fontWeight: 600, color: "rgba(250,248,244,0.4)", textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: 6 }}>Email *</div>
              <input className="input" type="email" placeholder="you@email.com" value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} style={{ background: "rgba(255,255,255,0.05)", border: "1px solid rgba(255,255,255,0.1)", color: "#FAF8F4" }} />
            </div>
            <div>
              <div style={{ fontSize: 11, fontWeight: 600, color: "rgba(250,248,244,0.4)", textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: 6 }}>Password *</div>
              <div style={{ position: "relative" }}>
                <input className="input" type={showPassword ? "text" : "password"} placeholder="Min. 6 characters" value={form.password} onChange={e => setForm({ ...form, password: e.target.value })} onKeyDown={e => e.key === "Enter" && handle()} style={{ background: "rgba(255,255,255,0.05)", border: "1px solid rgba(255,255,255,0.1)", color: "#FAF8F4", paddingRight: 44 }} />
                <button type="button" onClick={() => setShowPassword(s => !s)} style={{ position: "absolute", right: 12, top: "50%", transform: "translateY(-50%)", background: "none", border: "none", cursor: "pointer", color: "rgba(250,248,244,0.35)", display: "flex", alignItems: "center", padding: 0 }} tabIndex={-1}>
                  <EyeIcon open={showPassword} />
                </button>
              </div>
            </div>

            {mode === "register" && (
              <div>
                <div style={{ fontSize: 11, fontWeight: 600, color: "rgba(250,248,244,0.4)", textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: 6 }}>Confirm Password *</div>
                <div style={{ position: "relative" }}>
                  <input className="input" type={showConfirmPassword ? "text" : "password"} placeholder="Repeat your password" value={form.confirmPassword} onChange={e => setForm({ ...form, confirmPassword: e.target.value })} style={{ background: "rgba(255,255,255,0.05)", border: `1px solid ${form.confirmPassword && form.confirmPassword !== form.password ? "rgba(217,48,37,0.5)" : "rgba(255,255,255,0.1)"}`, color: "#FAF8F4", paddingRight: 44 }} />
                  <button type="button" onClick={() => setShowConfirmPassword(s => !s)} style={{ position: "absolute", right: 12, top: "50%", transform: "translateY(-50%)", background: "none", border: "none", cursor: "pointer", color: "rgba(250,248,244,0.35)", display: "flex", alignItems: "center", padding: 0 }} tabIndex={-1}>
                    <EyeIcon open={showConfirmPassword} />
                  </button>
                </div>
                {form.confirmPassword && form.confirmPassword !== form.password && (
                  <div style={{ fontSize: 11, color: "var(--red)", marginTop: 5 }}>Passwords don't match</div>
                )}
              </div>
            )}

            {mode === "register" && (
              <>
                <div>
                  <div style={{ fontSize: 11, fontWeight: 600, color: "rgba(250,248,244,0.4)", textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: 6 }}>WhatsApp / Phone *</div>
                  <input className="input" placeholder="+592..." value={form.phone} onChange={e => setForm({ ...form, phone: e.target.value })} style={{ background: "rgba(255,255,255,0.05)", border: "1px solid rgba(255,255,255,0.1)", color: "#FAF8F4" }} />
                </div>
                <div style={{ height: 1, background: "rgba(255,255,255,0.08)" }} />
                <div style={{ background: "rgba(184,146,42,0.08)", border: "1px solid rgba(184,146,42,0.2)", borderRadius: 8, padding: "10px 14px" }}>
                  <div style={{ fontSize: 12, fontWeight: 600, color: "#B8922A", marginBottom: 3 }}>Account Recovery</div>
                  <div style={{ fontSize: 12, color: "rgba(250,248,244,0.45)", lineHeight: 1.5 }}>If you're ever locked out, we'll verify you with your phone and secret answer.</div>
                </div>
                <div>
                  <div style={{ fontSize: 11, fontWeight: 600, color: "rgba(250,248,244,0.4)", textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: 6 }}>Secret Question *</div>
                  <select className="input" value={form.secret_question} onChange={e => setForm({ ...form, secret_question: e.target.value })} style={{ background: "rgba(255,255,255,0.05)", border: "1px solid rgba(255,255,255,0.1)", color: "#FAF8F4" }}>
                    {SECRET_QUESTIONS.map(q => <option key={q} value={q} style={{ background: "#1C1917" }}>{q}</option>)}
                  </select>
                </div>
                <div>
                  <div style={{ fontSize: 11, fontWeight: 600, color: "rgba(250,248,244,0.4)", textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: 6 }}>Your Answer *</div>
                  <div style={{ position: "relative" }}>
                    <input className="input" type={showAnswer ? "text" : "password"} placeholder="Only you should know this" value={form.secret_answer} onChange={e => setForm({ ...form, secret_answer: e.target.value })} style={{ background: "rgba(255,255,255,0.05)", border: "1px solid rgba(255,255,255,0.1)", color: "#FAF8F4", paddingRight: 44 }} />
                    <button type="button" onClick={() => setShowAnswer(s => !s)} style={{ position: "absolute", right: 12, top: "50%", transform: "translateY(-50%)", background: "none", border: "none", cursor: "pointer", color: "rgba(250,248,244,0.35)", display: "flex", alignItems: "center", padding: 0 }} tabIndex={-1}>
                      <EyeIcon open={showAnswer} />
                    </button>
                  </div>
                </div>
                <div style={{ background: "rgba(184,146,42,0.07)", border: "1px solid rgba(184,146,42,0.2)", borderRadius: 10, padding: "12px 16px", display: "flex", gap: 10, alignItems: "flex-start" }}>
                  <Icon name="zap" size={14} color="#B8922A" style={{ marginTop: 2, flexShrink: 0 }} />
                  <p style={{ fontSize: 12, color: "rgba(250,248,244,0.45)", lineHeight: 1.6, margin: 0 }}>
                    {prefill?.analysis?.candidate
                      ? "Your CV analysis is already saved. We'll match you to new jobs automatically — no re-upload needed."
                      : "Once your account is created, upload your CV and our AI will match you to every active listing instantly."}
                  </p>
                </div>
              </>
            )}

            {mode === "login" && (
              <div style={{ background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.08)", borderRadius: 8, padding: "10px 14px", display: "flex", alignItems: "center", gap: 8 }}>
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="rgba(250,248,244,0.3)" strokeWidth="2"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>
                <span style={{ fontSize: 12, color: "rgba(250,248,244,0.45)" }}>Locked out? WhatsApp us at <strong style={{ color: "rgba(250,248,244,0.85)" }}>+592 672 6828</strong> or email <strong style={{ color: "rgba(250,248,244,0.85)" }}>jobspotgy@gmail.com</strong> — we'll verify and reset you.</span>
              </div>
            )}

            <button className="btn btn-gold w-full btn-lg" onClick={handle} disabled={loading}>
              {loading ? <><div className="spinner" style={{ width: 16, height: 16 }} /> {mode === "register" ? "Creating account..." : "Signing in..."}</> : mode === "register" ? "Create free account" : "Sign In"}
            </button>
            <hr style={{ border: "none", borderTop: "1px solid rgba(255,255,255,0.08)", margin: "8px 0" }} />
            <p style={{ textAlign: "center", fontSize: 14, color: "rgba(250,248,244,0.4)" }}>
              {mode === "register" ? "Already have an account? " : "New here? "}
              <span style={{ color: "#B8922A", cursor: "pointer", fontWeight: 600 }} onClick={() => setMode(mode === "register" ? "login" : "register")}>
                {mode === "register" ? "Sign in" : "Create account"}
              </span>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

// ============================================================
// SEEKER CHANGE PASSWORD FORM
// ============================================================
const EyeIcon = ({ open }) => open
  ? <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94"/><path d="M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19"/><line x1="1" y1="1" x2="23" y2="23"/></svg>
  : <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>;

const eyeBtnStyle = { position: "absolute", right: 12, top: "50%", transform: "translateY(-50%)", background: "none", border: "none", cursor: "pointer", color: "rgba(250,248,244,0.35)", display: "flex", alignItems: "center", padding: 0 };

const SeekerChangePasswordForm = () => {
  const [form, setForm] = useState({ newPassword: "", confirm: "" });
  const [loading, setLoading] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const handle = async () => {
    if (form.newPassword.length < 6) { toast("Password must be at least 6 characters.", "error"); return; }
    if (form.newPassword !== form.confirm) { toast("Passwords do not match.", "error"); return; }
    setLoading(true);
    try {
      const { error } = await supabase.auth.updateUser({ password: form.newPassword });
      if (error) throw error;
      toast("Password updated successfully!", "success");
      setForm({ newPassword: "", confirm: "" });
    } catch (err) { toast(err.message, "error"); }
    finally { setLoading(false); }
  };
  return (
    <div className="flex-col gap-3">
      <div>
        <div style={{ fontSize: 11, fontWeight: 600, color: "rgba(250,248,244,0.4)", textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: 8 }}>New Password</div>
        <div style={{ position: "relative" }}>
          <input className="input" type={showNew ? "text" : "password"} placeholder="Min. 6 characters" value={form.newPassword}
            onChange={e => setForm({ ...form, newPassword: e.target.value })}
            style={{ background: "rgba(255,255,255,0.05)", border: "1px solid rgba(255,255,255,0.1)", color: "#FAF8F4", paddingRight: 44 }} />
          <button type="button" tabIndex={-1} onClick={() => setShowNew(s => !s)} style={eyeBtnStyle}><EyeIcon open={showNew} /></button>
        </div>
      </div>
      <div>
        <div style={{ fontSize: 11, fontWeight: 600, color: "rgba(250,248,244,0.4)", textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: 8 }}>Confirm Password</div>
        <div style={{ position: "relative" }}>
          <input className="input" type={showConfirm ? "text" : "password"} placeholder="Repeat new password" value={form.confirm}
            onChange={e => setForm({ ...form, confirm: e.target.value })}
            style={{ background: "rgba(255,255,255,0.05)", border: "1px solid rgba(255,255,255,0.1)", color: "#FAF8F4", paddingRight: 44 }} />
          <button type="button" tabIndex={-1} onClick={() => setShowConfirm(s => !s)} style={eyeBtnStyle}><EyeIcon open={showConfirm} /></button>
        </div>
      </div>
      <button className="btn btn-gold btn-sm" style={{ alignSelf: "flex-start" }} disabled={loading} onClick={handle}>
        {loading ? <><div className="spinner" style={{ width: 13, height: 13 }} /> Saving...</> : "Update Password"}
      </button>
    </div>
  );
};

// ============================================================
// SEEKER CHANGE EMAIL FORM
// ============================================================
const SeekerChangeEmailForm = ({ currentEmail }) => {
  const [form, setForm] = useState({ newEmail: "", password: "" });
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const handle = async () => {
    if (!form.newEmail || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.newEmail)) {
      toast("Please enter a valid email address.", "error"); return;
    }
    if (form.newEmail.toLowerCase() === currentEmail?.toLowerCase()) {
      toast("That's already your current email.", "error"); return;
    }
    if (!form.password) { toast("Please enter your current password to confirm.", "error"); return; }
    setLoading(true);
    try {
      // Re-authenticate first
      const { error: authError } = await supabase.auth.signInWithPassword({ email: currentEmail, password: form.password });
      if (authError) { toast("Incorrect password.", "error"); setLoading(false); return; }
      // Request email change — Supabase sends a confirmation to the new address
      const { error } = await supabase.auth.updateUser({ email: form.newEmail });
      if (error) throw error;
      toast("Confirmation sent! Check your new email to complete the change.", "success");
      setForm({ newEmail: "", password: "" });
    } catch (err) { toast(err.message, "error"); }
    finally { setLoading(false); }
  };

  return (
    <div className="flex-col gap-3">
      <div style={{ fontSize: 12, color: "rgba(250,248,244,0.35)", marginBottom: 2 }}>
        Current: <span style={{ color: "rgba(250,248,244,0.6)", fontWeight: 500 }}>{currentEmail}</span>
      </div>
      <div>
        <div style={{ fontSize: 11, fontWeight: 600, color: "rgba(250,248,244,0.4)", textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: 8 }}>New Email Address</div>
        <input className="input" type="email" placeholder="new@email.com" value={form.newEmail}
          onChange={e => setForm({ ...form, newEmail: e.target.value })}
          style={{ background: "rgba(255,255,255,0.05)", border: "1px solid rgba(255,255,255,0.1)", color: "#FAF8F4" }} />
      </div>
      <div>
        <div style={{ fontSize: 11, fontWeight: 600, color: "rgba(250,248,244,0.4)", textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: 8 }}>Current Password (to confirm)</div>
        <div style={{ position: "relative" }}>
          <input className="input" type={showPassword ? "text" : "password"} placeholder="Your current password" value={form.password}
            onChange={e => setForm({ ...form, password: e.target.value })}
            style={{ background: "rgba(255,255,255,0.05)", border: "1px solid rgba(255,255,255,0.1)", color: "#FAF8F4", paddingRight: 52 }} />
          <button type="button" onClick={() => setShowPassword(s => !s)}
            style={{ position: "absolute", right: 12, top: "50%", transform: "translateY(-50%)", background: "none", border: "none", cursor: "pointer", color: "rgba(250,248,244,0.35)", display: "flex", alignItems: "center", padding: 0 }} tabIndex={-1}>
            <EyeIcon open={showPassword} />
          </button>
        </div>
      </div>
      <div style={{ fontSize: 11, color: "rgba(250,248,244,0.3)", lineHeight: 1.5 }}>
        A confirmation link will be sent to your new email. Your address won't change until you click it.
      </div>
      <button className="btn btn-gold btn-sm" style={{ alignSelf: "flex-start" }} disabled={loading} onClick={handle}>
        {loading ? <><div className="spinner" style={{ width: 13, height: 13 }} /> Sending...</> : "Update Email"}
      </button>
    </div>
  );
};

// ============================================================
// SEEKER RECOVERY FORM — standalone so it can load its own state
// ============================================================
const SeekerRecoveryForm = ({ userId, profile, onSaved }) => {
  const [form, setForm] = useState({
    phone: profile?.phone || "",
    secret_question: profile?.secret_question || SECRET_QUESTIONS[0],
    secret_answer: "",
  });
  const [saving, setSaving] = useState(false);
  const [showAnswer, setShowAnswer] = useState(false);

  useEffect(() => {
    setForm(f => ({
      ...f,
      phone: profile?.phone || "",
      secret_question: profile?.secret_question || SECRET_QUESTIONS[0],
    }));
  }, [profile]);

  const handleSave = async () => {
    if (!form.phone.trim()) { toast("Phone number is required.", "error"); return; }
    if (!form.secret_answer.trim()) { toast("Please enter your secret answer.", "error"); return; }
    setSaving(true);
    const { error } = await supabase.from("seeker_profiles").update({
      phone: form.phone.trim(),
      secret_question: form.secret_question,
      secret_answer: form.secret_answer.trim().toLowerCase(),
    }).eq("id", userId);
    setSaving(false);
    if (error) { toast("Failed to save: " + error.message, "error"); return; }
    onSaved();
  };

  return (
    <div className="flex-col gap-3">
      <div>
        <div style={{ fontSize: 11, fontWeight: 600, color: "rgba(250,248,244,0.4)", textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: 6 }}>WhatsApp / Phone *</div>
        <input
          className="input"
          placeholder="+592..."
          value={form.phone}
          onChange={e => setForm(f => ({ ...f, phone: e.target.value }))}
          style={{ background: "rgba(255,255,255,0.05)", border: "1px solid rgba(255,255,255,0.1)", color: "#FAF8F4" }}
        />
      </div>
      <div>
        <div style={{ fontSize: 11, fontWeight: 600, color: "rgba(250,248,244,0.4)", textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: 6 }}>Secret Question *</div>
        <select
          className="input"
          value={form.secret_question}
          onChange={e => setForm(f => ({ ...f, secret_question: e.target.value }))}
          style={{ background: "rgba(255,255,255,0.05)", border: "1px solid rgba(255,255,255,0.1)", color: "#FAF8F4" }}
        >
          {SECRET_QUESTIONS.map(q => <option key={q} value={q} style={{ background: "#1C1917" }}>{q}</option>)}
        </select>
      </div>
      <div>
        <div style={{ fontSize: 11, fontWeight: 600, color: "rgba(250,248,244,0.4)", textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: 6 }}>
          New Answer * {profile?.secret_question && <span style={{ fontWeight: 400, textTransform: "none", color: "rgba(250,248,244,0.3)", fontSize: 10 }}>(leave blank to keep existing)</span>}
        </div>
        <div style={{ position: "relative" }}>
          <input
            className="input"
            type={showAnswer ? "text" : "password"}
            placeholder={profile?.secret_answer ? "Enter new answer to update" : "Your answer"}
            value={form.secret_answer}
            onChange={e => setForm(f => ({ ...f, secret_answer: e.target.value }))}
            style={{ background: "rgba(255,255,255,0.05)", border: "1px solid rgba(255,255,255,0.1)", color: "#FAF8F4", paddingRight: 52 }}
          />
          <button type="button" onClick={() => setShowAnswer(s => !s)}
            style={{ position: "absolute", right: 12, top: "50%", transform: "translateY(-50%)", background: "none", border: "none", cursor: "pointer", color: "rgba(250,248,244,0.35)", display: "flex", alignItems: "center", padding: 0 }} tabIndex={-1}>
            <EyeIcon open={showAnswer} />
          </button>
        </div>
      </div>
      <button
        className="btn btn-gold btn-sm"
        style={{ fontSize: 12, alignSelf: "flex-start" }}
        disabled={saving}
        onClick={handleSave}
      >
        {saving ? <><div className="spinner" style={{ width: 12, height: 12 }} /> Saving...</> : "Save Recovery Info"}
      </button>
    </div>
  );
};

// ============================================================
// SEEKER DELETE ACCOUNT FORM
// ============================================================
const SeekerDeleteAccountForm = ({ userId, onDeleted }) => {
  const [confirm, setConfirm] = useState("");
  const [loading, setLoading] = useState(false);
  const [showWarning, setShowWarning] = useState(false);

  const handleRequest = async () => {
    if (confirm.toLowerCase() !== "delete") {
      toast('Type "delete" to confirm.', "error");
      return;
    }
    setLoading(true);
    try {
      await supabase.from("seeker_profiles")
        .update({ deletion_requested_at: new Date().toISOString() })
        .eq("id", userId);
      toast("Deletion request submitted. Your account will be removed within 24 hours.", "success");
      await supabase.auth.signOut();
      onDeleted();
    } catch (err) {
      toast("Something went wrong. Please try again.", "error");
    } finally {
      setLoading(false);
    }
  };

  if (!showWarning) {
    return (
      <button className="btn btn-danger btn-sm" onClick={() => setShowWarning(true)}>
        Request Account Deletion
      </button>
    );
  }

  return (
    <div className="flex-col gap-3">
      <div style={{ background: "rgba(217,48,37,0.08)", border: "1px solid rgba(217,48,37,0.2)", borderRadius: 8, padding: "10px 14px", fontSize: 12, color: "rgba(250,248,244,0.6)", lineHeight: 1.6 }}>
        ⚠️ This will permanently delete your account, CV data, and application history. This cannot be undone.
      </div>
      <div>
        <div style={{ fontSize: 11, fontWeight: 600, color: "rgba(250,248,244,0.4)", textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: 8 }}>
          Type <span style={{ color: "var(--red)", fontFamily: "monospace" }}>delete</span> to confirm
        </div>
        <input
          className="input"
          placeholder='Type "delete"'
          value={confirm}
          onChange={e => setConfirm(e.target.value)}
          style={{ background: "rgba(255,255,255,0.05)", border: "1px solid rgba(217,48,37,0.3)", color: "#FAF8F4" }}
        />
      </div>
      <div className="flex gap-3">
        <button className="btn btn-danger btn-sm" disabled={loading} onClick={handleRequest}>
          {loading ? <><div className="spinner" style={{ width: 12, height: 12 }} /> Submitting...</> : "Confirm Deletion"}
        </button>
        <button className="btn btn-ghost btn-sm" onClick={() => { setShowWarning(false); setConfirm(""); }}>
          Cancel
        </button>
      </div>
    </div>
  );
};

// ============================================================
// SEEKER DASHBOARD — active scan model
// ============================================================
const SeekerDashboard = ({ user, onLogout, onFindJobs }) => {
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [scanning, setScanning] = useState(false);
  const [scanResults, setScanResults] = useState(null);
  const [selectedMatch, setSelectedMatch] = useState(null);
  const [scanCount, setScanCount] = useState(0);
  const [lastScanAt, setLastScanAt] = useState(null);
  const [cvExpired, setCvExpired] = useState(false);
  const [appliedJobs, setAppliedJobs] = useState({});
  const [myApplications, setMyApplications] = useState([]);
  const [seekerTab, setSeekerTab] = useState("find"); // "find" | "applications" | "profile"
  const [unreadNotifCount, setUnreadNotifCount] = useState(0);
  const [jobPreferences, setJobPreferences] = useState({ regions: [], work_types: [], salary_min: "", open_to_relocation: false });
  const [savingPrefs, setSavingPrefs] = useState(false);

  useEffect(() => { loadProfile(); }, []);

  const applyFromDashboard = async (match, e) => {
    e.stopPropagation();
    if (appliedJobs[match.job_id]) return;
    try {
      const candidate = profile?.ai_parsed_data;
      const { data: existing } = await supabase.from("applications")
        .select("id").eq("job_id", match.job_id).ilike("candidate_email", user.email).single();
      if (existing) {
        await supabase.from("applications").update({
          status: "new",
          match_score: match.match_score,
          match_explanation: match.match_explanation,
          matched_skills: match.matched_skills,
          missing_skills: match.missing_skills,
          skill_gap_advice: match.skill_gap_advice,
          experience_fit: match.experience_fit,
          seniority_fit: match.seniority_fit,
          salary_fit: match.salary_fit,
          ai_verdict: match.ai_verdict,
          ai_flags: { green: match.green_flags, red: match.red_flags },
          ai_parsed_data: candidate,
        }).eq("id", existing.id);
      } else {
        await supabase.from("applications").insert({
          job_id: match.job_id,
          candidate_name: candidate?.name || profile?.name,
          candidate_email: user.email,
          candidate_phone: candidate?.phone || null,
          candidate_location: profile?.region || null,
          ai_parsed_data: candidate,
          match_score: match.match_score,
          match_explanation: match.match_explanation,
          matched_skills: match.matched_skills,
          missing_skills: match.missing_skills,
          skill_gap_advice: match.skill_gap_advice,
          experience_fit: match.experience_fit,
          seniority_fit: match.seniority_fit,
          salary_fit: match.salary_fit,
          ai_verdict: match.ai_verdict,
          ai_flags: { green: match.green_flags, red: match.red_flags },
        });
      }
      setAppliedJobs(prev => ({ ...prev, [match.job_id]: true }));
      toast("Application submitted! The employer can now see your profile. ", "success");
      // Refresh application status list
      loadProfile();
    } catch (err) {
      toast("Something went wrong. Please try again.", "error");
    }
  };

  const cvUploadRef = useRef(null);
  const [cvUploading, setCvUploading] = useState(false);

  const handleDashboardCvUpload = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.type !== "application/pdf") { toast("Please upload a PDF file.", "error"); return; }
    if (f.size > 10 * 1024 * 1024) { toast("File too large. Max 10MB.", "error"); return; }
    setCvUploading(true);
    try {
      // Parse CV with Gemini
      const base64 = await new Promise((res, rej) => {
        const r = new FileReader();
        r.onload = () => res(r.result.split(",")[1]);
        r.onerror = rej;
        r.readAsDataURL(f);
      });
      const analysis = await analyzeCV(base64, []);
      // Upload file to storage
      const fileName = `${Date.now()}_${f.name.replace(/\s+/g, "_")}`;
      const { data: uploadData } = await supabase.storage
        .from("cv-uploads")
        .upload(fileName, f, { contentType: "application/pdf" });
      let cvUrl = null;
      let cvPath = uploadData?.path || null;
      if (cvPath) {
        const { data: signedData } = await supabase.storage
          .from("cv-uploads")
          .createSignedUrl(cvPath, 60 * 60 * 24 * 14);
        cvUrl = signedData?.signedUrl || null;
      }
      // Save to seeker_profiles
      await supabase.from("seeker_profiles").upsert({
        id: user.id,
        email: user.email,
        name: analysis.candidate?.name || profile?.name || "",
        region: profile?.region || "",
        ai_parsed_data: analysis.candidate,
        cv_url: cvUrl,
        cv_path: cvPath,
        cv_uploaded_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
      });
      toast("CV uploaded successfully! You can now scan for jobs.", "success");
      await loadProfile();
    } catch (err) {
      toast(err.message || "Upload failed. Please try again.", "error");
    } finally {
      setCvUploading(false);
      if (cvUploadRef.current) cvUploadRef.current.value = "";
    }
  };

  const loadProfile = async () => {
    setLoading(true);
    const { data: prof } = await supabase.from("seeker_profiles").select("*").eq("id", user.id).single();
    setProfile(prof);
    if (prof) {
      const uploadedAt = prof.cv_uploaded_at ? new Date(prof.cv_uploaded_at) : prof.updated_at ? new Date(prof.updated_at) : null;
      const expired = !uploadedAt || !prof.ai_parsed_data || (Date.now() - uploadedAt.getTime()) > 30 * 24 * 60 * 60 * 1000;
      setCvExpired(expired);
      const month = new Date().toISOString().slice(0, 7);
      setScanCount(prof.scan_month === month ? (prof.scan_count || 0) : 0);
      setLastScanAt(prof.last_scan_at || null);
      // Load saved job preferences
      if (prof.job_preferences && Object.keys(prof.job_preferences).length > 0) {
        setJobPreferences({
          regions: prof.job_preferences.regions || [],
          work_types: prof.job_preferences.work_types || [],
          salary_min: prof.job_preferences.salary_min || "",
          open_to_relocation: prof.job_preferences.open_to_relocation || false,
        });
      }
    } else {
      setCvExpired(true);
    }

    // Load this seeker's applications with job titles — case-insensitive email match
    const { data: apps } = await supabase
      .from("applications")
      .select("id, job_id, status, created_at, jobs(title, employer_id, employers(company_name))")
      .ilike("candidate_email", user.email)
      .not("status", "in", '("does_not_meet","archived")')
      .order("created_at", { ascending: false });
    setMyApplications(apps || []);
    const alreadyApplied = {};
    (apps || []).forEach(a => { alreadyApplied[a.job_id] = true; });
    setAppliedJobs(alreadyApplied);

    // Count unread seeker notifications
    if (prof?.id) {
      const { data: notifs } = await supabase
        .from("seeker_notifications")
        .select("id")
        .eq("seeker_id", prof.id)
        .is("read_at", null);
      setUnreadNotifCount((notifs || []).length);
    }

    setLoading(false);
  };

  // Daily scan cap — 1 AI scan per 24 hours per candidate
  const SCAN_COOLDOWN_MS = 24 * 60 * 60 * 1000;
  const msSinceLastScan = lastScanAt ? Date.now() - new Date(lastScanAt).getTime() : Infinity;
  const onScanCooldown = msSinceLastScan < SCAN_COOLDOWN_MS;
  const scanCooldownRemaining = onScanCooldown ? SCAN_COOLDOWN_MS - msSinceLastScan : 0;
  const scanCooldownLabel = (() => {
    if (!onScanCooldown) return "";
    const hrs = Math.floor(scanCooldownRemaining / (60 * 60 * 1000));
    const mins = Math.floor((scanCooldownRemaining % (60 * 60 * 1000)) / (60 * 1000));
    return hrs > 0 ? `${hrs}h ${mins}m` : `${mins}m`;
  })();

  const runScan = async () => {
    if (cvExpired) { return; }
    if (!profile?.ai_parsed_data) { return; }
    if (onScanCooldown) {
      toast(`You've already scanned today. Try again in ${scanCooldownLabel}.`, "error");
      return;
    }
    setScanning(true);
    setScanResults(null);
    setSelectedMatch(null);
    try {
      const { data: jobs } = await supabase.from("jobs").select("*").eq("status", "active").or(`expires_at.is.null,expires_at.gt.${new Date().toISOString()}`);
      if (!jobs?.length) { setScanResults([]); setScanning(false); return; }

      // Build candidate profile enriched with their saved preferences
      const enrichedCandidate = {
        ...profile.ai_parsed_data,
        job_preferences: {
          preferred_regions: jobPreferences.regions,
          preferred_work_types: jobPreferences.work_types,
          minimum_salary_gyd: jobPreferences.salary_min || null,
          open_to_relocation: jobPreferences.open_to_relocation,
        }
      };

      const analysis = await analyzeCV(null, jobs, enrichedCandidate);
      const matches = analysis.matches || [];

      // Region-aware sort using saved preferences
      const preferredRegions = jobPreferences.regions.map(r => r.toLowerCase());
      const sortedMatches = [...matches].sort((a, b) => {
        const jobA = jobs.find(j => j.id === a.job_id);
        const jobB = jobs.find(j => j.id === b.job_id);
        const locA = (jobA?.location || "").toLowerCase();
        const locB = (jobB?.location || "").toLowerCase();
        const aPreferred = preferredRegions.length === 0 || preferredRegions.some(r => locA.includes(r.split("—")[0].trim()));
        const bPreferred = preferredRegions.length === 0 || preferredRegions.some(r => locB.includes(r.split("—")[0].trim()));
        if (a.match_score === b.match_score) return bPreferred - aPreferred;
        if (aPreferred && !bPreferred && b.match_score - a.match_score < 10) return -1;
        if (bPreferred && !aPreferred && a.match_score - b.match_score < 10) return 1;
        return b.match_score - a.match_score;
      });

      setScanResults({ matches: sortedMatches, jobs, candidate: profile.ai_parsed_data, preferredRegions });

      if (sortedMatches.length > 0) {
        const jobIds = sortedMatches.map(m => m.job_id);
        const { data: existing } = await supabase.from("applications")
          .select("job_id").ilike("candidate_email", user.email).in("job_id", jobIds);
        if (existing?.length) {
          const alreadyApplied = {};
          existing.forEach(a => { alreadyApplied[a.job_id] = true; });
          setAppliedJobs(alreadyApplied);
        }
      }

      const month = new Date().toISOString().slice(0, 7);
      const newCount = (profile.scan_month === month ? (profile.scan_count || 0) : 0) + 1;
      const nowIso = new Date().toISOString();
      setScanCount(newCount);
      setLastScanAt(nowIso);
      await supabase.from("seeker_profiles").update({ scan_count: newCount, scan_month: month, last_scan_at: nowIso }).eq("id", user.id);
    } catch (err) {
      toast("Scan failed. Please try again.", "error");
    }
    setScanning(false);
  };

  const cvDaysLeft = profile?.updated_at
    ? Math.max(0, 30 - Math.floor((Date.now() - new Date(profile.updated_at).getTime()) / (1000 * 60 * 60 * 24)))
    : 0;

  return (
    <div style={{ minHeight: "100vh", background: "#0E0D0B" }}>
      <style>{styles}</style>

      {/* Hidden CV file input — triggered by all Upload/Re-upload CV buttons */}
      <input
        ref={cvUploadRef}
        type="file"
        accept="application/pdf"
        style={{ display: "none" }}
        onChange={handleDashboardCvUpload}
      />

      {/* Header */}
      <div className="seeker-header" style={{ borderBottom: "1px solid rgba(255,255,255,0.07)", padding: "0 40px", height: 56, display: "flex", alignItems: "center", justifyContent: "space-between", background: "rgba(14,13,11,0.95)", backdropFilter: "blur(12px)", position: "sticky", top: 0, zIndex: 40 }}>
        <div className="flex items-center gap-3">
          <div style={{ width: 30, height: 30, borderRadius: 8, overflow: "hidden" }}>
            <img src="/icon-512x512.png" alt="JobSpot GY" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
          </div>
          <span style={{ fontSize: 16, fontWeight: 700, fontFamily: "Syne,sans-serif", color: "#FAF8F4" }}>JobSpot <span style={{ color: "rgba(250,248,244,0.3)", fontWeight: 400, fontSize: 12 }}>GY</span></span>
          <div style={{ width: 1, height: 18, background: "rgba(255,255,255,0.1)" }} />
          {/* Tab nav */}
          <div className="seeker-header-tabs" style={{ display: "flex", gap: 4 }}>
            <button
              onClick={() => setSeekerTab("find")}
              style={{ padding: "5px 14px", borderRadius: 6, fontSize: 13, fontWeight: 600, border: "none", cursor: "pointer", background: seekerTab === "find" ? "rgba(184,146,42,0.15)" : "transparent", color: seekerTab === "find" ? "#D4A83C" : "rgba(250,248,244,0.4)", transition: "all 0.15s" }}
            >
              Find Jobs
            </button>
            <button
              onClick={async () => {
                setSeekerTab("applications");
                // Mark all notifications as read
                if (profile?.id && unreadNotifCount > 0) {
                  await supabase.from("seeker_notifications")
                    .update({ read_at: new Date().toISOString() })
                    .eq("seeker_id", profile.id)
                    .is("read_at", null);
                  setUnreadNotifCount(0);
                }
              }}
              style={{ position: "relative", padding: "5px 14px", borderRadius: 6, fontSize: 13, fontWeight: 600, border: "none", cursor: "pointer", background: seekerTab === "applications" ? "rgba(184,146,42,0.15)" : "transparent", color: seekerTab === "applications" ? "#D4A83C" : "rgba(250,248,244,0.4)", transition: "all 0.15s" }}
            >
              My Applications
              {unreadNotifCount > 0 && (
                <span style={{ position: "absolute", top: 2, right: 2, width: 8, height: 8, background: "var(--green)", borderRadius: "50%", border: "2px solid #0E0D0B" }} />
              )}
            </button>
            <button
              onClick={() => setSeekerTab("profile")}
              style={{ padding: "5px 14px", borderRadius: 6, fontSize: 13, fontWeight: 600, border: "none", cursor: "pointer", background: seekerTab === "profile" ? "rgba(184,146,42,0.15)" : "transparent", color: seekerTab === "profile" ? "#D4A83C" : "rgba(250,248,244,0.4)", transition: "all 0.15s" }}
            >
              My Profile
            </button>
          </div>
        </div>
        <div className="seeker-header-actions flex items-center gap-3">
          <button className="btn btn-gold btn-sm" onClick={() => cvUploadRef.current?.click()} disabled={cvUploading}>
            <Icon name="upload" size={13} color="#1A1714" /> {cvUploading ? "Uploading..." : cvExpired ? "Re-upload CV" : "Update CV"}
          </button>
          <button className="btn btn-ghost btn-sm" style={{ color: "rgba(250,248,244,0.5)" }} onClick={onLogout}>Sign out</button>
        </div>
      </div>

      {/* Mobile tab strip — shown only on small screens */}
      <div style={{ display: "none" }} className="seeker-mobile-tabs">
        <style>{`
          @media (max-width: 768px) {
            .seeker-mobile-tabs {
              display: flex !important;
              background: rgba(14,13,11,0.98);
              border-bottom: 1px solid rgba(255,255,255,0.07);
              padding: 0 16px;
              gap: 4px;
              position: sticky;
              top: 56px;
              z-index: 39;
            }
            .seeker-mtab {
              flex: 1;
              padding: 10px 4px;
              background: none;
              border: none;
              font-family: 'DM Sans', sans-serif;
              font-size: 12px;
              font-weight: 600;
              color: rgba(250,248,244,0.4);
              cursor: pointer;
              border-bottom: 2px solid transparent;
              transition: all 0.15s;
              white-space: nowrap;
              text-align: center;
            }
            .seeker-mtab.active {
              color: #D4A83C;
              border-bottom-color: #D4A83C;
            }
          }
        `}</style>
        <button className={`seeker-mtab ${seekerTab === "find" ? "active" : ""}`} onClick={() => setSeekerTab("find")}>Find Jobs</button>
        <button className={`seeker-mtab ${seekerTab === "applications" ? "active" : ""}`}
          onClick={async () => {
            setSeekerTab("applications");
            if (profile?.id && unreadNotifCount > 0) {
              await supabase.from("seeker_notifications").update({ read_at: new Date().toISOString() }).eq("seeker_id", profile.id).is("read_at", null);
              setUnreadNotifCount(0);
            }
          }}
          style={{ position: "relative" }}
        >
          Applications {unreadNotifCount > 0 && <span style={{ display: "inline-block", width: 7, height: 7, background: "var(--green)", borderRadius: "50%", marginLeft: 4, verticalAlign: "middle" }} />}
        </button>
        <button className={`seeker-mtab ${seekerTab === "profile" ? "active" : ""}`} onClick={() => setSeekerTab("profile")}>Profile</button>
      </div>

      <div className="seeker-content" style={{ maxWidth: 760, margin: "0 auto", padding: "40px 40px 80px" }}>

        {loading ? (
          <div className="flex justify-center" style={{ padding: 80 }}><div className="spinner" style={{ width: 28, height: 28 }} /></div>
        ) : (
          <>
            {/* -- MY PROFILE TAB -- */}
            {seekerTab === "profile" && profile && (
              <>
              <div style={{ background: "rgba(184,146,42,0.08)", border: "1px solid rgba(184,146,42,0.2)", borderRadius: 16, padding: 24, marginBottom: 24 }}>
                <div className="profile-header-row flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <div style={{ width: 48, height: 48, borderRadius: "50%", background: "rgba(184,146,42,0.15)", display: "flex", alignItems: "center", justifyContent: "center", fontFamily: "Syne,sans-serif", fontSize: 18, fontWeight: 700, color: "var(--gold)" }}>
                      {(profile.name || user.email)?.[0]?.toUpperCase()}
                    </div>
                    <div>
                      <div style={{ fontSize: 18, fontWeight: 700, fontFamily: "Syne,sans-serif", color: "#FAF8F4" }}>{profile.name || "Job Seeker"}</div>
                      <div style={{ fontSize: 13, color: "rgba(250,248,244,0.45)" }}>{profile.region || user.email}</div>
                    </div>
                  </div>
                  <div style={{ textAlign: "right" }}>
                    <div style={{ fontSize: 13, color: cvExpired ? "var(--red)" : cvDaysLeft <= 7 ? "var(--amber)" : "var(--green)", fontWeight: 600 }}>
                      {cvExpired ? "CV expired" : `CV expires in ${cvDaysLeft}d`}
                    </div>
                    <div style={{ fontSize: 11, color: "rgba(250,248,244,0.3)", marginTop: 2 }}>{scanCount} scan{scanCount !== 1 ? "s" : ""} this month</div>
                  </div>
                </div>
                {profile.ai_parsed_data && (
                  <div className="flex gap-3 mt-4" style={{ flexWrap: "wrap" }}>
                    {profile.ai_parsed_data.seniority_level && <span className="badge badge-gold">{profile.ai_parsed_data.seniority_level}</span>}
                    {profile.ai_parsed_data.total_experience_years > 0 && <span style={{ fontSize: 11, fontWeight: 600, padding: "2px 8px", borderRadius: 4, background: "rgba(255,255,255,0.08)", color: "rgba(250,248,244,0.5)" }}>{profile.ai_parsed_data.total_experience_years}y exp</span>}
                    {[
                      ...(profile.ai_parsed_data.skills?.technical || []),
                      ...(profile.ai_parsed_data.skills?.soft || []),
                      ...(profile.ai_parsed_data.skills?.domain || []),
                      ...(profile.ai_parsed_data.top_skills || []),
                    ].slice(0, 5).map((s, i) => (
                      <span key={i} style={{ fontSize: 12, padding: "3px 10px", borderRadius: 4, background: "rgba(255,255,255,0.06)", border: "1px solid rgba(255,255,255,0.1)", color: "rgba(250,248,244,0.55)" }}>{s}</span>
                    ))}
                  </div>
                )}

                {/* -- Job Preferences -- */}
                <div style={{ marginTop: 20, paddingTop: 18, borderTop: "1px solid rgba(255,255,255,0.07)" }}>
                  <div style={{ fontSize: 11, fontWeight: 700, color: "rgba(250,248,244,0.4)", textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: 14 }}>Job Preferences</div>

                  {/* Regions */}
                  <div style={{ marginBottom: 14 }}>
                    <div style={{ fontSize: 12, color: "rgba(250,248,244,0.5)", marginBottom: 8, fontWeight: 500 }}>Regions I'm open to working in</div>
                    <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                      {[
                        "Region 4 — Demerara-Mahaica (Georgetown)",
                        "Region 3 — Essequibo Islands-West Demerara",
                        "Region 5 — Mahaica-Berbice",
                        "Region 6 — East Berbice-Corentyne",
                        "Region 10 — Upper Demerara-Berbice (Linden)",
                        "Region 1 — Barima-Waini",
                        "Region 2 — Pomeroon-Supenaam",
                        "Region 7 — Cuyuni-Mazaruni",
                        "Region 8 — Potaro-Siparuni",
                        "Region 9 — Upper Takutu-Upper Essequibo (Lethem)",
                        "Remote (Any Region)",
                      ].map(region => {
                        const selected = jobPreferences.regions.includes(region);
                        return (
                          <button key={region} onClick={() => setJobPreferences(prev => ({
                            ...prev,
                            regions: selected ? prev.regions.filter(r => r !== region) : [...prev.regions, region]
                          }))}
                            style={{ padding: "5px 12px", borderRadius: 20, fontSize: 12, fontWeight: 500, border: `1px solid ${selected ? "var(--gold-dim)" : "rgba(255,255,255,0.1)"}`, background: selected ? "rgba(184,146,42,0.15)" : "rgba(255,255,255,0.04)", color: selected ? "#D4A83C" : "rgba(250,248,244,0.45)", cursor: "pointer", transition: "all 0.15s" }}>
                            {region.split("—")[0].trim().replace("Region ", "R").replace(" (Georgetown)", "").replace(" (Linden)", "").replace(" (Lethem)", "")}
                          </button>
                        );
                      })}
                    </div>
                  </div>

                  {/* Work type */}
                  <div style={{ marginBottom: 14 }}>
                    <div style={{ fontSize: 12, color: "rgba(250,248,244,0.5)", marginBottom: 8, fontWeight: 500 }}>Work type</div>
                    <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                      {["onsite", "remote", "hybrid"].map(wt => {
                        const selected = jobPreferences.work_types.includes(wt);
                        return (
                          <button key={wt} onClick={() => setJobPreferences(prev => ({
                            ...prev,
                            work_types: selected ? prev.work_types.filter(w => w !== wt) : [...prev.work_types, wt]
                          }))}
                            style={{ padding: "5px 14px", borderRadius: 20, fontSize: 12, fontWeight: 500, border: `1px solid ${selected ? "var(--gold-dim)" : "rgba(255,255,255,0.1)"}`, background: selected ? "rgba(184,146,42,0.15)" : "rgba(255,255,255,0.04)", color: selected ? "#D4A83C" : "rgba(250,248,244,0.45)", cursor: "pointer", transition: "all 0.15s", textTransform: "capitalize" }}>
                            {wt}
                          </button>
                        );
                      })}
                    </div>
                  </div>

                  {/* Salary min + relocation */}
                  <div style={{ display: "flex", alignItems: "center", gap: 16, flexWrap: "wrap", marginBottom: 14 }}>
                    <div>
                      <div style={{ fontSize: 12, color: "rgba(250,248,244,0.5)", marginBottom: 6, fontWeight: 500 }}>Minimum salary (GYD/month)</div>
                      <input
                        className="input"
                        type="number"
                        placeholder="e.g. 80000"
                        value={jobPreferences.salary_min}
                        onChange={e => setJobPreferences(prev => ({ ...prev, salary_min: e.target.value }))}
                        style={{ width: 180, fontSize: 13, background: "rgba(255,255,255,0.05)", border: "1px solid rgba(255,255,255,0.1)", color: "#FAF8F4" }}
                      />
                    </div>
                    <div style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 20 }}>
                      <button onClick={() => setJobPreferences(prev => ({ ...prev, open_to_relocation: !prev.open_to_relocation }))}
                        style={{ width: 36, height: 20, borderRadius: 10, background: jobPreferences.open_to_relocation ? "var(--gold)" : "rgba(255,255,255,0.1)", border: "none", cursor: "pointer", position: "relative", transition: "all 0.2s", flexShrink: 0 }}>
                        <div style={{ width: 14, height: 14, borderRadius: "50%", background: "#fff", position: "absolute", top: 3, left: jobPreferences.open_to_relocation ? 19 : 3, transition: "left 0.2s" }} />
                      </button>
                      <span style={{ fontSize: 12, color: "rgba(250,248,244,0.5)" }}>Open to relocation</span>
                    </div>
                  </div>

                  <button
                    className="btn btn-gold btn-sm"
                    style={{ fontSize: 12 }}
                    disabled={savingPrefs}
                    onClick={async () => {
                      setSavingPrefs(true);
                      const { error } = await supabase.from("seeker_profiles")
                        .update({ job_preferences: jobPreferences })
                        .eq("id", user.id);
                      setSavingPrefs(false);
                      if (error) toast("Failed to save preferences.", "error");
                      else toast("Preferences saved — your next scan will use these. ✓", "success");
                    }}
                  >
                    {savingPrefs ? <><div className="spinner" style={{ width: 12, height: 12 }} /> Saving...</> : "Save Preferences"}
                  </button>
                </div>

              </div>

              </>
            )}

            {/* -- PROFILE TAB: no CV yet prompt (always shown when no profile) -- */}
            {seekerTab === "profile" && !profile && !loading && (
              <div style={{ background: "rgba(184,146,42,0.06)", border: "1px solid rgba(184,146,42,0.18)", borderRadius: 16, padding: 28, marginBottom: 24, display: "flex", alignItems: "center", justifyContent: "space-between", gap: 20, flexWrap: "wrap" }}>
                <div className="flex items-center gap-4">
                  <div style={{ width: 48, height: 48, borderRadius: "50%", background: "rgba(184,146,42,0.12)", border: "1px solid rgba(184,146,42,0.2)", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                    <Icon name="user" size={22} color="var(--gold)" />
                  </div>
                  <div>
                    <div style={{ fontSize: 15, fontWeight: 700, fontFamily: "Syne, sans-serif", color: "#FAF8F4", marginBottom: 4 }}>No CV uploaded yet</div>
                    <div style={{ fontSize: 12, color: "rgba(250,248,244,0.45)", lineHeight: 1.5 }}>Upload your CV to create your profile. We'll parse your skills and match you to jobs automatically.</div>
                  </div>
                </div>
                <button className="btn btn-gold btn-sm" onClick={() => cvUploadRef.current?.click()} disabled={cvUploading}>
                  <Icon name="upload" size={13} color="#1A1714" /> {cvUploading ? "Uploading..." : "Upload CV"}
                </button>
              </div>
            )}

            {/* -- PROFILE TAB: Account Recovery — always visible -- */}
            {seekerTab === "profile" && !loading && (
              <>
              <div style={{ background: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,255,255,0.08)", borderRadius: 16, padding: 24, marginTop: profile ? 0 : 0, marginBottom: 16 }}>
                <div style={{ fontSize: 14, fontWeight: 700, color: "rgba(250,248,244,0.8)", marginBottom: 4 }}>Account Recovery</div>
                <div style={{ fontSize: 12, color: "rgba(250,248,244,0.4)", marginBottom: 16, lineHeight: 1.5 }}>Keep this up to date — if you're ever locked out, JobSpot will verify you using your phone and secret answer.</div>
                <SeekerRecoveryForm userId={user.id} profile={profile} onSaved={() => toast("Recovery info saved ✓", "success")} />
              </div>

              {/* -- Change Email -- */}
              <div style={{ background: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,255,255,0.08)", borderRadius: 16, padding: 24, marginBottom: 16 }}>
                <div style={{ fontSize: 14, fontWeight: 700, color: "rgba(250,248,244,0.8)", marginBottom: 4 }}>Change Email</div>
                <div style={{ fontSize: 12, color: "rgba(250,248,244,0.4)", marginBottom: 16, lineHeight: 1.5 }}>Update the email address used to sign in to your JobSpot account.</div>
                <SeekerChangeEmailForm currentEmail={user.email} />
              </div>

              {/* -- Change Password -- */}
              <div style={{ background: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,255,255,0.08)", borderRadius: 16, padding: 24, marginBottom: 16 }}>
                <div style={{ fontSize: 14, fontWeight: 700, color: "rgba(250,248,244,0.8)", marginBottom: 4 }}>Change Password</div>
                <div style={{ fontSize: 12, color: "rgba(250,248,244,0.4)", marginBottom: 16, lineHeight: 1.5 }}>Update the password for your JobSpot account.</div>
                <SeekerChangePasswordForm />
              </div>

              {/* -- Delete Account -- */}
              <div style={{ background: "rgba(217,48,37,0.04)", border: "1px solid rgba(217,48,37,0.15)", borderRadius: 16, padding: 24, marginBottom: 16 }}>
                <div style={{ fontSize: 14, fontWeight: 700, color: "rgba(250,248,244,0.8)", marginBottom: 4 }}>Delete Account</div>
                <div style={{ fontSize: 12, color: "rgba(250,248,244,0.4)", marginBottom: 16, lineHeight: 1.5 }}>Permanently delete your JobSpot account and all associated data. This cannot be undone.</div>
                <SeekerDeleteAccountForm userId={user.id} onDeleted={onLogout} />
              </div>
              </>
            )}

            {/* -- FIND JOBS TAB -- */}
            {seekerTab === "find" && (
              <>
            {/* CV expired warning */}
            {cvExpired && (
              <div className="cv-banner" style={{ background: "rgba(217,48,37,0.08)", border: "1px solid rgba(217,48,37,0.25)", borderRadius: 12, padding: "16px 20px", marginBottom: 24, display: "flex", alignItems: "center", justifyContent: "space-between", gap: 16 }}>
                <div className="flex items-center gap-3">
                  <Icon name="shield" size={18} color="var(--red)" />
                  <div>
                    <div style={{ fontSize: 14, fontWeight: 600, color: "#FAF8F4" }}>
                      {!profile?.ai_parsed_data ? "No CV uploaded yet" : "Your CV has expired"}
                    </div>
                    <div style={{ fontSize: 12, color: "rgba(250,248,244,0.4)", marginTop: 2 }}>
                      {!profile?.ai_parsed_data
                        ? "Upload your CV so we can scan jobs against your profile."
                        : "CVs are deleted after 30 days for your privacy. Re-upload to keep scanning."}
                    </div>
                  </div>
                </div>
                <button className="btn btn-gold btn-sm" onClick={() => cvUploadRef.current?.click()} disabled={cvUploading}>
                  {cvUploading ? "Uploading..." : !profile?.ai_parsed_data ? "Upload CV" : "Re-upload CV"}
                </button>
              </div>
            )}

            {/* Scan button — primary action */}
            {!cvExpired && (
              <>
              <style>{`
                @keyframes scanPulse {
                  0%, 100% { transform: scale(1); opacity: 1; }
                  50% { transform: scale(1.08); opacity: 0.7; }
                }
                @keyframes scanRing {
                  0% { transform: scale(0.8); opacity: 0.6; }
                  100% { transform: scale(2.2); opacity: 0; }
                }
                @keyframes scanBar {
                  0% { width: 0%; opacity: 0.4; }
                  50% { opacity: 1; }
                  100% { width: 100%; opacity: 0.4; }
                }
                @keyframes scanDot {
                  0%, 100% { opacity: 0.2; transform: translateY(0); }
                  50% { opacity: 1; transform: translateY(-4px); }
                }
                @keyframes scanFlyIn {
                  0% { opacity: 0; transform: translateX(40px) scale(0.92); }
                  100% { opacity: 1; transform: translateX(0) scale(1); }
                }
                @keyframes scanGlow {
                  0%, 100% { box-shadow: 0 0 24px rgba(184,146,42,0.25), 0 4px 24px rgba(0,0,0,0.4); }
                  50% { box-shadow: 0 0 48px rgba(212,168,60,0.5), 0 4px 32px rgba(0,0,0,0.5); }
                }
                .scan-ring {
                  position: absolute;
                  inset: 0;
                  border-radius: 50%;
                  border: 2px solid rgba(184,146,42,0.5);
                  animation: scanRing 1.6s ease-out infinite;
                }
                .scan-ring:nth-child(2) { animation-delay: 0.5s; }
                .scan-ring:nth-child(3) { animation-delay: 1s; }
              `}</style>

              {scanning ? (
                /* ── ANIMATED SCANNING STATE ── */
                <div style={{ textAlign: "center", marginBottom: 32, padding: "8px 0" }}>
                  {/* Radar orb */}
                  <div style={{ display: "flex", justifyContent: "center", marginBottom: 24 }}>
                    <div style={{ position: "relative", width: 80, height: 80 }}>
                      <div className="scan-ring" />
                      <div className="scan-ring" style={{ animationDelay: "0.53s" }} />
                      <div className="scan-ring" style={{ animationDelay: "1.06s" }} />
                      <div style={{
                        position: "absolute", inset: 0, margin: "auto",
                        width: 48, height: 48, borderRadius: "50%",
                        background: "linear-gradient(135deg, #B8922A, #D4A83C)",
                        display: "flex", alignItems: "center", justifyContent: "center",
                        animation: "scanGlow 1.6s ease-in-out infinite",
                      }}>
                        <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#1A1714" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                          <circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
                        </svg>
                      </div>
                    </div>
                  </div>

                  {/* Label */}
                  <div style={{ fontSize: 17, fontWeight: 700, fontFamily: "Syne,sans-serif", color: "#FAF8F4", marginBottom: 6, animation: "pulse 1.6s ease-in-out infinite" }}>
                    Analysing matches...
                  </div>
                  <div style={{ fontSize: 12, color: "rgba(250,248,244,0.35)", marginBottom: 20 }}>
                    Scanning all active jobs against your CV profile
                  </div>

                  {/* Progress bar */}
                  <div style={{ maxWidth: 280, margin: "0 auto 20px", height: 3, background: "rgba(255,255,255,0.06)", borderRadius: 2, overflow: "hidden" }}>
                    <div style={{
                      height: "100%", borderRadius: 2,
                      background: "linear-gradient(90deg, transparent, #D4A83C, transparent)",
                      animation: "scanBar 1.8s ease-in-out infinite",
                    }} />
                  </div>

                  {/* Animated job category pills */}
                  <div style={{ display: "flex", flexWrap: "wrap", gap: 8, justifyContent: "center", maxWidth: 320, margin: "0 auto" }}>
                    {["Banking & Finance","Healthcare","Tech & Startups","Oil & Energy","Admin & Clerical","Accounting"].map((cat, i) => (
                      <div key={cat} style={{
                        padding: "5px 12px", borderRadius: 20,
                        border: "1px solid rgba(184,146,42,0.25)",
                        background: "rgba(184,146,42,0.07)",
                        fontSize: 11, color: "rgba(250,248,244,0.45)",
                        animation: `scanFlyIn 0.4s ease forwards, pulse 2s ease-in-out ${0.3 + i * 0.15}s infinite`,
                        animationDelay: `${i * 0.1}s, ${0.3 + i * 0.2}s`,
                        opacity: 0,
                      }}>
                        {cat}
                      </div>
                    ))}
                  </div>
                </div>
              ) : (
                /* ── NORMAL BUTTON STATE ── */
                <div style={{ textAlign: "center", marginBottom: 32 }}>
                  <button
                    className="btn btn-gold"
                    style={{ fontSize: 16, padding: "16px 48px", borderRadius: 12 }}
                    onClick={runScan}
                    disabled={scanning || onScanCooldown}
                  >
                    <Icon name="search" size={16} color="#1A1714" /> {onScanCooldown ? "Scan used for today" : "Scan for jobs"}
                  </button>
                  <div style={{ fontSize: 12, color: "rgba(250,248,244,0.3)", marginTop: 10 }}>
                    {onScanCooldown
                      ? `You get 1 scan per day — next scan available in ${scanCooldownLabel}`
                      : "Scans all active jobs against your saved CV profile"}
                  </div>
                </div>
              )}
              </>
            )}

            {/* Scan results */}
            {scanResults !== null && (
              <div className="animate-fade-in">
                <div style={{ fontSize: 18, fontWeight: 700, fontFamily: "Syne,sans-serif", color: "#FAF8F4", marginBottom: 4 }}>
                  {scanResults.matches?.length > 0 ? `${scanResults.matches.length} matches found` : "No matches found"}
                </div>
                {scanResults.matches?.length > 0 && scanResults.preferredRegions?.length > 0 && (() => {
                  const inRegion = scanResults.matches.filter(m => {
                    const job = scanResults.jobs?.find(j => j.id === m.job_id);
                    return scanResults.preferredRegions.some(r => (job?.location || "").toLowerCase().includes(r.split("—")[0].trim()));
                  }).length;
                  const outside = scanResults.matches.length - inRegion;
                  return (
                    <div style={{ fontSize: 12, color: "rgba(250,248,244,0.35)", marginBottom: 16 }}>
                      {inRegion > 0 && <span style={{ color: "var(--green)" }}>{inRegion} in your preferred region{inRegion !== 1 ? "s" : ""}</span>}
                      {inRegion > 0 && outside > 0 && <span> · </span>}
                      {outside > 0 && <span>{outside} outside your preferred regions</span>}
                    </div>
                  );
                })()}

                {!scanResults.matches?.length ? (
                  <div style={{ background: "#1C1917", border: "1px solid rgba(255,255,255,0.07)", borderRadius: 12, padding: "40px 24px", textAlign: "center" }}>
                    <div style={{ fontSize: 14, color: "rgba(250,248,244,0.4)", lineHeight: 1.6 }}>No active jobs match your profile right now. Check back tomorrow — new jobs are posted regularly.</div>
                  </div>
                ) : (
                  <div className="flex-col gap-4">
                    {scanResults.matches.map((match, i) => {
                      const job = scanResults.jobs?.find(j => j.id === match.job_id);
                      if (!job) return null;
                      const isOpen = selectedMatch === match.job_id;
                      const preferredRegions = scanResults.preferredRegions || [];
                      const isPreferred = preferredRegions.length === 0 || preferredRegions.some(r => (job.location || "").toLowerCase().includes(r.split("—")[0].trim()));
                      return (
                        <div key={match.job_id} style={{ background: "#1C1917", border: `1px solid ${isPreferred && preferredRegions.length > 0 ? "rgba(45,158,78,0.25)" : i === 0 ? "rgba(184,146,42,0.35)" : "rgba(255,255,255,0.07)"}`, borderRadius: 12, overflow: "hidden", cursor: "pointer" }} onClick={() => setSelectedMatch(isOpen ? null : match.job_id)}>
                          <div style={{ padding: "18px 20px" }}>
                            <div className="flex items-start justify-between mb-3">
                              <div className="flex items-center gap-3">
                                <div style={{ width: 32, height: 32, background: i === 0 ? "rgba(184,146,42,0.2)" : "rgba(255,255,255,0.06)", borderRadius: 8, display: "flex", alignItems: "center", justifyContent: "center", fontFamily: "Syne,sans-serif", fontSize: 12, fontWeight: 700, color: "var(--gold)", flexShrink: 0 }}>#{i + 1}</div>
                                <div>
                                  <div className="flex items-center gap-2">
                                    <div style={{ fontSize: 15, fontWeight: 600, color: "#FAF8F4", fontFamily: "Syne,sans-serif" }}>{job.title}</div>
                                    {preferredRegions.length > 0 && (isPreferred
                                      ? <span style={{ fontSize: 10, fontWeight: 700, background: "rgba(45,158,78,0.2)", color: "#6EE7A0", padding: "2px 7px", borderRadius: 4, textTransform: "uppercase", letterSpacing: "0.05em", flexShrink: 0 }}>Your Region</span>
                                      : <span style={{ fontSize: 10, fontWeight: 600, background: "rgba(255,255,255,0.06)", color: "rgba(250,248,244,0.35)", padding: "2px 7px", borderRadius: 4, flexShrink: 0 }}>Outside Region</span>
                                    )}
                                  </div>
                                  <div style={{ fontSize: 12, color: "rgba(250,248,244,0.4)", marginTop: 2 }}>{job.location} · {job.work_type?.replace("_", " ")}</div>
                                </div>
                              </div>
                              <ScoreDisplay score={match.match_score} size="lg" />
                            </div>
                            <div style={{ height: 4, background: "rgba(255,255,255,0.08)", borderRadius: 2, overflow: "hidden", marginBottom: 10 }}>
                              <div style={{ height: "100%", borderRadius: 2, background: getScoreColor(match.match_score), width: `${match.match_score}%`, transition: "width 1s ease" }} />
                            </div>
                            {job.salary_min && <div style={{ fontSize: 12, color: "rgba(250,248,244,0.35)", marginBottom: 6 }}>{formatGYD(job.salary_min)} – {formatGYD(job.salary_max)} / year</div>}
                            {!isOpen && <div style={{ fontSize: 12, color: "var(--gold)", fontWeight: 500 }}>Tap to view details & apply →</div>}
                          </div>

                          {isOpen && (
                            <div style={{ borderTop: "1px solid rgba(255,255,255,0.07)", padding: "18px 20px", animation: "fadeUp 0.2s ease" }}>
                              <div className="grid-2 gap-4 mb-4">
                                <div>
                                  <div style={{ fontSize: 11, fontWeight: 700, color: "rgba(250,248,244,0.3)", textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 6 }}>Why you match</div>
                                  <p style={{ fontSize: 13, color: "rgba(250,248,244,0.6)", lineHeight: 1.6 }}>{match.match_explanation}</p>
                                </div>
                                <div>
                                  <div style={{ fontSize: 11, fontWeight: 700, color: "rgba(250,248,244,0.3)", textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 6 }}>Smart verdict</div>
                                  <p style={{ fontSize: 13, color: "rgba(250,248,244,0.6)", lineHeight: 1.6, fontStyle: "italic" }}>"{match.ai_verdict}"</p>
                                </div>
                              </div>
                              <div className="grid-2 gap-4 mb-4">
                                <div>
                                  <div style={{ fontSize: 11, fontWeight: 700, color: "rgba(250,248,244,0.3)", textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 6 }}>Matched skills</div>
                                  <SkillTags skills={match.matched_skills} color="green" />
                                </div>
                                <div>
                                  <div style={{ fontSize: 11, fontWeight: 700, color: "rgba(250,248,244,0.3)", textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 6 }}>Skill gaps</div>
                                  <SkillTags skills={match.missing_skills} color="red" />
                                </div>
                              </div>
                              <div className="flex items-center justify-between">
                                <div className="flex gap-4">
                                  <span style={{ fontSize: 12, color: "rgba(250,248,244,0.35)" }}>Exp: <span style={{ color: match.experience_fit === "strong" ? "var(--green)" : "rgba(250,248,244,0.6)" }}>{match.experience_fit}</span></span>
                                  <span style={{ fontSize: 12, color: "rgba(250,248,244,0.35)" }}>Salary: <span style={{ color: match.salary_fit === "within_range" ? "var(--green)" : "rgba(250,248,244,0.6)" }}>{match.salary_fit?.replace(/_/g, " ")}</span></span>
                                </div>
                                <button
                                  className={`btn btn-sm ${appliedJobs[match.job_id] ? "btn-green" : "btn-gold"}`}
                                  disabled={appliedJobs[match.job_id]}
                                  onClick={e => applyFromDashboard(match, e)}
                                >
                                  {appliedJobs[match.job_id] ? <><Icon name="check" size={13} color="#fff" /> Applied</> : "Apply →"}
                                </button>
                              </div>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            )}

            {/* Tip */}
            {!scanning && scanResults === null && !cvExpired && (
              <div style={{ marginTop: 16, padding: "16px 20px", background: "rgba(184,146,42,0.05)", borderRadius: 12, border: "1px solid rgba(184,146,42,0.12)" }}>
                <div className="flex items-center gap-3">
                  <Icon name="zap" size={14} color="var(--gold)" />
                  <p style={{ fontSize: 13, color: "rgba(250,248,244,0.4)", margin: 0, lineHeight: 1.6 }}>
                    <strong style={{ color: "rgba(250,248,244,0.7)" }}>How it works.</strong> Hit Scan for jobs and our AI checks every active job against your saved CV profile. Your CV auto-expires after 30 days — just re-upload to keep scanning.
                  </p>
                </div>
              </div>
            )}

            {/* -- End Find Jobs Tab -- */}
              </>
            )}

            {/* -- MY APPLICATIONS TAB -- */}
            {seekerTab === "applications" && (
              <div className="animate-fade-in">
                <div style={{ fontSize: 22, fontWeight: 800, fontFamily: "Syne,sans-serif", color: "#FAF8F4", marginBottom: 6 }}>My Applications</div>
                <p style={{ fontSize: 13, color: "rgba(250,248,244,0.4)", marginBottom: 24 }}>Your applications from the last 30 days. Status updates in real time.</p>

                {myApplications.length === 0 ? (
                  <div style={{ background: "#1C1917", border: "1px solid rgba(255,255,255,0.07)", borderRadius: 12, padding: "48px 24px", textAlign: "center" }}>
                    <div style={{ fontSize: 32, marginBottom: 12 }}></div>
                    <div style={{ fontSize: 15, fontWeight: 600, color: "#FAF8F4", marginBottom: 8 }}>No applications yet</div>
                    <div style={{ fontSize: 13, color: "rgba(250,248,244,0.4)", marginBottom: 20 }}>Scan for jobs and apply — your applications will appear here.</div>
                    <button className="btn btn-gold btn-sm" onClick={() => setSeekerTab("find")}>Find Jobs →</button>
                  </div>
                ) : (
                  <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                    {myApplications.filter(app => {
                      const age = Date.now() - new Date(app.created_at).getTime();
                      return age < 30 * 24 * 60 * 60 * 1000;
                    }).map(app => {
                      const statusMap = {
                        new:                 { label: "Under Review",                          color: "rgba(250,248,244,0.5)",  dot: "rgba(250,248,244,0.2)",  bg: "rgba(255,255,255,0.03)" },
                        shortlisted:         { label: "You've been shortlisted ✓",             color: "#D4A83C",                dot: "#D4A83C",                bg: "rgba(184,146,42,0.07)"  },
                        interview_scheduled: { label: "Interview requested — check WhatsApp",  color: "#A78BFA",                dot: "#7C3AED",                bg: "rgba(124,58,237,0.06)"  },
                        offer_made:          { label: "Offer made — check your email",         color: "#6EE7A0",                dot: "#2D9E4E",                bg: "rgba(45,158,78,0.06)"   },
                        hired:               { label: "You got the job! ",                   color: "#6EE7A0",                dot: "#2D9E4E",                bg: "rgba(45,158,78,0.08)"   },
                        rejected:            { label: "This position has been filled",         color: "rgba(250,248,244,0.25)", dot: "rgba(255,255,255,0.1)",  bg: "transparent"            },
                      };
                      const s = statusMap[app.status] || statusMap["new"];
                      const jobTitle = app.jobs?.title || "Job listing";
                      const company  = app.jobs?.employers?.company_name || "";
                      const isHighlight = ["shortlisted","interview_scheduled","offer_made","hired"].includes(app.status);
                      return (
                        <div key={app.id} style={{ background: isHighlight ? s.bg : "#1C1917", border: `1px solid ${isHighlight ? s.dot + "44" : "rgba(255,255,255,0.07)"}`, borderRadius: 12, padding: "16px 20px", display: "flex", alignItems: "center", gap: 16 }}>
                          <div style={{ width: 10, height: 10, borderRadius: "50%", background: s.dot, flexShrink: 0, boxShadow: isHighlight ? `0 0 8px ${s.dot}` : "none" }} />
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <div style={{ fontSize: 15, fontWeight: 600, color: "#FAF8F4", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", marginBottom: 3 }}>{jobTitle}</div>
                            <div style={{ fontSize: 12, color: "rgba(250,248,244,0.35)" }}>
                              {company && <span>{company} · </span>}Applied {timeAgo(app.created_at)}
                            </div>
                          </div>
                          <div style={{ fontSize: 13, fontWeight: 700, color: s.color, textAlign: "right", flexShrink: 0, maxWidth: 240 }}>
                            {s.label}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};


const UpgradeRequestModal = ({ employer, onClose }) => {
  const [step, setStep] = useState("compare"); // "compare" | "pay"
  const [selectedPlan, setSelectedPlan] = useState("professional");
  const [paymentMethod, setPaymentMethod] = useState("mmg");
  const [loading, setLoading] = useState(false);

  const currentPlan = employer?.plan || "free";

  // Full feature matrix — every real feature we've built
  const FEATURES = [
    { category: "Jobs & Listings", items: [
      { label: "Active job listings",          free: "5 jobs",      starter: "8 jobs",        pro: "Unlimited" },
      { label: "Shareable job link",           free: true,          starter: true,            pro: true },
      { label: "Job expiry extension",         free: true,          starter: true,            pro: true },
    ]},
    { category: "Candidate Intelligence", items: [
      { label: "AI match score per candidate", free: true,          starter: true,            pro: true },
      { label: "Candidates visible per job",   free: "Top 8",       starter: "Top 15",        pro: "All candidates" },
      { label: "Full AI fit reports",          free: false,         starter: true,            pro: true },
      { label: "Skill gap analysis",           free: false,         starter: true,            pro: true },
      { label: "Green flags & watch points",   free: false,         starter: true,            pro: true },
      { label: "Candidate search",             free: false,         starter: true,            pro: true },
      { label: "CV downloads / month",         free: "20",          starter: "100",           pro: "Unlimited" },
    ]},
    { category: "Pipeline & Workflow", items: [
      { label: "Kanban pipeline view",         free: false,         starter: true,            pro: true },
      { label: "Interview scheduling",         free: false,         starter: false,           pro: true },
      { label: "Interview date picker",        free: false,         starter: false,           pro: true },
      { label: "Candidate status tracking",    free: false,         starter: true,            pro: true },
      { label: "Archive candidates",           free: true,          starter: true,            pro: true },
      { label: "Does Not Meet Expectations",   free: false,         starter: true,            pro: true },
    ]},
    { category: "Notifications & Alerts", items: [
      { label: "Real-time new applicant alerts", free: true,        starter: true,            pro: true },
      { label: "Notification history",          free: true,         starter: true,            pro: true },
      { label: "Per-job new applicant badge",   free: true,         starter: true,            pro: true },
    ]},
    { category: "Account & Support", items: [
      { label: "Employer dashboard",           free: true,          starter: true,            pro: true },
      { label: "Onboarding checklist",         free: true,          starter: true,            pro: true },
      { label: "Featured homepage listings",    free: false,         starter: false,           pro: "2 slots · 7 days" },
      { label: "Priority support",             free: false,         starter: false,           pro: true },
    ]},
  ];

  const planMeta = {
    free:         { name: "Free",         price: "GYD 0",             sub: "Current plan",          color: "var(--text3)",  bg: "var(--bg3)" },
    starter:      { name: "Starter",      price: "GYD 12,000/yr",     sub: "~GYD 1,000/month",      color: "var(--gold)",   bg: "rgba(184,146,42,0.07)" },
    professional: { name: "Professional", price: "GYD 36,000/yr",     sub: "~GYD 3,000/month",      color: "var(--blue)",   bg: "var(--blue-bg)" },
  };

  const renderCell = (val, planKey) => {
    const isSelected = planKey === selectedPlan;
    if (val === true)  return <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke={isSelected ? planMeta[planKey].color : "var(--green)"} strokeWidth="2.5"><polyline points="20 6 9 17 4 12"/></svg>;
    if (val === false) return <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="var(--border)" strokeWidth="2"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>;
    return <span style={{ fontSize: 12, fontWeight: 600, color: isSelected ? planMeta[planKey].color : "var(--text2)" }}>{val}</span>;
  };

  const submitRequest = async () => {
    setLoading(true);
    try {
      const amounts = { starter: 12000, professional: 36000 };
      const { data: payment, error } = await supabase.from("payments").insert({
        employer_id: employer.id,
        plan: selectedPlan,
        amount_gyd: amounts[selectedPlan],
        payment_method: paymentMethod,
        payment_status: "pending",
      }).select().single();
      if (error) throw error;
      if (!payment) throw new Error("Payment record could not be created. Please try again.");
      toast("Upgrade request submitted! We\'ll approve within a few hours.", "success");
      const activeNumber = getActiveWhatsApp();
      const msg = "Hi JobSpot! I\'ve submitted an upgrade request.\n\nCompany: " + employer.company_name + "\nPlan: " + planMeta[selectedPlan].name + "\nAmount: " + planMeta[selectedPlan].price + "\nPayment via: " + paymentMethod.toUpperCase() + "\nRef ID: " + payment.id + "\n\nPlease find my payment receipt attached.";
      if (activeNumber) openWhatsApp(activeNumber, msg);
      else toast("WhatsApp number not set yet — contact JobSpot directly.", "info");
      onClose();
    } catch (err) {
      toast(err.message, "error");
    } finally {
      setLoading(false);
    }
  };

  // -- STEP 1: Premium plan cards --
  if (step === "compare") return (
    <div className="modal-backdrop" onClick={onClose}>
      <div onClick={e => e.stopPropagation()} style={{ background: "var(--bg2)", border: "1px solid var(--border)", borderRadius: 20, width: "100%", maxWidth: 720, animation: "fadeUp 0.25s ease", overflow: "hidden" }}>

        {/* Header */}
        <div style={{ padding: "28px 28px 0", display: "flex", alignItems: "flex-start", justifyContent: "space-between" }}>
          <div>
            <div style={{ fontSize: 11, fontWeight: 700, color: "var(--gold)", textTransform: "uppercase", letterSpacing: "0.1em", marginBottom: 8 }}>Unlock JobSpot</div>
            <h2 className="font-display" style={{ fontSize: 24, fontWeight: 800, letterSpacing: "-0.02em", marginBottom: 6 }}>Choose your plan</h2>
            <p style={{ fontSize: 14, color: "var(--text2)" }}>Start free. Scale when you're ready. Cancel anytime.</p>
          </div>
          <button className="btn btn-ghost btn-sm" onClick={onClose}><Icon name="x" size={16} /></button>
        </div>

        {/* Plan cards */}
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 16, padding: "24px 28px 28px" }}>
          {[
            {
              key: "free",
              name: "Free",
              price: "GYD 0",
              sub: "Forever free",
              color: "var(--text3)",
              border: "var(--border2)",
              bg: "var(--bg3)",
              features: [
                { t: "5 active job listings" },
                { t: "Top 8 candidates per job" },
                { t: "AI match scores" },
                { t: "Real-time notifications" },
                { t: "Shareable job links" },
                { t: "20 CV downloads / month" },
                { t: "Archive candidates" },
              ],
              locked: ["Full AI fit reports", "Candidate search", "Pipeline view", "Interview calendar"],
            },
            {
              key: "starter",
              name: "Starter",
              price: "GYD 12,000",
              sub: "per year · ~GYD 1,000/mo",
              color: "var(--gold)",
              border: "var(--gold-dim)",
              bg: "rgba(184,146,42,0.05)",
              badge: currentPlan === "starter" ? "CURRENT" : null,
              features: [
                { t: "8 active job listings" },
                { t: "Top 15 candidates per job" },
                { t: "Full AI fit reports" },
                { t: "Skill gap analysis" },
                { t: "Green flags & watch points" },
                { t: "Candidate search" },
                { t: "Kanban pipeline view" },
                { t: "Does Not Meet Expectations" },
                { t: "100 CV downloads / month" },
              ],
              locked: ["Interview calendar & scheduling"],
            },
            {
              key: "professional",
              name: "Professional",
              price: "GYD 36,000",
              sub: "per year · ~GYD 3,000/mo",
              color: "#1A6FD4",
              border: "#1A6FD4",
              bg: "linear-gradient(160deg, #1C1917 0%, #1a2535 100%)",
              textLight: true,
              badge: currentPlan === "professional" ? "CURRENT" : "MOST POPULAR",
              badgeColor: currentPlan === "professional" ? "var(--text3)" : "var(--gold)",
              features: [
                { t: "Unlimited active jobs" },
                { t: "ALL candidates, no cap" },
                { t: "Full AI fit reports" },
                { t: "Skill gap analysis" },
                { t: "Candidate search" },
                { t: "Kanban pipeline view" },
                { t: "Interview calendar + scheduling" },
                { t: "Interview date picker" },
                { t: "2 featured homepage slots · 7 days each" },
                { t: "Does Not Meet Expectations" },
                { t: "Unlimited CV downloads" },
                { t: "Priority support" },
              ],
              locked: [],
            },
          ].map(plan => {
            const isSelected = selectedPlan === plan.key;
            const isCurrent = currentPlan === plan.key;
            const isLocked = plan.key === "free" || isCurrent;
            return (
              <div key={plan.key}
                onClick={() => { if (!isLocked) setSelectedPlan(plan.key); }}
                style={{
                  background: plan.bg,
                  border: "2px solid " + (isSelected && !isCurrent ? plan.border : plan.key === "professional" ? "rgba(26,111,212,0.5)" : "var(--border2)"),
                  borderRadius: 16, padding: "22px 20px 20px",
                  cursor: isLocked ? "default" : "pointer",
                  transition: "all 0.2s", position: "relative",
                  boxShadow: isSelected && plan.key === "professional" ? "0 8px 32px rgba(26,111,212,0.2)" : "none",
                }}>
                {plan.badge && (
                  <div style={{ position: "absolute", top: -12, left: "50%", transform: "translateX(-50%)", background: plan.badgeColor || "var(--text3)", color: plan.badgeColor === "var(--gold)" ? "#1A1714" : "#fff", fontSize: 9, fontWeight: 800, padding: "3px 12px", borderRadius: 10, whiteSpace: "nowrap", letterSpacing: "0.08em" }}>{plan.badge}</div>
                )}
                <div className="font-display" style={{ fontSize: 15, fontWeight: 800, color: plan.textLight ? "#fff" : plan.color, marginBottom: 6, letterSpacing: "-0.01em" }}>{plan.name}</div>
                <div style={{ marginBottom: 18 }} />

                <div style={{ height: 1, background: plan.textLight ? "rgba(255,255,255,0.1)" : "var(--border2)", marginBottom: 16 }} />

                <div style={{ display: "flex", flexDirection: "column", gap: 9 }}>
                  {plan.features.map((f, fi) => (
                    <div key={fi} style={{ display: "flex", alignItems: "flex-start", gap: 8 }}>
                      <div style={{ width: 16, height: 16, borderRadius: "50%", background: plan.textLight ? "rgba(45,158,78,0.25)" : "rgba(45,158,78,0.12)", border: "1px solid rgba(45,158,78,0.3)", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, marginTop: 1 }}>
                        <svg width="9" height="9" viewBox="0 0 24 24" fill="none" stroke={plan.textLight ? "#6EE7A0" : "var(--green)"} strokeWidth="3"><polyline points="20 6 9 17 4 12"/></svg>
                      </div>
                      <span style={{ fontSize: 12, color: plan.textLight ? "rgba(255,255,255,0.85)" : "var(--text)", lineHeight: 1.4 }}>{f.t}</span>
                    </div>
                  ))}
                  {plan.locked?.map((f, fi) => (
                    <div key={"l"+fi} style={{ display: "flex", alignItems: "flex-start", gap: 8, opacity: 0.4 }}>
                      <div style={{ width: 16, height: 16, borderRadius: "50%", background: "var(--bg3)", border: "1px solid var(--border2)", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, marginTop: 1 }}>
                        <Icon name="lock" size={8} color="var(--text3)" />
                      </div>
                      <span style={{ fontSize: 12, color: "var(--text3)", lineHeight: 1.4 }}>{f}</span>
                    </div>
                  ))}
                </div>

                {!isLocked && (
                  <button
                    className={plan.key === "professional" ? "btn btn-gold btn-sm" : "btn btn-outline btn-sm"}
                    style={{ width: "100%", marginTop: 20, fontSize: 13, fontWeight: 600, ...(plan.key === "professional" ? {} : { color: plan.color, borderColor: plan.border }) }}
                    onClick={e => { e.stopPropagation(); setSelectedPlan(plan.key); setStep("pay"); }}
                  >
                    {isCurrent ? "Current plan" : "Get " + plan.name + " →"}
                  </button>
                )}
                {isCurrent && (
                  <div style={{ marginTop: 20, textAlign: "center", fontSize: 12, color: plan.textLight ? "rgba(255,255,255,0.4)" : "var(--text3)", fontWeight: 500 }}>Your current plan</div>
                )}
              </div>
            );
          })}
        </div>

        {/* Footer note */}
        <div style={{ padding: "0 28px 20px", textAlign: "center" }}>
          <p style={{ fontSize: 12, color: "var(--text3)" }}>✦ All plans include a 24–48hr activation after payment via MMG or Bank Transfer · No hidden fees</p>
        </div>
      </div>
    </div>
  );

  // -- STEP 2: Payment --
  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="modal" style={{ maxWidth: 480 }} onClick={e => e.stopPropagation()}>
        <div className="flex items-center gap-3 mb-6">
          <button className="btn btn-ghost btn-sm" onClick={() => setStep("compare")} style={{ padding: "6px 10px" }}>← Back</button>
          <div>
            <h2 className="font-display" style={{ fontSize: 18, fontWeight: 700 }}>Complete your upgrade</h2>
            <p style={{ fontSize: 13, color: "var(--text2)" }}>{planMeta[selectedPlan].name} · {planMeta[selectedPlan].price}</p>
          </div>
          <button className="btn btn-ghost btn-sm" style={{ marginLeft: "auto" }} onClick={onClose}><Icon name="x" size={16} /></button>
        </div>

        {/* Selected plan summary */}
        <div style={{ background: planMeta[selectedPlan].bg, border: "1px solid " + planMeta[selectedPlan].color, borderRadius: 12, padding: "14px 18px", marginBottom: 20 }}>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
            <div className="font-display" style={{ fontSize: 16, fontWeight: 700, color: planMeta[selectedPlan].color }}>{planMeta[selectedPlan].name}</div>
            <div style={{ fontSize: 18, fontWeight: 800, color: planMeta[selectedPlan].color, fontFamily: "Syne" }}>{planMeta[selectedPlan].price}</div>
          </div>
          <div style={{ display: "flex", gap: 16, marginTop: 10, flexWrap: "wrap" }}>
            {selectedPlan === "starter" ? [
              "8 active jobs", "Top 15 candidates", "Full AI reports", "Pipeline view", "Candidate search", "100 CV downloads/month"
            ] : [
              "Unlimited jobs", "All candidates", "Interview calendar", "Pipeline view", "Priority support", "Unlimited CV downloads"
            ].map((f, i) => (
              <div key={i} style={{ display: "flex", alignItems: "center", gap: 5, fontSize: 12, color: planMeta[selectedPlan].color }}>
                <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3"><polyline points="20 6 9 17 4 12"/></svg>
                {f}
              </div>
            ))}
          </div>
        </div>

        {/* Payment method */}
        <div className="mb-5">
          <label className="label mb-2">Payment Method</label>
          <div className="flex-col gap-2">
            {[
              { id: "mmg",  label: "MMG Mobile Money", desc: getSetting("mmg_name") && getSetting("mmg_phone") ? getSetting("mmg_name") + " · " + getSetting("mmg_phone") : getSetting("mmg_details", "MMG Mobile Money") },
              { id: "bank", label: "Bank Transfer",    desc: getSetting("bank_details", "Bank Transfer") },
            ].filter(m => m.id === "mmg" ? getSetting("mmg_enabled","true") === "true" : getSetting("bank_enabled","true") === "true").map(m => (
              <div key={m.id} onClick={() => setPaymentMethod(m.id)} style={{ border: "1px solid " + (paymentMethod === m.id ? "var(--gold-dim)" : "var(--border2)"), background: paymentMethod === m.id ? "rgba(201,168,76,0.04)" : "var(--bg)", borderRadius: 8, padding: "11px 14px", cursor: "pointer", display: "flex", alignItems: "center", gap: 12 }}>
                <div style={{ width: 16, height: 16, borderRadius: "50%", border: "2px solid " + (paymentMethod === m.id ? "var(--gold)" : "var(--border)"), display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                  {paymentMethod === m.id && <div style={{ width: 8, height: 8, borderRadius: "50%", background: "var(--gold)" }} />}
                </div>
                <div>
                  <div style={{ fontSize: 14, fontWeight: 500, color: "var(--text)" }}>{m.label}</div>
                  <div style={{ fontSize: 12, color: "var(--text3)" }}>{m.desc}</div>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div style={{ background: "rgba(201,168,76,0.06)", border: "1px solid rgba(201,168,76,0.15)", borderRadius: 8, padding: "12px 16px", marginBottom: 20 }}>
          <div className="flex items-center gap-2 mb-1">
            <Icon name="info" size={13} color="var(--gold)" />
            <span style={{ fontSize: 12, fontWeight: 600, color: "var(--gold)" }}>How it works</span>
          </div>
          <p style={{ fontSize: 13, color: "var(--text2)", lineHeight: 1.6 }}>Make your payment, then click below to send us your receipt on WhatsApp. We'll activate your plan within a few hours.</p>
        </div>

        <button className="btn btn-whatsapp w-full btn-lg" onClick={submitRequest} disabled={loading}>
          {loading ? <div className="spinner" style={{ width: 16, height: 16 }} /> : <Icon name="whatsapp" size={18} color="#fff" />}
          Send Receipt on WhatsApp
        </button>
      </div>
    </div>
  );
};

// ============================================================
// POST JOB MODAL
// ============================================================
const PostJobModal = ({ employer, jobs, onClose, onSuccess }) => {
  const [loading, setLoading] = useState(false);
  const [flierMode, setFlierMode] = useState(false);
  const [flierFile, setFlierFile] = useState(null);
  const [flierPreview, setFlierPreview] = useState(null);
  const [extracting, setExtracting] = useState(false);
  const [extracted, setExtracted] = useState(false);
  const [reviewConfirmed, setReviewConfirmed] = useState(false);
  const plan = employer?.plan || "free";
  const jobLimit = plan === "free" ? 5 : plan === "starter" ? 8 : 9999;

  const handleFlierUpload = async (file) => {
    if (!file) return;
    const allowed = ["application/pdf", "image/png", "image/jpeg", "image/jpg"];
    if (!allowed.includes(file.type)) { toast("Please upload a PDF or PNG/JPG image.", "error"); return; }
    setFlierFile(file);
    setExtracted(false);
    setReviewConfirmed(false);

    // Show preview for images
    if (file.type.startsWith("image/")) {
      const reader = new FileReader();
      reader.onload = e => setFlierPreview(e.target.result);
      reader.readAsDataURL(file);
    } else {
      setFlierPreview(null);
    }

    // Convert to base64 and send to Gemini via Edge Function
    setExtracting(true);
    try {
      const base64 = await new Promise((res, rej) => {
        const r = new FileReader();
        r.onload = () => res(r.result.split(",")[1]);
        r.onerror = () => rej(new Error("Failed to read file"));
        r.readAsDataURL(file);
      });

      const mimeType = file.type;
      const prompt = `You are a job posting data extractor. Analyse this job flier (PDF or image) and extract the following fields. Return ONLY a JSON object with no markdown, no extra text, just raw JSON.

Fields to extract:
{
  "title": "job title",
  "description": "full job description / about the role",
  "requirements": "skills, qualifications, experience required",
  "responsibilities": "day-to-day duties and responsibilities",
  "benefits": "benefits, perks, compensation mentioned",
  "category": "best matching category from this list: ${JOB_CATEGORIES.join(", ")}",
  "work_type": "onsite OR remote OR hybrid",
  "location": "location if mentioned, otherwise empty string",
  "salary_min": null or number in GYD,
  "salary_max": null or number in GYD
}

If a field is not found in the flier, use an empty string or null. Do not invent information.`;

      const res = await fetch(
        SUPABASE_URL + "/functions/v1/gemini-scan",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "Authorization": "Bearer " + SUPABASE_ANON_KEY,
          },
          body: JSON.stringify({
            prompt,
            pdfBase64: base64,
            mimeType,
          }),
        }
      );

      const data = await res.json();
      if (!res.ok) throw new Error(data.error || "Extraction failed");

      const raw = data.text || "";
      const cleaned = raw.replace(/```json\n?/g, "").replace(/```\n?/g, "").trim();
      let parsed;
      try { parsed = JSON.parse(cleaned); } catch {
        const match = cleaned.match(/\{[\s\S]*\}/);
        if (match) parsed = JSON.parse(match[0]); else throw new Error("Could not parse response");
      }

      // Map extracted location to closest region option
      const regionMap = {
        "georgetown": "Region 4 — Demerara-Mahaica (Georgetown)",
        "demerara": "Region 4 — Demerara-Mahaica (Georgetown)",
        "region 4": "Region 4 — Demerara-Mahaica (Georgetown)",
        "linden": "Region 10 — Upper Demerara-Berbice (Linden)",
        "berbice": "Region 6 — East Berbice-Corentyne",
        "lethem": "Region 9 — Upper Takutu-Upper Essequibo (Lethem)",
      };
      let mappedLocation = form.location;
      if (parsed.location) {
        const loc = parsed.location.toLowerCase();
        for (const [key, val] of Object.entries(regionMap)) {
          if (loc.includes(key)) { mappedLocation = val; break; }
        }
      }

      setForm(prev => ({
        ...prev,
        title: parsed.title || prev.title,
        description: parsed.description || prev.description,
        requirements: parsed.requirements || prev.requirements,
        responsibilities: parsed.responsibilities || prev.responsibilities,
        benefits: parsed.benefits || prev.benefits,
        category: JOB_CATEGORIES.includes(parsed.category) ? parsed.category : prev.category,
        work_type: ["onsite","remote","hybrid"].includes(parsed.work_type) ? parsed.work_type : prev.work_type,
        location: mappedLocation,
        salary_min: parsed.salary_min || prev.salary_min,
        salary_max: parsed.salary_max || prev.salary_max,
      }));

      setExtracted(true);
      toast("Job details extracted! Review and confirm before posting.", "success");
    } catch (err) {
      toast(err.message || "Could not extract details. Please fill in manually.", "error");
    } finally {
      setExtracting(false);
    }
  };

  // Notify matching seekers in the background after a job is posted
  const notifyMatchingSeekers = async (jobId, jobData) => {
    try {
      // Build a keyword set from the new job
      const rawWords = [
        ...(jobData.title || "").split(/\s+/),
        ...(jobData.requirements || "").split(/[\s,;.]+/),
        ...(jobData.category || "").split(/\s+/),
      ].map(w => w.toLowerCase().replace(/[^a-z0-9]/g, "")).filter(w => w.length > 3);
      const jobKeywords = new Set(rawWords);

      // Fetch seeker profiles that have CV data
      const { data: seekers } = await supabase
        .from("seeker_profiles")
        .select("id, ai_parsed_data")
        .not("ai_parsed_data", "is", null);

      if (!seekers?.length) return;

      // Find seekers whose skills/industries overlap with the job
      const matchingIds = seekers.filter(s => {
        const skills   = (s.ai_parsed_data?.skills   || []).map(sk => sk.toLowerCase());
        const industries = (s.ai_parsed_data?.industries || []).map(i => i.toLowerCase());
        const allTerms = [...skills, ...industries].join(" ");
        return [...jobKeywords].some(kw => allTerms.includes(kw));
      }).map(s => s.id);

      if (!matchingIds.length) return;

      // Insert a job alert row per matching seeker
      // (requires a `job_alerts` table: id, seeker_id, job_id, job_title, read bool, created_at)
      const alerts = matchingIds.map(seekerId => ({
        seeker_id: seekerId,
        job_id: jobId,
        job_title: jobData.title,
        read: false,
        created_at: new Date().toISOString(),
      }));
      await supabase.from("job_alerts").insert(alerts);
    } catch {
      // Non-critical — fail silently
    }
  };

  // Quick-pick helpers — days from today
  const daysFromNow = (d) => new Date(Date.now() + d * 86400000).toISOString().split("T")[0];

  const [form, setForm] = useState({
    title: "", description: "", requirements: "", responsibilities: "",
    benefits: "", category: "", work_type: "onsite", location: "Region 4 — Demerara-Mahaica (Georgetown)",
    salary_min: "", salary_max: "", salary_visible: true,
    expires_at: daysFromNow(30), // default 30 days
  });

  const submit = async (e) => {
    e.preventDefault();
    if (!employer?.id) { toast("Your employer profile could not be loaded. Please refresh and try again.", "error"); return; }
    if (!form.expires_at) { toast("Please set an expiry date for this posting.", "error"); return; }
    const expiry = new Date(form.expires_at);
    if (expiry <= new Date()) { toast("Expiry date must be in the future.", "error"); return; }
    const activeJobCount = (jobs || []).filter(j => j.status === "active").length;
    if (activeJobCount >= jobLimit) {
      toast(`Your ${plan} plan allows ${jobLimit} active jobs. Close or archive a job first, or upgrade your plan.`, "error");
      return;
    }
    setLoading(true);
    try {
      const { data: newJob, error } = await supabase.from("jobs").insert({
        employer_id: employer.id,
        ...form,
        salary_min: form.salary_min ? parseFloat(form.salary_min) : null,
        salary_max: form.salary_max ? parseFloat(form.salary_max) : null,
        expires_at: new Date(form.expires_at).toISOString(),
        extended: false,
      }).select().single();
      if (error) throw error;
      notifyMatchingSeekers(newJob.id, newJob); // fire-and-forget
      onSuccess();
    } catch (err) {
      toast(err.message, "error");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="modal" style={{ maxWidth: 640 }} onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-6">
          <h2 className="font-display" style={{ fontSize: 20, fontWeight: 700 }}>Post a New Job</h2>
          <button className="btn btn-ghost btn-sm" onClick={onClose}><Icon name="x" size={16} /></button>
        </div>

        {/* Mode toggle */}
        <div style={{ display: "flex", gap: 8, marginBottom: 24 }}>
          <button type="button"
            onClick={() => { setFlierMode(false); setExtracted(false); setFlierFile(null); setFlierPreview(null); }}
            style={{ flex: 1, padding: "10px 0", borderRadius: 8, border: `1px solid ${!flierMode ? "var(--gold)" : "var(--border2)"}`, background: !flierMode ? "rgba(184,146,42,0.1)" : "transparent", color: !flierMode ? "var(--gold)" : "var(--text2)", fontWeight: 600, fontSize: 13, cursor: "pointer" }}>
            ✏️ Fill in manually
          </button>
          <button type="button"
            onClick={() => setFlierMode(true)}
            style={{ flex: 1, padding: "10px 0", borderRadius: 8, border: `1px solid ${flierMode ? "var(--gold)" : "var(--border2)"}`, background: flierMode ? "rgba(184,146,42,0.1)" : "transparent", color: flierMode ? "var(--gold)" : "var(--text2)", fontWeight: 600, fontSize: 13, cursor: "pointer" }}>
            📄 Upload job flier
          </button>
        </div>

        {/* Flier upload zone */}
        {flierMode && (
          <div style={{ marginBottom: 24 }}>
            <label style={{ display: "block", fontSize: 13, fontWeight: 600, color: "var(--text2)", marginBottom: 8, textTransform: "uppercase", letterSpacing: "0.06em" }}>Upload Flier (PDF or Image)</label>
            <div
              onClick={() => document.getElementById("flier-input").click()}
              onDragOver={e => e.preventDefault()}
              onDrop={e => { e.preventDefault(); handleFlierUpload(e.dataTransfer.files[0]); }}
              style={{ border: `2px dashed ${flierFile ? "var(--gold)" : "var(--border2)"}`, borderRadius: 12, padding: 32, textAlign: "center", cursor: "pointer", background: flierFile ? "rgba(184,146,42,0.05)" : "transparent", transition: "all 0.2s" }}>
              <input id="flier-input" type="file" accept=".pdf,.png,.jpg,.jpeg" style={{ display: "none" }} onChange={e => handleFlierUpload(e.target.files[0])} />
              {extracting ? (
                <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 12 }}>
                  <div className="spinner" style={{ width: 28, height: 28 }} />
                  <p style={{ color: "var(--gold)", fontSize: 14, fontWeight: 600 }}>Reading your flier...</p>
                  <p style={{ color: "var(--text3)", fontSize: 12 }}>AI is extracting job details</p>
                </div>
              ) : flierFile ? (
                <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 8 }}>
                  {flierPreview && <img src={flierPreview} alt="Flier preview" style={{ maxHeight: 120, borderRadius: 8, marginBottom: 8, objectFit: "contain" }} />}
                  <p style={{ color: "var(--gold)", fontSize: 14, fontWeight: 600 }}>✓ {flierFile.name}</p>
                  <p style={{ color: "var(--text3)", fontSize: 12 }}>Click to replace</p>
                </div>
              ) : (
                <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 8 }}>
                  <div style={{ fontSize: 32 }}>📄</div>
                  <p style={{ color: "var(--text2)", fontSize: 14, fontWeight: 600 }}>Drop your flier here or click to browse</p>
                  <p style={{ color: "var(--text3)", fontSize: 12 }}>PDF, PNG, or JPG — AI will extract all job details automatically</p>
                </div>
              )}
            </div>

            {/* Extracted confirmation */}
            {extracted && (
              <div style={{ marginTop: 16, padding: "12px 16px", background: "rgba(45,158,78,0.08)", border: "1px solid rgba(45,158,78,0.25)", borderRadius: 10 }}>
                <p style={{ color: "var(--green)", fontSize: 13, fontWeight: 600, marginBottom: 8 }}>✓ Details extracted successfully — review the fields below before posting</p>
                <label style={{ display: "flex", alignItems: "center", gap: 8, cursor: "pointer", fontSize: 13, color: "var(--text2)" }}>
                  <input type="checkbox" checked={reviewConfirmed} onChange={e => setReviewConfirmed(e.target.checked)}
                    style={{ width: 16, height: 16, accentColor: "var(--gold)" }} />
                  I've reviewed the extracted details and they are accurate
                </label>
              </div>
            )}

            {!extracted && !extracting && (
              <p style={{ fontSize: 12, color: "var(--text3)", marginTop: 8, textAlign: "center" }}>
                Upload a flier to auto-fill the form below, then review before posting
              </p>
            )}
          </div>
        )}

        <form onSubmit={submit} className="flex-col gap-4">
          <div>
            <label className="label">Job Title *</label>
            <input className="input" placeholder="e.g. Senior Software Engineer" value={form.title} onChange={e => setForm({ ...form, title: e.target.value })} required />
          </div>
          <div>
            <label className="label">Job Description *</label>
            <textarea className="input" placeholder="Describe the role, team, and what success looks like..." value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} required style={{ minHeight: 120 }} />
          </div>
          <div>
            <label className="label">Requirements *</label>
            <textarea className="input" placeholder="Skills, experience, qualifications required..." value={form.requirements} onChange={e => setForm({ ...form, requirements: e.target.value })} required style={{ minHeight: 80 }} />
          </div>
          <div>
            <label className="label">Responsibilities</label>
            <textarea className="input" placeholder="Day-to-day responsibilities..." value={form.responsibilities} onChange={e => setForm({ ...form, responsibilities: e.target.value })} style={{ minHeight: 80 }} />
          </div>
          <div className="grid-2">
            <div>
              <label className="label">Category</label>
              <select className="input" value={form.category} onChange={e => setForm({ ...form, category: e.target.value })} required>
                <option value="">Select a category...</option>
                {JOB_CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
            <div>
              <label className="label">Work Type</label>
              <select className="input" value={form.work_type} onChange={e => setForm({ ...form, work_type: e.target.value })}>
                <option value="onsite">On-site</option>
                <option value="remote">Remote</option>
                <option value="hybrid">Hybrid</option>
              </select>
            </div>
          </div>
          <div className="grid-2">
            <div>
              <label className="label">Region</label>
              <select className="input" value={form.location} onChange={e => setForm({ ...form, location: e.target.value })}>
                <option value="Region 4 — Demerara-Mahaica (Georgetown)">Region 4 — Georgetown</option>
                <option value="Region 1 — Barima-Waini">Region 1 — Barima-Waini</option>
                <option value="Region 2 — Pomeroon-Supenaam">Region 2 — Pomeroon-Supenaam</option>
                <option value="Region 3 — Essequibo Islands-West Demerara">Region 3 — Essequibo Islands</option>
                <option value="Region 5 — Mahaica-Berbice">Region 5 — Mahaica-Berbice</option>
                <option value="Region 6 — East Berbice-Corentyne">Region 6 — East Berbice-Corentyne</option>
                <option value="Region 7 — Cuyuni-Mazaruni">Region 7 — Cuyuni-Mazaruni</option>
                <option value="Region 8 — Potaro-Siparuni">Region 8 — Potaro-Siparuni</option>
                <option value="Region 9 — Upper Takutu-Upper Essequibo (Lethem)">Region 9 — Lethem</option>
                <option value="Region 10 — Upper Demerara-Berbice (Linden)">Region 10 — Linden</option>
              </select>
            </div>
          </div>
          <div className="grid-2">
            <div>
              <label className="label">Salary Min (GYD/year)</label>
              <input className="input" type="number" placeholder="e.g. 1500000" value={form.salary_min} onChange={e => setForm({ ...form, salary_min: e.target.value })} />
            </div>
            <div>
              <label className="label">Salary Max (GYD/year)</label>
              <input className="input" type="number" placeholder="e.g. 2400000" value={form.salary_max} onChange={e => setForm({ ...form, salary_max: e.target.value })} />
            </div>
          </div>

          {/* Mandatory expiry date */}
          <div>
            <label className="label">Posting Expiry Date * <span style={{ color: "var(--text3)", fontWeight: 400, textTransform: "none", letterSpacing: 0 }}>— listing + all applications deleted on this date</span></label>
            <div className="flex gap-2 mb-2">
              {[7, 14, 30, 60].map(d => (
                <button key={d} type="button"
                  className={`btn btn-sm ${form.expires_at === daysFromNow(d) ? "btn-gold" : "btn-outline"}`}
                  onClick={() => setForm({ ...form, expires_at: daysFromNow(d) })}>
                  {d}d
                </button>
              ))}
            </div>
            <input className="input" type="date" value={form.expires_at} min={daysFromNow(1)}
              onChange={e => setForm({ ...form, expires_at: e.target.value })} required />
          </div>

          <div className="flex gap-3 justify-end mt-2">
            <button type="button" className="btn btn-outline" onClick={onClose}>Cancel</button>
            <button type="submit" className="btn btn-gold" disabled={loading || (flierMode && extracted && !reviewConfirmed)}>
              {loading ? <><div className="spinner" style={{ width: 16, height: 16 }} />Posting...</> : <><Icon name="plus" size={16} />Post Job</>}
            </button>
          </div>
          {flierMode && extracted && !reviewConfirmed && (
            <p style={{ fontSize: 12, color: "var(--gold)", textAlign: "right", marginTop: 4 }}>Please confirm you've reviewed the extracted details above</p>
          )}
        </form>
      </div>
    </div>
  );
};


// ============================================================
// EMPLOYER DASHBOARD
// ============================================================
const EmployerDashboard = ({ user, onLogout }) => {
  const [tab, setTab] = useState("home");
  const [employer, setEmployer] = useState(null);
  const [jobs, setJobs] = useState([]);
  const [selectedJob, setSelectedJob] = useState(null);
  const [applicants, setApplicants] = useState([]);
  const [showPostJob, setShowPostJob] = useState(false);
  const [showUpgrade, setShowUpgrade] = useState(false);
  const [selectedApplicant, setSelectedApplicant] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notifications, setNotifications] = useState([]); // loaded from Supabase notifications table
  const [unreadCount, setUnreadCount] = useState(0);
  const [newAppIds, setNewAppIds] = useState(new Set());

  const [dismissedAnnouncement, setDismissedAnnouncement] = useState(false);
  useEffect(() => { loadData(); }, []);

  // Load notifications from Supabase on mount
  useEffect(() => {
    if (!user?.id) return;
    const loadNotifications = async () => {
      const { data } = await supabase
        .from("notifications")
        .select("*")
        .eq("employer_id", user.id)
        .order("created_at", { ascending: false })
        .limit(50);
      if (data) {
        setNotifications(data);
        setUnreadCount(data.filter(n => !n.read).length);
      }
    };
    loadNotifications();
  }, [user?.id]);

  // Supabase realtime — fires when a new application comes in
  useEffect(() => {
    if (!employer || !jobs.length) return;
    const jobIds = jobs.map(j => j.id);
    const channel = supabase
      .channel("new-applications-" + employer.id)
      .on("postgres_changes", { event: "INSERT", schema: "public", table: "applications" }, async (payload) => {
        const app = payload.new;
        if (!jobIds.includes(app.job_id)) return;
        const job = jobs.find(j => j.id === app.job_id);

        // Write notification to Supabase
        const { data: inserted } = await supabase.from("notifications").insert({
          employer_id: employer.id,
          job_id: app.job_id,
          application_id: app.id,
          job_title: job?.title || "a job",
          candidate_name: app.candidate_name || "A candidate",
          match_score: app.match_score,
          read: false,
        }).select().single();

        if (inserted) {
          setNotifications(prev => [inserted, ...prev]);
          setUnreadCount(c => c + 1);
        }
        setNewAppIds(prev => new Set([...prev, app.id]));
        setJobs(prev => prev.map(j => j.id === app.job_id ? { ...j, application_count: (j.application_count || 0) + 1 } : j));
        toast(`New applicant for "${job?.title || "your job"}" — ${app.candidate_name || "A candidate"} (${Math.round(app.match_score || 0)}%)`, "info");
      })
      .subscribe();
    return () => { supabase.removeChannel(channel); };
  }, [employer?.id, jobs.length]);

  const [selectedJobIds, setSelectedJobIds] = useState([]);
  const [editingJob, setEditingJob] = useState(null);
  const [candidateSearch, setCandidateSearch] = useState("");
  const [pipelineView, setPipelineView] = useState(false);
  const [interviewModal, setInterviewModal] = useState(null);
  const [interviewDate, setInterviewDate] = useState("");
  const [interviewTime, setInterviewTime] = useState("10:00");
  const [draggedId, setDraggedId] = useState(null);
  const [dismissedEmails, setDismissedEmails] = useState(new Set());
  const [showNewOnly, setShowNewOnly] = useState(false);
  const [candidateNotes, setCandidateNotes] = useState({}); // { appId: draftText }

  const PIPELINE_COLS = [
    { id: "new",                  label: "New",         color: "#1A6FD4", bg: "#EEF5FE" },
    { id: "shortlisted",          label: "Shortlisted", color: "#B8922A", bg: "rgba(184,146,42,0.1)" },
    { id: "interview_scheduled",  label: "Interview",   color: "#7C3AED", bg: "#F3F0FF" },
    { id: "offer_made",           label: "Offer Made",  color: "#2D9E4E", bg: "#F0FAF3" },
    { id: "hired",                label: "Hired",       color: "#2D9E4E", bg: "#F0FAF3" },
  ];

  const downloadCV = async (app) => {
    const used = employer?.cv_downloads_used || 0;
    if (used >= downloadLimit) {
      toast(`Monthly download limit reached (${downloadLimit}). Upgrade to download more CVs.`, "error");
      return;
    }
    // Open the window immediately on user gesture — before any awaits —
    // so the browser doesn't treat it as a popup
    const win = window.open("", "_blank");
    if (!win) {
      toast("Please allow popups for this site to download CVs.", "error");
      return;
    }
    win.document.write("<p style='font-family:sans-serif;padding:24px'>Preparing CV download...</p>");

    // Try to get a fresh signed URL from cv_path (avoids expired-URL failures)
    let urlToOpen = app.cv_url;
    if (app.cv_path) {
      const { data: fresh } = await supabase.storage
        .from("cv-uploads")
        .createSignedUrl(app.cv_path, 60 * 60);
      if (fresh?.signedUrl) urlToOpen = fresh.signedUrl;
    }
    if (!urlToOpen) {
      win.close();
      toast("CV file is no longer available. The candidate may need to re-upload.", "error");
      return;
    }
    await supabase.from("employers").update({ cv_downloads_used: used + 1 }).eq("id", employer.id);
    setEmployer(e => ({ ...e, cv_downloads_used: used + 1 }));
    win.location.href = urlToOpen;
  };

  const movePipelineCard = async (app, newStatus) => {
    if (newStatus === app.status) return;
    if (newStatus === "interview_scheduled") {
      setInterviewModal({ app, targetStatus: newStatus });
      const d = new Date(); d.setDate(d.getDate() + 3);
      setInterviewDate(d.toISOString().split("T")[0]);
      return;
    }
    if (newStatus === "hired") {
      if (!selectedJob?.id) { toast("No job selected. Please select a job first.", "error"); return; }
      // Mark this candidate as hired
      await supabase.from("applications").update({ status: "hired" }).eq("id", app.id);
      // Delete all other applicants for this job
      await supabase.from("applications").delete().eq("job_id", selectedJob.id).neq("id", app.id);
      // Mark job as closed (keep visible)
      await supabase.from("jobs").update({ status: "closed" }).eq("id", selectedJob.id);
      setJobs(j => j.map(x => x.id === selectedJob.id ? { ...x, status: "closed" } : x));
      setApplicants([{ ...app, status: "hired" }]);
      setPipelineView(false);
      toast(`${app.candidate_name || "Candidate"} has been hired! Job marked as closed.`, "success");
      return;
    }
    await supabase.from("applications").update({ status: newStatus }).eq("id", app.id);
    setApplicants(prev => prev.map(a => a.id === app.id ? { ...a, status: newStatus } : a));
    notifySeekerStatusChange(app, newStatus);
  };

  const confirmInterview = async () => {
    if (!interviewModal || !interviewDate) return;
    const dt = interviewDate + "T" + interviewTime + ":00";
    const { error } = await supabase.from("applications").update({ status: "interview_scheduled", interview_date: dt }).eq("id", interviewModal.app.id);
    if (error) { toast("Failed to schedule interview: " + error.message, "error"); return; }
    setApplicants(prev => prev.map(a => a.id === interviewModal.app.id ? { ...a, status: "interview_scheduled", interview_date: dt } : a));
    notifySeekerStatusChange({ ...interviewModal.app, job_id: interviewModal.app.job_id }, "interview_scheduled");
    toast("Interview scheduled for " + new Date(dt).toLocaleDateString("en-GY", { weekday: "short", month: "short", day: "numeric" }) + " at " + interviewTime, "success");
    setInterviewModal(null);
  };

  // Load dismissed emails — from Supabase (source of truth) + localStorage (fallback/cache)
  useEffect(() => {
    if (!user?.id) return;
    const load = async () => {
      // Try Supabase first
      const { data: emp } = await supabase.from("employers").select("dismissed_emails").eq("id", user.id).single();
      if (emp?.dismissed_emails?.length) {
        const merged = new Set(emp.dismissed_emails.map(e => e.toLowerCase()));
        setDismissedEmails(merged);
        localStorage.setItem(`jobspot_dismissed_${user.id}`, JSON.stringify([...merged]));
        return;
      }
      // Fallback to localStorage
      const stored = localStorage.getItem(`jobspot_dismissed_${user.id}`);
      if (stored) {
        try { setDismissedEmails(new Set(JSON.parse(stored))); } catch {}
      }
    };
    load();
  }, [user?.id]);

  const saveDismissed = async (set) => {
    setDismissedEmails(set);
    const arr = [...set];
    localStorage.setItem(`jobspot_dismissed_${user.id}`, JSON.stringify(arr));
    // Persist to Supabase so it works across devices
    await supabase.from("employers").update({ dismissed_emails: arr }).eq("id", user.id);
  };

  const [archivedApplicants, setArchivedApplicants] = useState([]);
  const [globalArchived, setGlobalArchived] = useState([]);
  const [globalArchiveLoading, setGlobalArchiveLoading] = useState(false);

  const loadGlobalArchive = async () => {
    if (!jobs.length) return;
    setGlobalArchiveLoading(true);
    const jobIds = jobs.map(j => j.id);
    const { data } = await supabase
      .from("applications")
      .select("*, jobs(title)")
      .in("job_id", jobIds)
      .eq("status", "archived")
      .order("created_at", { ascending: false });
    setGlobalArchived(data || []);
    setGlobalArchiveLoading(false);
  };

  const archiveCandidate = async (app) => {
    const { error } = await supabase.from("applications").update({ status: "archived" }).eq("id", app.id);
    if (error) { toast("Failed to archive candidate: " + error.message, "error"); return; }
    setApplicants(prev => prev.filter(a => a.id !== app.id));
    setArchivedApplicants(prev => [{ ...app, status: "archived" }, ...prev]);
    toast(`${app.candidate_name || "Candidate"} moved to Archive.`, "info");
  };

  const restoreCandidate = async (app) => {
    const { error } = await supabase.from("applications").update({ status: "new" }).eq("id", app.id);
    if (error) { toast("Failed to restore candidate: " + error.message, "error"); return; }
    setArchivedApplicants(prev => prev.filter(a => a.id !== app.id));
    setApplicants(prev => [...prev, { ...app, status: "new" }].sort((a, b) => b.match_score - a.match_score));
    toast(`${app.candidate_name || "Candidate"} restored to the leaderboard.`, "success");
  };

  const loadArchived = async (jobId) => {
    const { data } = await supabase.from("applications").select("*").eq("job_id", jobId).eq("status", "archived").order("match_score", { ascending: false });
    setArchivedApplicants(data || []);
  };

  const dismissCandidate = async (app) => {
    if (plan === "free") { setShowUpgrade(true); return; }
    const { error } = await supabase.from("applications").update({ status: "does_not_meet" }).eq("id", app.id);
    if (error) { toast("Failed to dismiss candidate: " + error.message, "error"); return; }
    const next = new Set(dismissedEmails);
    if (app.candidate_email) next.add(app.candidate_email.toLowerCase());
    await saveDismissed(next);
    setApplicants(prev => prev.filter(a => a.id !== app.id));
    toast(`${app.candidate_name || "Candidate"} marked as "Does Not Meet Expectations" and will be hidden from all your future listings.`, "info");
  };

  const loadData = async () => {
    setLoading(true);
    const { data: emp } = await supabase.from("employers").select("*").eq("id", user.id).single();
    if (!emp) {
      // Employer row missing — create a minimal one so the dashboard isn't broken
      const { data: newEmp } = await supabase.from("employers").insert({
        id: user.id,
        company_name: user.user_metadata?.company_name || "My Company",
        contact_name: user.user_metadata?.contact_name || "",
        contact_email: user.email,
        plan: "free",
      }).select().single();
      setEmployer(newEmp || null);
      if (newEmp) toast("Employer profile initialised. Please update your company details.", "info");
      else toast("Could not load your employer profile. Please refresh.", "error");
    } else {
      // Monthly CV download counter reset — if last reset was a prior calendar month, zero it out
      const now = new Date();
      const lastReset = emp.cv_downloads_reset_at ? new Date(emp.cv_downloads_reset_at) : null;
      const isNewMonth = !lastReset ||
        lastReset.getFullYear() < now.getFullYear() ||
        lastReset.getMonth() < now.getMonth();
      if (isNewMonth && emp.cv_downloads_used > 0) {
        await supabase.from("employers").update({
          cv_downloads_used: 0,
          cv_downloads_reset_at: now.toISOString(),
        }).eq("id", emp.id);
        emp.cv_downloads_used = 0;
        emp.cv_downloads_reset_at = now.toISOString();
      }
      setEmployer(emp);
    }
    const { data: jobsData } = await supabase.from("employer_job_stats").select("*").eq("employer_id", user.id).order("created_at", { ascending: false });
    // Auto-close expired active jobs (mark as closed rather than deleting — preserves applicant history)
    const now = new Date().toISOString();
    const toClose = (jobsData || []).filter(j => j.expires_at && j.expires_at < now && j.status === "active").map(j => j.id);
    if (toClose.length > 0) {
      await supabase.from("jobs").update({ status: "closed" }).in("id", toClose);
      jobsData.forEach(j => { if (toClose.includes(j.id)) j.status = "closed"; });
      toast(`${toClose.length} job${toClose.length > 1 ? "s" : ""} expired and ${toClose.length > 1 ? "have" : "has"} been closed. Applicant data is preserved.`, "info");
    }
    setJobs(jobsData || []);
    setLoading(false);
  };

  const deleteJob = async (id) => {
    const { error } = await supabase.from("jobs").delete().eq("id", id);
    if (error) { toast("Failed to delete job: " + error.message, "error"); return; }
    setJobs(j => j.filter(x => x.id !== id));
    if (selectedJob?.id === id) { setSelectedJob(null); setApplicants([]); }
    toast("Job deleted", "success");
  };

  const toggleFeatured = async (job) => {
    if (plan !== "professional") { setShowUpgrade(true); return; }
    const isCurrentlyFeatured = job.featured && job.featured_until && new Date(job.featured_until) > new Date();
    if (!isCurrentlyFeatured) {
      // Check how many are currently featured (max 2)
      const now = new Date();
      const activeFeatures = jobs.filter(j => j.featured && j.featured_until && new Date(j.featured_until) > now && j.id !== job.id);
      if (activeFeatures.length >= 2) {
        toast("You can feature up to 2 jobs at a time. Remove a featured job first.", "error");
        return;
      }
    }
    if (isCurrentlyFeatured) {
      // Turn off
      const { error } = await supabase.from("jobs").update({ featured: false, featured_until: null }).eq("id", job.id);
      if (error) { toast("Failed to update: " + error.message, "error"); return; }
      setJobs(j => j.map(x => x.id === job.id ? { ...x, featured: false, featured_until: null } : x));
      toast("Removed from homepage feature.", "info");
    } else {
      // Turn on — set 7-day expiry
      const expiresAt = new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString();
      const { error } = await supabase.from("jobs").update({ featured: true, featured_until: expiresAt }).eq("id", job.id);
      if (error) { toast("Failed to feature job: " + error.message, "error"); return; }
      setJobs(j => j.map(x => x.id === job.id ? { ...x, featured: true, featured_until: expiresAt } : x));
      toast("Job is now featured on the JobSpot homepage for 7 days!", "success");
    }
  };

  const removeDuplicates = async () => {
    if (!selectedJob) return;
    // Group applicants by candidate_email (fallback to candidate_name)
    const seen = new Map();
    const toDelete = [];
    [...applicants]
      .sort((a, b) => b.match_score - a.match_score) // keep highest score
      .forEach(app => {
        const key = (app.candidate_email || app.candidate_name || app.id).toLowerCase().trim();
        if (seen.has(key)) {
          toDelete.push(app.id);
        } else {
          seen.set(key, app.id);
        }
      });
    if (toDelete.length === 0) { toast("No duplicates found.", "info"); return; }
    const { error } = await supabase.from("applications").delete().in("id", toDelete);
    if (error) {
      toast("Failed to remove duplicates: " + error.message, "error");
      return; // Don't update UI — Supabase didn't confirm the delete
    }
    setApplicants(prev => prev.filter(a => !toDelete.includes(a.id)));
    toast(`${toDelete.length} duplicate${toDelete.length > 1 ? "s" : ""} removed. Best score kept.`, "success");
  };

  const toggleAccepting = async () => {
    if (!selectedJob) return;
    const newStatus = selectedJob.status === "paused" ? "active" : "paused";
    const { error } = await supabase.from("jobs").update({ status: newStatus }).eq("id", selectedJob.id);
    if (error) { toast("Failed to update job status: " + error.message, "error"); return; }
    setSelectedJob(prev => ({ ...prev, status: newStatus }));
    setJobs(prev => prev.map(j => j.id === selectedJob.id ? { ...j, status: newStatus } : j));
    toast(newStatus === "paused" ? "Job paused — no new applications will be accepted." : "Job reopened — now accepting applications.", newStatus === "paused" ? "info" : "success");
  };

  const bulkDeleteJobs = async () => {
    const { error } = await supabase.from("jobs").delete().in("id", selectedJobIds);
    if (error) { toast("Failed to delete jobs: " + error.message, "error"); return; }
    setJobs(j => j.filter(x => !selectedJobIds.includes(x.id)));
    if (selectedJob && selectedJobIds.includes(selectedJob.id)) { setSelectedJob(null); setApplicants([]); }
    setSelectedJobIds([]);
    toast(`${selectedJobIds.length} job(s) deleted`, "success");
  };

  const toggleSelectJob = (id) => setSelectedJobIds(s => s.includes(id) ? s.filter(x => x !== id) : [...s, id]);

  const loadApplicants = async (jobId) => {
    // Track what was "new" before this view — any notification for this job
    const notifIds = new Set(notifications.filter(n => n.job_id === jobId).map(n => n.id));
    setNewAppIds(notifIds);
    const { data } = await supabase.from("applications")
      .select("*")
      .eq("job_id", jobId)
      .not("status", "in", '("does_not_meet","archived")')
      .order("match_score", { ascending: false });
    // Filter out globally dismissed emails using in-memory set (synced from Supabase on load)
    const clean = (data || []).filter(a => !dismissedEmails.has((a.candidate_email || "").toLowerCase()));

    // Load archived separately
    await loadArchived(jobId);
    setApplicants(clean);
    setSelectedJob(jobs.find(j => j.id === jobId));
    // Mark notifications for this job as read in Supabase
    if (notifIds.size > 0) {
      supabase.from("notifications").update({ read: true }).eq("employer_id", user.id).eq("job_id", jobId).eq("read", false);
      setNotifications(prev => prev.map(n => n.job_id === jobId ? { ...n, read: true } : n));
      setUnreadCount(prev => Math.max(0, prev - notifIds.size));
    }
  };

  const notifySeekerStatusChange = async (app, status) => {
    const notifyStatuses = ["shortlisted", "interview_scheduled", "offer_made", "hired"];
    if (!notifyStatuses.includes(status)) return;
    if (!app.candidate_email) return;
    // Find seeker by email
    const { data: seeker } = await supabase
      .from("seeker_profiles")
      .select("id")
      .ilike("email", app.candidate_email)
      .maybeSingle();
    if (!seeker) return;
    // Upsert so re-triggering the same status doesn't create duplicates
    await supabase.from("seeker_notifications").upsert({
      seeker_id: seeker.id,
      job_id: app.job_id,
      job_title: selectedJob?.title || "",
      status,
      match_score: app.match_score,
      read_at: null,
      created_at: new Date().toISOString(),
    }, { onConflict: "seeker_id,job_id,status" });
  };

  const updateStatus = async (appId, status) => {
    const { error } = await supabase.from("applications").update({ status }).eq("id", appId);
    if (error) { toast("Failed to update status: " + error.message, "error"); return; }
    setApplicants(a => a.map(x => x.id === appId ? { ...x, status } : x));
    toast("Status updated", "success");
    const app = applicants.find(a => a.id === appId);
    if (app) notifySeekerStatusChange(app, status);
    if (status === "hired" && selectedJob) {
      const { error: delErr } = await supabase.from("applications").delete().eq("job_id", selectedJob.id).neq("id", appId);
      const { error: closeErr } = await supabase.from("jobs").update({ status: "closed" }).eq("id", selectedJob.id);
      if (delErr || closeErr) { toast("Candidate marked hired, but couldn't auto-close the job. Please close it manually.", "error"); return; }
      setJobs(j => j.map(x => x.id === selectedJob.id ? { ...x, status: "closed" } : x));
      setApplicants(a => a.filter(x => x.id === appId));
      toast("Position filled! Job marked as closed.", "success");
    }
  };

  const saveProfile = async (fields) => {
    const { error } = await supabase.from("employers").update(fields).eq("id", user.id);
    if (error) toast(error.message, "error");
    else { toast("Profile updated!", "success"); loadData(); }
  };

  const plan = employer?.plan || "free";
  const limit = plan === "free" ? 8 : plan === "starter" ? 15 : 999;
  const downloadLimit = plan === "free" ? 20 : plan === "starter" ? 100 : 999999;

  const navItems = [
    { id: "home",         icon: "zap",      label: "Dashboard" },
    { id: "jobs",         icon: "briefcase", label: "My Jobs" },
    { id: "notifications",icon: "bell",      label: "Notifications" },
    { id: "archive",      icon: "archive",   label: "Archive" },
    { id: "post",         icon: "plus",      label: "Post a Job" },
    { id: "account",      icon: "settings",  label: "Account" },
  ];

  // Onboarding checklist — stored per employer in localStorage
  const onboardingKey = user?.id ? "jobspot_onboarded_" + user.id : null;
  const [onboardingDone, setOnboardingDone] = useState(() => {
    if (!user?.id) return false;
    return localStorage.getItem("jobspot_onboarded_" + user.id) === "true";
  });
  const dismissOnboarding = () => {
    if (onboardingKey) localStorage.setItem(onboardingKey, "true");
    setOnboardingDone(true);
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", height: "100vh", background: "var(--bg)" }}>
      <style>{styles}</style>
      <style>{`
        .employer-mobile-header { display: none; }
        .employer-mobile-tabs { display: none; }
        @media (max-width: 768px) {
          .employer-mobile-header {
            display: flex !important;
            position: sticky; top: 0; z-index: 50;
            align-items: center; justify-content: space-between;
            padding: 0 16px; height: 56px;
            background: #1C1917;
            border-bottom: 1px solid rgba(255,255,255,0.08);
            flex-shrink: 0;
          }
          .employer-mobile-tabs {
            display: flex !important;
            overflow-x: auto;
            -webkit-overflow-scrolling: touch;
            scrollbar-width: none;
            background: #1C1917;
            border-bottom: 1px solid rgba(255,255,255,0.07);
            padding: 0 12px;
            gap: 0;
            flex-shrink: 0;
            position: sticky; top: 56px; z-index: 48;
          }
          .employer-mobile-tabs::-webkit-scrollbar { display: none; }
          .emp-tab {
            flex-shrink: 0;
            padding: 10px 14px;
            background: none; border: none;
            font-family: 'DM Sans', sans-serif;
            font-size: 12px; font-weight: 600;
            color: rgba(255,255,255,0.35);
            cursor: pointer;
            border-bottom: 2px solid transparent;
            transition: all 0.15s;
            white-space: nowrap;
          }
          .emp-tab.active { color: #D4A83C; border-bottom-color: #D4A83C; }
        }
      `}</style>

      {/* ── MOBILE TOP BAR ── */}
      <div className="employer-mobile-header">
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <div style={{ width: 28, height: 28, borderRadius: 7, overflow: "hidden" }}>
            <img src="/icon-512x512.png" alt="JobSpot GY" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
          </div>
          <span style={{ fontFamily: "Syne,sans-serif", fontSize: 15, fontWeight: 800, color: "#fff" }}>
            JobSpot<span style={{ fontSize: 10, color: "rgba(255,255,255,0.3)", fontWeight: 400, marginLeft: 3 }}>GY</span>
          </span>
          {employer && <span style={{ fontSize: 11, color: "rgba(255,255,255,0.35)", marginLeft: 2 }}>· {employer.company_name}</span>}
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          {unreadCount > 0 && (
            <button onClick={() => setTab("notifications")} style={{ position: "relative", background: "rgba(255,255,255,0.07)", border: "none", borderRadius: 8, width: 34, height: 34, display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer" }}>
              <Icon name="bell" size={15} color="rgba(255,255,255,0.7)" />
              <span style={{ position: "absolute", top: 4, right: 4, width: 8, height: 8, background: "var(--red)", borderRadius: "50%", border: "2px solid #1C1917" }} />
            </button>
          )}
          <button className="btn btn-gold btn-sm" style={{ fontSize: 12, padding: "6px 14px" }} onClick={() => setShowPostJob(true)}>+ Post Job</button>
        </div>
      </div>

      {/* ── MOBILE TAB STRIP ── */}
      <div className="employer-mobile-tabs">
        {navItems.filter(i => i.id !== "post").map(item => (
          <button key={item.id} className={`emp-tab ${tab === item.id ? "active" : ""}`}
            onClick={() => {
              setTab(item.id);
              if (item.id === "notifications") {
                supabase.from("notifications").update({ read: true }).eq("employer_id", user.id).eq("read", false);
                setUnreadCount(0);
                setNotifications(prev => prev.map(n => ({ ...n, read: true })));
              }
              if (item.id === "archive") { loadGlobalArchive(); }
              if (item.id !== "jobs") { setSelectedJob(null); setApplicants([]); setSelectedApplicant(null); }
            }}>
            {item.label}
            {item.id === "notifications" && unreadCount > 0 && (
              <span style={{ display: "inline-block", width: 7, height: 7, background: "var(--red)", borderRadius: "50%", marginLeft: 4, verticalAlign: "middle" }} />
            )}
          </button>
        ))}
        <button className="emp-tab" style={{ color: "rgba(255,255,255,0.25)" }} onClick={onLogout}>Sign out</button>
      </div>

      {/* ── SIDEBAR + MAIN CONTENT ── */}
      <div style={{ display: "flex", flex: 1, overflow: "hidden" }}>

      <aside className="sidebar">
        {/* Logo */}
        <div style={{ padding: "22px 20px 16px", borderBottom: "1px solid rgba(255,255,255,0.07)" }}>
          <div className="flex items-center gap-2">
            <div style={{ width: 30, height: 30, borderRadius: 8, overflow: "hidden", flexShrink: 0 }}>
              <img src="/icon-512x512.png" alt="JobSpot GY" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
            </div>
            <span className="font-display" style={{ fontSize: 16, fontWeight: 800, color: "#fff", letterSpacing: "-0.01em" }}>JobSpot</span>
            <span style={{ fontSize: 10, color: "rgba(255,255,255,0.3)", fontWeight: 500, marginLeft: 1 }}>GY</span>
          </div>
        </div>

        {/* Employer badge */}
        {employer && (
          <div style={{ margin: "12px 10px 4px", background: "rgba(255,255,255,0.06)", border: "1px solid rgba(255,255,255,0.09)", borderRadius: 10, padding: "11px 14px" }}>
            <div style={{ fontSize: 13, fontWeight: 600, color: "rgba(255,255,255,0.9)", marginBottom: 4, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{employer.company_name}</div>
            <div className="flex items-center gap-2">
              <PlanBadge plan={employer.plan} />
            </div>
          </div>
        )}

        {/* Nav */}
        <nav style={{ flex: 1, padding: "8px 0" }}>
          {navItems.map(item => (
            <div key={item.id} className={`nav-item ${tab === item.id ? "active" : ""}`}
              onClick={() => {
                if (item.id === "post") { setShowPostJob(true); return; }
                setTab(item.id);
                if (item.id === "notifications") {
                  // Mark all as read in Supabase
                  supabase.from("notifications").update({ read: true }).eq("employer_id", user.id).eq("read", false);
                  setUnreadCount(0);
                  setNotifications(prev => prev.map(n => ({ ...n, read: true })));
                }
                if (item.id === "archive") { loadGlobalArchive(); }
                if (item.id !== "jobs") { setSelectedJob(null); setApplicants([]); setSelectedApplicant(null); }
              }}>
              <Icon name={item.icon} size={15} color={tab === item.id ? "#D4A83C" : "rgba(255,255,255,0.45)"} />
              {item.label}
              {item.id === "notifications" && unreadCount > 0 && (
                <span className="notif-badge" style={{ marginLeft: "auto" }}>{unreadCount > 9 ? "9+" : unreadCount}</span>
              )}
            </div>
          ))}
        </nav>

        {/* Upgrade nudge */}
        <div style={{ padding: "12px 10px" }}>
          {plan === "free" && (
            <div style={{ background: "rgba(184,146,42,0.12)", border: "1px solid rgba(184,146,42,0.25)", borderRadius: 10, padding: "12px 14px", marginBottom: 8 }}>
              <div style={{ fontSize: 12, fontWeight: 700, color: "#D4A83C", marginBottom: 4 }}>Upgrade to Starter</div>
              <div style={{ fontSize: 11, color: "rgba(255,255,255,0.45)", marginBottom: 10, lineHeight: 1.5 }}>See all candidates, full smart reports & more</div>
              <button className="btn btn-gold w-full btn-sm" onClick={() => setShowUpgrade(true)}>Unlock Full Access</button>
            </div>
          )}
          <div className="nav-item" style={{ borderTop: "1px solid rgba(255,255,255,0.07)", paddingTop: 12, marginTop: 4 }} onClick={onLogout}>
            <Icon name="logout" size={15} color="rgba(255,255,255,0.35)" />
            <span style={{ color: "rgba(255,255,255,0.35)" }}>Sign out</span>
          </div>
        </div>
      </aside>

      <main className="employer-main" style={{ flex: 1, overflow: "auto", padding: "32px 40px" }}>

        {/* ── ANNOUNCEMENT BANNER ── */}
        {(() => {
          const msg = getSetting("announcement", "");
          const expires = getSetting("announcement_expires", "");
          // Check expiry
          if (!msg) return null;
          if (expires && new Date() > new Date(expires)) return null;
          // Check if employer permanently dismissed this exact message
          const dismissKey = "dismissed_ann_" + btoa(msg).slice(0, 20);
          if (typeof window !== "undefined" && localStorage.getItem(dismissKey)) return null;
          if (dismissedAnnouncement) return null;
          return (
            <div style={{ background: "linear-gradient(135deg, rgba(184,146,42,0.12), rgba(184,146,42,0.06))", border: "1px solid rgba(184,146,42,0.35)", borderRadius: 12, padding: "14px 20px", marginBottom: 24, display: "flex", alignItems: "flex-start", gap: 14, animation: "fadeUp 0.4s ease" }}>
              <div style={{ width: 32, height: 32, background: "rgba(184,146,42,0.15)", border: "1px solid rgba(184,146,42,0.3)", borderRadius: 8, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="var(--gold)" strokeWidth="2"><path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07A19.5 19.5 0 0 1 4.69 12 19.79 19.79 0 0 1 1.61 3.38 2 2 0 0 1 3.6 1h3a2 2 0 0 1 2 1.72c.127.96.361 1.903.7 2.81a2 2 0 0 1-.45 2.11L7.91 8.73a16 16 0 0 0 6.29 6.29l.91-.91a2 2 0 0 1 2.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0 1 22 16.92z"/></svg>
              </div>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 11, fontWeight: 700, color: "var(--gold)", textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: 4 }}>Message from JobSpot</div>
                <div style={{ fontSize: 14, color: "var(--text)", lineHeight: 1.6 }}>{msg}</div>
              </div>
              <button onClick={() => {
                // Permanently dismiss this message in localStorage
                const key = "dismissed_ann_" + btoa(msg).slice(0, 20);
                localStorage.setItem(key, "1");
                setDismissedAnnouncement(true);
              }} style={{ background: "none", border: "none", cursor: "pointer", color: "var(--text3)", fontSize: 18, lineHeight: 1, padding: 0, flexShrink: 0 }}>×</button>
            </div>
          );
        })()}

        {/* Jobs list */}
        {/* ============ DASHBOARD HOME ============ */}
        {tab === "home" && employer && (
          <div style={{ maxWidth: 860 }}>

            {/* Welcome header */}
            <div className="employer-welcome-row flex items-center justify-between mb-6">
              <div>
                <h1 className="font-display" style={{ fontSize: 26, fontWeight: 800, marginBottom: 4, letterSpacing: "-0.02em" }}>
                  Welcome back, <span className="gold-text">{employer.company_name}</span>
                </h1>
                <p style={{ fontSize: 14, color: "var(--text2)" }}>Here's what's happening with your hiring today.</p>
              </div>
              <button className="btn btn-gold" onClick={() => setShowPostJob(true)}>
                <Icon name="plus" size={15} /> Post a Job
              </button>
            </div>

            {/* Onboarding checklist — shows until dismissed */}
            {!onboardingDone && (
              <div style={{ background: "var(--bg2)", border: "1px solid rgba(184,146,42,0.3)", borderRadius: 16, padding: "24px 28px", marginBottom: 24, position: "relative" }}>
                <button onClick={dismissOnboarding} style={{ position: "absolute", top: 16, right: 16, background: "none", border: "none", cursor: "pointer", color: "var(--text3)", fontSize: 18, lineHeight: 1 }}>×</button>
                <div className="flex items-center gap-3 mb-4">
                  <div style={{ width: 36, height: 36, borderRadius: 10, overflow: "hidden", flexShrink: 0 }}>
                    <img src="/icon-512x512.png" alt="JobSpot GY" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                  </div>
                  <div>
                    <div className="font-display" style={{ fontSize: 16, fontWeight: 700 }}>Get started with JobSpot</div>
                    <div style={{ fontSize: 13, color: "var(--text2)" }}>Complete these steps to start finding great candidates</div>
                  </div>
                </div>
                <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                  {[
                    {
                      done: !!(employer.company_name && employer.contact_name),
                      label: "Complete your company profile",
                      sub: "Add your contact name and company details",
                      action: () => setTab("account"),
                      cta: "Go to Account →"
                    },
                    {
                      done: jobs.length > 0,
                      label: "Post your first job",
                      sub: "Describe the role and let AI do the matching",
                      action: () => setShowPostJob(true),
                      cta: "Post a Job →"
                    },
                    {
                      done: jobs.some(j => (j.application_count || 0) > 0),
                      label: "Receive your first applicant",
                      sub: "Share your listing and watch candidates roll in",
                      action: null,
                      cta: null
                    },
                  ].map((step, si) => (
                    <div key={si} style={{ display: "flex", alignItems: "center", gap: 14, padding: "12px 16px", background: step.done ? "rgba(45,158,78,0.05)" : "var(--bg3)", border: `1px solid ${step.done ? "rgba(45,158,78,0.2)" : "var(--border2)"}`, borderRadius: 10 }}>
                      <div style={{ width: 24, height: 24, borderRadius: "50%", background: step.done ? "var(--green)" : "var(--bg2)", border: `2px solid ${step.done ? "var(--green)" : "var(--border)"}`, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                        {step.done && <Icon name="check" size={12} color="#fff" />}
                      </div>
                      <div style={{ flex: 1 }}>
                        <div style={{ fontSize: 14, fontWeight: 600, color: step.done ? "var(--text2)" : "var(--text)", textDecoration: step.done ? "line-through" : "none" }}>{step.label}</div>
                        <div style={{ fontSize: 12, color: "var(--text3)" }}>{step.sub}</div>
                      </div>
                      {!step.done && step.action && (
                        <button className="btn btn-outline btn-sm" style={{ fontSize: 12, whiteSpace: "nowrap" }} onClick={step.action}>{step.cta}</button>
                      )}
                    </div>
                  ))}
                </div>
                {/* Progress bar */}
                {(() => {
                  const done = [!!(employer.company_name && employer.contact_name), jobs.length > 0, jobs.some(j => (j.application_count || 0) > 0)].filter(Boolean).length;
                  return (
                    <div style={{ marginTop: 16 }}>
                      <div style={{ display: "flex", justifyContent: "space-between", fontSize: 12, color: "var(--text3)", marginBottom: 6 }}>
                        <span>{done} of 3 complete</span>
                        {done === 3 && <span style={{ color: "var(--green)", fontWeight: 600, cursor: "pointer" }} onClick={dismissOnboarding}>All done — dismiss ✓</span>}
                      </div>
                      <div style={{ height: 4, background: "var(--border2)", borderRadius: 2, overflow: "hidden" }}>
                        <div style={{ height: "100%", borderRadius: 2, background: "var(--gold)", width: `${(done/3)*100}%`, transition: "width 0.5s ease" }} />
                      </div>
                    </div>
                  );
                })()}
              </div>
            )}

            {/* Stats row */}
            <div className="employer-stats-grid" style={{ display: "grid", gridTemplateColumns: "repeat(4, minmax(0,1fr))", gap: 12, marginBottom: 24 }}>
              {[
                {
                  label: "Active Jobs",
                  value: jobs.filter(j => j.status === "active").length,
                  sub: `${jobs.filter(j => j.status === "closed").length} filled`,
                  color: "var(--gold)",
                  icon: "briefcase"
                },
                {
                  label: "Total Applicants",
                  value: jobs.reduce((s, j) => s + (j.application_count || 0), 0),
                  sub: "across all listings",
                  color: "var(--blue)",
                  icon: "users"
                },
                {
                  label: "New This Week",
                  value: notifications.length,
                  sub: notifications.length === 0 ? "all caught up" : "unread alerts",
                  color: notifications.length > 0 ? "var(--red)" : "var(--green)",
                  icon: "bell"
                },
                {
                  label: "Top Match",
                  value: jobs.reduce((best, j) => Math.max(best, j.top_match_score || 0), 0) > 0
                    ? Math.round(jobs.reduce((best, j) => Math.max(best, j.top_match_score || 0), 0)) + "%"
                    : "—",
                  sub: "best candidate score",
                  color: "var(--green)",
                  icon: "zap"
                },
              ].map((stat, si) => (
                <div key={si} style={{ background: "var(--bg2)", border: "1px solid var(--border2)", borderRadius: 12, padding: "18px 20px" }}>
                  <div className="flex items-center justify-between mb-3">
                    <span style={{ fontSize: 11, fontWeight: 600, color: "var(--text3)", textTransform: "uppercase", letterSpacing: "0.07em" }}>{stat.label}</span>
                    <div style={{ width: 28, height: 28, background: "var(--bg3)", borderRadius: 7, display: "flex", alignItems: "center", justifyContent: "center" }}>
                      <Icon name={stat.icon} size={13} color={stat.color} />
                    </div>
                  </div>
                  <div className="font-display" style={{ fontSize: 28, fontWeight: 800, color: stat.color, lineHeight: 1, marginBottom: 4 }}>{stat.value}</div>
                  <div style={{ fontSize: 12, color: "var(--text3)" }}>{stat.sub}</div>
                </div>
              ))}
            </div>

            {/* Active jobs with applicant counts */}
            <div className="employer-home-split" style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16, marginBottom: 24 }}>
              {/* Jobs needing attention */}
              <div style={{ background: "var(--bg2)", border: "1px solid var(--border2)", borderRadius: 12, overflow: "hidden" }}>
                <div style={{ padding: "16px 20px", borderBottom: "1px solid var(--border2)", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                  <span className="font-display" style={{ fontSize: 14, fontWeight: 700 }}>Your Listings</span>
                  <button className="btn btn-ghost btn-sm" style={{ fontSize: 12 }} onClick={() => setTab("jobs")}>View all →</button>
                </div>
                {jobs.filter(j => j.status === "active").length === 0 ? (
                  <div style={{ padding: "32px 20px", textAlign: "center" }}>
                    <div style={{ fontSize: 13, color: "var(--text3)", marginBottom: 12 }}>No active jobs yet</div>
                    <button className="btn btn-gold btn-sm" onClick={() => setShowPostJob(true)}><Icon name="plus" size={13} />Post a Job</button>
                  </div>
                ) : (
                  <div>
                    {jobs.filter(j => j.status === "active").slice(0, 4).map(job => (
                      <div key={job.id} style={{ padding: "12px 20px", borderBottom: "1px solid var(--border2)", display: "flex", alignItems: "center", gap: 12, cursor: "pointer" }}
                        onClick={() => { loadApplicants(job.id); setTab("jobs"); }}>
                        <div style={{ flex: 1, overflow: "hidden" }}>
                          <div style={{ fontSize: 13, fontWeight: 600, color: "var(--text)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{job.title}</div>
                          <div style={{ fontSize: 11, color: "var(--text3)", marginTop: 2 }}>{job.location?.split("—")[0].trim()}</div>
                        </div>
                        <div style={{ textAlign: "right", flexShrink: 0 }}>
                          <div style={{ fontSize: 16, fontWeight: 700, color: (job.application_count || 0) > 0 ? "var(--text)" : "var(--text3)" }}>{job.application_count || 0}</div>
                          <div style={{ fontSize: 10, color: "var(--text3)", textTransform: "uppercase", letterSpacing: "0.05em" }}>applicants</div>
                        </div>
                        {job.top_match_score > 0 && (
                          <div className={`score-pill ${getScoreClass(job.top_match_score)}`} style={{ minWidth: 48, height: 28, fontSize: 12 }}>{Math.round(job.top_match_score)}%</div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Recent notifications / activity */}
              <div style={{ background: "var(--bg2)", border: "1px solid var(--border2)", borderRadius: 12, overflow: "hidden" }}>
                <div style={{ padding: "16px 20px", borderBottom: "1px solid var(--border2)", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                  <span className="font-display" style={{ fontSize: 14, fontWeight: 700 }}>Recent Activity</span>
                  {unreadCount > 0 && <span className="notif-badge">{unreadCount} new</span>}
                </div>
                {notifications.length === 0 ? (
                  <div style={{ padding: "32px 20px", textAlign: "center" }}>
                    <Icon name="bell" size={24} color="var(--border)" />
                    <div style={{ fontSize: 13, color: "var(--text3)", marginTop: 10 }}>No new activity yet</div>
                    <div style={{ fontSize: 12, color: "var(--text3)", marginTop: 4 }}>New applicants will appear here in real time</div>
                  </div>
                ) : (
                  <div>
                    {notifications.slice(0, 4).map((n, ni) => (
                      <div key={n.id || ni} style={{ padding: "11px 20px", borderBottom: "1px solid var(--border2)", display: "flex", alignItems: "center", gap: 10, cursor: "pointer" }}
                        onClick={() => { loadApplicants(n.job_id); setTab("jobs"); }}>
                        <div style={{ width: 8, height: 8, borderRadius: "50%", background: "var(--blue)", flexShrink: 0 }} />
                        <div style={{ flex: 1, overflow: "hidden" }}>
                          <div style={{ fontSize: 13, fontWeight: 500, color: "var(--text)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                            {n.candidate_name} → <span style={{ color: "var(--gold)" }}>{n.job_title}</span>
                          </div>
                          <div style={{ fontSize: 11, color: "var(--text3)" }}>{timeAgo(n.created_at)}</div>
                        </div>
                        <div className={`score-pill ${getScoreClass(n.match_score)}`} style={{ minWidth: 44, height: 24, fontSize: 11 }}>{Math.round(n.match_score || 0)}%</div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* Upgrade nudge — free plan only */}
            {plan === "free" && (
              <div style={{ background: "linear-gradient(135deg, rgba(184,146,42,0.08), rgba(184,146,42,0.03))", border: "1px solid rgba(184,146,42,0.25)", borderRadius: 16, padding: "24px 28px", display: "flex", alignItems: "center", gap: 20 }}>
                <div style={{ width: 44, height: 44, background: "var(--gold)", borderRadius: 12, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                  <Icon name="crown" size={20} color="#1A1714" />
                </div>
                <div style={{ flex: 1 }}>
                  <div className="font-display" style={{ fontSize: 16, fontWeight: 700, marginBottom: 4 }}>Unlock the full platform</div>
                  <div style={{ fontSize: 13, color: "var(--text2)" }}>Pipeline view, candidate search, interview calendar, unlimited reports — from GYD 12,000/year.</div>
                </div>
                <button className="btn btn-gold btn-lg" style={{ whiteSpace: "nowrap", flexShrink: 0 }} onClick={() => setShowUpgrade(true)}>
                  Upgrade Now
                </button>
              </div>
            )}

          </div>
        )}

        {tab === "jobs" && !selectedJob && (
          <>
            <div className="flex items-center justify-between mb-6">
              <div>
                <h1 className="font-display" style={{ fontSize: 24, fontWeight: 700, marginBottom: 4 }}>My Jobs</h1>
                <p style={{ color: "var(--text2)", fontSize: 14 }}>{jobs.length} listings · {jobs.filter(j => j.status === "active").length} active</p>
              </div>
              <button className="btn btn-gold" onClick={() => setShowPostJob(true)}><Icon name="plus" size={16} />Post a Job</button>
            </div>

            {/* 5-day expiry warnings */}
            {jobs.filter(j => {
              if (!j.expires_at || j.status !== "active") return false;
              const daysLeft = Math.ceil((new Date(j.expires_at) - Date.now()) / 86400000);
              return daysLeft <= 5 && daysLeft > 0 && !j.extended;
            }).map(j => {
              const daysLeft = Math.ceil((new Date(j.expires_at) - Date.now()) / 86400000);
              return (
                <div key={`warn-${j.id}`} className="flex items-center justify-between mb-3" style={{ background: "rgba(210,153,34,0.08)", border: "1px solid rgba(210,153,34,0.35)", borderRadius: 10, padding: "12px 16px" }}>
                  <div className="flex items-center gap-3">
                    <Icon name="alert" size={16} color="var(--amber)" />
                    <span style={{ fontSize: 13, color: "var(--amber)" }}>
                      <strong>"{j.title}"</strong> will be deleted in {daysLeft} day{daysLeft !== 1 ? "s" : ""}. Extend or let it close?
                    </span>
                  </div>
                  <div className="flex gap-2">
                    <button className="btn btn-outline btn-sm" onClick={async () => {
                      const newExpiry = new Date(j.expires_at);
                      newExpiry.setDate(newExpiry.getDate() + 14);
                      await supabase.from("jobs").update({ expires_at: newExpiry.toISOString(), extended: true }).eq("id", j.id);
                      toast(`"${j.title}" extended by 2 weeks.`, "success");
                      loadData();
                    }}>+2 Weeks</button>
                    <button className="btn btn-outline btn-sm" onClick={async () => {
                      const newExpiry = new Date(j.expires_at);
                      newExpiry.setDate(newExpiry.getDate() + 28);
                      await supabase.from("jobs").update({ expires_at: newExpiry.toISOString(), extended: true }).eq("id", j.id);
                      toast(`"${j.title}" extended by 4 weeks.`, "success");
                      loadData();
                    }}>+4 Weeks</button>
                    <button className="btn btn-ghost btn-sm" style={{ color: "var(--text3)" }} onClick={async () => {
                      await supabase.from("jobs").update({ extended: true }).eq("id", j.id);
                      loadData();
                    }}>Let it expire</button>
                  </div>
                </div>
              );
            })}

            {loading ? (
              <div className="flex-col gap-3">{[1,2,3].map(i => <div key={i} className="skeleton" style={{ height: 90, borderRadius: 12 }} />)}</div>
            ) : jobs.length === 0 ? (
              <EmptyState icon="briefcase" title="No jobs yet" subtitle="Post your first job and start receiving smart-ranked candidates."
                action={<button className="btn btn-gold" onClick={() => setShowPostJob(true)}><Icon name="plus" size={16} />Post Your First Job</button>} />
            ) : (
              <>
                {jobs.filter(j => {
                  if (!j.expires_at || j.extended) return false;
                  const daysLeft = Math.ceil((new Date(j.expires_at) - Date.now()) / 86400000);
                  return daysLeft <= 5 && daysLeft > 0;
                }).map(j => {
                  const daysLeft = Math.ceil((new Date(j.expires_at) - Date.now()) / 86400000);
                  return (
                    <div key={`warn-${j.id}`} className="flex items-center justify-between" style={{ background: "rgba(210,153,34,0.08)", border: "1px solid rgba(210,153,34,0.35)", borderRadius: 10, padding: "12px 16px", marginBottom: 4 }}>
                      <div className="flex items-center gap-2">
                        <Icon name="alert" size={15} color="var(--amber)" />
                        <span style={{ fontSize: 13, color: "var(--amber)", fontWeight: 500 }}>
                          <strong>"{j.title}"</strong> will be deleted in {daysLeft} day{daysLeft !== 1 ? "s" : ""}. Extend or let it close?
                        </span>
                      </div>
                      <div className="flex gap-2">
                        <button className="btn btn-outline btn-sm" onClick={async () => {
                          const newExpiry = new Date(j.expires_at);
                          newExpiry.setDate(newExpiry.getDate() + 14);
                          await supabase.from("jobs").update({ expires_at: newExpiry.toISOString(), extended: true }).eq("id", j.id);
                          toast(`"${j.title}" extended by 2 weeks.`, "success");
                          loadData();
                        }}>+2 Weeks</button>
                        <button className="btn btn-outline btn-sm" onClick={async () => {
                          const newExpiry = new Date(j.expires_at);
                          newExpiry.setDate(newExpiry.getDate() + 28);
                          await supabase.from("jobs").update({ expires_at: newExpiry.toISOString(), extended: true }).eq("id", j.id);
                          toast(`"${j.title}" extended by 4 weeks.`, "success");
                          loadData();
                        }}>+4 Weeks</button>
                        <button className="btn btn-ghost btn-sm" style={{ color: "var(--text3)" }} onClick={async () => {
                          await supabase.from("jobs").update({ extended: true }).eq("id", j.id);
                          loadData();
                        }}>Let it expire</button>
                      </div>
                    </div>
                  );
                })}

                {selectedJobIds.length > 0 && (
                  <div className="flex items-center gap-3 mb-3" style={{ background: "var(--bg2)", border: "1px solid var(--border)", borderRadius: 10, padding: "10px 16px" }}>
                    <span style={{ fontSize: 13, color: "var(--text2)" }}>{selectedJobIds.length} selected</span>
                    <button className="btn btn-danger btn-sm" onClick={bulkDeleteJobs}>Delete Selected</button>
                    <button className="btn btn-ghost btn-sm" onClick={() => setSelectedJobIds([])}>Clear</button>
                  </div>
                )}
                <div className="flex-col gap-3">
                  {jobs.map(job => (
                    <div key={job.id} className="card" style={{ border: selectedJobIds.includes(job.id) ? "1px solid var(--gold-dim)" : undefined }}>
                      <div className="job-card-row flex items-center justify-between">
                        <div className="flex items-center gap-3" style={{ flex: 1 }}>
                          <input type="checkbox" checked={selectedJobIds.includes(job.id)} onChange={() => toggleSelectJob(job.id)}
                            style={{ width: 15, height: 15, accentColor: "var(--gold)", flexShrink: 0, cursor: "pointer" }} onClick={e => e.stopPropagation()} />
                          <div style={{ flex: 1, cursor: "pointer" }} onClick={() => loadApplicants(job.id)}>
                            <div className="flex items-center gap-3 mb-2">
                              <span className="font-display" style={{ fontSize: 16, fontWeight: 600 }}>{job.title}</span>
                              <span className={`badge ${job.status === "active" ? "badge-green" : job.status === "expired" ? "badge-red" : job.status === "closed" ? "badge-blue" : job.status === "paused" ? "badge-amber" : "badge-gray"}`}>
                                {job.status === "closed" ? "✓ Filled" : job.status === "paused" ? "⏸ Paused" : job.status}
                              </span>
                              <span className="badge badge-gray">{job.work_type}</span>
                              {notifications.some(n => n.job_id === job.id) && (
                                <span style={{ display: "inline-flex", alignItems: "center", gap: 4, fontSize: 11, fontWeight: 700, background: "var(--blue-bg)", color: "var(--blue)", padding: "2px 7px", borderRadius: 4 }}>
                                  <span style={{ width: 6, height: 6, background: "var(--blue)", borderRadius: "50%", display: "inline-block" }} />
                                  {notifications.filter(n => n.job_id === job.id).length} new
                                </span>
                              )}
                            </div>
                            <div className="job-card-meta flex items-center gap-4">
                              <span style={{ fontSize: 13, color: "var(--text3)" }}><Icon name="map_pin" size={12} /> {job.location}</span>
                              {job.salary_min && <span style={{ fontSize: 13, color: "var(--text3)" }}>{formatGYD(job.salary_min)} – {formatGYD(job.salary_max)}</span>}
                              <span style={{ fontSize: 13, color: "var(--text3)" }}><Icon name="clock" size={12} /> {timeAgo(job.created_at)}</span>
                            </div>
                          </div>
                        </div>
                        <div className="job-card-actions flex items-center gap-4" style={{ flexShrink: 0 }}>
                          <div style={{ textAlign: "center", cursor: "pointer" }} onClick={() => loadApplicants(job.id)}>
                            <div className="font-display" style={{ fontSize: 22, fontWeight: 700, color: "var(--text)" }}>{job.application_count || 0}</div>
                            <div style={{ fontSize: 11, color: "var(--text3)", textTransform: "uppercase", letterSpacing: "0.05em" }}>Applicants</div>
                          </div>
                          {job.top_match_score > 0 && (
                            <div style={{ textAlign: "center", cursor: "pointer" }} onClick={() => loadApplicants(job.id)}>
                              <div className="font-display" style={{ fontSize: 22, fontWeight: 700, color: getScoreColor(job.top_match_score) }}>{Math.round(job.top_match_score)}%</div>
                              <div style={{ fontSize: 11, color: "var(--text3)", textTransform: "uppercase", letterSpacing: "0.05em" }}>Top match</div>
                            </div>
                          )}
                          <div className="flex gap-2">
                            {/* Feature on homepage — Pro only */}
                            {(() => {
                              const isActive = job.featured && job.featured_until && new Date(job.featured_until) > new Date();
                              const daysLeft = isActive ? Math.ceil((new Date(job.featured_until) - new Date()) / (1000 * 60 * 60 * 24)) : 0;
                              return plan === "professional" ? (
                                <button
                                  onClick={() => toggleFeatured(job)}
                                  title={isActive ? `Featured on homepage — ${daysLeft}d left. Click to remove.` : "Feature this job on the JobSpot homepage for 7 days"}
                                  style={{ display: "inline-flex", alignItems: "center", gap: 5, padding: "5px 12px", borderRadius: 7, fontSize: 12, fontWeight: 600, cursor: "pointer", fontFamily: "DM Sans, sans-serif", transition: "all 0.2s", border: isActive ? "1px solid rgba(184,146,42,0.5)" : "1px solid var(--border2)", background: isActive ? "rgba(184,146,42,0.12)" : "transparent", color: isActive ? "var(--gold)" : "var(--text3)" }}
                                >
                                  <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/></svg>
                                  {isActive ? `Featured · ${daysLeft}d` : "Feature"}
                                </button>
                              ) : (
                                <button
                                  onClick={() => setShowUpgrade(true)}
                                  title="Upgrade to Professional to feature jobs on the homepage"
                                  style={{ display: "inline-flex", alignItems: "center", gap: 5, padding: "5px 12px", borderRadius: 7, fontSize: 12, fontWeight: 500, cursor: "pointer", fontFamily: "DM Sans, sans-serif", border: "1px solid var(--border2)", background: "transparent", color: "var(--text3)", opacity: 0.6 }}
                                >
                                  <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/></svg>
                                  Feature
                                  <span style={{ fontSize: 9, fontWeight: 700, background: "var(--gold)", color: "#FAF8F4", padding: "1px 5px", borderRadius: 3 }}>PRO</span>
                                </button>
                              );
                            })()}
                            <button className="btn btn-outline btn-sm" onClick={() => {
                              const url = window.location.origin + window.location.pathname + "?job=" + job.id;
                              navigator.clipboard.writeText(url).then(() => toast("Share link copied! Send it on WhatsApp or Facebook.", "success")).catch(() => toast(url, "info"));
                            }} title="Copy shareable link">
                              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="18" cy="5" r="3"/><circle cx="6" cy="12" r="3"/><circle cx="18" cy="19" r="3"/><line x1="8.59" y1="13.51" x2="15.42" y2="17.49"/><line x1="15.41" y1="6.51" x2="8.59" y2="10.49"/></svg>
                              <span className="job-btn-label">Share</span>
                            </button>
                            <button className="btn btn-outline btn-sm" onClick={() => setEditingJob(job)}><Icon name="edit" size={13} /><span className="job-btn-label">Edit</span></button>
                            <button className="btn btn-danger btn-sm" onClick={() => deleteJob(job.id)}><Icon name="trash" size={13} /></button>
                          </div>
                          <Icon name="arrow_right" size={18} color="var(--text3)" style={{ cursor: "pointer" }} onClick={() => loadApplicants(job.id)} />
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </>
            )}
          </>
        )}

        {/* Applicants view */}
        {tab === "jobs" && selectedJob && (
          <>
            {/* Row 1 — back + title */}
            <div className="applicant-header-row1 flex items-center gap-3 mb-3">
              <button className="btn btn-ghost btn-sm" onClick={() => { setSelectedJob(null); setApplicants([]); setSelectedApplicant(null); setNewAppIds(new Set()); setCandidateSearch(""); setPipelineView(false); }}>← Jobs</button>
              <div style={{ width: 1, height: 20, background: "var(--border2)", flexShrink: 0 }} />
              <div style={{ flex: 1, minWidth: 0 }}>
                <h1 className="font-display" style={{ fontSize: 20, fontWeight: 700, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{selectedJob.title}</h1>
                <span style={{ fontSize: 13, color: "var(--text2)" }}>{applicants.length} applicants · Smart-ranked</span>
              </div>
            </div>

            {/* Row 2 — toolbar actions */}
            <div className="applicant-toolbar mb-4" style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>

                {/* Stop/Resume accepting */}
                <button
                  onClick={toggleAccepting}
                  title={selectedJob.status === "paused" ? "Reopen job to new applications" : "Stop accepting new applications"}
                  style={{ display: "inline-flex", alignItems: "center", gap: 6, padding: "7px 14px", borderRadius: 8, fontSize: 12, fontWeight: 600, cursor: "pointer", fontFamily: "DM Sans, sans-serif", transition: "all 0.2s", border: selectedJob.status === "paused" ? "1px solid rgba(45,158,78,0.4)" : "1px solid var(--border2)", background: selectedJob.status === "paused" ? "rgba(45,158,78,0.08)" : "var(--bg2)", color: selectedJob.status === "paused" ? "var(--green)" : "var(--text2)" }}
                >
                  {selectedJob.status === "paused"
                    ? <><svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><circle cx="12" cy="12" r="10"/><polyline points="10 8 16 12 10 16 10 8"/></svg> Reopen</>
                    : <><svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="6" y="4" width="4" height="16"/><rect x="14" y="4" width="4" height="16"/></svg> Pause</>
                  }
                </button>

                {/* Remove duplicates */}
                <button
                  onClick={removeDuplicates}
                  title="Scan for duplicate candidates and keep the highest-scoring version"
                  style={{ display: "inline-flex", alignItems: "center", gap: 6, padding: "7px 12px", borderRadius: 8, fontSize: 11, fontWeight: 600, cursor: "pointer", fontFamily: "DM Sans, sans-serif", transition: "all 0.2s", border: "1px solid var(--border2)", background: "var(--bg2)", color: "var(--text2)" }}
                >
                  <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/><path d="M10 11v6"/><path d="M14 11v6"/><path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2"/></svg>
                  Duplicates
                </button>

                {/* View toggle — Pipeline is Starter/Pro only */}
                {plan !== "free" ? (
                  <div style={{ display: "flex", background: "#1C1917", border: "1px solid rgba(255,255,255,0.08)", borderRadius: 9, padding: 3, gap: 2 }}>
                    <button onClick={() => setPipelineView(false)} style={{ display: "inline-flex", alignItems: "center", gap: 5, padding: "5px 12px", borderRadius: 7, fontSize: 12, fontWeight: 600, border: "none", cursor: "pointer", background: !pipelineView ? "rgba(184,146,42,0.15)" : "transparent", color: !pipelineView ? "#D4A83C" : "rgba(255,255,255,0.4)", transition: "all 0.15s", fontFamily: "DM Sans, sans-serif" }}>
                      <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="8" y1="6" x2="21" y2="6"/><line x1="8" y1="12" x2="21" y2="12"/><line x1="8" y1="18" x2="21" y2="18"/><line x1="3" y1="6" x2="3.01" y2="6"/><line x1="3" y1="12" x2="3.01" y2="12"/><line x1="3" y1="18" x2="3.01" y2="18"/></svg>
                      List
                    </button>
                    <button onClick={() => setPipelineView(true)} style={{ display: "inline-flex", alignItems: "center", gap: 5, padding: "5px 12px", borderRadius: 7, fontSize: 12, fontWeight: 600, border: "none", cursor: "pointer", background: pipelineView ? "rgba(184,146,42,0.15)" : "transparent", color: pipelineView ? "#D4A83C" : "rgba(255,255,255,0.4)", transition: "all 0.15s", fontFamily: "DM Sans, sans-serif" }}>
                      <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="3" width="7" height="7"/><rect x="14" y="3" width="7" height="7"/><rect x="3" y="14" width="7" height="7"/><rect x="14" y="14" width="7" height="7"/></svg>
                      Pipeline
                    </button>
                  </div>
                ) : (
                  <div style={{ display: "flex", alignItems: "center", gap: 5, padding: "7px 12px", background: "var(--bg3)", border: "1px solid var(--border2)", borderRadius: 8, fontSize: 12, color: "var(--text3)", cursor: "not-allowed" }}>
                    Pipeline <span style={{ fontSize: 9, fontWeight: 700, background: "var(--gold)", color: "#FAF8F4", padding: "1px 5px", borderRadius: 3, marginLeft: 4 }}>PRO</span>
                  </div>
                )}

                {/* Search bar */}
                {plan !== "free" ? (
                  <div style={{ position: "relative", flex: 1, minWidth: 140 }}>
                    <Icon name="search" size={14} color="var(--text3)" style={{ position: "absolute", left: 10, top: "50%", transform: "translateY(-50%)", pointerEvents: "none" }} />
                    <input
                      className="input"
                      placeholder="Search candidates..."
                      value={candidateSearch}
                      onChange={e => setCandidateSearch(e.target.value)}
                      style={{ paddingLeft: 32, width: "100%", fontSize: 13, height: 36 }}
                    />
                  </div>
                ) : (
                  <button className="btn btn-gold btn-sm" onClick={() => setShowUpgrade(true)}>
                    <Icon name="crown" size={14} /> Upgrade
                  </button>
                )}
            </div>

            {/* ====== PIPELINE VIEW ====== */}
            {pipelineView && plan !== "free" && (
              <div style={{ overflowX: "auto", paddingBottom: 16, WebkitOverflowScrolling: "touch" }}>
                {/* Mobile hint */}
                <div className="pipeline-mobile-hint" style={{ display: "none", marginBottom: 10, padding: "8px 12px", background: "rgba(184,146,42,0.08)", border: "1px solid rgba(184,146,42,0.2)", borderRadius: 8, fontSize: 12, color: "var(--gold)" }}>
                  💡 Tap the <strong>stage buttons</strong> on each card (→ New, → Interview etc.) to move candidates between stages
                </div>
                <div className="pipeline-cols-wrap" style={{ display: "flex", gap: 12, minWidth: 900 }}>
                  {PIPELINE_COLS.map(col => {
                    const colApps = applicants.filter(a => (a.status || "new") === col.id);
                    return (
                      <div
                        key={col.id}
                        style={{ flex: 1, minWidth: 180, background: "var(--bg3)", border: "1px solid var(--border2)", borderRadius: 12, overflow: "hidden" }}
                        onDragOver={e => e.preventDefault()}
                        onDrop={e => {
                          e.preventDefault();
                          const appId = e.dataTransfer.getData("appId");
                          const app = applicants.find(a => a.id === appId);
                          if (app) movePipelineCard(app, col.id);
                          setDraggedId(null);
                        }}
                      >
                        {/* Column header */}
                        <div style={{ padding: "12px 14px 10px", borderBottom: "1px solid var(--border2)", display: "flex", alignItems: "center", justifyContent: "space-between", background: col.bg }}>
                          <span style={{ fontSize: 12, fontWeight: 700, color: col.color, textTransform: "uppercase", letterSpacing: "0.06em" }}>{col.label}</span>
                          <span style={{ fontSize: 11, fontWeight: 700, background: col.color, color: "#fff", borderRadius: 10, padding: "1px 7px" }}>{colApps.length}</span>
                        </div>
                        {/* Cards */}
                        <div style={{ padding: "10px 8px", display: "flex", flexDirection: "column", gap: 8, minHeight: 120 }}>
                          {colApps.map(app => {
                            const parsed = app.ai_parsed_data || {};
                            const hasInterview = app.interview_date;
                            return (
                              <div
                                key={app.id}
                                draggable
                                onDragStart={e => { e.dataTransfer.setData("appId", app.id); setDraggedId(app.id); }}
                                onDragEnd={() => setDraggedId(null)}
                                style={{
                                  background: "var(--bg2)", border: draggedId === app.id ? "1px solid var(--gold-dim)" : "1px solid var(--border2)",
                                  borderRadius: 8, padding: "10px 12px", cursor: "grab",
                                  opacity: draggedId === app.id ? 0.5 : 1,
                                  transition: "all 0.15s", boxShadow: "0 1px 4px rgba(0,0,0,0.05)"
                                }}
                              >
                                <div style={{ fontSize: 13, fontWeight: 600, color: "var(--text)", marginBottom: 4, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                                  {app.candidate_name || "Anonymous"}
                                </div>
                                <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: hasInterview ? 8 : 0 }}>
                                  {parsed.seniority_level && <span className="badge badge-gold" style={{ fontSize: 9 }}>{parsed.seniority_level}</span>}
                                  <span className={`score-pill ${getScoreClass(app.match_score)}`} style={{ minWidth: 40, height: 22, fontSize: 11, padding: "0 8px" }}>{Math.round(app.match_score)}%</span>
                                </div>
                                {hasInterview && (
                                  <div style={{ display: "flex", alignItems: "center", gap: 5, padding: "5px 8px", background: "#F3F0FF", border: "1px solid rgba(124,58,237,0.2)", borderRadius: 6, marginTop: 6 }}>
                                    <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="#7C3AED" strokeWidth="2"><rect x="3" y="4" width="18" height="18" rx="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></svg>
                                    <span style={{ fontSize: 11, color: "#7C3AED", fontWeight: 500 }}>
                                      {new Date(app.interview_date).toLocaleDateString("en-GY", { month: "short", day: "numeric" })} · {new Date(app.interview_date).toLocaleTimeString("en-GY", { hour: "2-digit", minute: "2-digit" })}
                                    </span>
                                  </div>
                                )}
                                {/* Quick move buttons — tap-friendly on mobile */}
                                <div className="pipeline-move-btns" style={{ display: "flex", gap: 4, marginTop: 8, flexWrap: "wrap" }}>
                                  {PIPELINE_COLS.filter(c => c.id !== col.id).map(c => (
                                    <button key={c.id} onClick={() => movePipelineCard(app, c.id)}
                                      style={{ fontSize: 11, padding: "4px 8px", borderRadius: 6, border: `1px solid ${c.color}22`, background: `${c.color}11`, color: c.color, cursor: "pointer", whiteSpace: "nowrap", fontFamily: "DM Sans, sans-serif", fontWeight: 600 }}>
                                      → {c.label}
                                    </button>
                                  ))}
                                </div>
                                {/* Download CV button */}
                                <div style={{ marginTop: 8 }}>
                                  {app.cv_url || app.cv_path ? (
                                    <button
                                      onClick={() => downloadCV(app)}
                                      style={{ display: "inline-flex", alignItems: "center", gap: 5, padding: "5px 10px", borderRadius: 6, fontSize: 11, fontWeight: 600, cursor: "pointer", fontFamily: "DM Sans, sans-serif", border: "1px solid rgba(184,146,42,0.35)", background: "rgba(184,146,42,0.08)", color: "var(--gold)", width: "100%" }}>
                                      <Icon name="download" size={11} color="var(--gold)" />
                                      Download CV {plan === "free" ? `(${downloadLimit - (employer?.cv_downloads_used || 0)} left)` : ""}
                                    </button>
                                  ) : (
                                    <span style={{ fontSize: 11, color: "var(--text3)", fontStyle: "italic" }}>No CV on file</span>
                                  )}
                                </div>
                              </div>
                            );
                          })}
                          {colApps.length === 0 && (
                            <div style={{ textAlign: "center", padding: "20px 8px", color: "var(--text3)", fontSize: 12, border: "1px dashed var(--border2)", borderRadius: 8 }}>
                              Drop here
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* ====== LEADERBOARD VIEW (default) ====== */}
            {!pipelineView && (
              <div>

            {/* New applicants banner */}
            {newAppIds.size > 0 && (
              <div style={{ background: "var(--blue-bg)", border: "1px solid rgba(26,111,212,0.25)", borderRadius: 10, padding: "12px 18px", marginBottom: 20, display: "flex", alignItems: "center", gap: 12 }}>
                <div style={{ width: 32, height: 32, background: "var(--blue)", borderRadius: 8, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                  <Icon name="bell" size={15} color="#fff" />
                </div>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 13, fontWeight: 600, color: "var(--blue)" }}>{newAppIds.size} new applicant{newAppIds.size > 1 ? "s" : ""} since your last visit</div>
                  <div style={{ fontSize: 12, color: "var(--blue)", opacity: 0.75, marginTop: 1 }}>New entries are highlighted in blue below</div>
                </div>
                <button className="btn btn-sm" style={{ background: "#fff", border: "1px solid rgba(26,111,212,0.25)", color: "var(--blue)", fontSize: 12 }} onClick={() => setNewAppIds(new Set())}>Dismiss</button>
              </div>
            )}

            {/* -- "New only" filter bar -- */}
            {applicants.length > 0 && (
              <div className="flex items-center justify-between mb-3" style={{ background: "var(--bg2)", border: "1px solid var(--border2)", borderRadius: 10, padding: "10px 16px" }}>
                <div className="flex items-center gap-2">
                  <span style={{ fontSize: 13, color: "var(--text2)" }}>
                    Showing{" "}
                    <strong style={{ color: "var(--text)" }}>
                      {showNewOnly
                        ? applicants.filter(a => a.status === "new").length + " unactioned"
                        : applicants.length + " total"}
                    </strong>{" "}
                    candidate{applicants.length !== 1 ? "s" : ""}
                  </span>
                  {showNewOnly && applicants.filter(a => a.status !== "new").length > 0 && (
                    <span style={{ fontSize: 12, color: "var(--text3)" }}>
                      · {applicants.filter(a => a.status !== "new").length} actioned (hidden)
                    </span>
                  )}
                </div>
                <button
                  onClick={() => setShowNewOnly(v => !v)}
                  style={{
                    display: "inline-flex", alignItems: "center", gap: 6,
                    padding: "5px 12px", borderRadius: 6, fontSize: 12, fontWeight: 600,
                    border: "1px solid " + (showNewOnly ? "var(--gold-dim)" : "var(--border2)"),
                    background: showNewOnly ? "rgba(184,146,42,0.08)" : "transparent",
                    color: showNewOnly ? "var(--gold)" : "var(--text3)",
                    cursor: "pointer", transition: "all 0.15s",
                  }}
                >
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><circle cx="11" cy="11" r="8"/><polyline points="21 21 16.65 16.65"/></svg>
                  {showNewOnly ? "New only ✓" : "New only"}
                </button>
              </div>
            )}

            {applicants.length === 0 ? (
              <EmptyState icon="users" title="No applicants yet" subtitle="Candidates who upload their CV will appear here, ranked by match score." />
            ) : (
              <div className="flex-col gap-3">
                {(() => {
                  const q = candidateSearch.toLowerCase().trim();
                  const base = showNewOnly ? applicants.filter(a => a.status === "new") : applicants;
                  const filtered = q
                    ? base.filter(app => {
                        const parsed = app.ai_parsed_data || {};
                        return (
                          app.candidate_name?.toLowerCase().includes(q) ||
                          (app.matched_skills || []).some(s => s.toLowerCase().includes(q)) ||
                          (app.missing_skills || []).some(s => s.toLowerCase().includes(q)) ||
                          (parsed.industries || []).some(s => s.toLowerCase().includes(q)) ||
                          parsed.seniority_level?.toLowerCase().includes(q)
                        );
                      })
                    : base;
                  return filtered.map((app, i) => {
                  const isLocked = i >= limit;
                  const parsed = app.ai_parsed_data || {};
                  const flags = app.ai_flags || { green: [], red: [] };
                  const rankColors = [
                    { bg: "rgba(184,146,42,0.12)", border: "rgba(184,146,42,0.35)", color: "var(--gold)" },
                    { bg: "rgba(158,152,144,0.12)", border: "rgba(158,152,144,0.35)", color: "var(--text2)" },
                    { bg: "rgba(176,101,60,0.12)", border: "rgba(176,101,60,0.35)", color: "#B0653C" },
                  ];
                  const rankStyle = rankColors[i] || { bg: "var(--bg3)", border: "var(--border2)", color: "var(--text3)" };
                  const salaryFitColor = (v) => v === "within_range" ? "var(--green)" : v === "above_range" || v === "below_range" ? "var(--amber)" : "var(--text3)";
                  const levelFitColor = (v) => v === "aligned" ? "var(--green)" : v ? "var(--amber)" : "var(--text3)";
                  const isNew = newAppIds?.has(app.id);
                  // Dark card color scheme
                  const darkRankColors = [
                    { bg: "rgba(184,146,42,0.2)", border: "rgba(184,146,42,0.5)", color: "#D4A83C" },
                    { bg: "rgba(180,180,180,0.12)", border: "rgba(180,180,180,0.3)", color: "#A0A0A0" },
                    { bg: "rgba(176,101,60,0.2)", border: "rgba(176,101,60,0.45)", color: "#C47A45" },
                  ];
                  const dRank = darkRankColors[i] || { bg: "rgba(255,255,255,0.06)", border: "rgba(255,255,255,0.1)", color: "rgba(255,255,255,0.4)" };
                  const dBorder = isNew ? "1px solid rgba(26,111,212,0.5)" : i === 0 ? "1px solid rgba(184,146,42,0.3)" : "1px solid rgba(255,255,255,0.07)";
                  const dSalary = (v) => v === "within_range" ? "#4ADE80" : v === "above_range" || v === "below_range" ? "#FBBF24" : "rgba(255,255,255,0.35)";
                  const dLevel = (v) => v === "aligned" ? "#4ADE80" : v ? "#FBBF24" : "rgba(255,255,255,0.35)";
                  return (
                    <div key={app.id} style={{ background: i === 0 ? "linear-gradient(135deg, #1C1917 0%, #211e18 100%)" : "#1A1714", border: dBorder, borderRadius: 14, overflow: "hidden", marginBottom: 12, transition: "all 0.2s ease", boxShadow: i === 0 ? "0 4px 24px rgba(184,146,42,0.08)" : "0 2px 12px rgba(0,0,0,0.2)", opacity: isLocked ? 0.6 : 1 }}>
                      {isLocked ? (
                        <div style={{ padding: "20px 24px", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                          <div className="flex items-center gap-3">
                            <div style={{ width: 36, height: 36, background: "rgba(255,255,255,0.05)", borderRadius: 8, display: "flex", alignItems: "center", justifyContent: "center" }}>
                              <Icon name="lock" size={16} color="rgba(255,255,255,0.3)" />
                            </div>
                            <div>
                              <div style={{ fontSize: 14, color: "rgba(255,255,255,0.4)", fontWeight: 500 }}>Candidate #{i + 1} — Locked</div>
                              <div style={{ fontSize: 12, color: "rgba(255,255,255,0.25)" }}>Upgrade your plan to see this candidate</div>
                            </div>
                          </div>
                          <div className="flex items-center gap-3">
                            <div className={`score-pill ${getScoreClass(app.match_score)}`} style={{ filter: "blur(4px)" }}>{app.match_score}%</div>
                            <button className="btn btn-gold btn-sm" onClick={() => setShowUpgrade(true)}>Unlock</button>
                          </div>
                        </div>
                      ) : (
                        <>
                          <div style={{ padding: "18px 20px 0" }}>
                            {/* Header row */}
                            <div className="flex items-center gap-3 mb-3">
                              <div style={{ width: 36, height: 36, background: dRank.bg, border: `1px solid ${dRank.border}`, borderRadius: 8, display: "flex", alignItems: "center", justifyContent: "center", fontFamily: "Syne", fontSize: 12, fontWeight: 700, color: dRank.color, flexShrink: 0 }}>#{i + 1}</div>
                              <div style={{ flex: 1 }}>
                                <div className="flex items-center gap-2" style={{ flexWrap: "wrap" }}>
                                  <span className="font-display" style={{ fontSize: 15, fontWeight: 700, color: "#FAF8F4" }}>{app.candidate_name || "Anonymous"}</span>
                                  {isNew && <span style={{ fontSize: 10, fontWeight: 700, background: "rgba(26,111,212,0.2)", color: "#60A5FA", padding: "2px 7px", borderRadius: 4, textTransform: "uppercase", letterSpacing: "0.05em", border: "1px solid rgba(26,111,212,0.3)" }}>New</span>}
                                  {app.created_at && (
                                    <span style={{ fontSize: 11, color: "rgba(255,255,255,0.3)", display: "inline-flex", alignItems: "center", gap: 3 }}>
                                      <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>
                                      {new Date(app.created_at).toLocaleDateString("en-GY", { day: "numeric", month: "short", year: "numeric" })}
                                    </span>
                                  )}
                                </div>
                                <div className="flex items-center gap-2" style={{ marginTop: 3 }}>
                                  {parsed.seniority_level && <span style={{ fontSize: 10, fontWeight: 700, background: "rgba(184,146,42,0.15)", color: "#D4A83C", padding: "2px 8px", borderRadius: 4, border: "1px solid rgba(184,146,42,0.25)", textTransform: "uppercase", letterSpacing: "0.04em" }}>{parsed.seniority_level}</span>}
                                  {parsed.total_experience_years > 0 && <span style={{ fontSize: 12, color: "rgba(255,255,255,0.4)" }}>{parsed.total_experience_years}y exp</span>}
                                  {parsed.stability_signal && <span style={{ fontSize: 12, color: parsed.stability_signal === "stable" ? "#4ADE80" : "#FBBF24", fontWeight: 500 }}>● {parsed.stability_signal}</span>}
                                </div>
                              </div>
                              <ScoreDisplay score={app.match_score} app={app} />
                            </div>

                            {/* Score bar */}
                            <div style={{ marginBottom: 0 }}>
                              <div style={{ height: 3, background: "rgba(255,255,255,0.06)", borderRadius: 2, overflow: "hidden" }}>
                                <div style={{ height: "100%", borderRadius: 2, background: getScoreColor(app.match_score), width: `${app.match_score}%`, transition: "width 1s ease" }} />
                              </div>
                            </div>

                            {/* Interview date chip */}
                            {app.interview_date && (
                              <div style={{ display: "inline-flex", alignItems: "center", gap: 6, margin: "10px 0 0", padding: "5px 10px", background: "rgba(124,58,237,0.15)", border: "1px solid rgba(124,58,237,0.3)", borderRadius: 20, fontSize: 12, color: "#A78BFA", fontWeight: 500 }}>
                                <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><rect x="3" y="4" width="18" height="18" rx="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></svg>
                                Interview · {new Date(app.interview_date).toLocaleDateString("en-GY", { month: "short", day: "numeric" })} at {new Date(app.interview_date).toLocaleTimeString("en-GY", { hour: "2-digit", minute: "2-digit" })}
                              </div>
                            )}

                            {/* Stats grid */}
                            <div className="cand-stats-grid" style={{ display: "grid", gridTemplateColumns: "repeat(4, minmax(0,1fr))", margin: "12px -20px 0", borderTop: "1px solid rgba(255,255,255,0.06)", borderBottom: "1px solid rgba(255,255,255,0.06)" }}>
                              {[
                                { label: "Avg Tenure", value: parsed.average_tenure_months ? `${(parsed.average_tenure_months / 12).toFixed(1)}y` : "—", color: null },
                                { label: "Industries", value: parsed.industries?.slice(0,3).join(" · ") || "—", color: null, small: true },
                                { label: "Salary Fit", value: app.salary_fit?.replace(/_/g, " ") || "—", color: dSalary(app.salary_fit) },
                                { label: "Level Fit", value: app.seniority_fit || "—", color: dLevel(app.seniority_fit) },
                              ].map((s, si) => (
                                <div key={si} style={{ padding: "10px 14px", borderRight: si < 3 ? "1px solid rgba(255,255,255,0.06)" : "none" }}>
                                  <div style={{ fontSize: 10, color: "rgba(255,255,255,0.3)", textTransform: "uppercase", letterSpacing: "0.07em", fontWeight: 600, marginBottom: 4 }}>{s.label}</div>
                                  <div style={{ fontSize: s.small ? 12 : 13, fontWeight: 600, color: s.color || "rgba(255,255,255,0.85)", lineHeight: 1.3 }}>{s.value}</div>
                                </div>
                              ))}
                            </div>

                            {/* Skill tags row */}
                            <div style={{ padding: "10px 0 0", display: "flex", flexWrap: "wrap", gap: 6 }}>
                              {(app.matched_skills || []).slice(0,3).map((sk, ski) => (
                                <span key={ski} style={{
                                  display: "inline-flex", alignItems: "center", gap: 4,
                                  padding: "4px 10px", borderRadius: 20, fontSize: 12, fontWeight: 500,
                                  background: "rgba(45,158,78,0.18)", color: "#4ADE80",
                                  border: "1px solid rgba(45,158,78,0.3)",
                                  maxWidth: 180, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap"
                                }}>
                                  <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3"><polyline points="20 6 9 17 4 12"/></svg>
                                  {sk.length > 22 ? sk.slice(0, 22) + "..." : sk}
                                </span>
                              ))}
                              {(app.missing_skills || []).slice(0,2).map((sk, ski) => (
                                <span key={ski} style={{
                                  display: "inline-flex", alignItems: "center", gap: 4,
                                  padding: "4px 10px", borderRadius: 20, fontSize: 12, fontWeight: 500,
                                  background: "rgba(217,48,37,0.12)", color: "#F87171",
                                  border: "1px solid rgba(217,48,37,0.25)",
                                  maxWidth: 180, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap"
                                }}>
                                  <svg width="9" height="9" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
                                  {sk.length > 22 ? sk.slice(0, 22) + "..." : sk}
                                </span>
                              ))}
                              {((app.matched_skills?.length || 0) + (app.missing_skills?.length || 0)) > 5 && (
                                <span style={{ padding: "4px 10px", borderRadius: 20, fontSize: 12, background: "rgba(255,255,255,0.06)", color: "rgba(255,255,255,0.35)", border: "1px solid rgba(255,255,255,0.08)" }}>
                                  +{(app.matched_skills?.length || 0) + (app.missing_skills?.length || 0) - 5} more
                                </span>
                              )}
                            </div>

                            {/* Card action bar */}
                            <div className="cand-action-bar flex items-center justify-between" style={{ borderTop: "1px solid rgba(255,255,255,0.06)", margin: "12px -20px 0", padding: "10px 20px 14px" }}>
                              <div className="flex items-center gap-2">
                                <button style={{ background: "none", border: "none", cursor: "pointer", fontSize: 12, color: "#D4A83C", fontFamily: "DM Sans, sans-serif", fontWeight: 600, padding: "5px 0", display: "flex", alignItems: "center", gap: 4 }} onClick={() => setSelectedApplicant(selectedApplicant === app.id ? null : app.id)}>
                                  {selectedApplicant === app.id ? "Collapse" : "Full report →"}
                                </button>
                              </div>
                              <div className="flex items-center gap-2">
                                {/* Does Not Meet Expectations */}
                                <button
                                  style={{ display: "inline-flex", alignItems: "center", gap: 5, padding: "5px 12px", borderRadius: 7, fontSize: 12, fontWeight: 500, cursor: "pointer", fontFamily: "DM Sans, sans-serif", transition: "all 0.2s", border: "1px solid rgba(193,125,32,0.3)", background: "transparent", color: plan === "free" ? "rgba(255,255,255,0.25)" : "#FBBF24" }}
                                  onClick={() => dismissCandidate(app)}
                                  title="Hide this candidate from all your future listings"
                                >
                                  {plan === "free" && <span style={{ fontSize: 9, fontWeight: 700, background: "var(--gold)", color: "#fff", padding: "1px 5px", borderRadius: 3 }}>PRO</span>}
                                  <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10"/><line x1="4.93" y1="4.93" x2="19.07" y2="19.07"/></svg>
                                  Doesn't Meet
                                </button>
                                {/* Archive */}
                                <button
                                  style={{ display: "inline-flex", alignItems: "center", gap: 5, padding: "5px 12px", borderRadius: 7, fontSize: 12, fontWeight: 500, cursor: "pointer", fontFamily: "DM Sans, sans-serif", transition: "all 0.2s", border: "1px solid rgba(255,255,255,0.1)", background: "transparent", color: "rgba(255,255,255,0.45)" }}
                                  onClick={() => archiveCandidate(app)}
                                  title="Move to Archive — you can restore them later"
                                >
                                  <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="21 8 21 21 3 21 3 8"/><rect x="1" y="3" width="22" height="5"/><line x1="10" y1="12" x2="14" y2="12"/></svg>
                                  Archive
                                </button>
                              </div>
                            </div>
                          </div>

                          {selectedApplicant === app.id ? (
                            <>
                              <div className="sheet-backdrop-emp" onClick={() => setSelectedApplicant(null)} />
                              <div className="applicant-full-report" style={{ padding: "0 24px 24px", borderTop: "1px solid rgba(255,255,255,0.06)", paddingTop: 20 }}>
                                {/* Drag handle — mobile only */}
                                <div className="applicant-sheet-handle" style={{ width: 36, height: 4, background: "rgba(255,255,255,0.15)", borderRadius: 2, margin: "12px auto 16px" }} />
                                <div className="applicant-full-report-inner">

                              {/* Interview scheduled banner */}
                              {app.interview_date && (
                                <div style={{ background: "rgba(124,58,237,0.12)", border: "1px solid rgba(124,58,237,0.3)", borderRadius: 8, padding: "12px 16px", marginBottom: 16, display: "flex", alignItems: "center", gap: 10 }}>
                                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#A78BFA" strokeWidth="2"><rect x="3" y="4" width="18" height="18" rx="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></svg>
                                  <div>
                                    <div style={{ fontSize: 11, fontWeight: 700, color: "#A78BFA", textTransform: "uppercase", letterSpacing: "0.05em" }}>Interview Scheduled</div>
                                    <div style={{ fontSize: 13, color: "#C4B5FD", fontWeight: 500, marginTop: 2 }}>
                                      {new Date(app.interview_date).toLocaleDateString("en-GY", { weekday: "long", year: "numeric", month: "long", day: "numeric" })} at {new Date(app.interview_date).toLocaleTimeString("en-GY", { hour: "2-digit", minute: "2-digit" })}
                                    </div>
                                  </div>
                                  <button style={{ marginLeft: "auto", fontSize: 11, color: "#A78BFA", border: "1px solid rgba(124,58,237,0.3)", background: "transparent", borderRadius: 6, padding: "4px 10px", cursor: "pointer", fontFamily: "DM Sans, sans-serif" }}
                                    onClick={() => { setInterviewModal({ app, targetStatus: "interview_scheduled" }); setInterviewDate(app.interview_date.split("T")[0]); setInterviewTime(app.interview_date.split("T")[1]?.slice(0,5) || "10:00"); }}>
                                    Reschedule
                                  </button>
                                </div>
                              )}

                              {/* Smart verdict */}
                              {app.ai_verdict && (
                                <div style={{ background: "rgba(184,146,42,0.08)", border: "1px solid rgba(184,146,42,0.2)", borderRadius: 8, padding: "12px 16px", marginBottom: 16 }}>
                                  <div style={{ fontSize: 11, fontWeight: 700, color: "#D4A83C", textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: 6 }}>Smart Verdict</div>
                                  <p style={{ fontSize: 14, color: "rgba(255,255,255,0.8)", fontStyle: "italic", lineHeight: 1.6 }}>"{app.ai_verdict}"</p>
                                </div>
                              )}

                              {/* Match explanation + Skill gap */}
                              <div className="grid-2 gap-4 mb-4">
                                <div>
                                  <div style={{ fontSize: 10, fontWeight: 700, color: "rgba(255,255,255,0.35)", textTransform: "uppercase", letterSpacing: "0.07em", marginBottom: 8 }}>Match Explanation</div>
                                  <p style={{ fontSize: 13, color: "rgba(255,255,255,0.7)", lineHeight: 1.7 }}>{app.match_explanation}</p>
                                </div>
                                <div>
                                  <div style={{ fontSize: 10, fontWeight: 700, color: "rgba(255,255,255,0.35)", textTransform: "uppercase", letterSpacing: "0.07em", marginBottom: 8 }}>Skill Gap Advice</div>
                                  <p style={{ fontSize: 13, color: "rgba(255,255,255,0.7)", lineHeight: 1.7 }}>{app.skill_gap_advice || "No significant gaps identified."}</p>
                                </div>
                              </div>

                              {/* Matched / Missing skills */}
                              <div className="grid-2 gap-4 mb-4">
                                <div>
                                  <div style={{ fontSize: 10, fontWeight: 700, color: "rgba(255,255,255,0.35)", textTransform: "uppercase", letterSpacing: "0.07em", marginBottom: 8 }}>Matched Skills</div>
                                  <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                                    {(app.matched_skills || []).map((sk, ski) => (
                                      <span key={ski} style={{ padding: "3px 10px", borderRadius: 20, fontSize: 12, background: "rgba(45,158,78,0.15)", color: "#4ADE80", border: "1px solid rgba(45,158,78,0.25)" }}>{sk}</span>
                                    ))}
                                  </div>
                                </div>
                                <div>
                                  <div style={{ fontSize: 10, fontWeight: 700, color: "rgba(255,255,255,0.35)", textTransform: "uppercase", letterSpacing: "0.07em", marginBottom: 8 }}>Missing Skills</div>
                                  <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                                    {(app.missing_skills || []).map((sk, ski) => (
                                      <span key={ski} style={{ padding: "3px 10px", borderRadius: 20, fontSize: 12, background: "rgba(217,48,37,0.12)", color: "#F87171", border: "1px solid rgba(217,48,37,0.2)" }}>{sk}</span>
                                    ))}
                                    {(!app.missing_skills || app.missing_skills.length === 0) && <span style={{ fontSize: 13, color: "rgba(255,255,255,0.3)" }}>None identified</span>}
                                  </div>
                                </div>
                              </div>

                              {/* Green flags + Watch points */}
                              {(flags.green?.length > 0 || flags.red?.length > 0) && (
                                <div className="grid-2 gap-4 mb-4">
                                  <div>
                                    <div style={{ fontSize: 10, fontWeight: 700, color: "rgba(255,255,255,0.35)", textTransform: "uppercase", letterSpacing: "0.07em", marginBottom: 8 }}>Green Flags</div>
                                    {flags.green?.map((f, fi) => (
                                      <div key={fi} style={{ display: "flex", alignItems: "flex-start", gap: 8, marginBottom: 6, fontSize: 13, color: "#4ADE80", lineHeight: 1.5 }}>
                                        <Icon name="check" size={12} color="#4ADE80" style={{ marginTop: 2, flexShrink: 0 }} /> {f}
                                      </div>
                                    ))}
                                  </div>
                                  <div>
                                    <div style={{ fontSize: 10, fontWeight: 700, color: "rgba(255,255,255,0.35)", textTransform: "uppercase", letterSpacing: "0.07em", marginBottom: 8 }}>Watch Points</div>
                                    {flags.red?.map((f, fi) => (
                                      <div key={fi} style={{ display: "flex", alignItems: "flex-start", gap: 8, marginBottom: 6, fontSize: 13, color: "#FBBF24", lineHeight: 1.5 }}>
                                        <Icon name="alert" size={12} color="#FBBF24" style={{ marginTop: 2, flexShrink: 0 }} /> {f}
                                      </div>
                                    ))}
                                  </div>
                                </div>
                              )}

                              {/* Private Notes */}
                              <div style={{ marginBottom: 16, paddingBottom: 16, borderBottom: "1px solid rgba(255,255,255,0.06)" }}>
                                <div style={{ fontSize: 10, fontWeight: 700, color: "rgba(255,255,255,0.35)", textTransform: "uppercase", letterSpacing: "0.07em", marginBottom: 8, display: "flex", alignItems: "center", gap: 6 }}>
                                  <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>
                                  Private Notes
                                </div>
                                <textarea
                                  placeholder="Add private notes about this candidate — only you can see these..."
                                  value={candidateNotes[app.id] !== undefined ? candidateNotes[app.id] : (app.employer_notes || "")}
                                  onChange={e => setCandidateNotes(prev => ({ ...prev, [app.id]: e.target.value }))}
                                  style={{ width: "100%", minHeight: 72, fontSize: 13, resize: "vertical", marginBottom: 8, background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.08)", borderRadius: 8, padding: "10px 14px", color: "rgba(255,255,255,0.8)", fontFamily: "DM Sans, sans-serif", outline: "none" }}
                                />
                                <button
                                  style={{ display: "inline-flex", alignItems: "center", gap: 6, padding: "6px 14px", borderRadius: 7, fontSize: 12, fontWeight: 600, cursor: "pointer", fontFamily: "DM Sans, sans-serif", border: "1px solid rgba(255,255,255,0.12)", background: "rgba(255,255,255,0.05)", color: "rgba(255,255,255,0.6)" }}
                                  onClick={async () => {
                                    const note = candidateNotes[app.id];
                                    if (note === undefined) { toast("Nothing to save.", "info"); return; }
                                    const { error } = await supabase.from("applications").update({ employer_notes: note }).eq("id", app.id);
                                    if (error) { toast("Save failed — make sure the employer_notes column exists in your applications table.", "error"); return; }
                                    setApplicants(prev => prev.map(a => a.id === app.id ? { ...a, employer_notes: note } : a));
                                    toast("Note saved", "success");
                                  }}
                                >
                                  <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><polyline points="20 6 9 17 4 12"/></svg>
                                  Save Note
                                </button>
                              </div>

                              {/* Status buttons + actions */}
                              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", paddingTop: 16, borderTop: "1px solid rgba(255,255,255,0.06)", flexWrap: "wrap", gap: 10 }}>
                                <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                                  {["shortlisted", "interview_scheduled", "offer_made", "hired", "rejected"].map(s => (
                                    <button key={s}
                                      onClick={() => updateStatus(app.id, s)}
                                      style={{ padding: "5px 12px", borderRadius: 7, fontSize: 11, fontWeight: 600, cursor: "pointer", fontFamily: "DM Sans, sans-serif", textTransform: "capitalize", transition: "all 0.15s", border: app.status === s ? "1px solid #D4A83C" : "1px solid rgba(255,255,255,0.12)", background: app.status === s ? "rgba(184,146,42,0.2)" : "rgba(255,255,255,0.04)", color: app.status === s ? "#D4A83C" : "rgba(255,255,255,0.5)" }}>
                                      {s.replace(/_/g, " ")}
                                    </button>
                                  ))}
                                </div>
                                <div style={{ display: "flex", gap: 6 }}>
                                  {(app.cv_url || app.cv_path) ? (
                                    <button
                                      style={{ display: "inline-flex", alignItems: "center", gap: 5, padding: "5px 12px", borderRadius: 7, fontSize: 12, fontWeight: 500, cursor: "pointer", fontFamily: "DM Sans, sans-serif", border: "1px solid rgba(255,255,255,0.12)", background: "rgba(255,255,255,0.05)", color: "rgba(255,255,255,0.6)" }}
                                      onClick={() => downloadCV(app)}>
                                      <Icon name="download" size={12} />Download CV {plan === "free" ? `(${downloadLimit - (employer?.cv_downloads_used || 0)} left)` : ""}
                                    </button>
                                  ) : (
                                    <span style={{ display: "inline-flex", alignItems: "center", gap: 5, padding: "5px 12px", borderRadius: 7, fontSize: 12, fontFamily: "DM Sans, sans-serif", color: "rgba(255,255,255,0.25)", border: "1px solid rgba(255,255,255,0.06)", background: "rgba(255,255,255,0.02)" }}>
                                      No CV on file
                                    </span>
                                  )}
                                  {app.candidate_email && (
                                    <a href={`mailto:${app.candidate_email}`} style={{ display: "inline-flex", alignItems: "center", gap: 5, padding: "5px 12px", borderRadius: 7, fontSize: 12, fontWeight: 500, cursor: "pointer", fontFamily: "DM Sans, sans-serif", border: "1px solid rgba(255,255,255,0.12)", background: "rgba(255,255,255,0.05)", color: "rgba(255,255,255,0.6)", textDecoration: "none" }}>
                                      <Icon name="mail" size={12} />Email
                                    </a>
                                  )}
                                  {app.candidate_phone && (
                                    <button style={{ display: "inline-flex", alignItems: "center", gap: 5, padding: "5px 12px", borderRadius: 7, fontSize: 12, fontWeight: 600, cursor: "pointer", fontFamily: "DM Sans, sans-serif", background: "#25D366", border: "1px solid #25D366", color: "#fff" }}
                                      onClick={() => openWhatsApp(app.candidate_phone, `Hi ${app.candidate_name}, I found your profile on JobSpot and would like to discuss the ${selectedJob.title} position at ${employer.company_name}.`)}>
                                      <Icon name="whatsapp" size={12} color="#fff" />WhatsApp
                                    </button>
                                  )}
                                </div>
                              </div>
                              <button style={{ background: "none", border: "none", cursor: "pointer", fontSize: 12, color: "rgba(255,255,255,0.3)", fontFamily: "DM Sans, sans-serif", marginTop: 16, padding: 0 }} onClick={() => setSelectedApplicant(null)}>Collapse ↓</button>
                                </div>{/* end applicant-full-report-inner */}
                              </div>{/* end applicant-full-report */}
                            </>
                          ) : null}
                        </>
                      )}
                    </div>
                  );
                  });
                })()}
                {candidateSearch.trim() && applicants.filter(app => {
                  const q = candidateSearch.toLowerCase().trim();
                  const parsed = app.ai_parsed_data || {};
                  return (
                    app.candidate_name?.toLowerCase().includes(q) ||
                    (app.matched_skills || []).some(s => s.toLowerCase().includes(q)) ||
                    (app.missing_skills || []).some(s => s.toLowerCase().includes(q)) ||
                    (parsed.industries || []).some(s => s.toLowerCase().includes(q)) ||
                    parsed.seniority_level?.toLowerCase().includes(q)
                  );
                }).length === 0 && (
                  <div style={{ textAlign: "center", padding: "40px 0", color: "var(--text3)" }}>
                    <Icon name="search" size={24} color="var(--border)" />
                    <div style={{ marginTop: 12, fontSize: 14 }}>No candidates match "{candidateSearch}"</div>
                  </div>
                )}
              </div>
            )}

            {/* Archive section */}
            {archivedApplicants.length > 0 && (
              <div style={{ marginTop: 32 }}>
                <div className="flex items-center gap-3 mb-4" style={{ paddingBottom: 12, borderBottom: "1px solid var(--border2)" }}>
                  <div style={{ width: 28, height: 28, background: "var(--bg3)", border: "1px solid var(--border2)", borderRadius: 8, display: "flex", alignItems: "center", justifyContent: "center" }}>
                    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="var(--text3)" strokeWidth="2"><polyline points="21 8 21 21 3 21 3 8"/><rect x="1" y="3" width="22" height="5"/><line x1="10" y1="12" x2="14" y2="12"/></svg>
                  </div>
                  <div>
                    <span className="font-display" style={{ fontSize: 15, fontWeight: 600, color: "var(--text2)" }}>Archived</span>
                    <span style={{ fontSize: 12, color: "var(--text3)", marginLeft: 8 }}>{archivedApplicants.length} candidate{archivedApplicants.length !== 1 ? "s" : ""} · not visible in leaderboard</span>
                  </div>
                </div>
                <div className="flex-col gap-2">
                  {archivedApplicants.map((app) => {
                    const parsed = app.ai_parsed_data || {};
                    return (
                      <div key={app.id} style={{ background: "var(--bg2)", border: "1px solid var(--border2)", borderRadius: 10, padding: "14px 18px", display: "flex", alignItems: "center", gap: 14, opacity: 0.75 }}>
                        <div style={{ flex: 1 }}>
                          <div className="flex items-center gap-2 mb-1">
                            <span style={{ fontSize: 14, fontWeight: 600, color: "var(--text2)" }}>{app.candidate_name || "Anonymous"}</span>
                            {parsed.seniority_level && <span className="badge badge-gray" style={{ fontSize: 10 }}>{parsed.seniority_level}</span>}
                          </div>
                          <div style={{ fontSize: 12, color: "var(--text3)" }}>
                            {parsed.total_experience_years > 0 && `${parsed.total_experience_years}y exp · `}
                            Archived {timeAgo(app.updated_at || app.created_at)}
                          </div>
                        </div>
                        <div className={`score-pill ${getScoreClass(app.match_score)}`} style={{ minWidth: 52, height: 30, fontSize: 13, opacity: 0.6 }}>{Math.round(app.match_score)}%</div>
                        <button
                          className="btn btn-outline btn-sm"
                          style={{ fontSize: 12, whiteSpace: "nowrap" }}
                          onClick={() => restoreCandidate(app)}
                        >
                          ↩ Restore
                        </button>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
              </div>
            )}
          </>
        )}

        {/* ============ ARCHIVE TAB ============ */}
        {tab === "archive" && (
          <div style={{ maxWidth: 760 }}>
            <div className="flex items-center justify-between mb-6">
              <div>
                <h1 className="font-display" style={{ fontSize: 24, fontWeight: 700, marginBottom: 4 }}>Archive</h1>
                <p style={{ color: "var(--text2)", fontSize: 14 }}>Candidates you've archived across all job listings</p>
              </div>
              {globalArchived.length > 0 && (
                <span style={{ fontSize: 13, color: "var(--text3)", background: "var(--bg3)", border: "1px solid var(--border2)", borderRadius: 8, padding: "4px 12px" }}>
                  {globalArchived.length} archived
                </span>
              )}
            </div>

            {globalArchiveLoading ? (
              <div className="flex-col gap-3">{[1,2,3].map(i => <div key={i} className="skeleton" style={{ height: 72, borderRadius: 12 }} />)}</div>
            ) : globalArchived.length === 0 ? (
              <div className="card" style={{ textAlign: "center", padding: "56px 32px" }}>
                <div style={{ width: 52, height: 52, background: "var(--bg3)", border: "1px solid var(--border2)", borderRadius: 14, display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 16px" }}>
                  <Icon name="archive" size={22} color="var(--text3)" />
                </div>
                <div className="font-display" style={{ fontSize: 16, fontWeight: 600, marginBottom: 8 }}>No archived candidates yet</div>
                <p style={{ fontSize: 14, color: "var(--text2)", maxWidth: 340, margin: "0 auto" }}>When you archive a candidate from a job listing, they'll appear here. You can restore them at any time.</p>
              </div>
            ) : (
              <>
                {/* Group by job */}
                {(() => {
                  const byJob = {};
                  globalArchived.forEach(app => {
                    const key = app.job_id;
                    if (!byJob[key]) byJob[key] = { title: app.jobs?.title || "Unknown job", apps: [] };
                    byJob[key].apps.push(app);
                  });
                  return Object.entries(byJob).map(([jobId, group]) => (
                    <div key={jobId} style={{ marginBottom: 28 }}>
                      {/* Job group header */}
                      <div className="flex items-center gap-3 mb-3">
                        <div style={{ width: 28, height: 28, background: "rgba(184,146,42,0.1)", border: "1px solid rgba(184,146,42,0.2)", borderRadius: 7, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                          <Icon name="briefcase" size={13} color="var(--gold)" />
                        </div>
                        <div>
                          <span className="font-display" style={{ fontSize: 14, fontWeight: 700, color: "var(--text)" }}>{group.title}</span>
                          <span style={{ fontSize: 12, color: "var(--text3)", marginLeft: 8 }}>{group.apps.length} archived</span>
                        </div>
                        <button className="btn btn-ghost btn-sm" style={{ marginLeft: "auto", fontSize: 12 }}
                          onClick={() => { loadApplicants(jobId); setTab("jobs"); }}>
                          View listing →
                        </button>
                      </div>

                      {/* Candidate rows */}
                      <div className="flex-col gap-2">
                        {group.apps.map(app => {
                          const parsed = app.ai_parsed_data || {};
                          return (
                            <div key={app.id} style={{ background: "var(--bg2)", border: "1px solid var(--border2)", borderRadius: 10, padding: "14px 18px", display: "flex", alignItems: "center", gap: 14 }}>
                              {/* Avatar initials */}
                              <div style={{ width: 38, height: 38, borderRadius: "50%", background: "var(--bg3)", border: "1px solid var(--border2)", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, fontFamily: "Syne", fontSize: 13, fontWeight: 700, color: "var(--text3)" }}>
                                {(app.candidate_name || "?").split(" ").map(n => n[0]).slice(0,2).join("").toUpperCase()}
                              </div>
                              <div style={{ flex: 1 }}>
                                <div className="flex items-center gap-2 mb-1">
                                  <span style={{ fontSize: 14, fontWeight: 600, color: "var(--text)" }}>{app.candidate_name || "Anonymous"}</span>
                                  {parsed.seniority_level && <span className="badge badge-gray" style={{ fontSize: 10 }}>{parsed.seniority_level}</span>}
                                </div>
                                <div style={{ fontSize: 12, color: "var(--text3)" }}>
                                  {parsed.total_experience_years > 0 && `${parsed.total_experience_years}y exp · `}
                                  {app.candidate_email && `${app.candidate_email} · `}
                                  Archived {timeAgo(app.updated_at || app.created_at)}
                                </div>
                              </div>
                              <div className={`score-pill ${getScoreClass(app.match_score)}`} style={{ minWidth: 52, height: 30, fontSize: 13, opacity: 0.7 }}>{Math.round(app.match_score)}%</div>
                              <button
                                className="btn btn-outline btn-sm"
                                style={{ fontSize: 12, whiteSpace: "nowrap" }}
                                onClick={async () => {
                                  await supabase.from("applications").update({ status: "new" }).eq("id", app.id);
                                  setGlobalArchived(prev => prev.filter(a => a.id !== app.id));
                                  toast(`${app.candidate_name || "Candidate"} restored to the leaderboard.`, "success");
                                }}
                              >
                                ↩ Restore
                              </button>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  ));
                })()}
              </>
            )}
          </div>
        )}

        {/* Notifications tab */}
        {tab === "notifications" && (
          <div style={{ maxWidth: 680 }}>
            <div className="flex items-center justify-between mb-6">
              <div>
                <h1 className="font-display" style={{ fontSize: 24, fontWeight: 700, marginBottom: 4 }}>Notifications</h1>
                <p style={{ color: "var(--text2)", fontSize: 14 }}>New applicants across your job listings</p>
              </div>
              {notifications.length > 0 && (
                <button className="btn btn-ghost btn-sm" style={{ color: "var(--text3)" }} onClick={async () => {
                  await supabase.from("notifications").delete().eq("employer_id", user.id);
                  setNotifications([]);
                  setUnreadCount(0);
                }}>Clear all</button>
              )}
            </div>

            {notifications.length === 0 ? (
              <div className="card" style={{ textAlign: "center", padding: "48px 32px" }}>
                <div style={{ width: 48, height: 48, background: "var(--bg3)", border: "1px solid var(--border2)", borderRadius: 12, display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 16px" }}>
                  <Icon name="bell" size={20} color="var(--text3)" />
                </div>
                <div className="font-display" style={{ fontSize: 16, fontWeight: 600, marginBottom: 8 }}>No notifications yet</div>
                <p style={{ fontSize: 14, color: "var(--text2)" }}>When someone applies to one of your jobs, you'll see it here instantly.</p>
              </div>
            ) : (
              <div className="flex-col gap-2">
                {notifications.map((n, ni) => (
                  <div key={n.id || ni} className="card card-hover"
                    style={{ padding: "14px 18px", borderLeft: !n.read ? "3px solid var(--blue)" : "3px solid transparent", opacity: n.read ? 0.7 : 1 }}
                    onClick={() => {
                      const job = jobs.find(j => j.id === n.job_id);
                      if (job) { loadApplicants(n.job_id); setTab("jobs"); }
                    }}>
                    <div className="flex items-center gap-3">
                      <div style={{ width: 38, height: 38, background: n.read ? "var(--bg3)" : "var(--blue-bg)", border: `1px solid ${n.read ? "var(--border2)" : "rgba(26,111,212,0.2)"}`, borderRadius: 10, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                        <Icon name="user" size={17} color={n.read ? "var(--text3)" : "var(--blue)"} />
                      </div>
                      <div style={{ flex: 1 }}>
                        <div style={{ fontSize: 14, fontWeight: n.read ? 400 : 600, color: "var(--text)", marginBottom: 2 }}>
                          {n.candidate_name} applied to <span style={{ color: "var(--gold)" }}>{n.job_title}</span>
                        </div>
                        <div style={{ fontSize: 12, color: "var(--text3)" }}>{timeAgo(n.created_at)}</div>
                      </div>
                      {n.match_score > 0 && (
                        <div className={`score-pill ${getScoreClass(n.match_score)}`} style={{ minWidth: 48, height: 28, fontSize: 13 }}>{Math.round(n.match_score)}%</div>
                      )}
                      <Icon name="arrow_right" size={16} color="var(--text3)" />
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Account tab */}
        {tab === "account" && employer && (
          <div style={{ maxWidth: 600 }}>
            <h1 className="font-display" style={{ fontSize: 24, fontWeight: 700, marginBottom: 24 }}>Account Settings</h1>

            {/* Current plan */}
            <div className="card mb-4">
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-display" style={{ fontSize: 16, fontWeight: 600 }}>Current Plan</h3>
                <PlanBadge plan={employer.plan} />
              </div>
              {employer.plan === "free" ? (
                <div>
                  <p style={{ fontSize: 14, color: "var(--text2)", marginBottom: 16 }}>You're on the free plan. Upgrade to unlock full candidate intelligence.</p>
                  <div className="grid-2 gap-4">
                    {[
                      { name: "Starter", key: "starter", price: "GYD 12,000/year", features: ["8 active jobs", "Top 15 candidates per job", "Full AI fit reports + skill gaps", "Pipeline & candidate search", "Does Not Meet Expectations", "100 CV downloads/month"] },
                      { name: "Professional", key: "professional", price: "GYD 36,000/year", features: ["Unlimited active jobs", "All candidates, no cap", "Interview calendar & scheduling", "2 featured homepage slots", "Everything in Starter", "Priority support", "Unlimited CV downloads"] },
                    ].map((p, i) => (
                      <div key={i} style={{ background: "var(--bg3)", border: `1px solid ${i === 1 ? "rgba(201,168,76,0.3)" : "var(--border2)"}`, borderRadius: 12, padding: 20 }}>
                        {i === 1 && <div className="badge badge-gold mb-2" style={{ fontSize: 10 }}>Most popular</div>}
                        <div className="font-display" style={{ fontSize: 16, fontWeight: 700, marginBottom: 4 }}>{p.name}</div>
                        <div style={{ marginBottom: 12 }} />
                        <div className="flex-col gap-1 mb-4">
                          {p.features.map((f, fi) => (
                            <div key={fi} className="flex items-center gap-2" style={{ fontSize: 13, color: "var(--text2)" }}>
                              <Icon name="check" size={12} color="var(--green)" /> {f}
                            </div>
                          ))}
                        </div>
                        <button className={`btn w-full btn-sm ${i === 1 ? "btn-gold" : "btn-outline"}`} onClick={() => setShowUpgrade(true)}>
                          Upgrade Now
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              ) : (
                <div>
                  <p style={{ fontSize: 14, color: "var(--text2)", marginBottom: 16 }}>Active until: <strong>{employer.plan_expires_at ? new Date(employer.plan_expires_at).toLocaleDateString() : "Ongoing"}</strong></p>
                  {employer.plan === "starter" && (
                    <div style={{ background: "var(--bg3)", border: "1px solid var(--border2)", borderRadius: 12, padding: 16, display: "flex", alignItems: "center", justifyContent: "space-between", gap: 16 }}>
                      <div>
                        <div className="font-display" style={{ fontSize: 14, fontWeight: 700, marginBottom: 2 }}>Upgrade to Professional</div>
                        <div style={{ fontSize: 12, color: "var(--text2)" }}>Unlimited jobs · All candidates · Priority support</div>
                      </div>
                      <button className="btn btn-gold btn-sm" style={{ whiteSpace: "nowrap" }} onClick={() => setShowUpgrade(true)}>
                        <Icon name="crown" size={13} /> Upgrade
                      </button>
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Company profile */}
            <div className="card mb-4">
              <h3 className="font-display" style={{ fontSize: 16, fontWeight: 600, marginBottom: 16 }}>Company Profile</h3>
              <CompanyProfileForm employer={employer} onSave={saveProfile} />
            </div>

            {/* Change password */}
            <div className="card">
              <h3 className="font-display" style={{ fontSize: 16, fontWeight: 600, marginBottom: 4 }}>Change Password</h3>
              <p style={{ fontSize: 13, color: "var(--text2)", marginBottom: 16 }}>Update the password for your JobSpot account.</p>
              <ChangePasswordForm />
            </div>
          </div>
        )}
      </main>

      {showPostJob && employer && <PostJobModal employer={employer} jobs={jobs} onClose={() => setShowPostJob(false)} onSuccess={() => { setShowPostJob(false); loadData(); toast("Job posted successfully!", "success"); }} />}
      {showUpgrade && employer && <UpgradeRequestModal employer={employer} onClose={() => setShowUpgrade(false)} />}
      {editingJob && <EditJobModal job={editingJob} onClose={() => setEditingJob(null)} onSuccess={() => { setEditingJob(null); loadData(); toast("Job updated!", "success"); }} />}

      {/* Interview Date Picker Modal */}
      {interviewModal && (
        <div className="modal-backdrop" onClick={() => setInterviewModal(null)}>
          <div className="modal" style={{ maxWidth: 400 }} onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-5">
              <div>
                <h2 className="font-display" style={{ fontSize: 18, fontWeight: 700 }}>Schedule Interview</h2>
                <p style={{ fontSize: 13, color: "var(--text2)", marginTop: 2 }}>{interviewModal.app.candidate_name || "Candidate"}</p>
              </div>
              <button className="btn btn-ghost btn-sm" onClick={() => setInterviewModal(null)}><Icon name="x" size={16} /></button>
            </div>

            <div style={{ background: "#F3F0FF", border: "1px solid rgba(124,58,237,0.2)", borderRadius: 10, padding: "12px 16px", marginBottom: 20, display: "flex", alignItems: "center", gap: 10 }}>
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#7C3AED" strokeWidth="2"><rect x="3" y="4" width="18" height="18" rx="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></svg>
              <span style={{ fontSize: 13, color: "#7C3AED", fontWeight: 500 }}>Pick a date and time for the interview</span>
            </div>

            <div className="flex-col gap-3 mb-5">
              <div>
                <label className="label">Interview Date</label>
                <input
                  className="input"
                  type="date"
                  value={interviewDate}
                  min={new Date().toISOString().split("T")[0]}
                  onChange={e => setInterviewDate(e.target.value)}
                />
              </div>
              <div>
                <label className="label">Interview Time</label>
                <select className="input" value={interviewTime} onChange={e => setInterviewTime(e.target.value)}>
                  {["08:00","08:30","09:00","09:30","10:00","10:30","11:00","11:30","12:00","12:30",
                    "13:00","13:30","14:00","14:30","15:00","15:30","16:00","16:30","17:00"].map(t => (
                    <option key={t} value={t}>{t.replace(":", ":")}</option>
                  ))}
                </select>
              </div>
            </div>

            {interviewDate && (
              <div style={{ background: "rgba(45,158,78,0.06)", border: "1px solid rgba(45,158,78,0.2)", borderRadius: 8, padding: "10px 14px", marginBottom: 16, fontSize: 13, color: "var(--green)" }}>
                 {new Date(interviewDate + "T" + interviewTime).toLocaleDateString("en-GY", { weekday: "long", year: "numeric", month: "long", day: "numeric" })} at {interviewTime}
              </div>
            )}

            <div className="flex gap-3">
              <button className="btn btn-outline" style={{ flex: 1 }} onClick={() => setInterviewModal(null)}>Cancel</button>
              <button className="btn btn-gold" style={{ flex: 1, background: "#7C3AED", borderColor: "#7C3AED" }} onClick={confirmInterview} disabled={!interviewDate}>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="4" width="18" height="18" rx="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></svg>
                Confirm Interview
              </button>
            </div>
          </div>
        </div>
      )}

      <Toast />
      </div>
    </div>
  );
};

// ChangePasswordForm — used by both employer account and admin settings
const ChangePasswordForm = () => {
  const [form, setForm] = useState({ newPassword: "", confirm: "" });
  const [loading, setLoading] = useState(false);
  const handle = async () => {
    if (form.newPassword.length < 6) { toast("Password must be at least 6 characters.", "error"); return; }
    if (form.newPassword !== form.confirm) { toast("Passwords do not match.", "error"); return; }
    setLoading(true);
    try {
      const { error } = await supabase.auth.updateUser({ password: form.newPassword });
      if (error) throw error;
      toast("Password updated successfully!", "success");
      setForm({ newPassword: "", confirm: "" });
    } catch (err) { toast(err.message, "error"); }
    finally { setLoading(false); }
  };
  return (
    <div className="flex-col gap-3">
      <div>
        <label className="label">New Password</label>
        <input className="input" type="password" placeholder="Min. 6 characters" value={form.newPassword} onChange={e => setForm({ ...form, newPassword: e.target.value })} />
      </div>
      <div>
        <label className="label">Confirm Password</label>
        <input className="input" type="password" placeholder="Repeat new password" value={form.confirm} onChange={e => setForm({ ...form, confirm: e.target.value })} />
      </div>
      <button className="btn btn-gold btn-sm" style={{ alignSelf: "flex-start" }} disabled={loading} onClick={handle}>
        {loading ? <><div className="spinner" style={{ width: 14, height: 14 }} /> Saving...</> : "Update Password"}
      </button>
    </div>
  );
};

// EditJobModal — employer edits their own listing
const EditJobModal = ({ job, onClose, onSuccess }) => {
  const [form, setForm] = useState({
    title: job.title || "", location: job.location || "", work_type: job.work_type || "full_time",
    description: job.description || "", requirements: job.requirements || "",
    responsibilities: job.responsibilities || "", benefits: job.benefits || "",
    category: job.category || "",
    salary_min: job.salary_min || "", salary_max: job.salary_max || "",
    salary_visible: job.salary_visible !== false,
    status: job.status || "active",
  });
  const [loading, setLoading] = useState(false);
  const handle = async () => {
    if (!form.title.trim()) { toast("Job title is required.", "error"); return; }
    if (!form.description.trim()) { toast("Job description is required.", "error"); return; }
    if (!form.requirements.trim()) { toast("Requirements are required.", "error"); return; }
    setLoading(true);
    try {
      const { error } = await supabase.from("jobs").update({
        title: form.title, location: form.location, work_type: form.work_type,
        description: form.description, requirements: form.requirements,
        responsibilities: form.responsibilities, benefits: form.benefits,
        category: form.category,
        salary_min: form.salary_min ? Number(form.salary_min) : null,
        salary_max: form.salary_max ? Number(form.salary_max) : null,
        salary_visible: form.salary_visible,
        status: form.status,
      }).eq("id", job.id);
      if (error) throw error;
      onSuccess();
    } catch (err) { toast(err.message, "error"); }
    finally { setLoading(false); }
  };
  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="modal" style={{ maxWidth: 640 }} onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-6">
          <h2 className="font-display" style={{ fontSize: 20, fontWeight: 700 }}>Edit Job Listing</h2>
          <button className="btn btn-ghost btn-sm" onClick={onClose}><Icon name="x" size={16} /></button>
        </div>
        <div className="flex-col gap-4">
          <div>
            <label className="label">Job Title *</label>
            <input className="input" value={form.title} onChange={e => setForm({ ...form, title: e.target.value })} />
          </div>
          <div>
            <label className="label">Category</label>
            <select className="input" value={form.category} onChange={e => setForm({ ...form, category: e.target.value })}>
              <option value="">Select a category...</option>
              {JOB_CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
            </select>
          </div>
          <div className="grid-2">
            <div>
              <label className="label">Region</label>
              <select className="input" value={form.location} onChange={e => setForm({ ...form, location: e.target.value })}>
                <option value="Region 4 — Demerara-Mahaica (Georgetown)">Region 4 — Georgetown</option>
                <option value="Region 1 — Barima-Waini">Region 1 — Barima-Waini</option>
                <option value="Region 2 — Pomeroon-Supenaam">Region 2 — Pomeroon-Supenaam</option>
                <option value="Region 3 — Essequibo Islands-West Demerara">Region 3 — Essequibo Islands</option>
                <option value="Region 5 — Mahaica-Berbice">Region 5 — Mahaica-Berbice</option>
                <option value="Region 6 — East Berbice-Corentyne">Region 6 — East Berbice-Corentyne</option>
                <option value="Region 7 — Cuyuni-Mazaruni">Region 7 — Cuyuni-Mazaruni</option>
                <option value="Region 8 — Potaro-Siparuni">Region 8 — Potaro-Siparuni</option>
                <option value="Region 9 — Upper Takutu-Upper Essequibo (Lethem)">Region 9 — Lethem</option>
                <option value="Region 10 — Upper Demerara-Berbice (Linden)">Region 10 — Linden</option>
              </select>
            </div>
            <div>
              <label className="label">Work Type</label>
              <select className="input" value={form.work_type} onChange={e => setForm({ ...form, work_type: e.target.value })}>
                <option value="onsite">On-site</option>
                <option value="full_time">Full Time</option>
                <option value="part_time">Part Time</option>
                <option value="contract">Contract</option>
                <option value="remote">Remote</option>
                <option value="hybrid">Hybrid</option>
              </select>
            </div>
          </div>
          <div className="grid-2">
            <div>
              <label className="label">Salary Min (GYD)</label>
              <input className="input" type="number" value={form.salary_min} onChange={e => setForm({ ...form, salary_min: e.target.value })} placeholder="e.g. 150000" />
            </div>
            <div>
              <label className="label">Salary Max (GYD)</label>
              <input className="input" type="number" value={form.salary_max} onChange={e => setForm({ ...form, salary_max: e.target.value })} placeholder="e.g. 300000" />
            </div>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <input type="checkbox" id="edit_salary_visible" checked={form.salary_visible} onChange={e => setForm({ ...form, salary_visible: e.target.checked })} style={{ width: 16, height: 16, accentColor: "var(--gold)", cursor: "pointer" }} />
            <label htmlFor="edit_salary_visible" style={{ fontSize: 13, color: "var(--text2)", cursor: "pointer" }}>Show salary range publicly on the listing</label>
          </div>
          <div>
            <label className="label">Job Description *</label>
            <textarea className="input" value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} style={{ minHeight: 110 }} />
          </div>
          <div>
            <label className="label">Requirements *</label>
            <textarea className="input" value={form.requirements} onChange={e => setForm({ ...form, requirements: e.target.value })} style={{ minHeight: 80 }} />
          </div>
          <div>
            <label className="label">Responsibilities</label>
            <textarea className="input" placeholder="Day-to-day responsibilities..." value={form.responsibilities} onChange={e => setForm({ ...form, responsibilities: e.target.value })} style={{ minHeight: 80 }} />
          </div>
          <div>
            <label className="label">Benefits</label>
            <textarea className="input" placeholder="e.g. Health insurance, pension, remote Fridays..." value={form.benefits} onChange={e => setForm({ ...form, benefits: e.target.value })} style={{ minHeight: 60 }} />
          </div>
          <div>
            <label className="label">Status</label>
            <select className="input" value={form.status} onChange={e => setForm({ ...form, status: e.target.value })}>
              <option value="active">Active</option>
              <option value="paused">Paused</option>
              <option value="closed">Closed</option>
            </select>
          </div>
          <div className="flex gap-3" style={{ justifyContent: "flex-end", paddingTop: 8 }}>
            <button className="btn btn-outline" onClick={onClose}>Cancel</button>
            <button className="btn btn-gold" disabled={loading} onClick={handle}>
              {loading ? <><div className="spinner" style={{ width: 14, height: 14 }} /> Saving...</> : "Save Changes"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// Simple editable profile form
const CompanyProfileForm = ({ employer, onSave }) => {
  const [form, setForm] = useState({
    company_name: employer.company_name || "",
    contact_name: employer.contact_name || "",
    contact_phone: employer.contact_phone || "",
    secret_question: employer.secret_question || SECRET_QUESTIONS[0],
    secret_answer: employer.secret_answer || "",
  });
  const [saving, setSaving] = useState(false);
  const [showAnswer, setShowAnswer] = useState(false);
  const handleSave = async () => {
    if (!form.company_name.trim()) { toast("Company name is required.", "error"); return; }
    if (!form.contact_name.trim()) { toast("Contact name is required.", "error"); return; }
    if (!form.contact_phone.trim()) { toast("Phone number is required for account recovery.", "error"); return; }
    if (!form.secret_answer.trim()) { toast("Secret answer is required for account recovery.", "error"); return; }
    setSaving(true);
    await onSave({ ...form, secret_answer: form.secret_answer.trim().toLowerCase() });
    setSaving(false);
  };
  return (
    <div className="flex-col gap-4">
      <div>
        <label className="label">Company Name *</label>
        <input className="input" value={form.company_name} onChange={e => setForm({ ...form, company_name: e.target.value })} placeholder="e.g. Ramps & Rollers Ltd" />
      </div>
      <div className="grid-2">
        <div>
          <label className="label">Contact Name *</label>
          <input className="input" value={form.contact_name} onChange={e => setForm({ ...form, contact_name: e.target.value })} placeholder="Your full name" />
        </div>
        <div>
          <label className="label">WhatsApp / Phone *</label>
          <input className="input" value={form.contact_phone} onChange={e => setForm({ ...form, contact_phone: e.target.value })} placeholder="+592..." />
        </div>
      </div>
      <div style={{ height: 1, background: "var(--border2)" }} />
      <div>
        <div style={{ fontSize: 13, fontWeight: 600, color: "var(--text)", marginBottom: 4 }}>Account Recovery</div>
        <div style={{ fontSize: 12, color: "var(--text2)", marginBottom: 14, lineHeight: 1.5 }}>If you're ever locked out, JobSpot will verify you using your phone number and secret answer.</div>
        <div className="flex-col gap-3">
          <div>
            <label className="label">Secret Question *</label>
            <select className="input" value={form.secret_question} onChange={e => setForm({ ...form, secret_question: e.target.value })}>
              {SECRET_QUESTIONS.map(q => <option key={q} value={q}>{q}</option>)}
            </select>
          </div>
          <div>
            <label className="label">Your Answer *</label>
            <div style={{ position: "relative" }}>
              <input
                className="input"
                type={showAnswer ? "text" : "password"}
                placeholder="Your answer (case-insensitive)"
                value={form.secret_answer}
                onChange={e => setForm({ ...form, secret_answer: e.target.value })}
                style={{ paddingRight: 44 }}
              />
              <button type="button" onClick={() => setShowAnswer(s => !s)}
                style={{ position: "absolute", right: 12, top: "50%", transform: "translateY(-50%)", background: "none", border: "none", cursor: "pointer", color: "var(--text3)", fontSize: 12, fontWeight: 500 }}>
                {showAnswer ? "Hide" : "Show"}
              </button>
            </div>
          </div>
        </div>
      </div>
      <button className="btn btn-gold btn-sm" style={{ alignSelf: "flex-start" }} disabled={saving} onClick={handleSave}>
        {saving ? <><div className="spinner" style={{ width: 13, height: 13 }} /> Saving...</> : "Save Changes"}
      </button>
    </div>
  );
};


// ============================================================
// SEEKERS TAB — Admin: search + manage + reset password
// ============================================================
// ============================================================
// RESET PASSWORD MODAL — Admin: verify secret answer → set temp password
// ============================================================
const ResetPasswordModal = ({ user, userType, onClose }) => {
  // user has: id, email, secret_question, secret_answer, company_name / name
  const [step, setStep] = useState("verify"); // "verify" | "done"
  const [answer, setAnswer] = useState("");
  const [tempPassword, setTempPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const displayName = userType === "employer"
    ? (user.company_name || user.contact_email)
    : (user.name || user.email);

  const generatePassword = () => {
    const chars = "ABCDEFGHJKMNPQRSTUVWXYZabcdefghjkmnpqrstuvwxyz23456789";
    return Array.from({ length: 8 }, () => chars[Math.floor(Math.random() * chars.length)]).join("");
  };

  const handleVerify = async () => {
    setError("");
    const expected = (user.secret_answer || "").trim().toLowerCase();
    const given = answer.trim().toLowerCase();

    if (!given) { setError("Please enter the answer."); return; }
    if (!expected) { setError("This user has no secret answer set — cannot verify."); return; }
    if (given !== expected) { setError("Wrong answer. Try again."); return; }

    setLoading(true);
    const newPass = generatePassword();

    try {
      const res = await fetch(
        `${SUPABASE_URL}/functions/v1/reset-password`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "Authorization": `Bearer ${SUPABASE_ANON_KEY}`,
          },
          body: JSON.stringify({ user_id: user.id, new_password: newPass }),
        }
      );
      const data = await res.json();
      if (!res.ok || data.error) throw new Error(data.error || "Reset failed");
      setTempPassword(newPass);
      setStep("done");
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="modal" style={{ maxWidth: 440 }} onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-6">
          <h3 className="font-display" style={{ fontSize: 18, fontWeight: 700 }}>
            Reset Password
          </h3>
          <button className="btn btn-ghost btn-sm" onClick={onClose}>
            <Icon name="x" size={16} />
          </button>
        </div>

        {step === "verify" ? (
          <>
            <div style={{ marginBottom: 20 }}>
              <div style={{ fontSize: 13, color: "var(--text2)", marginBottom: 4 }}>Account</div>
              <div style={{ fontSize: 14, fontWeight: 600 }}>{displayName}</div>
              <div style={{ fontSize: 12, color: "var(--text3)" }}>{userType === "employer" ? user.contact_email : user.email}</div>
            </div>

            {user.secret_question ? (
              <>
                <div style={{ background: "var(--bg3)", border: "1px solid var(--border2)", borderRadius: 10, padding: "14px 16px", marginBottom: 20 }}>
                  <div style={{ fontSize: 10, fontWeight: 700, color: "var(--gold)", textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 6 }}>
                    Secret Question
                  </div>
                  <div style={{ fontSize: 14, color: "var(--text)", fontWeight: 500 }}>{user.secret_question}</div>
                </div>

                <div style={{ marginBottom: 20 }}>
                  <label style={{ fontSize: 12, fontWeight: 600, color: "var(--text2)", display: "block", marginBottom: 6 }}>
                    Their Answer
                  </label>
                  <input
                    className="input"
                    placeholder="Type what they tell you..."
                    value={answer}
                    onChange={e => { setAnswer(e.target.value); setError(""); }}
                    onKeyDown={e => e.key === "Enter" && handleVerify()}
                    autoFocus
                  />
                </div>

                {error && (
                  <div style={{ background: "rgba(248,81,73,0.1)", border: "1px solid rgba(248,81,73,0.3)", borderRadius: 8, padding: "10px 14px", marginBottom: 16, fontSize: 13, color: "var(--red)" }}>
                    {error}
                  </div>
                )}

                <div className="flex gap-3 justify-end">
                  <button className="btn btn-outline" onClick={onClose}>Cancel</button>
                  <button className="btn btn-gold" onClick={handleVerify} disabled={loading}>
                    {loading ? "Verifying..." : "Verify & Reset"}
                  </button>
                </div>
              </>
            ) : (
              <div style={{ color: "var(--text2)", fontSize: 14, marginBottom: 20 }}>
                This user has no secret question set. Cannot verify identity — ask them to contact you directly.
                <div className="flex justify-end mt-4">
                  <button className="btn btn-outline" onClick={onClose}>Close</button>
                </div>
              </div>
            )}
          </>
        ) : (
          <>
            <div style={{ background: "rgba(63,185,80,0.08)", border: "1px solid rgba(63,185,80,0.25)", borderRadius: 10, padding: "20px", marginBottom: 20, textAlign: "center" }}>
              <div style={{ fontSize: 12, color: "var(--text2)", marginBottom: 8 }}>✅ Identity verified! Temp password for {displayName}:</div>
              <div style={{ fontSize: 28, fontWeight: 800, fontFamily: "monospace", letterSpacing: "0.1em", color: "var(--green)", marginBottom: 8 }}>
                {tempPassword}
              </div>
              <button
                className="btn btn-outline btn-sm"
                onClick={() => { navigator.clipboard.writeText(tempPassword); toast("Copied!", "success"); }}
              >
                <Icon name="copy" size={13} /> Copy
              </button>
            </div>
            <p style={{ fontSize: 13, color: "var(--text2)", marginBottom: 20 }}>
              Give this temp password to <strong>{displayName}</strong>. They can log in with it and change their password from their account settings.
            </p>
            <div className="flex justify-end">
              <button className="btn btn-gold" onClick={onClose}>Done</button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

const SeekersTab = ({ seekers, onForceReset, onSuspend, onActivate }) => {
  const [search, setSearch] = useState("");
  const [expanded, setExpanded] = useState(null);

  const filtered = seekers.filter(s => {
    const q = search.toLowerCase();
    return !q ||
      s.name?.toLowerCase().includes(q) ||
      s.email?.toLowerCase().includes(q) ||
      s.phone?.toLowerCase().includes(q) ||
      s.region?.toLowerCase().includes(q);
  });

  const hasCv = (s) => !!(s.ai_parsed_data && s.cv_uploaded_at);
  const cvDaysLeft = (s) => {
    if (!s.cv_uploaded_at) return 0;
    return Math.max(0, 30 - Math.floor((Date.now() - new Date(s.cv_uploaded_at).getTime()) / (1000 * 60 * 60 * 24)));
  };

  return (
    <>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="font-display" style={{ fontSize: 24, fontWeight: 700, marginBottom: 4 }}>Seekers</h1>
          <p style={{ color: "var(--text2)", fontSize: 14 }}>
            {seekers.length} registered · {seekers.filter(s => hasCv(s)).length} with active CVs · {seekers.filter(s => s.status === "suspended").length} suspended
          </p>
        </div>
      </div>

      {/* Search */}
      <div className="flex items-center gap-3 mb-6" style={{ background: "var(--bg2)", border: "1px solid var(--border2)", borderRadius: 10, padding: "10px 16px" }}>
        <Icon name="search" size={16} color="var(--text3)" />
        <input
          className="input"
          style={{ background: "transparent", border: "none", padding: 0, boxShadow: "none", fontSize: 14 }}
          placeholder="Search by name, email, phone or region..."
          value={search}
          onChange={e => setSearch(e.target.value)}
        />
        {search && <button className="btn btn-ghost btn-sm" style={{ padding: "2px 8px" }} onClick={() => setSearch("")}><Icon name="x" size={14} /></button>}
      </div>

      {filtered.length === 0 ? (
        <EmptyState icon="user" title="No seekers found" subtitle={search ? `No results for "${search}"` : "No seekers have registered yet."} />
      ) : (
        <div className="flex-col gap-3">
          {filtered.map(s => {
            const isExpanded = expanded === s.id;
            const days = cvDaysLeft(s);
            const cvActive = hasCv(s) && days > 0;
            return (
              <div key={s.id} className="card" style={{ padding: 0, overflow: "hidden" }}>

                {/* Collapsed row */}
                <div className="flex items-center justify-between" style={{ padding: "16px 20px", cursor: "pointer" }} onClick={() => setExpanded(isExpanded ? null : s.id)}>
                  <div className="flex items-center gap-4">
                    {/* Avatar */}
                    <div style={{ width: 40, height: 40, borderRadius: "50%", background: "rgba(26,111,212,0.1)", border: "1px solid rgba(26,111,212,0.2)", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, fontFamily: "Syne", fontSize: 14, fontWeight: 700, color: "var(--blue)" }}>
                      {(s.name || "?").split(" ").map(n => n[0]).slice(0, 2).join("").toUpperCase()}
                    </div>
                    <div>
                      <div className="flex items-center gap-2 mb-1">
                        <span className="font-display" style={{ fontSize: 15, fontWeight: 600 }}>{s.name || "Unnamed"}</span>
                        {s.status === "suspended" && <span className="badge badge-red">Suspended</span>}
                        {cvActive
                          ? <span className="badge badge-green">CV Active · {days}d left</span>
                          : <span className="badge badge-gray">No CV</span>
                        }
                      </div>
                      <div className="flex items-center gap-3" style={{ flexWrap: "wrap" }}>
                        <span style={{ fontSize: 12, color: "var(--text3)" }}>{s.email}</span>
                        {s.phone && <span style={{ fontSize: 12, color: "var(--text3)" }}>· {s.phone}</span>}
                        {s.region && <span style={{ fontSize: 12, color: "var(--text3)" }}>· {s.region.split("—")[0].trim()}</span>}
                        <span style={{ fontSize: 12, color: "var(--text3)" }}>· Joined {timeAgo(s.created_at)}</span>
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-4">
                    {s.ai_parsed_data?.total_experience_years > 0 && (
                      <div style={{ textAlign: "center" }}>
                        <div className="font-display" style={{ fontSize: 16, fontWeight: 700, color: "var(--text)" }}>{s.ai_parsed_data.total_experience_years}y</div>
                        <div style={{ fontSize: 10, color: "var(--text3)", textTransform: "uppercase" }}>Exp</div>
                      </div>
                    )}
                    <Icon name={isExpanded ? "chevron_up" : "chevron_down"} size={16} color="var(--text3)" />
                  </div>
                </div>

                {/* Expanded panel */}
                {isExpanded && (
                  <div style={{ borderTop: "1px solid var(--border2)", background: "var(--bg3)" }}>

                    {/* Recovery info */}
                    <div style={{ padding: "14px 20px", borderBottom: "1px solid var(--border2)", display: "flex", flexWrap: "wrap", gap: 24 }}>
                      <div>
                        <div style={{ fontSize: 10, fontWeight: 700, color: "var(--text3)", textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 4 }}>Phone</div>
                        <div style={{ fontSize: 13, color: s.phone ? "var(--text)" : "var(--text3)", fontWeight: s.phone ? 500 : 400 }}>
                          {s.phone || "Not set"}
                          {s.phone && (
                            <button className="btn btn-ghost btn-sm" style={{ marginLeft: 8, padding: "2px 8px", fontSize: 11 }}
                              onClick={() => openWhatsApp(s.phone, `Hi ${s.name || ""}, this is JobSpot GY.`)}>
                              WhatsApp
                            </button>
                          )}
                        </div>
                      </div>
                      <div>
                        <div style={{ fontSize: 10, fontWeight: 700, color: "var(--text3)", textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 4 }}>Region</div>
                        <div style={{ fontSize: 13, color: "var(--text)" }}>{s.region || "—"}</div>
                      </div>
                      {s.ai_parsed_data?.seniority_level && (
                        <div>
                          <div style={{ fontSize: 10, fontWeight: 700, color: "var(--text3)", textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 4 }}>Seniority</div>
                          <div style={{ fontSize: 13, color: "var(--text)" }}>{s.ai_parsed_data.seniority_level}</div>
                        </div>
                      )}
                      {s.ai_parsed_data?.skills?.length > 0 && (
                        <div style={{ flex: 1, minWidth: 200 }}>
                          <div style={{ fontSize: 10, fontWeight: 700, color: "var(--text3)", textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 6 }}>Top Skills</div>
                          <div style={{ display: "flex", flexWrap: "wrap", gap: 4 }}>
                            {s.ai_parsed_data.skills.slice(0, 6).map((sk, i) => (
                              <span key={i} className="tag" style={{ fontSize: 11 }}>{sk}</span>
                            ))}
                          </div>
                        </div>
                      )}
                      <div style={{ flex: 1, minWidth: 220 }}>
                        <div style={{ fontSize: 10, fontWeight: 700, color: "var(--gold)", textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 4 }}>Secret Question</div>
                        <div style={{ fontSize: 12, color: "var(--text2)", marginBottom: 3 }}>{s.secret_question || <span style={{ color: "var(--text3)" }}>Not set</span>}</div>
                        <div style={{ fontSize: 13, color: s.secret_answer ? "var(--text)" : "var(--text3)", fontWeight: 600 }}>
                          Answer: {s.secret_answer || "Not set"}
                        </div>
                      </div>
                    </div>

                    {/* Action buttons */}
                    <div style={{ padding: "12px 20px", display: "flex", flexWrap: "wrap", gap: 10 }}>
                      <button className="btn btn-outline btn-sm" onClick={() => onForceReset(s)}>
                        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="11" width="18" height="11" rx="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/></svg>
                        Reset Password
                      </button>
                      <button className="btn btn-outline btn-sm" onClick={() => { navigator.clipboard.writeText(s.email); toast("Email copied!", "success"); }}>
                        <Icon name="copy" size={13} />Copy Email
                      </button>
                      {s.status === "suspended"
                        ? <button className="btn btn-green btn-sm" onClick={() => onActivate(s.id)}><Icon name="check" size={13} />Activate</button>
                        : <button className="btn btn-danger btn-sm" onClick={() => onSuspend(s.id, s.name || s.email)}>Suspend</button>
                      }
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </>
  );
};

// ============================================================
// EMPLOYERS TAB — Admin: search + manage + force-reset password
// ============================================================
const EmployersTab = ({ employers, jobs, onUpgrade, onSuspend, onActivate, onForceReset }) => {
  const [search, setSearch] = useState("");
  const [expanded, setExpanded] = useState(null);

  const filtered = employers.filter(e => {
    const q = search.toLowerCase();
    return !q ||
      e.company_name?.toLowerCase().includes(q) ||
      e.contact_email?.toLowerCase().includes(q) ||
      e.contact_name?.toLowerCase().includes(q);
  });

  return (
    <>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="font-display" style={{ fontSize: 24, fontWeight: 700, marginBottom: 4 }}>Employers</h1>
          <p style={{ color: "var(--text2)", fontSize: 14 }}>{employers.length} registered · {employers.filter(e => e.status !== "suspended").length} active</p>
        </div>
      </div>

      {/* Search bar */}
      <div className="flex items-center gap-3 mb-6" style={{ background: "var(--bg2)", border: "1px solid var(--border2)", borderRadius: 10, padding: "10px 16px" }}>
        <Icon name="search" size={16} color="var(--text3)" />
        <input
          className="input"
          style={{ background: "transparent", border: "none", padding: 0, boxShadow: "none", fontSize: 14 }}
          placeholder="Search by company, email or contact name..."
          value={search}
          onChange={e => setSearch(e.target.value)}
        />
        {search && (
          <button className="btn btn-ghost btn-sm" style={{ padding: "2px 8px" }} onClick={() => setSearch("")}>
            <Icon name="x" size={14} />
          </button>
        )}
      </div>

      {filtered.length === 0 ? (
        <EmptyState icon="users" title="No employers found" subtitle={search ? `No results for "${search}"` : "No employers have signed up yet."} />
      ) : (
        <div className="flex-col gap-3">
          {filtered.map(emp => {
            const empJobs = jobs.filter(j => j.employer_id === emp.id || j.employers?.id === emp.id);
            const isExpanded = expanded === emp.id;
            return (
              <div key={emp.id} className="card" style={{ padding: 0, overflow: "hidden" }}>
                <div className="flex items-center justify-between" style={{ padding: "16px 20px", cursor: "pointer" }} onClick={() => setExpanded(isExpanded ? null : emp.id)}>
                  <div className="flex items-center gap-4">
                    <div style={{ width: 40, height: 40, background: "rgba(201,168,76,0.1)", border: "1px solid rgba(201,168,76,0.15)", borderRadius: 10, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                      <Icon name="briefcase" size={18} color="var(--gold)" />
                    </div>
                    <div>
                      <div className="flex items-center gap-2 mb-1">
                        <span className="font-display" style={{ fontSize: 15, fontWeight: 600 }}>{emp.company_name}</span>
                        <PlanBadge plan={emp.plan} />
                        {emp.status === "suspended" && <span className="badge badge-red">Suspended</span>}
                      </div>
                      <div className="flex items-center gap-3">
                        <span style={{ fontSize: 12, color: "var(--text3)" }}>{emp.contact_email}</span>
                        {emp.contact_name && <span style={{ fontSize: 12, color: "var(--text3)" }}>· {emp.contact_name}</span>}
                        <span style={{ fontSize: 12, color: "var(--text3)" }}>· Joined {timeAgo(emp.created_at)}</span>
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-4">
                    <div style={{ textAlign: "center" }}>
                      <div className="font-display" style={{ fontSize: 18, fontWeight: 700, color: "var(--text)" }}>{empJobs.length}</div>
                      <div style={{ fontSize: 10, color: "var(--text3)", textTransform: "uppercase" }}>Jobs</div>
                    </div>
                    <Icon name={isExpanded ? "chevron_up" : "chevron_down"} size={16} color="var(--text3)" />
                  </div>
                </div>

                {isExpanded && (
                  <div style={{ borderTop: "1px solid var(--border2)", background: "var(--bg3)" }}>
                    {/* Recovery info panel */}
                    <div style={{ padding: "14px 20px", borderBottom: "1px solid var(--border2)", display: "flex", flexWrap: "wrap", gap: 24 }}>
                      <div>
                        <div style={{ fontSize: 10, fontWeight: 700, color: "var(--text3)", textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 4 }}>Phone</div>
                        <div style={{ fontSize: 13, color: emp.contact_phone ? "var(--text)" : "var(--text3)", fontWeight: emp.contact_phone ? 500 : 400 }}>
                          {emp.contact_phone || "Not set"}
                          {emp.contact_phone && (
                            <button className="btn btn-ghost btn-sm" style={{ marginLeft: 8, padding: "2px 8px", fontSize: 11 }}
                              onClick={() => openWhatsApp(emp.contact_phone, `Hi ${emp.contact_name || emp.company_name}, this is JobSpot GY.`)}>
                              WhatsApp
                            </button>
                          )}
                        </div>
                      </div>
                      <div>
                        <div style={{ fontSize: 10, fontWeight: 700, color: "var(--text3)", textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 4 }}>Plan Expires</div>
                        <div style={{ fontSize: 13, color: "var(--text)", fontWeight: 500 }}>
                          {emp.plan === "free" ? "—" : emp.plan_expires_at ? new Date(emp.plan_expires_at).toLocaleDateString("en-GY", { day: "numeric", month: "short", year: "numeric" }) : "No expiry set"}
                        </div>
                      </div>
                      <div style={{ flex: 1, minWidth: 200 }}>
                        <div style={{ fontSize: 10, fontWeight: 700, color: "var(--gold)", textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 4 }}>Secret Question</div>
                        <div style={{ fontSize: 12, color: "var(--text2)", marginBottom: 3 }}>{emp.secret_question || <span style={{ color: "var(--text3)" }}>Not set</span>}</div>
                        <div style={{ fontSize: 13, color: emp.secret_answer ? "var(--text)" : "var(--text3)", fontWeight: 600 }}>
                          Answer: {emp.secret_answer || "Not set"}
                        </div>
                      </div>
                    </div>
                    {/* Action buttons */}
                    <div style={{ padding: "12px 20px", display: "flex", flexWrap: "wrap", gap: 10 }}>
                      {["free", "starter", "professional"].filter(p => p !== emp.plan).map(p => (
                        <button key={p} className="btn btn-outline btn-sm" onClick={() => onUpgrade(emp.id, p)}>
                          <Icon name="crown" size={13} />Set {p.charAt(0).toUpperCase() + p.slice(1)}
                        </button>
                      ))}
                      {emp.status === "suspended"
                        ? <button className="btn btn-green btn-sm" onClick={() => onActivate(emp.id)}><Icon name="check" size={13} />Activate</button>
                        : <button className="btn btn-danger btn-sm" onClick={() => onSuspend(emp.id, emp.company_name)}>Suspend</button>
                      }
                      <button className="btn btn-outline btn-sm" onClick={() => { navigator.clipboard.writeText(emp.contact_email); toast("Email copied!", "success"); }}>
                        <Icon name="copy" size={13} />Copy Email
                      </button>
                      <button className="btn btn-outline btn-sm" onClick={() => onForceReset(emp)}>
                        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="11" width="18" height="11" rx="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/></svg>
                        Reset Password
                      </button>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </>
  );
};

// ============================================================
// PUBLIC JOBS PAGE — Featured Pro listings, SEO-friendly
// ============================================================
const JobsPage = ({ onBack, onSeekerLogin, seekerUser, onApply }) => {
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [expandedJob, setExpandedJob] = useState(null);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      const now = new Date().toISOString();
      const { data } = await supabase
        .from("jobs")
        .select("*, employers(company_name)")
        .eq("status", "active")
        .eq("featured", true)
        .gt("featured_until", now)
        .order("featured_until", { ascending: true })
        .limit(50);
      const filtered = (data || []).filter(j => !j.expires_at || new Date(j.expires_at) > new Date());
      setJobs(filtered);
      setLoading(false);
    };
    load();
  }, []);

  const getCompany = (job) => Array.isArray(job.employers) ? job.employers[0]?.company_name : job.employers?.company_name;

  const filtered = jobs.filter(job => {
    if (!search.trim()) return true;
    const q = search.toLowerCase();
    const company = (getCompany(job) || "").toLowerCase();
    return (
      job.title?.toLowerCase().includes(q) ||
      company.includes(q) ||
      job.location?.toLowerCase().includes(q) ||
      job.work_type?.toLowerCase().includes(q) ||
      job.category?.toLowerCase().includes(q)
    );
  });

  const daysLeft = (job) => job.featured_until
    ? Math.max(0, Math.ceil((new Date(job.featured_until) - new Date()) / (1000 * 60 * 60 * 24)))
    : 0;

  return (
    <div style={{ minHeight: "100vh", background: "#0E0D0B", color: "#FAF8F4", fontFamily: "DM Sans, sans-serif" }}>
      <style>{styles}</style>

      {/* Nav */}
      <nav className="page-nav" style={{ position: "sticky", top: 0, zIndex: 50, background: "rgba(14,13,11,0.92)", backdropFilter: "blur(20px)", borderBottom: "1px solid rgba(255,255,255,0.07)", padding: "0 48px", height: 56, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 16 }}>
          <button onClick={onBack} style={{ background: "none", border: "none", cursor: "pointer", color: "rgba(250,248,244,0.45)", fontSize: 13, fontFamily: "DM Sans, sans-serif", display: "flex", alignItems: "center", gap: 4 }}>← Back</button>
          <div style={{ width: 1, height: 18, background: "rgba(255,255,255,0.08)" }} />
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <div style={{ width: 26, height: 26, borderRadius: 7, overflow: "hidden" }}>
              <img src="/icon-512x512.png" alt="JobSpot GY" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
            </div>
            <span style={{ fontFamily: "Syne, sans-serif", fontSize: 15, fontWeight: 800, color: "#FAF8F4" }}>Job<span style={{ background: "linear-gradient(135deg,#B8922A,#D4A83C)", WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent" }}>Spot</span><span style={{ fontSize: 10, color: "rgba(250,248,244,0.3)", fontWeight: 400, marginLeft: 3 }}>GY</span></span>
          </div>
        </div>
        <div className="jobs-page-nav-actions" style={{ display: "flex", alignItems: "center", gap: 10 }}>
          {seekerUser ? (
            <button onClick={onApply} style={{ display: "inline-flex", alignItems: "center", gap: 6, padding: "7px 18px", background: "#B8922A", border: "none", borderRadius: 8, color: "#0E0D0B", fontFamily: "Syne, sans-serif", fontSize: 13, fontWeight: 700, cursor: "pointer" }}>
              <Icon name="zap" size={12} color="#0E0D0B" /> Find My Matches
            </button>
          ) : (
            <>
              <button onClick={() => onSeekerLogin({ loginMode: true })} style={{ background: "none", border: "1px solid rgba(255,255,255,0.12)", borderRadius: 8, padding: "6px 14px", color: "rgba(250,248,244,0.6)", fontSize: 13, cursor: "pointer", fontFamily: "DM Sans, sans-serif" }}>Sign in</button>
              <button onClick={() => onSeekerLogin({})} style={{ display: "inline-flex", alignItems: "center", gap: 6, padding: "7px 18px", background: "#B8922A", border: "none", borderRadius: 8, color: "#0E0D0B", fontFamily: "Syne, sans-serif", fontSize: 13, fontWeight: 700, cursor: "pointer" }}>
                <Icon name="zap" size={12} color="#0E0D0B" /> Apply Free
              </button>
            </>
          )}
        </div>
      </nav>

      {/* Header */}
      <div className="jobs-content" style={{ maxWidth: 900, margin: "0 auto", padding: "56px 48px 32px" }}>
        <div style={{ display: "inline-flex", alignItems: "center", gap: 7, background: "rgba(184,146,42,0.1)", border: "1px solid rgba(184,146,42,0.25)", borderRadius: 100, padding: "4px 14px", marginBottom: 20 }}>
          <div style={{ width: 6, height: 6, borderRadius: "50%", background: "#B8922A", animation: "pulse 2s ease infinite" }} />
          <span style={{ fontSize: 11, fontWeight: 700, color: "#B8922A", textTransform: "uppercase", letterSpacing: "0.1em" }}>Featured Vacancies</span>
        </div>
        <h1 style={{ fontFamily: "Syne, sans-serif", fontSize: "clamp(28px,4vw,42px)", fontWeight: 800, letterSpacing: "-0.02em", marginBottom: 12, lineHeight: 1.1 }}>
          Companies hiring<br /><span style={{ background: "linear-gradient(135deg,#B8922A,#D4A83C)", WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent" }}>in Guyana right now.</span>
        </h1>
        <p style={{ fontSize: 15, color: "rgba(250,248,244,0.45)", marginBottom: 32, maxWidth: 520, lineHeight: 1.7 }}>
          Every listing here is from a verified employer actively hiring. Upload your CV once — our AI matches you to every role instantly.
        </p>

        {/* Search bar */}
        <div style={{ display: "flex", alignItems: "center", gap: 12, background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 12, padding: "12px 18px", maxWidth: 560 }}>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="rgba(250,248,244,0.3)" strokeWidth="2"><circle cx="11" cy="11" r="8"/><path d="m21 21-4.35-4.35"/></svg>
          <input
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Search by job title, company, or region..."
            style={{ background: "none", border: "none", outline: "none", color: "#FAF8F4", fontSize: 14, fontFamily: "DM Sans, sans-serif", flex: 1 }}
          />
          {search && <button onClick={() => setSearch("")} style={{ background: "none", border: "none", cursor: "pointer", color: "rgba(250,248,244,0.3)", fontSize: 16 }}>x</button>}
        </div>
      </div>

      {/* Jobs list */}
      <div className="jobs-content" style={{ maxWidth: 900, margin: "0 auto", padding: "0 48px 80px" }}>
        {loading ? (
          <div style={{ display: "flex", justifyContent: "center", padding: 80 }}>
            <div className="spinner" style={{ width: 28, height: 28 }} />
          </div>
        ) : filtered.length === 0 ? (
          <div style={{ textAlign: "center", padding: "80px 0" }}>
            <div style={{ fontSize: 32, marginBottom: 16 }}>🔍</div>
            <div style={{ fontFamily: "Syne, sans-serif", fontSize: 18, fontWeight: 700, marginBottom: 8 }}>
              {search ? `No results for "${search}"` : "No featured jobs right now"}
            </div>
            <p style={{ color: "rgba(250,248,244,0.4)", fontSize: 14 }}>
              {search ? "Try a different search term." : "Check back soon — new listings are added regularly."}
            </p>
          </div>
        ) : (
          <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
            {/* Results count */}
            <div style={{ fontSize: 13, color: "rgba(250,248,244,0.35)", marginBottom: 4 }}>
              {filtered.length} featured {filtered.length === 1 ? "vacancy" : "vacancies"}{search ? ` matching "${search}"` : ""}
            </div>

            {filtered.map(job => {
              const company = getCompany(job);
              const isExpanded = expandedJob === job.id;
              const days = daysLeft(job);
              return (
                <div key={job.id} style={{ background: "#1A1714", border: "1px solid rgba(184,146,42,0.2)", borderRadius: 16, overflow: "hidden", transition: "all 0.2s ease", boxShadow: "0 2px 16px rgba(0,0,0,0.2)" }}>

                  {/* Card header — always visible */}
                  <div style={{ padding: "20px 24px", cursor: "pointer", display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 16 }}
                    onClick={() => setExpandedJob(isExpanded ? null : job.id)}>
                    <div style={{ flex: 1 }}>
                      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6, flexWrap: "wrap" }}>
                        <span style={{ fontFamily: "Syne, sans-serif", fontSize: 17, fontWeight: 700, color: "#FAF8F4" }}>{company || "Company"}</span>
                        <span style={{ fontSize: 9, fontWeight: 700, background: "rgba(184,146,42,0.15)", color: "#D4A83C", padding: "2px 7px", borderRadius: 4, border: "1px solid rgba(184,146,42,0.25)", textTransform: "uppercase", letterSpacing: "0.06em" }}>Featured</span>
                        {days <= 3 && days > 0 && <span style={{ fontSize: 10, fontWeight: 700, background: "rgba(217,48,37,0.12)", color: "#F87171", padding: "2px 7px", borderRadius: 4, border: "1px solid rgba(217,48,37,0.2)" }}>Expires in {days}d</span>}
                      </div>
                      <div style={{ fontSize: 15, fontWeight: 600, color: "#B8922A", marginBottom: 8 }}>{job.title}</div>
                      <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                        <span style={{ fontSize: 12, color: "rgba(250,248,244,0.4)", display: "flex", alignItems: "center", gap: 4 }}>
                          <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/><circle cx="12" cy="10" r="3"/></svg>
                          {job.location?.split("(")[0]?.trim() || "Guyana"}
                        </span>
                        {job.work_type && <span style={{ fontSize: 11, padding: "2px 8px", borderRadius: 20, background: "rgba(255,255,255,0.05)", border: "1px solid rgba(255,255,255,0.08)", color: "rgba(250,248,244,0.45)", textTransform: "capitalize" }}>{job.work_type.replace("_", " ")}</span>}
                        {job.salary_min && job.salary_visible && <span style={{ fontSize: 11, padding: "2px 8px", borderRadius: 20, background: "rgba(45,158,78,0.1)", border: "1px solid rgba(45,158,78,0.2)", color: "#4ADE80" }}>GYD {job.salary_min.toLocaleString()} – {(job.salary_max||0).toLocaleString()}/yr</span>}
                      </div>
                    </div>
                    <div style={{ display: "flex", alignItems: "center", gap: 12, flexShrink: 0 }}>
                      <div style={{ textAlign: "right" }}>
                        <div style={{ fontSize: 11, color: "rgba(250,248,244,0.25)", marginBottom: 2 }}>Featured for</div>
                        <div style={{ fontSize: 13, fontWeight: 600, color: days <= 3 ? "#F87171" : "rgba(250,248,244,0.5)" }}>{days}d left</div>
                      </div>
                      <div style={{ width: 28, height: 28, borderRadius: "50%", background: "rgba(255,255,255,0.05)", display: "flex", alignItems: "center", justifyContent: "center", transition: "transform 0.2s", transform: isExpanded ? "rotate(180deg)" : "rotate(0deg)" }}>
                        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="rgba(250,248,244,0.4)" strokeWidth="2.5"><polyline points="6 9 12 15 18 9"/></svg>
                      </div>
                    </div>
                  </div>

                  {/* Expanded content — public, full description for SEO */}
                  {isExpanded && (
                    <div style={{ borderTop: "1px solid rgba(255,255,255,0.06)", padding: "20px 24px", animation: "fadeUp 0.2s ease" }}>
                      {job.description && (
                        <div style={{ marginBottom: 20 }}>
                          <div style={{ fontSize: 10, fontWeight: 700, color: "rgba(250,248,244,0.35)", textTransform: "uppercase", letterSpacing: "0.07em", marginBottom: 8 }}>About the Role</div>
                          <p style={{ fontSize: 14, color: "rgba(250,248,244,0.65)", lineHeight: 1.8 }}>{job.description}</p>
                        </div>
                      )}
                      {job.requirements && (
                        <div style={{ marginBottom: 20 }}>
                          <div style={{ fontSize: 10, fontWeight: 700, color: "rgba(250,248,244,0.35)", textTransform: "uppercase", letterSpacing: "0.07em", marginBottom: 8 }}>Requirements</div>
                          <p style={{ fontSize: 14, color: "rgba(250,248,244,0.65)", lineHeight: 1.8 }}>{job.requirements}</p>
                        </div>
                      )}
                      {job.responsibilities && (
                        <div style={{ marginBottom: 20 }}>
                          <div style={{ fontSize: 10, fontWeight: 700, color: "rgba(250,248,244,0.35)", textTransform: "uppercase", letterSpacing: "0.07em", marginBottom: 8 }}>Responsibilities</div>
                          <p style={{ fontSize: 14, color: "rgba(250,248,244,0.65)", lineHeight: 1.8 }}>{job.responsibilities}</p>
                        </div>
                      )}
                      {job.benefits && (
                        <div style={{ marginBottom: 20 }}>
                          <div style={{ fontSize: 10, fontWeight: 700, color: "rgba(250,248,244,0.35)", textTransform: "uppercase", letterSpacing: "0.07em", marginBottom: 8 }}>Benefits</div>
                          <p style={{ fontSize: 14, color: "rgba(250,248,244,0.65)", lineHeight: 1.8 }}>{job.benefits}</p>
                        </div>
                      )}

                      {/* Apply CTA */}
                      <div style={{ borderTop: "1px solid rgba(255,255,255,0.06)", paddingTop: 20, display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 16 }}>
                        <div>
                          <div style={{ fontSize: 13, fontWeight: 600, color: "#FAF8F4", marginBottom: 3 }}>
                            {seekerUser ? "Ready to apply?" : "Want to apply for this role?"}
                          </div>
                          <div style={{ fontSize: 12, color: "rgba(250,248,244,0.35)" }}>
                            {seekerUser ? "Upload your CV and get your match score instantly." : "Create a free account — upload your CV and get AI-matched in seconds."}
                          </div>
                        </div>
                        <button
                          onClick={() => seekerUser ? onApply() : onSeekerLogin({})}
                          style={{ display: "inline-flex", alignItems: "center", gap: 8, padding: "12px 28px", background: "linear-gradient(135deg,#B8922A 0%,#D4A83C 50%,#B8922A 100%)", backgroundSize: "200% auto", animation: "shimmer 3s linear infinite", border: "none", borderRadius: 10, color: "#0E0D0B", fontFamily: "Syne, sans-serif", fontSize: 14, fontWeight: 800, cursor: "pointer", boxShadow: "0 4px 20px rgba(184,146,42,0.35)", whiteSpace: "nowrap" }}
                        >
                          <Icon name="zap" size={14} color="#0E0D0B" />
                          {seekerUser ? "Apply Now" : "Apply Free — Create Account"}
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

// ============================================================
// TERMS & CONDITIONS PAGE
// ============================================================
// ============================================================
// PRIVACY POLICY PAGE
// ============================================================
const PrivacyPage = ({ onBack }) => (
  <div style={{ minHeight: "100vh", background: "var(--bg)" }}>
    <style>{styles}</style>
    <nav className="page-nav" style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "18px 48px", borderBottom: "1px solid var(--border2)", background: "rgba(13,17,23,0.95)", backdropFilter: "blur(16px)", position: "sticky", top: 0, zIndex: 50 }}>
      <div className="flex items-center gap-3">
        <div style={{ width: 30, height: 30, borderRadius: 8, overflow: "hidden" }}>
          <img src="/icon-512x512.png" alt="JobSpot GY" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
        </div>
        <span className="font-display" style={{ fontSize: 17, fontWeight: 800 }}>JobSpot<span style={{ fontSize: 11, color: "var(--text3)", fontWeight: 400, marginLeft: 3 }}>GY</span></span>
      </div>
      <button className="btn btn-ghost btn-sm" onClick={onBack}>← Back to Home</button>
    </nav>

    <div className="terms-content" style={{ maxWidth: 780, margin: "0 auto", padding: "64px 48px 96px" }}>
      <div style={{ marginBottom: 48 }}>
        <div style={{ fontSize: 11, color: "var(--gold)", fontWeight: 700, letterSpacing: "0.12em", textTransform: "uppercase", marginBottom: 12 }}>Legal</div>
        <h1 className="font-display" style={{ fontSize: 40, fontWeight: 800, letterSpacing: "-0.02em", marginBottom: 12 }}>Privacy Policy</h1>
        <p style={{ fontSize: 14, color: "var(--text3)" }}>Last updated: May 2025 · Applies to JobSpot GY and the Skilled Trades directory.</p>
      </div>

      {[
        {
          title: "1. Introduction",
          body: `JobSpot GY ("we", "us", "our") is committed to protecting your privacy. This Privacy Policy explains what information we collect, how we use it, and your rights in relation to it. This policy applies to all users of the JobSpot GY platform and the Skilled Trades directory, whether accessed via our website or mobile application.`
        },
        {
          title: "2. Information We Collect",
          body: `We collect the following categories of information:\n\n• Job Seekers: Name, email address, phone number, CV/résumé content including work history, education, and skills. This is provided voluntarily when you upload your CV.\n\n• Employers: Name, company name, email address, phone number, and job listing details provided during account registration.\n\n• Skilled Tradespeople: Name, trade category, region, village/community, phone number, WhatsApp number, years of experience, daily rate, profile photo, and a short bio. This is provided voluntarily when registering a listing.\n\n• Reviewers: Name and phone number collected when submitting a review. Phone numbers are used solely to enforce a one-review-per-person limit and are never displayed publicly.\n\n• Usage Data: We may collect non-personal technical information such as device type, browser type, and general usage patterns to improve platform performance.`
        },
        {
          title: "3. How We Use Your Information",
          body: `We use the information we collect solely to provide and improve our services:\n\n• To match job seekers with relevant job listings using our automated matching technology.\n• To display tradesperson profiles to clients searching for skilled services in Guyana.\n• To allow employers to review matched candidates and manage job listings.\n• To verify review authenticity and prevent duplicate submissions.\n• To communicate with users regarding their account, listings, or support requests.\n• To improve platform features and user experience.`
        },
        {
          title: "4. We Do Not Sell or Share Your Information",
          body: `We do not sell, rent, trade, or share your personal information with any third party for marketing or commercial purposes. Your data is never passed to advertisers, data brokers, or any external organisations. Information collected on this platform is used exclusively to deliver our services to you.`
        },
        {
          title: "5. Data Storage & Security",
          body: `Your data is stored securely using Supabase, a trusted cloud database provider. We implement reasonable technical and organisational measures to protect your information from unauthorised access, loss, or disclosure. Profile photos are stored using secure cloud storage and are accessible only via a unique, non-guessable URL. While we take data security seriously, no system is completely impenetrable and we cannot guarantee absolute security.`
        },
        {
          title: "6. Data Retention",
          body: `Job seeker CV data is automatically deleted after 30 days or when a matched position is closed. Tradesperson listings remain active until the provider removes them or their listing expires. Employer account data is retained for the duration of the active subscription and for a reasonable period thereafter for record-keeping. Review data is retained to maintain the integrity of the platform's rating system. You may request deletion of your data at any time by contacting us.`
        },
        {
          title: "7. Third-Party Services",
          body: `Our platform uses the following third-party services to operate:\n\n• Supabase — database and file storage (supabase.com)\n• Vercel — web hosting and deployment (vercel.com)\n• Google Fonts — typography (fonts.google.com)\n• WhatsApp — direct communication between users and tradespeople (initiated by users voluntarily)\n\nThese services have their own privacy policies. We encourage you to review them. We do not control how these services handle data beyond what is required to deliver our platform.`
        },
        {
          title: "8. Children's Privacy",
          body: `JobSpot GY is not directed at children under the age of 13. We do not knowingly collect personal information from children. If you believe a child has provided us with personal information, please contact us immediately and we will take steps to remove that information.`
        },
        {
          title: "9. Your Rights",
          body: `You have the right to:\n\n• Access the personal information we hold about you.\n• Request correction of inaccurate information.\n• Request deletion of your personal information.\n• Withdraw consent for data processing at any time.\n\nTo exercise any of these rights, contact us at jobspotgy@gmail.com. We will respond within 5 business days.`
        },
        {
          title: "10. Changes to This Policy",
          body: `We may update this Privacy Policy from time to time to reflect changes in our practices or legal requirements. When we do, we will update the "Last updated" date at the top of this page. Continued use of the platform after changes constitutes your acceptance of the revised policy. We encourage you to review this page periodically.`
        },
        {
          title: "11. Contact Us",
          body: `If you have any questions, concerns, or requests regarding this Privacy Policy or how we handle your data, please contact us at:\n\nJobSpot GY\nEmail: jobspotgy@gmail.com\nGuyana\n\nWe aim to respond to all privacy-related enquiries within 2 business days.`
        },
      ].map((section, i) => (
        <div key={i} style={{ marginBottom: 36, paddingBottom: 36, borderBottom: i < 10 ? "1px solid var(--border2)" : "none" }}>
          <h2 className="font-display" style={{ fontSize: 18, fontWeight: 700, marginBottom: 12, color: "var(--text)" }}>{section.title}</h2>
          <p style={{ fontSize: 14, color: "var(--text2)", lineHeight: 1.8, whiteSpace: "pre-line" }}>{section.body}</p>
        </div>
      ))}

      <div style={{ background: "rgba(201,168,76,0.06)", border: "1px solid rgba(201,168,76,0.2)", borderRadius: 14, padding: "24px 28px", marginTop: 16 }}>
        <div className="font-display" style={{ fontSize: 15, fontWeight: 700, marginBottom: 8, color: "var(--gold)" }}>Questions about your privacy?</div>
        <p style={{ fontSize: 14, color: "var(--text2)", marginBottom: 12 }}>We take your privacy seriously. Reach out anytime and we'll be happy to help.</p>
        <a href="mailto:jobspotgy@gmail.com" className="btn btn-gold btn-sm">
          <Icon name="mail" size={14} /> jobspotgy@gmail.com
        </a>
      </div>
    </div>
  </div>
);

const TermsPage = ({ onBack }) => (
  <div style={{ minHeight: "100vh", background: "var(--bg)" }}>
    <style>{styles}</style>
    <nav className="page-nav" style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "18px 48px", borderBottom: "1px solid var(--border2)", background: "rgba(13,17,23,0.95)", backdropFilter: "blur(16px)", position: "sticky", top: 0, zIndex: 50 }}>
      <div className="flex items-center gap-3">
        <div style={{ width: 30, height: 30, borderRadius: 8, overflow: "hidden" }}>
          <img src="/icon-512x512.png" alt="JobSpot GY" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
        </div>
        <span className="font-display" style={{ fontSize: 17, fontWeight: 800 }}>JobSpot<span style={{ fontSize: 11, color: "var(--text3)", fontWeight: 400, marginLeft: 3 }}>GY</span></span>
      </div>
      <button className="btn btn-ghost btn-sm" onClick={onBack}>← Back to Home</button>
    </nav>

    <div className="terms-content" style={{ maxWidth: 780, margin: "0 auto", padding: "64px 48px 96px" }}>
      <div style={{ marginBottom: 48 }}>
        <div style={{ fontSize: 11, color: "var(--gold)", fontWeight: 700, letterSpacing: "0.12em", textTransform: "uppercase", marginBottom: 12 }}>Legal</div>
        <h1 className="font-display" style={{ fontSize: 40, fontWeight: 800, letterSpacing: "-0.02em", marginBottom: 12 }}>Terms & Conditions</h1>
        <p style={{ fontSize: 14, color: "var(--text3)" }}>Last updated: January 2025 · Effective immediately upon use of the platform.</p>
      </div>

      {[
        {
          title: "1. Acceptance of Terms",
          body: `By accessing or using JobSpot GY ("the Platform"), you agree to be bound by these Terms and Conditions. If you do not agree to these terms, please do not use the Platform. These terms apply to all users including job seekers, employers, and visitors.`
        },
        {
          title: "2. About JobSpot GY",
          body: `JobSpot GY is a smart job matching platform designed specifically for the Guyanese job market. We connect employers with qualified candidates using advanced matching technology. JobSpot GY is operated and maintained in Guyana. For enquiries, contact us at jobspotgy@gmail.com.`
        },
        {
          title: "3. For Job Seekers",
          body: `By uploading your CV to JobSpot GY, you grant us a limited, non-exclusive licence to process and analyse your CV for the purpose of job matching. Your CV will be matched against active job listings and your results will be presented to you. Employers may view your profile and contact information if you are matched to their vacancy. You may not submit CVs containing false, misleading, or fraudulent information. CVs and associated data are automatically deleted after 30 days or when a position is filled. JobSpot GY does not guarantee employment or interviews.`
        },
        {
          title: "4. For Employers",
          body: `Employers must register and maintain an account in good standing to post vacancies. All job listings must be genuine, lawful, and accurately described. You may not post listings that discriminate on the basis of race, gender, religion, nationality, disability, age, or any other protected characteristic under Guyanese law. Employers are responsible for making their own hiring decisions. JobSpot GY provides matching and ranking as a tool only — the final hiring decision always rests with the employer. Subscription fees are non-refundable once a plan has been activated. Plans expire as indicated at the time of purchase.`
        },
        {
          title: "5. Matching Technology & Fairness",
          body: `JobSpot GY uses automated matching technology to score and rank candidates based on skills, experience, and role requirements. Our matching engine evaluates professional qualifications only — it does not consider or factor in personal characteristics such as name, gender, age, ethnicity, or physical appearance. All hiring decisions are made exclusively by human employers. JobSpot GY is a tool to surface qualified candidates — not to make employment decisions. If you believe a match score is inaccurate or unfair, you may contact us at jobspotgy@gmail.com to raise a concern.`
        },
        {
          title: "6. Privacy & Data",
          body: `We collect only the information necessary to provide our service. Job seeker CV data is processed for matching purposes and is not sold to third parties. Employer account data is used to manage your subscription and listings. All CV data is automatically deleted after 30 days or upon position closure. By using the Platform you consent to your data being processed in accordance with this policy. For data removal requests, contact jobspotgy@gmail.com.`
        },
        {
          title: "7. Payments",
          body: `Employer subscription plans are paid in Guyanese Dollars (GYD) via MMG Mobile Money or Bank Transfer. Payment confirmation is required before plan activation. All payments are manually verified by JobSpot GY staff. Subscription plans are non-refundable once activated. JobSpot GY reserves the right to change pricing with reasonable notice.`
        },
        {
          title: "8. Prohibited Conduct",
          body: `You may not use the Platform to post fraudulent job listings, scrape data, attempt to reverse-engineer the matching technology, harass or contact other users outside of legitimate hiring purposes, or engage in any activity that violates Guyanese law. Violations may result in immediate account suspension without refund.`
        },
        {
          title: "9. Limitation of Liability",
          body: `JobSpot GY is provided on an "as is" basis. We do not guarantee uninterrupted service, specific match results, or employment outcomes. To the maximum extent permitted by Guyanese law, JobSpot GY shall not be liable for any indirect, incidental, or consequential damages arising from your use of the Platform.`
        },
        {
          title: "10. Changes to Terms",
          body: `JobSpot GY reserves the right to update these Terms at any time. Continued use of the Platform after changes constitutes acceptance of the revised Terms. We will make reasonable efforts to notify users of significant changes.`
        },
        {
          title: "11. Contact",
          body: `For questions, concerns, or support regarding these Terms or the Platform, please contact us at jobspotgy@gmail.com. We aim to respond within 2 business days.`
        },
      ].map((section, i) => (
        <div key={i} style={{ marginBottom: 36, paddingBottom: 36, borderBottom: i < 10 ? "1px solid var(--border2)" : "none" }}>
          <h2 className="font-display" style={{ fontSize: 18, fontWeight: 700, marginBottom: 12, color: "var(--text)" }}>{section.title}</h2>
          <p style={{ fontSize: 14, color: "var(--text2)", lineHeight: 1.8 }}>{section.body}</p>
        </div>
      ))}

      <div style={{ background: "rgba(201,168,76,0.06)", border: "1px solid rgba(201,168,76,0.2)", borderRadius: 14, padding: "24px 28px", marginTop: 16 }}>
        <div className="font-display" style={{ fontSize: 15, fontWeight: 700, marginBottom: 8, color: "var(--gold)" }}>Questions about these Terms?</div>
        <p style={{ fontSize: 14, color: "var(--text2)", marginBottom: 12 }}>We're happy to clarify anything. Reach out anytime.</p>
        <a href="mailto:jobspotgy@gmail.com" className="btn btn-gold btn-sm">
          <Icon name="mail" size={14} /> jobspotgy@gmail.com
        </a>
      </div>
    </div>

    <footer style={{ borderTop: "1px solid var(--border2)", padding: "24px 48px", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
      <span className="font-display" style={{ fontSize: 13, fontWeight: 700 }}>JobSpot <span style={{ color: "var(--text3)", fontWeight: 400, fontSize: 11 }}>GY</span></span>
      <span style={{ fontSize: 12, color: "var(--text3)" }}>© 2025 JobSpot GY. All rights reserved.</span>
    </footer>
  </div>
);

// ============================================================
// ADMIN PANEL
// ============================================================
const AdminPanel = ({ user, onLogout }) => {
  const [tab, setTab] = useState("overview");
  const [analytics, setAnalytics] = useState(null);
  const [employers, setEmployers] = useState([]);
  const [seekers, setSeekers] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [payments, setPayments] = useState([]);
  const [upgradeRequests, setUpgradeRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [confirm, setConfirm] = useState(null);
  const [resetTarget, setResetTarget] = useState(null); // { user, userType }
  const [tradeProviders, setTradeProviders] = useState([]);
  const [tradesLoading, setTradesLoading] = useState(false);
  const [providerPasswords, setProviderPasswords] = useState({});  // id → new password string
  const [providerSaving, setProviderSaving] = useState({});        // id → bool
  const [tradesSearch, setTradesSearch] = useState("");
  const [announcement, setAnnouncement] = useState("");
  const [jobSearch, setJobSearch] = useState("");
  const [settings, setSettings] = useState({
    whatsapp_primary: "", whatsapp_secondary: "", active_whatsapp: "primary",
    mmg_enabled: "true", bank_enabled: "true",
    mmg_name: "", mmg_phone: "", mmg_details: "MMG Mobile Money",
    bank_name: "", bank_bank: "", bank_account: "", bank_routing: "", bank_details: "Republic Bank · Acc: XXXX-XXXX",
    registrations_enabled: "true", maintenance_mode: "false", announcement: "", announcement_expires: "",
  });

  useEffect(() => { loadAll(); }, []);

  useEffect(() => {
    if (tab === "trades" && tradeProviders.length === 0) loadTradeProviders();
  }, [tab]);

  const loadTradeProviders = async () => {
    setTradesLoading(true);
    const { data, error } = await supabase
      .from("service_providers")
      .select("id, name, trade, trades, region, phone, email, status, deleted_at, secret_question, password_plain, avg_rating, review_count, created_at, kyc_id_url, kyc_selfie_url, rejection_reason")
      .order("created_at", { ascending: false });
    if (!error) setTradeProviders(data || []);
    setTradesLoading(false);
  };

  const deleteProvider = async (id) => {
    const { error } = await supabase.from("service_providers")
      .update({ deleted_at: new Date().toISOString(), status: "suspended" })
      .eq("id", id);
    if (!error) {
      setTradeProviders(p => p.filter(x => x.id !== id));
      toast("Provider removed from public listing. You can restore them anytime.", "success");
    } else { toast("Failed to remove provider: " + error.message, "error"); }
  };

  const restoreProvider = async (id) => {
    const { error } = await supabase.from("service_providers")
      .update({ deleted_at: null, status: "active" })
      .eq("id", id);
    if (!error) {
      setTradeProviders(p => p.map(x => x.id === id ? { ...x, deleted_at: null, status: "active" } : x));
      toast("Provider restored and live again.", "success");
    } else { toast("Failed to restore provider: " + error.message, "error"); }
  };

  const activateProvider = async (id) => {
    const { error } = await supabase.from("service_providers").update({ status: "active" }).eq("id", id);
    if (!error) {
      setTradeProviders(p => p.map(x => x.id === id ? { ...x, status: "active" } : x));
      toast("Provider activated", "success");
    } else { toast("Failed to update status", "error"); }
  };

  const resetProviderPassword = async (id) => {
    const newPass = (providerPasswords[id] || "").trim();
    if (!newPass) { toast("Enter a new password first.", "error"); return; }
    setProviderSaving(s => ({ ...s, [id]: true }));
    const { error } = await supabase.from("service_providers").update({ password_plain: newPass }).eq("id", id);
    setProviderSaving(s => ({ ...s, [id]: false }));
    if (!error) {
      setTradeProviders(p => p.map(x => x.id === id ? { ...x, password_plain: newPass } : x));
      setProviderPasswords(pw => ({ ...pw, [id]: "" }));
      toast("Password updated!", "success");
    } else { toast("Failed to update password", "error"); }
  };

  const approveKyc = async (id) => {
    const { error } = await supabase
      .from("service_providers")
      .update({ status: "verified", rejection_reason: null })
      .eq("id", id);
    if (!error) {
      setTradeProviders(p => p.map(x => x.id === id ? { ...x, status: "verified", rejection_reason: null } : x));
      toast("KYC approved — provider is now verified ✓", "success");
    } else { toast("Failed to approve KYC", "error"); }
  };

  const rejectKyc = async (id, reason) => {
    if (!reason || !reason.trim()) { toast("Please enter a rejection reason.", "error"); return; }
    const { error } = await supabase
      .from("service_providers")
      .update({ status: "suspended", rejection_reason: reason.trim() })
      .eq("id", id);
    if (!error) {
      setTradeProviders(p => p.map(x => x.id === id ? { ...x, status: "suspended", rejection_reason: reason.trim() } : x));
      toast("KYC rejected — provider suspended.", "success");
    } else { toast("Failed to reject KYC", "error"); }
  };

  const loadAll = async () => {
    setLoading(true);
    const [{ data: a }, { data: e }, { data: j }, { data: p }, { data: sk }, ps] = await Promise.all([
      supabase.from("admin_analytics").select("*").maybeSingle(),
      supabase.from("employers").select("*").order("created_at", { ascending: false }),
      supabase.from("jobs").select("*, employers(company_name)").order("created_at", { ascending: false }),
      supabase.from("payments").select("*, employers(company_name)").order("created_at", { ascending: false }),
      supabase.from("seeker_profiles").select("*").order("created_at", { ascending: false }),
      loadPlatformSettings(),
    ]);
    setAnalytics(a);
    setEmployers(e || []);
    setJobs(j || []);
    setPayments(p || []);
    setSeekers(sk || []);
    setUpgradeRequests((p || []).filter(x => x.payment_status === "pending"));
    // Populate settings state from Supabase
    setSettings({
      whatsapp_primary:      ps.whatsapp_primary      || "",
      whatsapp_secondary:    ps.whatsapp_secondary    || "",
      active_whatsapp:       ps.active_whatsapp       || "primary",
      mmg_enabled:           ps.mmg_enabled           ?? "true",
      bank_enabled:          ps.bank_enabled          ?? "true",
      mmg_name:              ps.mmg_name              || "",
      mmg_phone:             ps.mmg_phone             || "",
      mmg_details:           ps.mmg_details           || "",
      bank_name:             ps.bank_name             || "",
      bank_bank:             ps.bank_bank             || "",
      bank_account:          ps.bank_account          || "",
      bank_routing:          ps.bank_routing          || "",
      bank_details:          ps.bank_details          || "",
      registrations_enabled: ps.registrations_enabled ?? "true",
      maintenance_mode:      ps.maintenance_mode      ?? "false",
      announcement:          ps.announcement          || "",
      announcement_expires:  ps.announcement_expires  || "",
    });
    setLoading(false);
  };

  const updateSetting = async (key, value) => {
    await saveSetting(key, value);
    setSettings(s => ({ ...s, [key]: value }));
  };

  const suspendEmployer = async (id) => {
    await supabase.from("employers").update({ status: "suspended" }).eq("id", id);
    setEmployers(e => e.map(x => x.id === id ? { ...x, status: "suspended" } : x));
    toast("Employer suspended", "success");
    setConfirm(null);
  };

  const activateEmployer = async (id) => {
    await supabase.from("employers").update({ status: "active" }).eq("id", id);
    setEmployers(e => e.map(x => x.id === id ? { ...x, status: "active" } : x));
    toast("Employer activated", "success");
  };

  const upgradePlan = async (id, plan) => {
    const expires = new Date(); expires.setFullYear(expires.getFullYear() + 1);
    const limits = { free: { job: 5, cand: 8 }, starter: { job: 8, cand: 15 }, professional: { job: null, cand: null } };
    await supabase.from("employers").update({ plan, plan_expires_at: expires.toISOString(), job_post_limit: limits[plan].job, candidate_view_limit: limits[plan].cand }).eq("id", id);
    setEmployers(e => e.map(x => x.id === id ? { ...x, plan } : x));
  };

  const approveUpgrade = async (payment) => {
    await supabase.from("payments").update({ payment_status: "paid" }).eq("id", payment.id);
    await upgradePlan(payment.employer_id, payment.plan);
    setUpgradeRequests(r => r.filter(x => x.id !== payment.id));
    setPayments(p => p.map(x => x.id === payment.id ? { ...x, payment_status: "paid" } : x));
    toast(`${payment.employers?.company_name} upgraded to ${payment.plan}!`, "success");
    setConfirm(null);
  };

  const rejectUpgrade = async (paymentId) => {
    await supabase.from("payments").update({ payment_status: "rejected" }).eq("id", paymentId);
    setUpgradeRequests(r => r.filter(x => x.id !== paymentId));
    setPayments(p => p.map(x => x.id === paymentId ? { ...x, payment_status: "rejected" } : x));
    toast("Upgrade request rejected.", "info");
    setConfirm(null);
  };

  const deleteJob = async (id) => {
    await supabase.from("jobs").delete().eq("id", id);
    setJobs(j => j.filter(x => x.id !== id));
    toast("Job deleted", "success");
    setConfirm(null);
  };

  const pendingCount = upgradeRequests.length;

  const navItems = [
    { id: "overview",  icon: "chart",     label: "Overview" },
    { id: "analytics", icon: "zap",       label: "Analytics" },
    { id: "upgrades",  icon: "crown",     label: "Upgrade Requests", count: pendingCount },
    { id: "employers", icon: "users",     label: "Employers" },
    { id: "seekers",   icon: "user",      label: "Seekers" },
    { id: "jobs",      icon: "briefcase", label: "All Jobs" },
    { id: "payments",  icon: "dollar",    label: "Payments" },
    { id: "trades",    icon: "zap",       label: "Trades Providers" },
    { id: "settings",  icon: "settings",  label: "Settings" },
  ];

  return (
    <div style={{ display: "flex", height: "100vh", background: "var(--bg)" }}>
      <style>{styles}</style>

      <aside className="sidebar">
        <div style={{ padding: "20px 16px 12px" }}>
          <div className="flex items-center gap-2 mb-1">
            <div style={{ width: 28, height: 28, borderRadius: 7, overflow: "hidden" }}>
              <img src="/icon-512x512.png" alt="JobSpot GY" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
            </div>
            <span className="font-display" style={{ fontSize: 15, fontWeight: 800 }}>JobSpot</span>
          </div>
          <div style={{ fontSize: 10, color: "var(--text3)", textTransform: "uppercase", letterSpacing: "0.1em", marginTop: 2, paddingLeft: 38 }}>Admin</div>
        </div>

        <div style={{ margin: "0 8px 12px", background: "rgba(201,168,76,0.06)", border: "1px solid rgba(201,168,76,0.2)", borderRadius: 10, padding: "10px 14px" }}>
          <div className="flex items-center gap-2">
            <Icon name="shield" size={14} color="var(--gold)" />
            <span style={{ fontSize: 12, color: "var(--gold)", fontWeight: 600 }}>Super Admin</span>
          </div>
          <div style={{ fontSize: 11, color: "var(--text3)", marginTop: 2 }}>{user.email}</div>
        </div>

        <nav style={{ flex: 1, padding: "4px 0" }}>
          {navItems.map(item => (
            <div key={item.id} className={`nav-item ${tab === item.id ? "active" : ""}`} onClick={() => setTab(item.id)}>
              <Icon name={item.icon} size={16} />
              {item.label}
              {item.count > 0 && <span className="notif-badge">{item.count}</span>}
              {item.id === "employers" && employers.length > 0 && !item.count && (
                <span style={{ marginLeft: "auto", background: "var(--bg3)", borderRadius: 10, padding: "1px 7px", fontSize: 11, color: "var(--text3)" }}>{employers.length}</span>
              )}
              {item.id === "seekers" && seekers.length > 0 && !item.count && (
                <span style={{ marginLeft: "auto", background: "var(--bg3)", borderRadius: 10, padding: "1px 7px", fontSize: 11, color: "var(--text3)" }}>{seekers.length}</span>
              )}
              {item.id === "trades" && tradeProviders.length > 0 && (
                <span style={{ marginLeft: "auto", background: "var(--bg3)", borderRadius: 10, padding: "1px 7px", fontSize: 11, color: "var(--text3)" }}>{tradeProviders.length}</span>
              )}
            </div>
          ))}
        </nav>

        <div style={{ padding: "12px 8px" }}>
          <div className="nav-item" onClick={onLogout}><Icon name="logout" size={16} />Sign out</div>
        </div>
      </aside>

      <main style={{ flex: 1, overflow: "auto", padding: "32px 40px" }}>

        {/* -- OVERVIEW -- */}
        {tab === "overview" && (
          <>
            <div className="flex items-center justify-between mb-6">
              <div>
                <h1 className="font-display" style={{ fontSize: 24, fontWeight: 700, marginBottom: 4 }}>Platform Overview</h1>
                <p style={{ color: "var(--text2)", fontSize: 14 }}>Real-time JobSpot metrics</p>
              </div>
              <button className="btn btn-outline btn-sm" onClick={loadAll}><Icon name="refresh" size={14} />Refresh</button>
            </div>
            {loading ? (
              <div className="grid-4 mb-6">{[1,2,3,4].map(i => <div key={i} className="skeleton" style={{ height: 100 }} />)}</div>
            ) : analytics && (
              <>
                <div className="grid-4 mb-6">
                  {[
                    { label: "Total Employers", value: analytics.total_employers, delta: `${analytics.active_employers} active`, up: true },
                    { label: "Active Jobs", value: analytics.active_jobs, delta: `of ${analytics.total_jobs} total`, up: true },
                    { label: "CVs Processed", value: analytics.total_applications, delta: `${analytics.applications_this_week} this week`, up: true },
                    { label: "Revenue (GYD)", value: formatGYD(analytics.total_revenue_gyd || 0), delta: `MRR: ${formatGYD(analytics.mrr_gyd || 0)}`, up: true },
                  ].map((s, i) => (
                    <div key={i} className="stat-card">
                      <div className="stat-label">{s.label}</div>
                      <div className="stat-value mt-2">{s.value}</div>
                      <div className={`stat-delta ${s.up ? "up" : "down"}`}>↑ {s.delta}</div>
                    </div>
                  ))}
                </div>

                {pendingCount > 0 && (
                  <div className="card mb-6" style={{ background: "rgba(201,168,76,0.04)", border: "1px solid rgba(201,168,76,0.3)" }}>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <Icon name="bell" size={18} color="var(--gold)" />
                        <div>
                          <div style={{ fontSize: 15, fontWeight: 600, color: "var(--gold)" }}>{pendingCount} upgrade request{pendingCount > 1 ? "s" : ""} waiting</div>
                          <div style={{ fontSize: 13, color: "var(--text2)" }}>Review and approve employer plan upgrades</div>
                        </div>
                      </div>
                      <button className="btn btn-gold btn-sm" onClick={() => setTab("upgrades")}>Review Now</button>
                    </div>
                  </div>
                )}

                <div className="grid-2 gap-4">
                  <div className="card">
                    <h3 className="font-display" style={{ fontSize: 16, fontWeight: 600, marginBottom: 16 }}>Employer Plans</h3>
                    {[
                      { plan: "Free", count: analytics.free_employers, color: "var(--text3)" },
                      { plan: "Starter", count: analytics.starter_employers, color: "var(--gold)" },
                      { plan: "Professional", count: analytics.professional_employers, color: "var(--blue)" },
                    ].map((p, i) => {
                      const pct = analytics.total_employers > 0 ? Math.round((p.count / analytics.total_employers) * 100) : 0;
                      return (
                        <div key={i} style={{ marginBottom: 14 }}>
                          <div className="flex justify-between mb-1">
                            <span style={{ fontSize: 13, color: "var(--text2)" }}>{p.plan}</span>
                            <span style={{ fontSize: 13, fontWeight: 600, color: "var(--text)" }}>{p.count} <span style={{ color: "var(--text3)", fontWeight: 400 }}>({pct}%)</span></span>
                          </div>
                          <div className="progress-bar">
                            <div className="progress-fill" style={{ width: `${pct}%`, background: p.color }} />
                          </div>
                        </div>
                      );
                    })}
                  </div>
                  <div className="card">
                    <h3 className="font-display" style={{ fontSize: 16, fontWeight: 600, marginBottom: 16 }}>Recent Employers</h3>
                    <div className="flex-col gap-3">
                      {employers.slice(0, 5).map(e => (
                        <div key={e.id} className="flex items-center justify-between">
                          <div>
                            <div style={{ fontSize: 14, fontWeight: 500, color: "var(--text)" }}>{e.company_name}</div>
                            <div style={{ fontSize: 12, color: "var(--text3)" }}>Joined {timeAgo(e.created_at)}</div>
                          </div>
                          <PlanBadge plan={e.plan} />
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </>
            )}
          </>
        )}

        {/* -- ANALYTICS WAR ROOM -- */}
        {tab === "analytics" && (
          <div style={{ maxWidth: 960 }}>
            <div className="flex items-center justify-between mb-6">
              <div>
                <h1 className="font-display" style={{ fontSize: 24, fontWeight: 700, marginBottom: 4 }}>Analytics</h1>
                <p style={{ color: "var(--text2)", fontSize: 14 }}>Your platform at a glance — live from Supabase</p>
              </div>
              <button className="btn btn-outline btn-sm" onClick={loadAll}><Icon name="refresh" size={14} />Refresh</button>
            </div>

            {/* Top KPI strip */}
            <div style={{ display: "grid", gridTemplateColumns: "repeat(5, minmax(0,1fr))", gap: 10, marginBottom: 24 }}>
              {[
                { label: "Total Employers", value: analytics?.total_employers ?? "—", color: "var(--gold)" },
                { label: "Paying Customers", value: ((analytics?.starter_employers ?? 0) + (analytics?.professional_employers ?? 0)), color: "var(--green)" },
                { label: "Conversion Rate", value: analytics?.total_employers > 0 ? Math.round(((analytics.starter_employers + analytics.professional_employers) / analytics.total_employers) * 100) + "%" : "—", color: "var(--blue)" },
                { label: "Total CVs Uploaded", value: analytics?.total_applications ?? "—", color: "var(--gold)" },
                { label: "Total Revenue (GYD)", value: analytics?.total_revenue_gyd ? formatGYD(analytics.total_revenue_gyd) : "0", color: "var(--green)" },
              ].map((k, i) => (
                <div key={i} style={{ background: "var(--bg2)", border: "1px solid var(--border2)", borderRadius: 10, padding: "14px 16px" }}>
                  <div style={{ fontSize: 10, color: "var(--text3)", textTransform: "uppercase", letterSpacing: "0.07em", fontWeight: 600, marginBottom: 8 }}>{k.label}</div>
                  <div className="font-display" style={{ fontSize: 22, fontWeight: 800, color: k.color, lineHeight: 1 }}>{k.value}</div>
                </div>
              ))}
            </div>

            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16, marginBottom: 16 }}>

              {/* Plan distribution — visual funnel */}
              <div style={{ background: "var(--bg2)", border: "1px solid var(--border2)", borderRadius: 12, padding: "20px 24px" }}>
                <div className="font-display" style={{ fontSize: 15, fontWeight: 700, marginBottom: 16 }}>Plan Distribution</div>
                {[
                  { label: "Free", count: analytics?.free_employers ?? 0, color: "var(--text3)", bg: "var(--bg3)" },
                  { label: "Starter", count: analytics?.starter_employers ?? 0, color: "var(--gold)", bg: "rgba(184,146,42,0.1)" },
                  { label: "Professional", count: analytics?.professional_employers ?? 0, color: "var(--blue)", bg: "var(--blue-bg)" },
                ].map((p, i) => {
                  const total = (analytics?.total_employers ?? 1);
                  const pct = Math.round((p.count / total) * 100);
                  return (
                    <div key={i} style={{ marginBottom: 14 }}>
                      <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 6 }}>
                        <span style={{ fontSize: 13, color: "var(--text2)", fontWeight: 500 }}>{p.label}</span>
                        <span style={{ fontSize: 13, fontWeight: 700, color: p.color }}>{p.count} <span style={{ fontSize: 11, color: "var(--text3)", fontWeight: 400 }}>({pct}%)</span></span>
                      </div>
                      <div style={{ height: 8, background: "var(--bg3)", borderRadius: 4, overflow: "hidden" }}>
                        <div style={{ height: "100%", borderRadius: 4, background: p.color, width: pct + "%", transition: "width 1s ease" }} />
                      </div>
                    </div>
                  );
                })}
                <div style={{ marginTop: 16, padding: "12px 0", borderTop: "1px solid var(--border2)" }}>
                  <div style={{ fontSize: 12, color: "var(--text3)", marginBottom: 4 }}>Free → Paid conversion</div>
                  <div className="font-display" style={{ fontSize: 22, fontWeight: 800, color: "var(--green)" }}>
                    {analytics?.total_employers > 0 ? Math.round(((( analytics?.starter_employers ?? 0) + (analytics?.professional_employers ?? 0)) / analytics.total_employers) * 100) : 0}%
                  </div>
                </div>
              </div>

              {/* Top jobs by applicants */}
              <div style={{ background: "var(--bg2)", border: "1px solid var(--border2)", borderRadius: 12, padding: "20px 24px" }}>
                <div className="font-display" style={{ fontSize: 15, fontWeight: 700, marginBottom: 16 }}>Top Jobs by Applicants</div>
                {jobs.length === 0 ? (
                  <div style={{ color: "var(--text3)", fontSize: 13, textAlign: "center", padding: "24px 0" }}>No jobs yet</div>
                ) : (
                  [...jobs].sort((a, b) => (b.application_count || 0) - (a.application_count || 0)).slice(0, 6).map((job, i) => {
                    const maxCount = Math.max(...jobs.map(j => j.application_count || 0), 1);
                    const pct = Math.round(((job.application_count || 0) / maxCount) * 100);
                    return (
                      <div key={job.id} style={{ marginBottom: 12 }}>
                        <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 4 }}>
                          <span style={{ fontSize: 12, color: "var(--text)", fontWeight: 500, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", maxWidth: "70%" }}>{job.title}</span>
                          <span style={{ fontSize: 12, fontWeight: 700, color: "var(--text2)", flexShrink: 0 }}>{job.application_count || 0} applicants</span>
                        </div>
                        <div style={{ height: 6, background: "var(--bg3)", borderRadius: 3, overflow: "hidden" }}>
                          <div style={{ height: "100%", borderRadius: 3, background: i === 0 ? "var(--gold)" : "var(--border)", width: pct + "%", transition: "width 1s ease" }} />
                        </div>
                      </div>
                    );
                  })
                )}
              </div>
            </div>

            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16, marginBottom: 16 }}>

              {/* Region breakdown — computed from jobs */}
              <div style={{ background: "var(--bg2)", border: "1px solid var(--border2)", borderRadius: 12, padding: "20px 24px" }}>
                <div className="font-display" style={{ fontSize: 15, fontWeight: 700, marginBottom: 16 }}>Active Jobs by Region</div>
                {(() => {
                  const regionCounts = {};
                  jobs.filter(j => j.status === "active").forEach(j => {
                    const r = j.location?.split("—")[0]?.trim() || "Unknown";
                    regionCounts[r] = (regionCounts[r] || 0) + 1;
                  });
                  const sorted = Object.entries(regionCounts).sort((a, b) => b[1] - a[1]);
                  const max = sorted[0]?.[1] || 1;
                  return sorted.length === 0 ? (
                    <div style={{ color: "var(--text3)", fontSize: 13, textAlign: "center", padding: "24px 0" }}>No active jobs</div>
                  ) : sorted.map(([region, count], i) => (
                    <div key={region} style={{ marginBottom: 10 }}>
                      <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 4 }}>
                        <span style={{ fontSize: 12, color: "var(--text2)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", maxWidth: "80%" }}>{region}</span>
                        <span style={{ fontSize: 12, fontWeight: 700, color: "var(--text)", flexShrink: 0 }}>{count}</span>
                      </div>
                      <div style={{ height: 5, background: "var(--bg3)", borderRadius: 3, overflow: "hidden" }}>
                        <div style={{ height: "100%", borderRadius: 3, background: i === 0 ? "var(--blue)" : "var(--border)", width: Math.round((count/max)*100) + "%", transition: "width 1s ease" }} />
                      </div>
                    </div>
                  ));
                })()}
              </div>

              {/* Revenue + payments */}
              <div style={{ background: "var(--bg2)", border: "1px solid var(--border2)", borderRadius: 12, padding: "20px 24px" }}>
                <div className="font-display" style={{ fontSize: 15, fontWeight: 700, marginBottom: 4 }}>Revenue Breakdown</div>
                <div style={{ fontSize: 12, color: "var(--text3)", marginBottom: 16 }}>Approved payments only</div>
                {[
                  { label: "Starter plans sold", count: payments.filter(p => p.payment_status === "paid" && p.plan === "starter").length, rate: "GYD 12,000", color: "var(--gold)" },
                  { label: "Professional plans sold", count: payments.filter(p => p.payment_status === "paid" && p.plan === "professional").length, rate: "GYD 36,000", color: "var(--blue)" },
                  { label: "Pending payments", count: payments.filter(p => p.payment_status === "pending").length, rate: "awaiting", color: "var(--amber)" },
                  { label: "Rejected payments", count: payments.filter(p => p.payment_status === "rejected").length, rate: "", color: "var(--red)" },
                ].map((r, i) => (
                  <div key={i} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "10px 0", borderBottom: i < 3 ? "1px solid var(--border2)" : "none" }}>
                    <div>
                      <div style={{ fontSize: 13, color: "var(--text)", fontWeight: 500 }}>{r.label}</div>
                      <div style={{ fontSize: 11, color: "var(--text3)" }}>{r.rate}</div>
                    </div>
                    <div className="font-display" style={{ fontSize: 20, fontWeight: 800, color: r.color }}>{r.count}</div>
                  </div>
                ))}
                <div style={{ marginTop: 16, padding: "14px 16px", background: "rgba(45,158,78,0.06)", border: "1px solid rgba(45,158,78,0.2)", borderRadius: 8 }}>
                  <div style={{ fontSize: 11, color: "var(--text3)", textTransform: "uppercase", letterSpacing: "0.07em", marginBottom: 4 }}>Total Collected</div>
                  <div className="font-display gold-text" style={{ fontSize: 26, fontWeight: 800 }}>
                    {formatGYD(
                      payments.filter(p => p.payment_status === "paid").reduce((sum, p) => sum + (p.amount_gyd || 0), 0)
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* Platform health */}
            <div style={{ background: "var(--bg2)", border: "1px solid var(--border2)", borderRadius: 12, padding: "20px 24px" }}>
              <div className="font-display" style={{ fontSize: 15, fontWeight: 700, marginBottom: 16 }}>Platform Health</div>
              <div style={{ display: "grid", gridTemplateColumns: "repeat(4, minmax(0,1fr))", gap: 12 }}>
                {[
                  { label: "Active Jobs", value: jobs.filter(j => j.status === "active").length, total: jobs.length, color: "var(--green)" },
                  { label: "Filled Positions", value: jobs.filter(j => j.status === "closed").length, total: jobs.length, color: "var(--blue)" },
                  { label: "Active Employers", value: employers.filter(e => e.status !== "suspended").length, total: employers.length, color: "var(--gold)" },
                  { label: "Suspended Accounts", value: employers.filter(e => e.status === "suspended").length, total: employers.length, color: "var(--red)" },
                ].map((h, i) => {
                  const pct = h.total > 0 ? Math.round((h.value / h.total) * 100) : 0;
                  return (
                    <div key={i} style={{ background: "var(--bg3)", borderRadius: 10, padding: "14px 16px" }}>
                      <div style={{ fontSize: 10, color: "var(--text3)", textTransform: "uppercase", letterSpacing: "0.06em", fontWeight: 600, marginBottom: 8 }}>{h.label}</div>
                      <div className="font-display" style={{ fontSize: 24, fontWeight: 800, color: h.color, lineHeight: 1, marginBottom: 8 }}>{h.value}</div>
                      <div style={{ height: 4, background: "var(--border2)", borderRadius: 2, overflow: "hidden" }}>
                        <div style={{ height: "100%", borderRadius: 2, background: h.color, width: pct + "%", transition: "width 1s ease" }} />
                      </div>
                      <div style={{ fontSize: 11, color: "var(--text3)", marginTop: 4 }}>{pct}% of {h.total}</div>
                    </div>
                  );
                })}
              </div>
            </div>

          </div>
        )}

        {/* -- UPGRADE REQUESTS -- */}
        {tab === "upgrades" && (
          <>
            <div className="flex items-center justify-between mb-6">
              <div>
                <h1 className="font-display" style={{ fontSize: 24, fontWeight: 700, marginBottom: 4 }}>Upgrade Requests</h1>
                <p style={{ color: "var(--text2)", fontSize: 14 }}>Review and approve employer plan upgrades · Manual payment verification</p>
              </div>
              {pendingCount > 0 && <span className="badge badge-amber">{pendingCount} pending</span>}
            </div>

            {upgradeRequests.length === 0 ? (
              <EmptyState icon="check" title="All clear" subtitle="No pending upgrade requests. Employers who request upgrades will appear here." />
            ) : (
              <div className="flex-col gap-4 mb-8">
                {upgradeRequests.map(req => {
                  const employer = employers.find(e => e.id === req.employer_id);
                  return (
                    <div key={req.id} className="upgrade-request-card">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-4">
                          <div style={{ width: 44, height: 44, background: "rgba(201,168,76,0.1)", border: "1px solid rgba(201,168,76,0.2)", borderRadius: 10, display: "flex", alignItems: "center", justifyContent: "center" }}>
                            <Icon name="crown" size={20} color="var(--gold)" />
                          </div>
                          <div>
                            <div className="font-display" style={{ fontSize: 16, fontWeight: 600 }}>{req.employers?.company_name || "Unknown"}</div>
                            <div className="flex items-center gap-3 mt-1">
                              <span className="badge badge-gold">{req.plan}</span>
                              <span style={{ fontSize: 13, color: "var(--text2)" }}>{formatGYD(req.amount_gyd)}</span>
                              <span className="badge badge-gray">{req.payment_method?.toUpperCase()}</span>
                              <span style={{ fontSize: 12, color: "var(--text3)" }}>{timeAgo(req.created_at)}</span>
                            </div>
                          </div>
                        </div>
                        <div className="flex gap-3">
                          {employer && (
                            <button className="btn btn-outline btn-sm" onClick={() => openWhatsApp(employer.contact_phone || "", `Hi ${employer.contact_name || employer.company_name}, we received your upgrade request for JobSpot ${req.plan} plan. Can you please confirm your payment reference?`)}>
                              <Icon name="whatsapp" size={13} />WhatsApp
                            </button>
                          )}
                          <button className="btn btn-danger btn-sm" onClick={() => setConfirm({ action: () => rejectUpgrade(req.id), title: "Reject Request", message: `Reject upgrade request from ${req.employers?.company_name}?`, danger: true })}>
                            Reject
                          </button>
                          <button className="btn btn-green btn-sm" onClick={() => setConfirm({ action: () => approveUpgrade(req), title: "Approve Upgrade", message: `Approve ${req.plan} plan for ${req.employers?.company_name}? This will activate their account immediately.`, danger: false })}>
                            <Icon name="check" size={14} /> Approve
                          </button>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}

            {/* Recently processed */}
            {payments.filter(p => p.payment_status !== "pending").length > 0 && (
              <>
                <h3 className="font-display" style={{ fontSize: 16, fontWeight: 600, marginBottom: 16, color: "var(--text2)" }}>Recently Processed</h3>
                <div className="card" style={{ padding: 0, overflow: "hidden" }}>
                  <table className="data-table">
                    <thead>
                      <tr><th>Company</th><th>Plan</th><th>Amount</th><th>Method</th><th>Status</th><th>Date</th></tr>
                    </thead>
                    <tbody>
                      {payments.filter(p => p.payment_status !== "pending").slice(0, 10).map(p => (
                        <tr key={p.id}>
                          <td style={{ fontWeight: 500, color: "var(--text)" }}>{p.employers?.company_name || "—"}</td>
                          <td><PlanBadge plan={p.plan} /></td>
                          <td style={{ fontWeight: 600, color: "var(--gold)" }}>{formatGYD(p.amount_gyd)}</td>
                          <td><span className="badge badge-gray">{p.payment_method}</span></td>
                          <td><span className={`badge ${p.payment_status === "paid" ? "badge-green" : "badge-red"}`}>{p.payment_status}</span></td>
                          <td style={{ fontSize: 12 }}>{new Date(p.created_at).toLocaleDateString()}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </>
            )}
          </>
        )}

        {/* -- EMPLOYERS -- */}
        {tab === "employers" && (
          <EmployersTab
            employers={employers}
            jobs={jobs}
            onUpgrade={upgradePlan}
            onSuspend={(id, name) => setConfirm({ action: () => suspendEmployer(id), title: "Suspend Employer", message: `Suspend ${name}? They won't be able to post jobs.`, danger: true })}
            onActivate={activateEmployer}
            onForceReset={(emp) => setResetTarget({ user: emp, userType: "employer" })}
          />
        )}

        {/* -- SEEKERS -- */}
        {tab === "seekers" && (
          <SeekersTab
            seekers={seekers}
            onForceReset={(sk) => setResetTarget({ user: sk, userType: "seeker" })}
            onSuspend={async (id, name) => {
              setConfirm({
                action: async () => {
                  await supabase.from("seeker_profiles").update({ status: "suspended" }).eq("id", id);
                  setSeekers(s => s.map(x => x.id === id ? { ...x, status: "suspended" } : x));
                  toast(`${name} suspended.`, "success");
                  setConfirm(null);
                },
                title: "Suspend Seeker",
                message: `Suspend ${name}? They won't be able to scan for jobs.`,
                danger: true,
              });
            }}
            onActivate={async (id) => {
              await supabase.from("seeker_profiles").update({ status: "active" }).eq("id", id);
              setSeekers(s => s.map(x => x.id === id ? { ...x, status: "active" } : x));
              toast("Seeker activated.", "success");
            }}
          />
        )}

        {/* -- JOBS -- */}
        {tab === "jobs" && (
          <>
            <div className="flex items-center justify-between mb-4">
              <h1 className="font-display" style={{ fontSize: 24, fontWeight: 700 }}>All Jobs</h1>
              <span className="badge badge-gray">{jobs.length} total</span>
            </div>

            {/* Search bar */}
            <div style={{ position: "relative", marginBottom: 16 }}>
              <Icon name="search" size={14} color="var(--text3)" style={{ position: "absolute", left: 12, top: "50%", transform: "translateY(-50%)", pointerEvents: "none" }} />
              <input
                className="input"
                placeholder="Search by job title, employer, or status..."
                value={jobSearch}
                onChange={e => setJobSearch(e.target.value)}
                style={{ paddingLeft: 36, fontSize: 14 }}
              />
              {jobSearch && (
                <button onClick={() => setJobSearch("")} style={{ position: "absolute", right: 12, top: "50%", transform: "translateY(-50%)", background: "none", border: "none", cursor: "pointer", color: "var(--text3)", fontSize: 16 }}>×</button>
              )}
            </div>

            <div className="card" style={{ padding: 0, overflow: "hidden" }}>
              <table className="data-table">
                <thead>
                  <tr><th>Title</th><th>Employer</th><th>Status</th><th>Type</th><th>Applicants</th><th>Posted</th><th>Actions</th></tr>
                </thead>
                <tbody>
                  {jobs
                    .filter(j => {
                      if (!jobSearch.trim()) return true;
                      const q = jobSearch.toLowerCase();
                      return (
                        j.title?.toLowerCase().includes(q) ||
                        j.employers?.company_name?.toLowerCase().includes(q) ||
                        j.status?.toLowerCase().includes(q) ||
                        j.work_type?.toLowerCase().includes(q)
                      );
                    })
                    .map(j => (
                    <tr key={j.id}>
                      <td style={{ fontWeight: 500, color: "var(--text)", maxWidth: 200 }} className="truncate">{j.title}</td>
                      <td style={{ fontSize: 13 }}>{j.employers?.company_name || "—"}</td>
                      <td><span className={`badge ${j.status === "active" ? "badge-green" : j.status === "paused" ? "badge-amber" : "badge-gray"}`}>{j.status}</span></td>
                      <td><span className="badge badge-gray">{j.work_type}</span></td>
                      <td style={{ fontWeight: 600, color: "var(--text)" }}>{j.application_count || 0}</td>
                      <td style={{ fontSize: 12 }}>{timeAgo(j.created_at)}</td>
                      <td>
                        <div className="flex gap-2">
                          <button className="btn btn-outline btn-sm" onClick={() => supabase.from("jobs").update({ status: j.status === "active" ? "paused" : "active" }).eq("id", j.id).then(loadAll)}>
                            {j.status === "active" ? "Pause" : "Activate"}
                          </button>
                          <button className="btn btn-danger btn-sm" onClick={() => setConfirm({ action: () => deleteJob(j.id), title: "Delete Job", message: `Delete "${j.title}"? This removes all applicants too.`, danger: true })}>Delete</button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {jobs.filter(j => {
                if (!jobSearch.trim()) return true;
                const q = jobSearch.toLowerCase();
                return j.title?.toLowerCase().includes(q) || j.employers?.company_name?.toLowerCase().includes(q) || j.status?.toLowerCase().includes(q);
              }).length === 0 && (
                <EmptyState icon="briefcase" title={jobSearch ? `No jobs matching "${jobSearch}"` : "No jobs posted yet"} subtitle={jobSearch ? "Try a different search term." : "Jobs will appear here when employers post them."} />
              )}
            </div>
          </>
        )}

        {/* -- PAYMENTS -- */}
        {tab === "payments" && (
          <>
            <div className="flex items-center justify-between mb-6">
              <div>
                <h1 className="font-display" style={{ fontSize: 24, fontWeight: 700, marginBottom: 4 }}>Payments</h1>
                <p style={{ color: "var(--text2)", fontSize: 14 }}>All transactions · Revenue tracking</p>
              </div>
            </div>
            <div className="grid-3 mb-6">
              {[
                { label: "Total Revenue (GYD)", value: formatGYD(payments.filter(p => p.payment_status === "paid").reduce((s, p) => s + (p.amount_gyd || 0), 0)) },
                { label: "Paid Transactions", value: payments.filter(p => p.payment_status === "paid").length },
                { label: "Pending", value: payments.filter(p => p.payment_status === "pending").length },
              ].map((s, i) => (
                <div key={i} className="stat-card">
                  <div className="stat-label">{s.label}</div>
                  <div className="stat-value mt-2" style={{ fontSize: 22 }}>{s.value}</div>
                </div>
              ))}
            </div>
            <div className="card" style={{ padding: 0, overflow: "hidden" }}>
              <table className="data-table">
                <thead>
                  <tr><th>Employer</th><th>Plan</th><th>Amount (GYD)</th><th>Method</th><th>Status</th><th>Date</th><th>Actions</th></tr>
                </thead>
                <tbody>
                  {payments.map(p => (
                    <tr key={p.id}>
                      <td style={{ fontWeight: 500, color: "var(--text)" }}>{p.employers?.company_name || "—"}</td>
                      <td><PlanBadge plan={p.plan} /></td>
                      <td style={{ fontWeight: 600, color: "var(--gold)", fontFamily: "Syne" }}>{formatGYD(p.amount_gyd)}</td>
                      <td><span className="badge badge-gray">{p.payment_method}</span></td>
                      <td><span className={`badge ${p.payment_status === "paid" ? "badge-green" : p.payment_status === "pending" ? "badge-amber" : "badge-red"}`}>{p.payment_status}</span></td>
                      <td style={{ fontSize: 12 }}>{new Date(p.created_at).toLocaleDateString()}</td>
                      <td>
                        {p.payment_status === "pending" && (
                          <div className="flex gap-2">
                            <button className="btn btn-danger btn-sm" onClick={() => rejectUpgrade(p.id)}>Reject</button>
                            <button className="btn btn-green btn-sm" onClick={() => approveUpgrade(p)}><Icon name="check" size={13} />Approve</button>
                          </div>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {payments.length === 0 && <EmptyState icon="dollar" title="No payments yet" subtitle="Payments will appear here when employers upgrade their plans." />}
            </div>
          </>
        )}

        {/* -- TRADES PROVIDERS -- */}
        {tab === "trades" && (
          <>
            <div className="flex items-center justify-between mb-6">
              <div>
                <h1 className="font-display" style={{ fontSize: 24, fontWeight: 700, marginBottom: 4 }}>Trades Providers</h1>
                <p style={{ color: "var(--text2)", fontSize: 14 }}>Manage skilled trades listings, reset passwords, and control account status.</p>
              </div>
              <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                <div style={{ position: "relative" }}>
                  <svg style={{ position: "absolute", left: 10, top: "50%", transform: "translateY(-50%)", pointerEvents: "none" }} width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="var(--text3)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
                  </svg>
                  <input
                    className="input"
                    type="text"
                    placeholder="Search by name…"
                    value={tradesSearch}
                    onChange={e => setTradesSearch(e.target.value)}
                    style={{ paddingLeft: 32, width: 220 }}
                  />
                  {tradesSearch && (
                    <button onClick={() => setTradesSearch("")} style={{ position: "absolute", right: 10, top: "50%", transform: "translateY(-50%)", background: "none", border: "none", cursor: "pointer", color: "var(--text3)", fontSize: 16, lineHeight: 1, padding: 0 }}>×</button>
                  )}
                </div>
                <button className="btn btn-outline btn-sm" onClick={loadTradeProviders} disabled={tradesLoading}>
                  <Icon name="refresh" size={14} />{tradesLoading ? "Loading…" : "Refresh"}
                </button>
              </div>
            </div>

            {tradesLoading ? (
              <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
                {[1,2,3,4].map(i => <div key={i} className="skeleton" style={{ height: 110, borderRadius: 12 }} />)}
              </div>
            ) : tradeProviders.length === 0 ? (
              <div className="card" style={{ textAlign: "center", padding: "60px 40px" }}>
                <div style={{ fontSize: 36, marginBottom: 12 }}>🔧</div>
                <div className="font-display" style={{ fontSize: 16, fontWeight: 600, marginBottom: 8 }}>No providers found</div>
                <p style={{ color: "var(--text2)", fontSize: 14 }}>No service_providers records in the database yet.</p>
              </div>
            ) : (
              <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>

                {/* ── KYC PENDING SECTION ── */}
                {(() => {
                  const kycPending = tradeProviders.filter(p => p.status === "kyc_pending");
                  if (kycPending.length === 0) return null;
                  return (
                    <div style={{ marginBottom: 8 }}>
                      <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 12 }}>
                        <div style={{ fontSize: 13, fontWeight: 700, color: "var(--amber)", textTransform: "uppercase", letterSpacing: "0.07em" }}>
                          🪪 KYC Pending Review
                        </div>
                        <span className="badge badge-amber" style={{ fontSize: 10 }}>{kycPending.length}</span>
                      </div>
                      <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
                        {kycPending.map(provider => {
                          // Use a keyed state via providerPasswords slot for rejection reason
                          const rejectKey = `reject_${provider.id}`;
                          const rejectReason = providerPasswords[rejectKey] || "";
                          const tradeLabel = [provider.trade, ...(Array.isArray(provider.trades) ? provider.trades : [])].filter(Boolean).join(", ") || "—";
                          const joinDate = provider.created_at ? new Date(provider.created_at).toLocaleDateString("en-GY", { year: "numeric", month: "short", day: "numeric" }) : "—";
                          return (
                            <div key={provider.id} className="card" style={{ padding: "20px 24px", border: "1px solid rgba(193,125,32,0.35)", background: "rgba(193,125,32,0.03)" }}>
                              <div className="flex items-center justify-between" style={{ flexWrap: "wrap", gap: 10, marginBottom: 14 }}>
                                <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                                  <div style={{ width: 40, height: 40, borderRadius: 10, background: "rgba(193,125,32,0.12)", border: "1px solid rgba(193,125,32,0.3)", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, fontSize: 18 }}>
                                    🪪
                                  </div>
                                  <div>
                                    <div style={{ fontSize: 15, fontWeight: 700, color: "var(--text)", lineHeight: 1.2 }}>{provider.name || "—"}</div>
                                    <div style={{ fontSize: 12, color: "var(--text3)", marginTop: 2 }}>{tradeLabel} · Joined {joinDate}</div>
                                  </div>
                                </div>
                                <span className="badge badge-amber" style={{ fontSize: 10 }}>● KYC Pending</span>
                              </div>

                              {/* KYC Document Previews */}
                              <div style={{ display: "flex", gap: 12, marginBottom: 14, flexWrap: "wrap" }}>
                                {provider.kyc_id_url && (
                                  <div>
                                    <div style={{ fontSize: 10, fontWeight: 600, color: "var(--text3)", textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: 4 }}>ID Document</div>
                                    <a href={provider.kyc_id_url} target="_blank" rel="noopener noreferrer">
                                      <img src={provider.kyc_id_url} alt="ID" style={{ width: 140, height: 90, objectFit: "cover", borderRadius: 8, border: "1px solid var(--border2)" }} />
                                    </a>
                                  </div>
                                )}
                                {provider.kyc_selfie_url && (
                                  <div>
                                    <div style={{ fontSize: 10, fontWeight: 600, color: "var(--text3)", textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: 4 }}>Selfie</div>
                                    <a href={provider.kyc_selfie_url} target="_blank" rel="noopener noreferrer">
                                      <img src={provider.kyc_selfie_url} alt="Selfie" style={{ width: 140, height: 90, objectFit: "cover", borderRadius: 8, border: "1px solid var(--border2)" }} />
                                    </a>
                                  </div>
                                )}
                                {!provider.kyc_id_url && !provider.kyc_selfie_url && (
                                  <div style={{ fontSize: 13, color: "var(--text3)", fontStyle: "italic" }}>No documents uploaded yet.</div>
                                )}
                              </div>

                              {/* Approve / Reject */}
                              <div style={{ display: "flex", alignItems: "flex-end", gap: 10, flexWrap: "wrap" }}>
                                <button
                                  className="btn btn-green btn-sm"
                                  onClick={() => setConfirm({ title: "Approve KYC", message: `Approve KYC for ${provider.name}? They will become an active provider.`, action: () => { approveKyc(provider.id); setConfirm(null); } })}
                                >
                                  ✓ Approve
                                </button>
                                <div style={{ flex: "1 1 200px" }}>
                                  <input
                                    className="input"
                                    type="text"
                                    placeholder="Rejection reason…"
                                    value={rejectReason}
                                    onChange={e => setProviderPasswords(pw => ({ ...pw, [rejectKey]: e.target.value }))}
                                    style={{ fontSize: 13 }}
                                  />
                                </div>
                                <button
                                  className="btn btn-danger btn-sm"
                                  disabled={!rejectReason.trim()}
                                  onClick={() => setConfirm({ title: "Reject KYC", message: `Reject KYC for ${provider.name} and suspend their account?`, action: () => { rejectKyc(provider.id, rejectReason); setProviderPasswords(pw => ({ ...pw, [rejectKey]: "" })); setConfirm(null); } })}
                                >
                                  ✕ Reject
                                </button>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  );
                })()}

                {/* ── REGULAR PROVIDER CARDS ── */}
                {(() => {
                  const filtered = (tradesSearch.trim()
                    ? tradeProviders.filter(p => (p.name || "").toLowerCase().includes(tradesSearch.trim().toLowerCase()))
                    : tradeProviders
                  ).filter(p => p.status !== "kyc_pending");
                  if (filtered.length === 0 && !tradesSearch.trim()) return null;
                  if (filtered.length === 0) return (
                    <div style={{ textAlign: "center", padding: "40px 20px", color: "var(--text3)", fontSize: 14 }}>
                      No providers match "<strong style={{ color: "var(--text2)" }}>{tradesSearch}</strong>"
                    </div>
                  );
                  return filtered.map(provider => {
                  const tradeLabel = [provider.trade, ...(Array.isArray(provider.trades) ? provider.trades : [])].filter(Boolean).join(", ") || "—";
                  const isActive   = ["active", "verified"].includes(provider.status || "active");
                  const joinDate   = provider.created_at ? new Date(provider.created_at).toLocaleDateString("en-GY", { year: "numeric", month: "short", day: "numeric" }) : "—";
                  const newPw      = providerPasswords[provider.id] || "";
                  const saving     = providerSaving[provider.id] || false;

                  return (
                    <div key={provider.id} className="card" style={{ padding: "20px 24px", border: isActive ? "1px solid var(--border2)" : "1px solid rgba(217,48,37,0.25)", background: isActive ? "var(--bg2)" : "rgba(217,48,37,0.02)" }}>

                      {/* Top row — identity + status badge */}
                      <div className="flex items-center justify-between" style={{ flexWrap: "wrap", gap: 10, marginBottom: 14 }}>
                        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                          <div style={{ width: 40, height: 40, borderRadius: 10, background: isActive ? "rgba(184,146,42,0.12)" : "rgba(217,48,37,0.1)", border: `1px solid ${isActive ? "rgba(184,146,42,0.25)" : "rgba(217,48,37,0.25)"}`, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                            <Icon name="zap" size={16} color={isActive ? "var(--gold)" : "var(--red)"} />
                          </div>
                          <div>
                            <div style={{ fontSize: 15, fontWeight: 700, color: "var(--text)", lineHeight: 1.2 }}>{provider.name || "—"}</div>
                            <div style={{ fontSize: 12, color: "var(--text3)", marginTop: 2 }}>Joined {joinDate}</div>
                          </div>
                        </div>

                        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                          <span className={`badge ${provider.status === "verified" ? "badge-blue" : isActive ? "badge-green" : "badge-red"}`} style={{ fontSize: 10 }}>
                            {provider.status === "verified" ? "● Verified" : isActive ? "● Active" : "● Suspended"}
                          </span>
                          {isActive && provider.status !== "verified" && (
                            <button className="btn btn-outline btn-sm" style={{ borderColor: "var(--blue)", color: "var(--blue)" }}
                              onClick={() => setConfirm({ title: "Manual KYC Verification", message: `Mark ${provider.name} as verified? They will receive a verified badge.`, action: async () => { const { error } = await supabase.from("service_providers").update({ status: "verified", rejection_reason: null }).eq("id", provider.id); if (!error) { setTradeProviders(p => p.map(x => x.id === provider.id ? { ...x, status: "verified" } : x)); toast(`${provider.name} is now verified ✓`, "success"); } else { toast("Failed to verify provider", "error"); } setConfirm(null); } })}>
                              🪪 Verify
                            </button>
                          )}
                          <button className="btn btn-danger btn-sm" onClick={() => setConfirm({ title: "Delete Provider", message: `Permanently delete ${provider.name}'s profile? This cannot be undone.`, danger: true, action: () => { deleteProvider(provider.id); setConfirm(null); } })}>
                            Delete
                          </button>
                        </div>
                      </div>

                      {/* Info grid */}
                      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(180px, 1fr))", gap: "8px 20px", marginBottom: 16 }}>
                        {[
                          { label: "Trade(s)",  value: tradeLabel },
                          { label: "Region",    value: provider.region  || "—" },
                          { label: "Phone",     value: provider.phone   || "—" },
                          { label: "Email",     value: provider.email   || "—" },
                          { label: "Rating",    value: provider.avg_rating != null ? `${Number(provider.avg_rating).toFixed(1)} ★  (${provider.review_count || 0} reviews)` : "No ratings yet" },
                        ].map(({ label, value }) => (
                          <div key={label}>
                            <div style={{ fontSize: 10, fontWeight: 600, color: "var(--text3)", textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: 2 }}>{label}</div>
                            <div style={{ fontSize: 13, color: "var(--text)", wordBreak: "break-word" }}>{value}</div>
                          </div>
                        ))}
                      </div>

                      {/* Secret question — identity verification */}
                      <div style={{ background: "rgba(201,168,76,0.06)", border: "1px solid rgba(201,168,76,0.18)", borderRadius: 8, padding: "10px 14px", marginBottom: 14 }}>
                        <div style={{ fontSize: 10, fontWeight: 700, color: "var(--gold)", textTransform: "uppercase", letterSpacing: "0.07em", marginBottom: 4 }}>
                          🔐 Identity Verification (Secret Question)
                        </div>
                        <div style={{ fontSize: 13, color: "var(--text2)" }}>
                          <span style={{ color: "var(--text3)" }}>Q: </span>{provider.secret_question || "No secret question set"}
                        </div>
                      </div>

                      {/* Password reset */}
                      <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
                        <div style={{ flex: "1 1 220px" }}>
                          <label style={{ fontSize: 10, fontWeight: 600, color: "var(--text3)", textTransform: "uppercase", letterSpacing: "0.05em", display: "block", marginBottom: 4 }}>Reset Password</label>
                          <input
                            className="input"
                            type="text"
                            placeholder="Type new password…"
                            value={newPw}
                            onChange={e => setProviderPasswords(pw => ({ ...pw, [provider.id]: e.target.value }))}
                            style={{ maxWidth: 280 }}
                          />
                        </div>
                        <button
                          className="btn btn-gold btn-sm"
                          style={{ marginTop: 18, flexShrink: 0 }}
                          disabled={saving || !newPw.trim()}
                          onClick={() => resetProviderPassword(provider.id)}
                        >
                          {saving ? <><div className="spinner" style={{ width: 13, height: 13 }} />Saving…</> : "Save Password"}
                        </button>
                        {provider.password_plain && (
                          <div style={{ marginTop: 18, fontSize: 12, color: "var(--text3)", flexShrink: 0 }}>
                            Current: <span style={{ fontFamily: "monospace", color: "var(--text2)", background: "var(--bg3)", padding: "1px 6px", borderRadius: 4 }}>{provider.password_plain}</span>
                          </div>
                        )}
                      </div>

                    </div>
                  );
                  });
                })()}
              </div>
            )}
          </>
        )}

        {/* -- SETTINGS -- */}
        {tab === "settings" && (
          <div style={{ maxWidth: 640 }}>
            <h1 className="font-display" style={{ fontSize: 24, fontWeight: 700, marginBottom: 24 }}>Platform Settings</h1>

            {/* WhatsApp numbers */}
            <div className="card mb-4">
              <h3 className="font-display" style={{ fontSize: 16, fontWeight: 600, marginBottom: 4 }}>WhatsApp Payment Numbers</h3>
              <p style={{ fontSize: 13, color: "var(--text2)", marginBottom: 20 }}>Employers send receipts to the active number. Switch anytime — no code changes needed.</p>

              <div className="flex-col gap-4 mb-4">
                {[
                  { key: "whatsapp_primary",   label: "Primary Number",   which: "primary" },
                  { key: "whatsapp_secondary",  label: "Secondary Number", which: "secondary" },
                ].map(n => (
                  <div key={n.key}>
                    <div className="flex items-center gap-2 mb-2">
                      <label className="label" style={{ margin: 0 }}>{n.label}</label>
                      {settings.active_whatsapp === n.which && <span className="badge badge-green" style={{ fontSize: 10 }}>● Active</span>}
                    </div>
                    <div className="flex gap-3">
                      <input className="input" value={settings[n.key]} placeholder="e.g. 5921234567 (digits only)" style={{ maxWidth: 260 }}
                        onChange={e => setSettings(s => ({ ...s, [n.key]: e.target.value }))} />
                      <button className="btn btn-gold btn-sm" onClick={async () => { await updateSetting(n.key, settings[n.key]); toast(`${n.label} saved!`, "success"); }}>Save</button>
                    </div>
                  </div>
                ))}
              </div>

              <div style={{ borderTop: "1px solid var(--border2)", paddingTop: 16 }}>
                <label className="label mb-2">Active number for payment receipts</label>
                <div className="flex gap-3">
                  {["primary", "secondary"].map(which => (
                    <button key={which} className={`btn btn-sm ${settings.active_whatsapp === which ? "btn-green" : "btn-outline"}`}
                      onClick={async () => { await updateSetting("active_whatsapp", which); setSettings(s => ({ ...s, active_whatsapp: which })); toast(`Now using ${which} number`, "success"); }}>
                      {settings.active_whatsapp === which ? "✓ " : ""}{which.charAt(0).toUpperCase() + which.slice(1)}: {settings[`whatsapp_${which}`] || "not set"}
                    </button>
                  ))}
                </div>
              </div>
            </div>

            {/* Payment methods + details */}
            <div className="card mb-4">
              <h3 className="font-display" style={{ fontSize: 16, fontWeight: 600, marginBottom: 16 }}>Payment Methods</h3>
              <div className="flex-col gap-6">

                {/* MMG Mobile Money */}
                <div style={{ paddingBottom: 20, borderBottom: "1px solid var(--border2)" }}>
                  <div className="flex items-center justify-between mb-4">
                    <div style={{ fontSize: 14, fontWeight: 600, color: "var(--text)" }}>MMG Mobile Money</div>
                    <button className={`btn btn-sm ${settings.mmg_enabled === "true" ? "btn-green" : "btn-outline"}`}
                      onClick={async () => {
                        const val = settings.mmg_enabled === "true" ? "false" : "true";
                        await updateSetting("mmg_enabled", val);
                        toast(`MMG ${val === "true" ? "enabled" : "disabled"}`, "success");
                      }}>
                      {settings.mmg_enabled === "true" ? "Enabled" : "Disabled"}
                    </button>
                  </div>
                  <div className="grid-2 gap-3 mb-3">
                    <div>
                      <label className="label">Account Name</label>
                      <input className="input" value={settings.mmg_name || ""} placeholder="e.g. JobSpot GY"
                        onChange={e => setSettings(s => ({ ...s, mmg_name: e.target.value }))} />
                    </div>
                    <div>
                      <label className="label">Phone Number</label>
                      <input className="input" value={settings.mmg_phone || ""} placeholder="e.g. 592-623-4521"
                        onChange={e => setSettings(s => ({ ...s, mmg_phone: e.target.value }))} />
                    </div>
                  </div>
                  <div className="mb-3">
                    <label className="label">Payment Instructions (shown to employers)</label>
                    <input className="input" value={settings.mmg_details || ""} placeholder="e.g. Send to: +592 623 4521 (JobSpot GY)"
                      onChange={e => setSettings(s => ({ ...s, mmg_details: e.target.value }))} />
                  </div>
                  <button className="btn btn-gold btn-sm" onClick={async () => {
                    await Promise.all([
                      updateSetting("mmg_name", settings.mmg_name || ""),
                      updateSetting("mmg_phone", settings.mmg_phone || ""),
                      updateSetting("mmg_details", settings.mmg_details || ""),
                    ]);
                    toast("MMG details saved!", "success");
                  }}>Save MMG Details</button>
                </div>

                {/* Bank Transfer */}
                <div>
                  <div className="flex items-center justify-between mb-4">
                    <div style={{ fontSize: 14, fontWeight: 600, color: "var(--text)" }}>Bank Transfer</div>
                    <button className={`btn btn-sm ${settings.bank_enabled === "true" ? "btn-green" : "btn-outline"}`}
                      onClick={async () => {
                        const val = settings.bank_enabled === "true" ? "false" : "true";
                        await updateSetting("bank_enabled", val);
                        toast(`Bank Transfer ${val === "true" ? "enabled" : "disabled"}`, "success");
                      }}>
                      {settings.bank_enabled === "true" ? "Enabled" : "Disabled"}
                    </button>
                  </div>
                  <div className="grid-2 gap-3 mb-3">
                    <div>
                      <label className="label">Account Name</label>
                      <input className="input" value={settings.bank_name || ""} placeholder="e.g. JobSpot GY Ltd"
                        onChange={e => setSettings(s => ({ ...s, bank_name: e.target.value }))} />
                    </div>
                    <div>
                      <label className="label">Bank Name</label>
                      <input className="input" value={settings.bank_bank || ""} placeholder="e.g. Republic Bank"
                        onChange={e => setSettings(s => ({ ...s, bank_bank: e.target.value }))} />
                    </div>
                  </div>
                  <div className="grid-2 gap-3 mb-3">
                    <div>
                      <label className="label">Account Number</label>
                      <input className="input" value={settings.bank_account || ""} placeholder="e.g. 1234-5678-9012"
                        onChange={e => setSettings(s => ({ ...s, bank_account: e.target.value }))} />
                    </div>
                    <div>
                      <label className="label">Routing / Transit Number</label>
                      <input className="input" value={settings.bank_routing || ""} placeholder="e.g. 030-001"
                        onChange={e => setSettings(s => ({ ...s, bank_routing: e.target.value }))} />
                    </div>
                  </div>
                  <div className="mb-3">
                    <label className="label">Payment Instructions (shown to employers)</label>
                    <input className="input" value={settings.bank_details || ""} placeholder="e.g. Republic Bank · Acc: 1234-5678 · JobSpot GY Ltd"
                      onChange={e => setSettings(s => ({ ...s, bank_details: e.target.value }))} />
                  </div>
                  <button className="btn btn-gold btn-sm" onClick={async () => {
                    await Promise.all([
                      updateSetting("bank_name", settings.bank_name || ""),
                      updateSetting("bank_bank", settings.bank_bank || ""),
                      updateSetting("bank_account", settings.bank_account || ""),
                      updateSetting("bank_routing", settings.bank_routing || ""),
                      updateSetting("bank_details", settings.bank_details || ""),
                    ]);
                    toast("Bank details saved!", "success");
                  }}>Save Bank Details</button>
                </div>

              </div>
            </div>

            {/* Platform controls */}
            <div className="card mb-4">
              <h3 className="font-display" style={{ fontSize: 16, fontWeight: 600, marginBottom: 16 }}>Platform Controls</h3>
              {[
                { key: "registrations_enabled", label: "New Registrations", desc: "Allow new employer signups" },
                { key: "maintenance_mode",       label: "Maintenance Mode",  desc: "Take the platform offline" },
              ].map((c, i, arr) => (
                <div key={i} className="flex items-center justify-between" style={{ padding: "14px 0", borderBottom: i < arr.length - 1 ? "1px solid var(--border2)" : "none" }}>
                  <div>
                    <div style={{ fontSize: 14, fontWeight: 500, color: "var(--text)" }}>{c.label}</div>
                    <div style={{ fontSize: 12, color: "var(--text3)" }}>{c.desc}</div>
                  </div>
                  <button className={`btn btn-sm ${settings[c.key] === "true" ? "btn-green" : "btn-outline"}`}
                    onClick={async () => {
                      const val = settings[c.key] === "true" ? "false" : "true";
                      await updateSetting(c.key, val);
                      toast(`${c.label} ${val === "true" ? "on" : "off"}`, "info");
                    }}>
                    {settings[c.key] === "true" ? "On" : "Off"}
                  </button>
                </div>
              ))}
            </div>

            {/* Announcement */}
            <div className="card mb-4">
              <h3 className="font-display" style={{ fontSize: 16, fontWeight: 600, marginBottom: 4 }}>Platform Announcement</h3>
              <p style={{ fontSize: 13, color: "var(--text2)", marginBottom: 14 }}>Broadcast a message to all employers on their next login.</p>
              <textarea className="input" placeholder="e.g. We've launched a new feature..." style={{ minHeight: 80, marginBottom: 12 }} value={announcement} onChange={e => setAnnouncement(e.target.value)} />
              <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                <button className="btn btn-gold btn-sm" onClick={async () => {
                  if (!announcement.trim()) { toast("Write a message first.", "error"); return; }
                  const expires = new Date(Date.now() + 48 * 60 * 60 * 1000).toISOString();
                  await updateSetting("announcement", announcement);
                  await updateSetting("announcement_expires", expires);
                  toast("Announcement sent — auto-expires in 48 hours.", "success");
                  setAnnouncement("");
                }}>
                  <Icon name="megaphone" size={14} />Send Announcement
                </button>
                {getSetting("announcement", "") && (
                  <button className="btn btn-outline btn-sm" onClick={async () => {
                    await updateSetting("announcement", "");
                    await updateSetting("announcement_expires", "");
                    toast("Announcement cleared.", "info");
                  }}>
                    Clear
                  </button>
                )}
              </div>
              {getSetting("announcement", "") && (
                <div style={{ fontSize: 12, color: "var(--text3)", marginTop: 8 }}>
                  Active: "{getSetting("announcement", "").slice(0, 60)}{getSetting("announcement","").length > 60 ? "..." : ""}"
                  {getSetting("announcement_expires","") && ` · Expires ${new Date(getSetting("announcement_expires","")).toLocaleString("en-GY", { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" })}`}
                </div>
              )}
            </div>

            {/* Admin change password */}
            <div className="card">
              <h3 className="font-display" style={{ fontSize: 16, fontWeight: 600, marginBottom: 4 }}>Change Admin Password</h3>
              <p style={{ fontSize: 13, color: "var(--text2)", marginBottom: 16 }}>Update your own admin account password.</p>
              <ChangePasswordForm />
            </div>
          </div>
        )}
      </main>

      {confirm && <ConfirmModal {...confirm} onConfirm={confirm.action} onCancel={() => setConfirm(null)} />}
      {resetTarget && <ResetPasswordModal user={resetTarget.user} userType={resetTarget.userType} onClose={() => setResetTarget(null)} />}
      <Toast />
    </div>
  );
};

// ============================================================
// UNIFIED AUTH — single login/signup hub for all user types
// ============================================================
const UnifiedAuth = ({ onSeekerAuth, onEmployerAuth, onTradesClick, onBack, prefill }) => {
  const [role, setRole] = useState(null);
  const [mode, setMode] = useState("login");
  const [loading, setLoading] = useState(false);
  const [showEmpPass, setShowEmpPass] = useState(false);
  const [showEmpConfirm, setShowEmpConfirm] = useState(false);
  const [showEmpAnswer, setShowEmpAnswer] = useState(false);
  const [showSeekPass, setShowSeekPass] = useState(false);
  const [showSeekConfirm, setShowSeekConfirm] = useState(false);
  const [showSeekAnswer, setShowSeekAnswer] = useState(false);

  const [empForm, setEmpForm] = useState({
    email: "", password: "", confirmPassword: "", company_name: "", contact_name: "",
    contact_phone: "", secret_question: SECRET_QUESTIONS[0], secret_answer: "",
  });

  const [seekForm, setSekForm] = useState({
    email: prefill?.email || "", password: "", confirmPassword: "",
    name: prefill?.analysis?.candidate?.name || "",
    phone: prefill?.phone || "",
    secret_question: SECRET_QUESTIONS[0], secret_answer: "",
  });

  const handleEmployer = async () => {
    if (!empForm.email || !empForm.password) { toast("Please fill in all fields.", "error"); return; }
    if (mode === "register") {
      if (empForm.password.length < 6) { toast("Password must be at least 6 characters.", "error"); return; }
      if (empForm.password !== empForm.confirmPassword) { toast("Passwords do not match.", "error"); return; }
    }
    setLoading(true);
    try {
      if (mode === "login") {
        if (empForm.email === "admin@jobspot.gy" && empForm.password === "JobSpotGY@2025!") {
          onEmployerAuth({ email: ADMIN_EMAIL, id: "admin-local", app_metadata: {}, user_metadata: {} }); return;
        }
        const { data, error } = await supabase.auth.signInWithPassword({ email: empForm.email, password: empForm.password });
        if (error) throw error;
        if (data.user?.user_metadata?.role === "seeker") {
          await supabase.auth.signOut(); toast("That's a job seeker account — switch role to sign in.", "error"); return;
        }
        onEmployerAuth(data.user);
      } else {
        if (!empForm.company_name || !empForm.contact_name || !empForm.contact_phone || !empForm.secret_answer) {
          toast("Please fill in all required fields.", "error"); return;
        }
        const { data, error } = await supabase.auth.signUp({ email: empForm.email, password: empForm.password, options: { data: { role: "employer" } } });
        if (error) throw error;
        if (data.user) {
          await supabase.from("employers").insert({
            id: data.user.id, company_name: empForm.company_name, contact_name: empForm.contact_name,
            contact_email: empForm.email, contact_phone: empForm.contact_phone || null,
            secret_question: empForm.secret_question, secret_answer: empForm.secret_answer.trim().toLowerCase(),
          });
          toast("Employer account created! Welcome to JobSpot.", "success");
          onEmployerAuth(data.user);
        }
      }
    } catch (err) { toast(err.message, "error"); } finally { setLoading(false); }
  };

  const handleSeeker = async () => {
    if (!seekForm.email || !seekForm.password) { toast("Please fill in all fields.", "error"); return; }
    if (mode === "register") {
      if (!seekForm.name.trim()) { toast("Please enter your name.", "error"); return; }
      if (seekForm.password.length < 6) { toast("Password must be at least 6 characters.", "error"); return; }
      if (seekForm.password !== seekForm.confirmPassword) { toast("Passwords do not match.", "error"); return; }
      if (!seekForm.phone.trim()) { toast("Phone number is required.", "error"); return; }
      if (!seekForm.secret_answer.trim()) { toast("Please answer your secret question.", "error"); return; }
    }
    setLoading(true);
    try {
      if (mode === "login") {
        const { data, error } = await supabase.auth.signInWithPassword({ email: seekForm.email, password: seekForm.password });
        if (error) throw error;
        if (data.user?.user_metadata?.role === "employer") {
          await supabase.auth.signOut(); toast("That's an employer account — switch role to sign in.", "error"); return;
        }
        onSeekerAuth(data.user);
      } else {
        const { data, error } = await supabase.auth.signUp({
          email: seekForm.email, password: seekForm.password,
          options: { data: { role: "seeker", name: seekForm.name } },
        });
        if (error) throw error;
        if (data.user) {
          await supabase.from("seeker_profiles").upsert({
            id: data.user.id, email: seekForm.email, name: seekForm.name || "",
            phone: seekForm.phone.trim(), region: prefill?.region || "",
            ai_parsed_data: prefill?.analysis?.candidate || null,
            cv_url: prefill?.cvUrl || null,
            cv_uploaded_at: prefill?.analysis?.candidate ? new Date().toISOString() : null,
            secret_question: seekForm.secret_question,
            secret_answer: seekForm.secret_answer.trim().toLowerCase(),
            updated_at: new Date().toISOString(),
          });
          toast("Account created! Welcome to JobSpot.", "success");
          onSeekerAuth(data.user);
        }
      }
    } catch (err) { toast(err.message, "error"); } finally { setLoading(false); }
  };

  const handleGoogleAuth = async () => {
    if (!role || role === "trades") return;
    localStorage.setItem("hirespot_pending_role", role);
    const { error } = await supabase.auth.signInWithOAuth({
      provider: "google",
      options: { redirectTo: `${window.location.origin}/auth/callback` },
    });
    if (error) toast(error.message, "error");
  };

  const roles = [
    { id: "seeker",   icon: "search",    label: "I'm looking for a job",   sub: "Find jobs that match your CV. Track every application.",          color: "#2D9E4E", colorBg: "rgba(45,158,78,0.12)",   colorBorder: "rgba(45,158,78,0.25)"   },
    { id: "employer", icon: "briefcase", label: "Corporate / Employer Hiring", sub: "Post jobs and get best-ranked candidates delivered to you.",        color: "#B8922A", colorBg: "rgba(184,146,42,0.12)",  colorBorder: "rgba(184,146,42,0.25)"  },
    { id: "trades",   icon: "hammer",    label: "I'm a Skilled Tradesman",  sub: "Create or access your listing on the trades marketplace.",       color: "#60A5FA", colorBg: "rgba(96,165,250,0.12)",   colorBorder: "rgba(96,165,250,0.25)"  },
  ];

  const selectedRole = roles.find(r => r.id === role);
  const inputStyle = { background: "rgba(255,255,255,0.05)", border: "1px solid rgba(255,255,255,0.1)", color: "#FAF8F4" };
  const labelStyle = { fontSize: 11, fontWeight: 600, color: "rgba(250,248,244,0.4)", textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: 6 };

  return (
    <div style={{ minHeight: "100vh", background: "#0E0D0B", backgroundImage: "radial-gradient(ellipse 80% 50% at 50% 0%, rgba(184,146,42,0.08) 0%, transparent 70%)", display: "flex", alignItems: "center", justifyContent: "center", padding: "40px 20px", position: "relative" }}>
      <style>{styles}</style>
      <button onClick={role ? () => setRole(null) : onBack} style={{ position: "absolute", top: 24, left: 24, background: "none", border: "none", cursor: "pointer", color: "rgba(250,248,244,0.45)", fontSize: 13, fontFamily: "DM Sans, sans-serif", display: "flex", alignItems: "center", gap: 4 }}>
        ← {role ? "Back" : "Home"}
      </button>

      <div style={{ maxWidth: 480, width: "100%" }}>
        {/* Logo + heading */}
        <div style={{ textAlign: "center", marginBottom: 32 }}>
          <div style={{ width: 48, height: 48, borderRadius: 14, overflow: "hidden", margin: "0 auto 14px", boxShadow: "0 8px 24px rgba(184,146,42,0.3)" }}>
            <img src="/icon-512x512.png" alt="JobSpot GY" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
          </div>
          <div style={{ fontFamily: "Syne, sans-serif", fontSize: 22, fontWeight: 800, color: "#FAF8F4", marginBottom: 4 }}>
            {role ? (mode === "login" ? "Welcome back" : "Create your account") : "Login / Sign Up"}
          </div>
          <p style={{ fontSize: 14, color: "rgba(250,248,244,0.4)" }}>
            {role ? "Signing in as:" : "Choose your account type to continue"}
          </p>
          {role && (
            <div style={{ display: "inline-flex", alignItems: "center", gap: 6, marginTop: 8, background: selectedRole.colorBg, border: `1px solid ${selectedRole.colorBorder}`, borderRadius: 20, padding: "4px 14px" }}>
              <Icon name={selectedRole.icon} size={12} color={selectedRole.color} />
              <span style={{ fontSize: 12, fontWeight: 700, color: selectedRole.color }}>{selectedRole.label}</span>
            </div>
          )}
        </div>

        {/* STEP 1 — Role picker */}
        {!role && (
          <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
            <p style={{ textAlign: "center", fontSize: 11, color: "rgba(250,248,244,0.3)", marginBottom: 4, textTransform: "uppercase", letterSpacing: "0.1em", fontWeight: 600 }}>I am a...</p>
            {roles.map(r => (
              <button key={r.id} onClick={() => { if (r.id === "trades") { onTradesClick(true); } else { setRole(r.id); } }}
                style={{ display: "flex", alignItems: "center", gap: 16, background: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,255,255,0.08)", borderRadius: 14, padding: "18px 20px", cursor: "pointer", textAlign: "left", fontFamily: "DM Sans, sans-serif", width: "100%", transition: "all 0.18s" }}
                onMouseEnter={e => { e.currentTarget.style.background = r.colorBg; e.currentTarget.style.borderColor = r.colorBorder; }}
                onMouseLeave={e => { e.currentTarget.style.background = "rgba(255,255,255,0.03)"; e.currentTarget.style.borderColor = "rgba(255,255,255,0.08)"; }}>
                <div style={{ width: 46, height: 46, borderRadius: 12, background: r.colorBg, border: `1px solid ${r.colorBorder}`, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                  <Icon name={r.icon} size={20} color={r.color} />
                </div>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 15, fontWeight: 700, color: "#FAF8F4", marginBottom: 2 }}>{r.label}</div>
                  <div style={{ fontSize: 13, color: "rgba(250,248,244,0.4)" }}>{r.sub}</div>
                </div>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="rgba(250,248,244,0.25)" strokeWidth="2"><polyline points="9 18 15 12 9 6"/></svg>
              </button>
            ))}
          </div>
        )}

        {/* STEP 2 — Form */}
        {role && role !== "trades" && (
          <>
            {/* Login / Register tabs */}
            <div style={{ display: "flex", background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.08)", borderRadius: 10, padding: 4, marginBottom: 20 }}>
              {["login", "register"].map(m => (
                <button key={m} onClick={() => setMode(m)} style={{ flex: 1, padding: "9px 0", borderRadius: 7, border: "none", cursor: "pointer", fontFamily: "DM Sans, sans-serif", fontSize: 13, fontWeight: 600, transition: "all 0.15s", background: mode === m ? "rgba(255,255,255,0.08)" : "transparent", color: mode === m ? "#FAF8F4" : "rgba(250,248,244,0.35)" }}>
                  {m === "login" ? "Sign In" : "Create Account"}
                </button>
              ))}
            </div>

            <div style={{ background: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,255,255,0.08)", borderRadius: 16, padding: 28 }}>
              <div className="flex-col gap-4">

                {/* EMPLOYER FORM */}
                {role === "employer" && (
                  <>
                    {mode === "register" && (<>
                      <div><div style={labelStyle}>Company Name *</div><input className="input" placeholder="e.g. Republic Bank Guyana" value={empForm.company_name} onChange={e => setEmpForm({ ...empForm, company_name: e.target.value })} style={inputStyle} /></div>
                      <div><div style={labelStyle}>Your Name *</div><input className="input" placeholder="Contact person" value={empForm.contact_name} onChange={e => setEmpForm({ ...empForm, contact_name: e.target.value })} style={inputStyle} /></div>
                      <div><div style={labelStyle}>WhatsApp / Phone *</div><input className="input" placeholder="+592..." value={empForm.contact_phone} onChange={e => setEmpForm({ ...empForm, contact_phone: e.target.value })} style={inputStyle} /></div>
                      <div style={{ height: 1, background: "rgba(255,255,255,0.08)" }} />
                      <div style={{ background: "rgba(184,146,42,0.08)", border: "1px solid rgba(184,146,42,0.2)", borderRadius: 8, padding: "10px 14px" }}>
                        <div style={{ fontSize: 12, fontWeight: 600, color: "#B8922A", marginBottom: 3 }}>Account Recovery</div>
                        <div style={{ fontSize: 12, color: "rgba(250,248,244,0.45)", lineHeight: 1.5 }}>If you're ever locked out, we'll verify you with your phone and secret answer.</div>
                      </div>
                      <div><div style={labelStyle}>Secret Question *</div><select className="input" value={empForm.secret_question} onChange={e => setEmpForm({ ...empForm, secret_question: e.target.value })} style={inputStyle}>{SECRET_QUESTIONS.map(q => <option key={q} value={q} style={{ background: "#1C1917" }}>{q}</option>)}</select></div>
                    </>)}
                    <div><div style={labelStyle}>Email *</div><input className="input" type="email" placeholder="you@company.com" value={empForm.email} onChange={e => setEmpForm({ ...empForm, email: e.target.value })} style={inputStyle} /></div>
                    <div><div style={labelStyle}>Password *</div><div style={{ position: "relative" }}><input className="input" type={showEmpPass ? "text" : "password"} placeholder="Min. 6 characters" value={empForm.password} onChange={e => setEmpForm({ ...empForm, password: e.target.value })} onKeyDown={e => e.key === "Enter" && handleEmployer()} style={{ ...inputStyle, paddingRight: 44 }} /><button type="button" tabIndex={-1} onClick={() => setShowEmpPass(s => !s)} style={eyeBtnStyle}><EyeIcon open={showEmpPass} /></button></div></div>
                    {mode === "register" && <div><div style={labelStyle}>Confirm Password *</div><div style={{ position: "relative" }}><input className="input" type={showEmpConfirm ? "text" : "password"} placeholder="Repeat your password" value={empForm.confirmPassword} onChange={e => setEmpForm({ ...empForm, confirmPassword: e.target.value })} style={{ ...inputStyle, paddingRight: 44, borderColor: empForm.confirmPassword && empForm.confirmPassword !== empForm.password ? "rgba(217,48,37,0.5)" : "rgba(255,255,255,0.1)" }} /><button type="button" tabIndex={-1} onClick={() => setShowEmpConfirm(s => !s)} style={eyeBtnStyle}><EyeIcon open={showEmpConfirm} /></button></div>{empForm.confirmPassword && empForm.confirmPassword !== empForm.password && <div style={{ fontSize: 11, color: "var(--red)", marginTop: 5 }}>Passwords don't match</div>}</div>}
                    {mode === "register" && <div><div style={labelStyle}>Your Answer *</div><div style={{ position: "relative" }}><input className="input" placeholder="Only you should know this" value={empForm.secret_answer} onChange={e => setEmpForm({ ...empForm, secret_answer: e.target.value })} style={{ ...inputStyle, paddingRight: 44 }} /><button type="button" tabIndex={-1} onClick={() => setShowEmpAnswer(s => !s)} style={eyeBtnStyle}><EyeIcon open={showEmpAnswer} /></button></div></div>}
                    {mode === "login" && <div style={{ background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.08)", borderRadius: 8, padding: "10px 14px", display: "flex", alignItems: "center", gap: 8 }}><svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="rgba(250,248,244,0.3)" strokeWidth="2"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg><span style={{ fontSize: 12, color: "rgba(250,248,244,0.45)" }}>Locked out? WhatsApp <strong style={{ color: "rgba(250,248,244,0.85)" }}>+592 672 6828</strong> or email <strong style={{ color: "rgba(250,248,244,0.85)" }}>jobspotgy@gmail.com</strong></span></div>}
                    <button className="btn btn-gold w-full btn-lg" onClick={handleEmployer} disabled={loading}>{loading ? <><div className="spinner" style={{ width: 16, height: 16 }} /> Processing...</> : mode === "login" ? "Sign In" : "Create Account"}</button>
                  </>
                )}

                {/* SEEKER FORM */}
                {role === "seeker" && (
                  <>
                    {mode === "register" && <div><div style={labelStyle}>Your Name *</div><input className="input" placeholder="Full name" value={seekForm.name} onChange={e => setSekForm({ ...seekForm, name: e.target.value })} style={inputStyle} /></div>}
                    <div><div style={labelStyle}>Email *</div><input className="input" type="email" placeholder="you@email.com" value={seekForm.email} onChange={e => setSekForm({ ...seekForm, email: e.target.value })} style={inputStyle} /></div>
                    <div><div style={labelStyle}>Password *</div><div style={{ position: "relative" }}><input className="input" type={showSeekPass ? "text" : "password"} placeholder="Min. 6 characters" value={seekForm.password} onChange={e => setSekForm({ ...seekForm, password: e.target.value })} onKeyDown={e => e.key === "Enter" && handleSeeker()} style={{ ...inputStyle, paddingRight: 44 }} /><button type="button" tabIndex={-1} onClick={() => setShowSeekPass(s => !s)} style={eyeBtnStyle}><EyeIcon open={showSeekPass} /></button></div></div>
                    {mode === "register" && (<>
                      <div><div style={labelStyle}>Confirm Password *</div><div style={{ position: "relative" }}><input className="input" type={showSeekConfirm ? "text" : "password"} placeholder="Repeat your password" value={seekForm.confirmPassword} onChange={e => setSekForm({ ...seekForm, confirmPassword: e.target.value })} style={{ ...inputStyle, paddingRight: 44, borderColor: seekForm.confirmPassword && seekForm.confirmPassword !== seekForm.password ? "rgba(217,48,37,0.5)" : "rgba(255,255,255,0.1)" }} /><button type="button" tabIndex={-1} onClick={() => setShowSeekConfirm(s => !s)} style={eyeBtnStyle}><EyeIcon open={showSeekConfirm} /></button></div>{seekForm.confirmPassword && seekForm.confirmPassword !== seekForm.password && <div style={{ fontSize: 11, color: "var(--red)", marginTop: 5 }}>Passwords don't match</div>}</div>
                      <div><div style={labelStyle}>WhatsApp / Phone *</div><input className="input" placeholder="+592..." value={seekForm.phone} onChange={e => setSekForm({ ...seekForm, phone: e.target.value })} style={inputStyle} /></div>
                      <div style={{ height: 1, background: "rgba(255,255,255,0.08)" }} />
                      <div style={{ background: "rgba(184,146,42,0.08)", border: "1px solid rgba(184,146,42,0.2)", borderRadius: 8, padding: "10px 14px" }}>
                        <div style={{ fontSize: 12, fontWeight: 600, color: "#B8922A", marginBottom: 3 }}>Account Recovery</div>
                        <div style={{ fontSize: 12, color: "rgba(250,248,244,0.45)", lineHeight: 1.5 }}>If you're ever locked out, we'll verify you with your phone and secret answer.</div>
                      </div>
                      <div><div style={labelStyle}>Secret Question *</div><select className="input" value={seekForm.secret_question} onChange={e => setSekForm({ ...seekForm, secret_question: e.target.value })} style={inputStyle}>{SECRET_QUESTIONS.map(q => <option key={q} value={q} style={{ background: "#1C1917" }}>{q}</option>)}</select></div>
                      <div><div style={labelStyle}>Your Answer *</div><div style={{ position: "relative" }}><input className="input" type={showSeekAnswer ? "text" : "password"} placeholder="Only you should know this" value={seekForm.secret_answer} onChange={e => setSekForm({ ...seekForm, secret_answer: e.target.value })} style={{ ...inputStyle, paddingRight: 44 }} /><button type="button" tabIndex={-1} onClick={() => setShowSeekAnswer(s => !s)} style={eyeBtnStyle}><EyeIcon open={showSeekAnswer} /></button></div></div>
                    </>)}
                    {mode === "login" && <div style={{ background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.08)", borderRadius: 8, padding: "10px 14px", display: "flex", alignItems: "center", gap: 8 }}><svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="rgba(250,248,244,0.3)" strokeWidth="2"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg><span style={{ fontSize: 12, color: "rgba(250,248,244,0.45)" }}>Locked out? WhatsApp <strong style={{ color: "rgba(250,248,244,0.85)" }}>+592 672 6828</strong> or email <strong style={{ color: "rgba(250,248,244,0.85)" }}>jobspotgy@gmail.com</strong></span></div>}
                    <button className="btn btn-gold w-full btn-lg" onClick={handleSeeker} disabled={loading}>{loading ? <><div className="spinner" style={{ width: 16, height: 16 }} /> {mode === "register" ? "Creating account..." : "Signing in..."}</> : mode === "login" ? "Sign In" : "Create free account"}</button>
                  </>
                )}

              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

// ============================================================
// MAIN APP ROUTER
// ============================================================
export default function App() {
  const [screen, setScreen] = useState("landing"); // "landing"|"jobs"|"seeker"|"seeker-auth"|"seeker-dashboard"|"auth"|"employer"|"admin"|"terms"
  const [user, setUser] = useState(null);
  const [seekerUser, setSeekerUser] = useState(null);
  const [seekerAuthPrefill, setSeekerAuthPrefill] = useState(null);
  const [loading, setLoading] = useState(true);
  const [deepLinkJobId, setDeepLinkJobId] = useState(null);
  const [tradeFilterDeepLink, setTradeFilterDeepLink] = useState("");
  const [showLoginSheet, setShowLoginSheet] = useState(false);
  const [providerUser, setProviderUser] = useState(null);
  const [openTradesAuth, setOpenTradesAuth] = useState(false);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const hashParams = new URLSearchParams(window.location.hash.replace("#", "?").replace("job=", "job="));
    const jobId = params.get("job") || hashParams.get("job");
    if (jobId) {
      setDeepLinkJobId(jobId);
      setScreen("seeker");
    }
  }, []);

  // Fetches the full service_providers DB row for a trades auth user.
  // The Supabase auth session only carries auth fields (id, email, metadata) —
  // NOT the provider's trades, region, bio, status etc. Without this, the
  // ProviderDashboard receives an empty object and saves blank data back, wiping the profile.
  const loadProviderProfile = async (authUser) => {
    const { data } = await supabase
      .from("service_providers")
      .select("*")
      .ilike("email", authUser.email)
      .maybeSingle();
    // Fall back to auth user if DB row not found (e.g. not yet registered via trades form)
    return data || authUser;
  };

  useEffect(() => {
    loadPlatformSettings();
    supabase.auth.getSession().then(async ({ data: { session } }) => {
      if (session?.user) {
        const role = session.user.user_metadata?.role;
        if (session.user.email === ADMIN_EMAIL) {
          setUser(session.user);
          setScreen("admin");
        } else if (role === "seeker") {
          setSeekerUser(session.user);
          setScreen("seeker-dashboard");
        } else if (role === "trades") {
          const profile = await loadProviderProfile(session.user);
          setProviderUser(profile);
          setScreen("services");
        } else {
          setUser(session.user);
          setScreen("employer");
        }
      }
      setLoading(false);
    });
    const { data: { subscription } } = supabase.auth.onAuthStateChange(async (event, session) => {
      if (session?.user) {
        let userRole = session.user.user_metadata?.role;
        // Handle Google OAuth — assign the role the user selected before redirecting
        if (!userRole) {
          const pendingRole = localStorage.getItem("hirespot_pending_role");
          if (pendingRole) {
            localStorage.removeItem("hirespot_pending_role");
            await supabase.auth.updateUser({ data: { role: pendingRole } });
            userRole = pendingRole;
          }
        }
        // Only navigate on actual sign-in — not on TOKEN_REFRESHED or USER_UPDATED
        // This prevents the app from snapping users back to the dashboard mid-session
        if (event === "SIGNED_IN" || event === "INITIAL_SESSION") {
          if (userRole === "seeker") {
            setSeekerUser(session.user);
            setScreen("seeker-dashboard");
          } else if (userRole === "trades") {
            const profile = await loadProviderProfile(session.user);
            setProviderUser(profile);
            setScreen("services");
          } else {
            setUser(session.user);
            setScreen(session.user.email === ADMIN_EMAIL ? "admin" : "employer");
          }
        } else {
          // For TOKEN_REFRESHED, USER_UPDATED, etc. — update user state only, no navigation
          if (userRole === "seeker") setSeekerUser(session.user);
          else if (userRole === "trades") {
            const profile = await loadProviderProfile(session.user);
            setProviderUser(profile);
          }
          else setUser(session.user);
        }
      } else {
        setUser(null);
        setSeekerUser(null);
        setProviderUser(null);
      }
    });
    return () => subscription.unsubscribe();
  }, []);

  const handleAuth = (u) => {
    const role = u?.user_metadata?.role;
    if (role === "seeker") {
      supabase.auth.signOut();
      return;
    }
    setUser(u);
    setScreen(u.email === ADMIN_EMAIL ? "admin" : "employer");
  };

  const handleSeekerAuth = (u) => {
    setSeekerUser(u);
    setSeekerAuthPrefill(null);
    setScreen("seeker-dashboard");
  };

  const handleSeekerLoginPrompt = (prefill) => {
    setSeekerAuthPrefill(prefill);
    setScreen("unified-auth");
  };

  const handleLogout = async () => {
    await supabase.auth.signOut();
    setUser(null);
    setSeekerUser(null);
    setScreen("landing");
  };

  if (loading) return (
    <div style={{ height: "100vh", background: "#FAF8F4", display: "flex", alignItems: "center", justifyContent: "center" }}>
      <style>{styles}</style>
      <div className="flex-col items-center gap-4">
        <div style={{ width: 48, height: 48, borderRadius: 12, overflow: "hidden" }}>
          <img src="/icon-512x512.png" alt="JobSpot GY" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
        </div>
        <div className="spinner" />
      </div>
    </div>
  );

  // Screens where the bottom nav should NOT show (admin only)
  const hideBottomNav = ["admin"].includes(screen);
  // Which tab is active
  const activeTab = screen === "landing" ? "home"
    : screen === "jobs" ? "jobs"
    : (screen === "seeker" || screen === "seeker-dashboard") ? "matches"
    : (screen === "seeker-auth") ? "matches"
    : screen === "terms" ? "home"
    : screen === "privacy" ? "home"
    : screen === "employer" ? "home"
    : screen === "auth" ? "home"
    : screen === "services" ? "services"
    : "";

  // Profile tab handler — context-aware
  const handleProfileTab = () => {
    if (seekerUser) {
      // Logged in as seeker → go to their dashboard
      setScreen("seeker-dashboard");
    } else if (user) {
      // Logged in as employer → go to account tab
      setScreen("employer");
    } else {
      // Not logged in → go to login screen
      setScreen("unified-auth");
    }
  };

  const activeProfile = (screen === "seeker-dashboard" && seekerUser) || (screen === "employer" && user) || (!seekerUser && !user && (screen === "unified-auth" || screen === "auth"));

  return (
    <>
      <style>{styles}</style>
      {screen === "landing"          && <LandingPage onEmployerClick={() => setScreen(user ? "employer" : "unified-auth")} onSeekerClick={() => setScreen(seekerUser ? "seeker-dashboard" : "seeker")} onTermsClick={() => setScreen("terms")} onPrivacyClick={() => setScreen("privacy")} onAdminClick={() => setScreen("unified-auth")} onJobsClick={() => setScreen("jobs")} onServicesClick={(trade) => { setTradeFilterDeepLink(trade || ""); setScreen("services"); }} deepLinkJobId={deepLinkJobId} onDeepLink={() => setScreen("seeker")} />}
      {screen === "jobs"             && <JobsPage onBack={() => setScreen("landing")} seekerUser={seekerUser} onSeekerLogin={(prefill) => { setSeekerAuthPrefill(prefill); setScreen("unified-auth"); }} onApply={() => setScreen(seekerUser ? "seeker-dashboard" : "seeker")} />}
      {screen === "seeker"           && <JobSeekerPage onBack={() => setScreen("landing")} deepLinkJobId={deepLinkJobId} seekerUser={seekerUser} onSeekerLogin={handleSeekerLoginPrompt} />}
      {screen === "unified-auth"     && <UnifiedAuth prefill={seekerAuthPrefill} onSeekerAuth={(u) => { handleSeekerAuth(u); setSeekerAuthPrefill(null); }} onEmployerAuth={handleAuth} onTradesClick={(withAuth) => { setOpenTradesAuth(!!withAuth); setScreen("services"); }} onBack={() => setScreen("landing")} />}
      {screen === "seeker-auth"      && <UnifiedAuth prefill={seekerAuthPrefill} onSeekerAuth={(u) => { handleSeekerAuth(u); setSeekerAuthPrefill(null); }} onEmployerAuth={handleAuth} onTradesClick={(withAuth) => { setOpenTradesAuth(!!withAuth); setScreen("services"); }} onBack={() => setScreen("seeker")} />}
      {screen === "seeker-dashboard" && seekerUser && <SeekerDashboard user={seekerUser} onLogout={handleLogout} onFindJobs={() => setScreen("seeker")} />}
      {screen === "auth"             && <UnifiedAuth onSeekerAuth={handleSeekerAuth} onEmployerAuth={handleAuth} onTradesClick={(withAuth) => { setOpenTradesAuth(!!withAuth); setScreen("services"); }} onBack={() => setScreen("landing")} />}
      {screen === "employer"         && user && <EmployerDashboard user={user} onLogout={handleLogout} />}
      {screen === "admin"            && user && <AdminPanel user={user} onLogout={handleLogout} />}
      {screen === "terms"            && <TermsPage onBack={() => setScreen("landing")} />}
      {screen === "privacy"          && <PrivacyPage onBack={() => setScreen("landing")} />}
      {screen === "services"         && (
        <SkilledTradesPage
          onBack={() => setScreen("landing")}
          providerUser={providerUser}
          onProviderAuth={(p) => { setProviderUser(p); setOpenTradesAuth(false); }}
          onProviderLogout={() => setProviderUser(null)}
          openAuth={openTradesAuth}
          initialTradeFilter={tradeFilterDeepLink}
        />
      )}
      <Toast />

      {/* ── PERSISTENT MOBILE BOTTOM NAV ── */}
      {!hideBottomNav && (
        <>


          <nav className="app-bottom-nav">
            <button className={`app-nav-tab ${activeTab === "home" ? "active" : ""}`} onClick={() => { setShowLoginSheet(false); setScreen("landing"); }}>
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/><polyline points="9 22 9 12 15 12 15 22"/>
              </svg>
              Home
            </button>
            <button className={`app-nav-tab ${activeTab === "jobs" ? "active" : ""}`} onClick={() => { setShowLoginSheet(false); setScreen("jobs"); }}>
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                <rect x="2" y="7" width="20" height="14" rx="2"/><path d="M16 7V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v2"/>
              </svg>
              Jobs
            </button>
            <button className={`app-nav-tab ${activeTab === "matches" ? "active" : ""}`} onClick={() => { setShowLoginSheet(false); setScreen(seekerUser ? "seeker-dashboard" : "seeker"); }}>
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/>
              </svg>
              Matches
            </button>
            <button className={`app-nav-tab ${activeTab === "services" ? "active" : ""}`} onClick={() => { setShowLoginSheet(false); setScreen("services"); }}>
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                <path d="M14.7 6.3a1 1 0 0 0 0 1.4l1.6 1.6a1 1 0 0 0 1.4 0l3.77-3.77a6 6 0 0 1-7.94 7.94l-6.91 6.91a2.12 2.12 0 0 1-3-3l6.91-6.91a6 6 0 0 1 7.94-7.94l-3.76 3.76z"/>
              </svg>
              Trades
            </button>
            <button className={`app-nav-tab ${activeProfile ? "active" : ""}`} onClick={handleProfileTab}>
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/>
              </svg>
              Profile
            </button>
          </nav>
        </>
      )}
    </>
  );
}
